import {
  Box,
  Editable,
  EditableInput,
  EditablePreview,
  Heading,
  SimpleGrid,
  VStack,
  HStack,
  Image,
  Badge,
  Text,
  Card,
  Checkbox,
  CardBody,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Flex,
  Button,
  Icon,
  IconButton,
  Tooltip,
  useToast,
  keyframes,
  ScaleFade,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Textarea,
  Input,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Collapse,
} from '@chakra-ui/react'
import { MdChevronLeft, MdChevronRight, MdCheckCircle, MdStar, MdLayers, MdBarChart, MdInfo, MdDelete, MdZoomIn, MdZoomOut, MdCenterFocusWeak, MdWarning, MdSwapHoriz } from 'react-icons/md'
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { GuidedTour, TourStep } from './GuidedTour'
import { pathologyDisplayName, blotLabel, isCompleteStripId, isValidStripIdDraft } from '../utils/displayName'
import { resolvePathologyLabel } from '../ImmunoBlotSelection'
import { useTranslation } from 'react-i18next'
import { ManualResultSelector, checkPositifAllowed } from '../../../ManualResultSelector'
import type { ManualResult } from '../../../ManualResultSelector'
import { LuMessageSquare, LuEyeOff, LuTriangleAlert } from 'react-icons/lu'
import { useCHUConfig } from '../../../../contexts/CHUConfigContext'
import { isAIAccessible } from '../../../../config/chuConfig'
import { Line, Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js'
// @ts-ignore - chartjs-plugin-zoom types are built-in but not recognized
import zoomPlugin from 'chartjs-plugin-zoom'

// Bordeaux/Dynablot multitest : le témoin est rangé par pathologie, sous une
// clé tirée du `pathology_label` — relu par OCR à chaque analyse, donc
// instable dans son écriture (« Schisto II IgG », « SchistoII IgG »,
// « SchistoIIIgG » coexistent en base). Ranger sous le libellé brut éclatait
// le témoin d'une même recherche sur plusieurs clés, et il semblait perdu
// d'une analyse à l'autre. La clé passe donc par l'identifiant du catalogue,
// que `resolvePathologyLabel` retrouve malgré ces variantes.
//
// Le « z » de tête, en revanche, est CONSERVÉ : il ne vient pas de l'OCR mais
// de la feuille, et il marque une SÉRIE différente du même test — un autre
// bloc de bandelettes sur la plaque, avec son propre témoin. Le confondre avec
// la série sans « z » faisait servir le témoin de l'une comme référence de
// l'autre, et IRIS concluait sur des bandelettes dont personne n'avait désigné
// le contrôle. (Pour COMPTER les recherches, à l'inverse, les deux séries sont
// bien le même test — voir pathologyGroupKey dans utils/displayName.)
// La fonction doit être IDEMPOTENTE : elle s'applique aux libellés lus sur la
// plaque (« zAspergillus IgG ») comme aux clés déjà rangées en base
// (« z-ldbio-aspergillus-igg »), que l'effet de normalisation repasse à chaque
// chargement. Ne reconnaître la série que devant une lettre faisait retomber
// la clé déjà préfixée sur celle de la série sans « z » — et le témoin de
// l'une écrasait celui de l'autre au rechargement.
const Z_SERIES = /^\s*z(?=[-A-Za-z])/i

const canonicalPathologyKey = (rawLabel: string | null | undefined): string | null => {
  const base = resolvePathologyLabel(rawLabel)?.id ?? rawLabel ?? null
  if (!base) return base
  return Z_SERIES.test(rawLabel ?? '') ? `z-${base}` : base
}

// A confidently-read strip id: one letter + its number (e.g. "I8") — used to
// deduce a gap-filled suggestion for a NEIGHBOUR strip OCR failed on (see
// gapFilledStripIdSuggestions).
const STRIP_ID_SEQ_RE = /^([A-Za-z])(\d+)$/

/**
 * Peak-anchored alignment.
 * Replaces vanilla DTW (which minimises waveform distance and can match the sample's
 * strongest peak to the wrong control peak) with rank-by-intensity anchor matching:
 *   - take the top-N most intense peaks in reference and sample
 *   - pair them rank-by-rank (strongest sample peak ↔ strongest reference peak, etc.)
 *   - sort the anchors by position and interpolate the sample piecewise-linearly
 *     between adjacent anchors so the bands actually align.
 * Falls back to a simple linear remap when there are no peaks to anchor on.
 */
/**
 * Show the "Similarité avec le témoin" / band-ratio comparison panels.
 *
 * Hidden for now: the ratio comparison is a QUALITY indicator only — it is
 * computed from the calibrated band intensities and never feeds interpretBlot,
 * so none of the six LDBIO tests uses it in the final verdict (they all decide
 * on band PRESENCE: minimum_bands / required_band / extreme_bands / major_bands).
 * Displayed next to the verdict it read like a decision criterion, which was
 * misleading. The computation (`manualRatioComparison`) and its UI are kept
 * intact — flip this to `true` to bring them back.
 */
const SHOW_RATIO_COMPARISON = false

const alignProfilesByPeakAnchors = (
  reference: number[],
  sample: number[],
  referencePeaks: number[],
  samplePeaks: number[],
  topN: number = 5,
): number[] => {
  const n = reference.length
  const m = sample.length
  if (n === 0 || m === 0) return [...sample]

  // Filter peaks to valid range and sort by intensity descending
  const sortedRef = referencePeaks
    .filter(p => p >= 0 && p < n)
    .sort((a, b) => (reference[b] ?? 0) - (reference[a] ?? 0))
    .slice(0, topN)
  const sortedSample = samplePeaks
    .filter(p => p >= 0 && p < m)
    .sort((a, b) => (sample[b] ?? 0) - (sample[a] ?? 0))
    .slice(0, topN)

  const k = Math.min(sortedRef.length, sortedSample.length)

  // No usable peaks → simple linear remap so length matches reference
  if (k === 0) {
    const out = new Array<number>(n).fill(0)
    for (let i = 0; i < n; i++) {
      const j = Math.min(m - 1, Math.round((i / Math.max(1, n - 1)) * (m - 1)))
      out[i] = sample[j]
    }
    return out
  }

  // Rank-by-intensity matching, then re-sort anchors by reference position
  const anchors: Array<[number, number]> = []
  for (let i = 0; i < k; i++) anchors.push([sortedRef[i], sortedSample[i]])
  anchors.sort((a, b) => a[0] - b[0])

  // Deduplicate: keep only monotonically increasing sample positions
  const cleaned: Array<[number, number]> = []
  let lastSamplePos = -1
  for (const [r, s] of anchors) {
    if (s > lastSamplePos) {
      cleaned.push([r, s])
      lastSamplePos = s
    }
  }

  // Boundary anchors at 0 and end so the whole reference range gets mapped
  const fullAnchors: Array<[number, number]> = []
  if (cleaned.length === 0 || cleaned[0][0] > 0) fullAnchors.push([0, 0])
  fullAnchors.push(...cleaned)
  if (fullAnchors[fullAnchors.length - 1][0] < n - 1) fullAnchors.push([n - 1, m - 1])

  // Piecewise linear interpolation: for each reference idx, find sample idx via anchors
  const warped = new Array<number>(n).fill(0)
  let seg = 0
  for (let r = 0; r < n; r++) {
    while (seg < fullAnchors.length - 2 && r > fullAnchors[seg + 1][0]) seg++
    const [rL, sL] = fullAnchors[seg]
    const [rR, sR] = fullAnchors[seg + 1]
    const t = rR === rL ? 0 : (r - rL) / (rR - rL)
    const sIdxF = sL + t * (sR - sL)
    const sIdx = Math.max(0, Math.min(m - 1, Math.round(sIdxF)))
    warped[r] = sample[sIdx]
  }
  return warped
}

// DTW (Dynamic Time Warping) algorithm to align two profiles
// Uses Sakoe-Chiba band constraint to prevent excessive warping
// maxWarpRatio: maximum allowed warping as a fraction of profile length (default 15%)
// This prevents aligning distant noise (e.g. 713px) to real peaks (e.g. 420px)
// slopePenalty: penalty for horizontal/vertical moves to reduce plateaus (default 0.1)
// Kept for backward-compatibility / future use; current pipeline calls alignProfilesByPeakAnchors.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const alignProfilesWithDTW = (reference: number[], sample: number[], maxWarpRatio: number = 0.15, slopePenalty: number = 0.1): number[] => {
  const n = reference.length
  const m = sample.length

  // Sakoe-Chiba band width: max number of positions a point can be warped
  const bandWidth = Math.max(10, Math.floor(Math.max(n, m) * maxWarpRatio))

  // Normalize profiles for comparison
  const maxRef = Math.max(...reference)
  const maxSample = Math.max(...sample)
  const normRef = reference.map(v => v / (maxRef || 1))
  const normSample = sample.map(v => v / (maxSample || 1))

  // Build cost matrix with Sakoe-Chiba band constraint
  const dtw: number[][] = Array(n + 1).fill(null).map(() => Array(m + 1).fill(Infinity))
  dtw[0][0] = 0

  // Fill cost matrix — only within the band
  // Add slope penalty to discourage horizontal/vertical moves (which create plateaus)
  for (let i = 1; i <= n; i++) {
    // Sakoe-Chiba: j must stay within bandWidth of the diagonal
    const jCenter = Math.round((i / n) * m)
    const jMin = Math.max(1, jCenter - bandWidth)
    const jMax = Math.min(m, jCenter + bandWidth)

    for (let j = jMin; j <= jMax; j++) {
      const cost = Math.abs(normRef[i - 1] - normSample[j - 1])
      // Add penalty for non-diagonal moves to reduce plateaus
      const diagCost = dtw[i - 1][j - 1]                    // diagonal (preferred)
      const vertCost = dtw[i - 1][j] + slopePenalty         // vertical (creates plateau)
      const horizCost = dtw[i][j - 1] + slopePenalty        // horizontal (creates plateau)

      dtw[i][j] = cost + Math.min(diagCost, vertCost, horizCost)
    }
  }

  // Backtrack to find the optimal warping path
  const path: Array<[number, number]> = []
  let i = n, j = m

  while (i > 0 && j > 0) {
    path.unshift([i - 1, j - 1])
    const diag = dtw[i - 1][j - 1]
    const left = dtw[i][j - 1] + slopePenalty
    const up = dtw[i - 1][j] + slopePenalty

    if (diag <= left && diag <= up) {
      i--; j--
    } else if (left < up) {
      j--
    } else {
      i--
    }
  }

  // Warp sample to align with reference using linear interpolation
  // This reduces plateaus compared to simple averaging
  const warpedSample: number[] = new Array(n).fill(0)

  // Group path points by reference index
  const refToSampleMapping: Map<number, number[]> = new Map()
  for (const [refIdx, sampleIdx] of path) {
    if (!refToSampleMapping.has(refIdx)) {
      refToSampleMapping.set(refIdx, [])
    }
    refToSampleMapping.get(refIdx)!.push(sampleIdx)
  }

  // For each reference position, interpolate the sample values
  for (let k = 0; k < n; k++) {
    const sampleIndices = refToSampleMapping.get(k)
    if (sampleIndices && sampleIndices.length > 0) {
      if (sampleIndices.length === 1) {
        // Single mapping - use the value directly
        warpedSample[k] = sample[sampleIndices[0]]
      } else {
        // Multiple mappings - use weighted average favoring the middle
        // This creates a smoother transition instead of a flat plateau
        const weights = sampleIndices.map((_, idx) => {
          const center = (sampleIndices.length - 1) / 2
          const dist = Math.abs(idx - center)
          return 1 / (1 + dist * 0.5)
        })
        const totalWeight = weights.reduce((a, b) => a + b, 0)
        warpedSample[k] = sampleIndices.reduce((sum, sampleIdx, idx) =>
          sum + sample[sampleIdx] * weights[idx], 0) / totalWeight
      }
    }
  }

  // Post-processing: smooth out remaining plateaus with a small moving average
  // Only smooth regions where 3+ consecutive values are nearly identical
  const smoothed = [...warpedSample]
  const plateauThreshold = 0.001 * maxSample // threshold for detecting plateaus

  for (let k = 1; k < n - 1; k++) {
    const prev = warpedSample[k - 1]
    const curr = warpedSample[k]
    const next = warpedSample[k + 1]

    // Check if this is part of a plateau (nearly identical values)
    if (Math.abs(curr - prev) < plateauThreshold && Math.abs(curr - next) < plateauThreshold) {
      // Look ahead and behind to find non-plateau values for interpolation
      let leftVal = curr, rightVal = curr
      let leftIdx = k, rightIdx = k

      // Find left edge of plateau
      for (let l = k - 1; l >= 0; l--) {
        if (Math.abs(warpedSample[l] - curr) > plateauThreshold) {
          leftVal = warpedSample[l]
          leftIdx = l
          break
        }
      }

      // Find right edge of plateau
      for (let r = k + 1; r < n; r++) {
        if (Math.abs(warpedSample[r] - curr) > plateauThreshold) {
          rightVal = warpedSample[r]
          rightIdx = r
          break
        }
      }

      // Linear interpolation across the plateau
      if (leftIdx !== rightIdx) {
        const t = (k - leftIdx) / (rightIdx - leftIdx)
        smoothed[k] = leftVal + t * (rightVal - leftVal)
      }
    }
  }

  return smoothed
}

// Minimum intensity required for a peak to qualify as the control-line marker,
// as a FRACTION of the profile's own max — not an absolute value. Below this,
// the candidate is treated as noise/artifact (smudges, border pixels, faint
// background) and rejected — preventing the system from mistaking junk near
// the top of the crop for the reference band.
// Was a flat 2000, tuned against Nancy/Amiens's absolute intensity scale.
// Fraction of the profile (from the top) to scan for the control line — the
// manufacturer's own reference band, always printed above the strip's
// printed id and used to align témoin ↔ sample profiles and derive the kDa
// scale. Wide enough to comfortably hold every case measured (see below);
// searching further down risks catching a real sample band instead.
const CONTROL_LINE_SEARCH_FRAC = 0.35

// Helper function to find the control line position — the manufacturer's
// reference band, always the EARLIEST genuine local maximum in the search
// zone. Deliberately does NOT use the AI's global peak list at all: that
// list is filtered by height/prominence relative to the WHOLE profile's
// max, and this band is real but much fainter than the antibody-specific
// bands later on — measured on a real Aspergillus témoin, it sat at ~11% of
// the profile's peak intensity, below the global filter's floor and so
// entirely absent from that list. With no early candidate in the list, the
// old peaks-based logic fell through to whatever WAS in the list early
// enough (a wrong sample band) or a raw-pixel fallback (which, worse, isn't
// even required to be a real feature — it can land on an arbitrary point on
// a rising slope with no bump at all). Scanning the RAW profile directly,
// with intensity/prominence thresholds relative to THIS zone's own max (the
// same technique already used for Toxocara's zone_count peaks below) finds
// the real band regardless of how it compares to the rest of the strip.
// Re-verified across all 32 strips of a real 4-pathology Dynablot report:
// 28 landed in a tight, consistent 1.4-3.9% cluster — exactly what a fixed
// manufacturer print position should look like — with the 2 outliers each
// independently explained (one a témoin already documented elsewhere as
// sitting further in for this specific test; the other a single sample,
// not a systemic miss).
/**
 * First index where the signal leaves the background for good — the physical
 * top of the membrane inside the crop.
 *
 * The control line is printed at a fixed distance from THAT edge, not from the
 * top of the detected box: a plate's boxes carry different amounts of blank
 * margin above the strip (measured on a real Nancy Toxo plate: y_top from 793
 * to 873 px, two strips with ~45 px of empty lead-in). Measuring from the box
 * therefore scatters a fixed print across the plate, which is what made every
 * line need dragging by hand.
 */
export const findMembraneOnset = (profile: number[]): number => {
  if (!profile || profile.length < 10) return 0
  const searchLimit = Math.min(Math.round(profile.length * CONTROL_LINE_SEARCH_FRAC), profile.length - 1)
  if (searchLimit < 4) return 0
  const zoneMax = Math.max(...profile.slice(1, searchLimit + 1))
  const headLen = Math.max(5, Math.round(profile.length * 0.03))
  const background = Math.min(...profile.slice(0, headLen))
  if (!(zoneMax > background)) return 0
  // 12% above background, held for three points: a single noisy pixel is not
  // the membrane starting.
  const threshold = background + 0.12 * (zoneMax - background)
  for (let i = 1; i < searchLimit - 2; i += 1) {
    if (profile[i] > threshold && profile[i + 1] > threshold && profile[i + 2] > threshold) return i
  }
  return 0
}

/** Every credible local maximum of the search zone, earliest first. */
export const controlLineCandidates = (profile: number[]): number[] => {
  const out: number[] = []
  if (!profile || profile.length === 0) return out
  const searchLimit = Math.min(Math.round(profile.length * CONTROL_LINE_SEARCH_FRAC), profile.length - 1)
  const zoneStart = 1
  if (searchLimit - zoneStart < 2) return out
  const zoneMax = Math.max(...profile.slice(zoneStart, searchLimit + 1))
  if (!(zoneMax > 0)) return out
  const minIntensity = zoneMax * 0.05
  const minProminence = zoneMax * 0.02
  const PROM_WIN = 6
  for (let i = zoneStart + 1; i < searchLimit; i += 1) {
    const v = profile[i]
    const isLocalMax = v >= profile[i - 1] && v >= profile[i + 1] && (v > profile[i - 1] || v > profile[i + 1])
    if (!isLocalMax || v < minIntensity) continue
    let leftMin = v
    let rightMin = v
    for (let j = 1; j <= PROM_WIN; j += 1) {
      if (i - j >= 0) leftMin = Math.min(leftMin, profile[i - j])
      if (i + j < profile.length) rightMin = Math.min(rightMin, profile[i + j])
    }
    if (v - Math.max(leftMin, rightMin) >= minProminence) out.push(i)
  }
  return out
}

/**
 * How far a candidate may sit from the plate's consensus and still be taken
 * for the control line. Measured on a real Nancy Toxo plate: at 8 px the wrong
 * candidate still won on two strips (their first bump sits 6 to 8 px past the
 * expected position); from 5 px down the seven strips land within 2 px of each
 * other, and 3, 4 and 5 give an identical result. 4 sits in the middle of that
 * stable range.
 */
const CONTROL_LINE_CONSENSUS_TOLERANCE = 4

export const findControlLinePosition = (profile: number[], peaks: number[]): number => {
  if (!profile || profile.length === 0) return 0

  const searchLimit = Math.min(Math.round(profile.length * CONTROL_LINE_SEARCH_FRAC), profile.length - 1)
  const zoneStart = 1
  if (searchLimit - zoneStart < 2) return 0

  const zoneMax = Math.max(...profile.slice(zoneStart, searchLimit + 1))
  if (zoneMax > 0) {
    const MIN_INTENSITY_FRAC = 0.05
    const MIN_PROMINENCE_FRAC = 0.02
    const PROM_WIN = 6
    const minIntensity = zoneMax * MIN_INTENSITY_FRAC
    const minProminence = zoneMax * MIN_PROMINENCE_FRAC
    for (let i = zoneStart + 1; i < searchLimit; i++) {
      const v = profile[i]
      const isLocalMax = v >= profile[i - 1] && v >= profile[i + 1] && (v > profile[i - 1] || v > profile[i + 1])
      if (!isLocalMax || v < minIntensity) continue
      let leftMin = v
      let rightMin = v
      for (let j = 1; j <= PROM_WIN; j++) {
        if (i - j >= 0) leftMin = Math.min(leftMin, profile[i - j])
        if (i + j < profile.length) rightMin = Math.min(rightMin, profile[i + j])
      }
      const prominence = v - Math.max(leftMin, rightMin)
      if (prominence >= minProminence) return i
    }
  }

  // Nothing in the raw profile qualifies (e.g. a genuinely flat/blank early
  // zone) — fall back to the AI's global peak list, same absolute-floor
  // rule as before, for whatever edge case the local scan above can't help.
  const CONTROL_LINE_MIN_FRACTION = 0.15
  const CONTROL_LINE_MIN_INTENSITY_FLOOR = 150
  const minIntensity = Math.max(CONTROL_LINE_MIN_INTENSITY_FLOOR, CONTROL_LINE_MIN_FRACTION * Math.max(...profile))
  const peaksInZone = peaks.filter((p) => p > 0 && p <= searchLimit && (profile[p] ?? 0) >= minIntensity).sort((a, b) => a - b)
  if (peaksInZone.length > 0) return peaksInZone[0]

  // Nothing intense enough anywhere — no control line in this profile.
  // Return 0 so downstream chart math falls back to absolute pixel coords.
  return 0
}

/**
 * Distance from the membrane onset to the control line, agreed across a plate.
 *
 * All the strips of one plate carry the same manufacturer print, so the plate
 * measures the same quantity N times and the median is a far better estimate
 * than any single reading. Measured on a real Nancy Toxo plate: 6, 7, 7 and
 * 5 px on four strips, against 14, 16 and 28 px on the three the per-strip
 * scan got wrong — the median lands on 7 and rescues them.
 *
 * Returns null when the plate is too small or too inconsistent to vote, and
 * the per-strip scan then stands alone exactly as before.
 */
export const plateControlLineOffset = (profiles: number[][]): number | null => {
  const distances: number[] = []
  profiles.forEach((profile) => {
    if (!profile?.length) return
    const onset = findMembraneOnset(profile)
    const first = controlLineCandidates(profile)[0]
    if (first == null || first <= onset) return
    distances.push(first - onset)
  })
  if (distances.length < 3) return null
  const sorted = [...distances].sort((a, b) => a - b)
  const median = sorted[Math.floor(sorted.length / 2)]
  // A plate whose readings disagree wildly has nothing to agree ON: require
  // half of them to sit within the tolerance of the median before trusting it.
  const agreeing = sorted.filter((d) => Math.abs(d - median) <= CONTROL_LINE_CONSENSUS_TOLERANCE)
  if (agreeing.length * 2 < sorted.length) return null
  return Math.round(agreeing.reduce((a, b) => a + b, 0) / agreeing.length)
}

/**
 * The control line of one strip, using the plate's agreed offset when there is
 * one: the candidate closest to the expected position, or the expected
 * position itself when the band is too faint to have produced a candidate at
 * all — which is precisely the case the per-strip scan cannot handle.
 */
/** How far the real band may sit from the mounting line and still be it. */
const MOUNTING_LINE_SNAP = 14

export const resolveControlLinePosition = (
  profile: number[],
  peaks: number[],
  plateOffset: number | null,
  mountingLineFrac?: number | null,
): number => {
  // The worksheet's mounting line, when the analysis found one, beats every
  // heuristic: the strips are glued onto it precisely so the control band
  // lands there. It gives the expected position; the real band is then the
  // nearest peak, so the line drawn sits on an actual feature rather than on
  // a geometric guess — and a strip glued slightly off still gets its own
  // band rather than the line's position.
  if (mountingLineFrac != null && profile.length > 0) {
    const expected = Math.round(mountingLineFrac * profile.length)
    const near = controlLineCandidates(profile)
      .map((c) => ({ c, d: Math.abs(c - expected) }))
      .filter((x) => x.d <= MOUNTING_LINE_SNAP)
      .sort((a, b) => a.d - b.d)[0]
    return near ? near.c : Math.min(Math.max(expected, 0), profile.length - 1)
  }
  if (plateOffset == null) return findControlLinePosition(profile, peaks)
  const onset = findMembraneOnset(profile)
  const expected = onset + plateOffset
  const candidates = controlLineCandidates(profile)
  let best: number | null = null
  let bestDist = Infinity
  candidates.forEach((c) => {
    const d = Math.abs(c - expected)
    if (d < bestDist) { bestDist = d; best = c }
  })
  if (best != null && bestDist <= CONTROL_LINE_CONSENSUS_TOLERANCE) return best
  return Math.min(expected, Math.max(0, profile.length - 1))
}

// Paired-comparison pathologies (CIP-WB): the strip's own printed identifier
// ("A1", "K17"…) occupies up to ~40% of its raw length (see careflai-ai-1's
// _prep_landscape_id_region), a zone the control-line search window (35%,
// see findControlLinePosition) never reliably clears. Shared by
// comparePairedStrips (exclude that zone from the diff) and interpretBlot's
// kDa estimate below (its far edge doubles as the "start of the usable
// <120kDa zone" anchor) so both agree on exactly where it ends.
const ID_ZONE_RAW_FRAC = 0.42
const idZoneRawEnd = (blot: { profile: number[] }) => Math.round(blot.profile.length * ID_ZONE_RAW_FRAC)

// Helper to get aligned profile data for a blot
const getAlignedProfile = (
  blot: { profile: number[], peaks: number[] },
  plateOffset: number | null = null,
): { profile: number[], peaks: number[], offset: number } => {
  const offset = resolveControlLinePosition(blot.profile, blot.peaks, plateOffset)
  return {
    profile: blot.profile.slice(offset),
    peaks: blot.peaks.filter(p => p >= offset).map(p => p - offset),
    offset
  }
}

// Custom plugin to draw reference band lines
// Helper function to find P30 position dynamically
// P30 is the most intense peak in the middle portion of the profile (after control line)
// Plausible band of the profile for the P30 anchor, as a fraction of the strip
// height. Measured P30 lands at 66–70%; the window is widened well past that so
// an unusual crop still resolves rather than being rejected.
const P30_MIN_FRAC = 0.45
const P30_MAX_FRAC = 0.90

const findP30Position = (
  blot: any,
  minFrac: number = P30_MIN_FRAC,
  maxFrac: number = P30_MAX_FRAC,
  plateOffset: number | null = null,
): number | null => {
  if (!blot || !blot.profile || blot.profile.length === 0) return null
  if (!blot.peaks || blot.peaks.length === 0) return null

  const profile = blot.profile
  const controlLinePos = resolveControlLinePosition(profile, blot.peaks, plateOffset)

  // Search for the anchor (strongest peak) in the region after the control line.
  // "Most intense peak below the control line" alone is not enough: a strong
  // band higher up on the strip wins the vote and the anchor lands far too
  // early, which collapses the whole template projection. Measured on validated
  // strips, P30 sits at 66–70% of the profile regardless of source (PDF or PNG)
  // or resolution, so the search is bounded to a generous slice around that.
  // The window can be narrowed per test (see ANCHOR_WINDOW) when two strong bands
  // would otherwise trade the "most intense" title (e.g. Echinococcus P26-28/P7).
  const searchStart = Math.max(controlLinePos + 50, Math.round(profile.length * minFrac))
  const searchEnd = Math.min(profile.length - 50, Math.round(profile.length * maxFrac))

  let maxIntensity = 0
  let p30Position = null

  // Find the most intense peak in this region
  for (const peakIdx of blot.peaks) {
    if (peakIdx >= searchStart && peakIdx <= searchEnd) {
      if (profile[peakIdx] > maxIntensity) {
        maxIntensity = profile[peakIdx]
        p30Position = peakIdx
      }
    }
  }

  return p30Position
}

// Helper function to get dynamic ROI bounds based on P30 position from POSITIVE CONTROL (témoin)
// ROI = P30 du témoin ± 300 pixels
// If positiveControl is provided, use its P30 position; otherwise fallback to the blot's own P30
const getROIBounds = (
  blot: any,
  positiveControl?: any,
  calibratedPositions?: number[],
): { start: number, end: number } => {
  // Use the blot's profile length for bounds calculation
  const profileLength = blot?.profile?.length || 600

  // Prefer the operator's calibrated band positions — the ROI must follow the
  // region actually calibrated, not an auto-detected P30. Cover all calibrated
  // bands (± margin) while keeping a sensible minimum span around their centre.
  const calib = (calibratedPositions ?? []).filter((p) => Number.isFinite(p))
  if (calib.length > 0) {
    const min = Math.min(...calib)
    const max = Math.max(...calib)
    const center = (min + max) / 2
    const margin = 80
    const start = Math.min(min - margin, center - 300)
    const end = Math.max(max + margin, center + 300)
    return { start: Math.max(0, Math.floor(start)), end: Math.min(profileLength - 1, Math.ceil(end)) }
  }

  // Fallback: P30 of the positive control (or the blot itself), ± 300 px.
  const referenceBlot = positiveControl || blot
  const p30Pos = findP30Position(referenceBlot)
  if (p30Pos === null) {
    const midPoint = Math.floor(profileLength / 2)
    return { start: Math.max(0, midPoint - 300), end: Math.min(profileLength - 1, midPoint + 300) }
  }
  return {
    start: Math.max(0, p30Pos - 300),
    end: Math.min(profileLength - 1, p30Pos + 300)
  }
}

// Helper function to calibrate bands based on P30 reference and other detected peaks
// ── Template-driven band suggestion ─────────────────────────────────────────
// Band positions along an immunoblot strip are a FIXED fractional pattern — only
// the pixel SCALE varies between scans. Measured on 15 validated calibrations
// (12 local + 3 clinical), each band's fraction along the control-line→P30 span
// has a residual SD of ~1 % (≈2 px). So instead of the old "find P30, then assign
// the remaining peaks to bands by MW rank" — which caught the anchor but not the
// scale, letting the far bands (P40/P45) drift — we anchor on TWO reliable
// landmarks (control line = 0, P30 = 1) and place every band at its known
// fraction. Robust to missing / spurious peaks.
//
// Keyed by the config `pathology`. Extend per test as each is measured; move into
// the immunoblot config itself once validated across tests.
// Band positions as a fraction of the control-line → P30 span, measured on
// validated témoins (median over 8 strips per test). P30 is the bottom anchor,
// hence always 1.0. The IgG and IgM values agree closely on the bands they share
// (P33 0.944 both, P31 0.977/0.975, P40 0.863/0.870), which is expected — same
// LDBio membrane geometry, different antibody class.
// The bottom anchor (fraction 1.0) is whatever findP30Position returns — the most
// intense peak in the lower window. For Toxo that's P30; for Toxocara it's P24,
// verified to be the strongest, lowest band on every validated témoin, so the same
// anchor mechanism carries over unchanged. Toxocara reading is driven by P24 & P35
// (P32 is placed too but secondary); its three bands cluster in the last ~12 % of
// the span (the low-MW ladder sits far below the control line), and the P24→P35 gap
// was rock-stable at ~153 px across témoins.
const BAND_FRACTION_TEMPLATES: Record<string, Record<string, number>> = {
  'LDBIO TOXO II IgG CONFIRMATION': { P45: 0.836, P40: 0.863, P33: 0.944, P31: 0.977, P30: 1.0 },
  'LDBIO TOXO II IgM CONFIRMATION': { P40: 0.870, P38: 0.896, P33: 0.944, P31: 0.975, P30: 1.0 },
  'LDBIO TOXOCARA WB IgG CONFIRMATION': { P35: 0.876, P32: 0.909, P24: 1.0 },
  // Schisto reads on the two major zones P30-34 & P22-24 (positivity = either).
  // These are ZONES (a band can sit anywhere in the range), placed at the zone
  // centre here; the anchor P22-24 is the strongest, lowest band — findP30Position
  // lands on it on every témoin. Measured over 4 validated témoins (SD 0.15 %).
  'LDBIO SCHISTO II WB IgG': { 'P30-34': 0.861, 'P22-24': 1.0 },
  // Aspergillus: the anchor (findP30Position = strongest peak) is P18-20, NOT the
  // lowest band — so P16 sits BELOW the anchor with a fraction > 1.0, which the
  // projection handles natively. Measured over 4 validated témoins (SD ≤ 0.2 %).
  'LDBIO ASPERGILLUS WB IgG': { P30: 0.877, P22: 0.953, 'P18-20': 1.0, P16: 1.064 },
  // Echinococcus: anchored on P26-28 (fraction 1.0). Its two genus bands P26-28
  // (~0.63 of the profile) and P7 (~0.85) are both strong extremes that trade the
  // "most intense" title, so the default anchor flips between them. A narrower
  // anchor window (see ANCHOR_WINDOW) restricts the search to exclude P7, making
  // P26-28 the stable anchor. Measured over 4 validated témoins (SD ≤ 0.7 %).
  // Only the two genus markers (P7, P26-28) are suggested — P16 was dropped
  // (checked the live LabImmunoblotConfigs row: only P7/P26-28 have a
  // molecularWeight at all, P16 isn't a band in this config any more), so a
  // "P16" marker was being drawn with no config band behind it to validate or
  // interpret it against — projected into empty profile space with no real
  // peak anywhere nearby on a real témoin, not just imprecise.
  'LDBIO ECHINOCOCCUS WB IgG': { 'P26-28': 1.0, P7: 1.340 },
  // Leishmania reads on P14 OR P16, both low-MW bands sitting well down the
  // strip (notice: "situées en bas de la bandelette, généralement bien
  // isolées") — same major_bands pattern as Schisto. Anchor = P16, NOT P14:
  // findP30Position lands on the single strongest peak in the default
  // 0.45-0.90 window (789) — by far the most intense peak on the whole
  // profile — and P16 (16kDa, heavier) is known to run more intense than P14
  // (14kDa) on this test. Position confirms it too: zoomed pixel-by-pixel into
  // the raw crop on the témoin (case 77daa7da, blot C6), the region hides TWO
  // distinct bands ~23px apart (troughs at ~y787 and ~y810) that chu-bordeaux's
  // peak_detection.distance=45 (careflai-ai-1) can't resolve separately, so
  // they collapse into the one detected peak (789) — which sits right next to
  // the UPPER (closer-to-control-line) sub-band. Every other test's fraction
  // table (Toxo, Schisto, Aspergillus) confirms closer-to-control = heavier,
  // so that upper sub-band — and the peak representing it — is P16, not P14.
  // P14: unlike Aspergillus/Schisto where an unresolved second band has no
  // fraction at all, here it's measurable from the RAW PROFILE VALUES (not
  // just the image) on TWO independent témoins — a genuine local max exists
  // right after the P16 trough, close enough to collapse into the same
  // detected peak, but real: case 77daa7da/C6 (peak 789→trough 800→peak 808,
  // fraction (808-38)/(789-38)=1.025) and case 9c373026/C1 (peak 778→trough
  // 790→peak 799, fraction (799-26)/(778-26)=1.028) — within 0.3% of each
  // other. Never snaps to a real peak on either témoin OR most samples
  // (blot.peaks always merges it into the P16 entry, same underlying
  // detector limitation), so effectiveBandPositions carries it at this
  // projected fraction, unsnapped — same mechanism Aspergillus's P22 already
  // relies on (see suggestBandsFromTemplate's snapped ?? projected
  // fallback). On a SAMPLE, Pass 1 can't snap it either, but Pass 2's crest
  // scan (±12px, see identifyBands) now has its OWN distinct target to
  // measure — separate from P16's — so it can recover a real P14 crest the
  // same way it already recovers Aspergillus's P22.
  'LDBIO LEISHMANIA WB IgG': { P16: 1.0, P14: 1.027 },
  // Trichinella ES: three ES bands in one tight cluster, anchored on P41 — the
  // most intense of the pattern, as the notice says ("P37 et P41 sont les plus
  // intenses, P50 apparaît souvent plus pâle"), and ~2x P50 on both strips of
  // the plate where the two were detected. P37 sits BELOW the anchor (fraction > 1.0, like Aspergillus P16)
  // and has no detected peak of its own on ANY of the plate's three strips —
  // not for lack of signal (its profile value is 45-48 % of the strip max,
  // well clear of the 0.20 height threshold) but because it sits 29 px from
  // P41, under chu-bordeaux's peak_detection.distance=45, so the detector
  // merges the two. It is therefore carried at its projected fraction,
  // unsnapped — the same mechanism as Leishmania P14.
  // Measured on ONE validated témoin, the only Trichinella calibration we
  // have (case d94acea4, strip F20): control line 20, anchor 656, span 636.
  // Re-measure across témoins as they accumulate — every other line of this
  // table rests on 4 to 8 strips.
  'LDBIO TRICHINELLA ES WB IgG': { P50: 0.910, P41: 1.0, P37: 1.046 },
  // Cysticercosis: five bands spread over the whole readable strip, anchored on
  // P6-8 — the lowest band, and the most intense one the anchor window can see
  // on both Cysticercosis strips of the plate. P39 is the faint one the notice warns about: no
  // detected peak on either strip because its profile value reaches 19.2 % of
  // the strip max, just under the 0.20 height threshold. It is carried at its
  // projected fraction, unsnapped, so Pass 2's crest scan gets its own target
  // to measure — which matters here, where the notice needs only 2 of the 5
  // bands for a positive.
  // Measured on ONE validated témoin, the only Cysticercosis calibration we
  // have (case d94acea4, strip E24): control line 20, anchor 757, span 737.
  // P6-8's own manual placement landed 2 px past the anchor peak (759 vs 757);
  // written as exactly 1.0, this table's anchor convention — the projection
  // snaps back onto that peak either way.
  'LDBIO CYSTICERCOSIS WB IgG': { 'P50-55': 0.475, P39: 0.552, 'P23-26': 0.730, P12: 0.917, 'P6-8': 1.0 },
}

// Per-test override for the anchor search window (fraction of the profile height),
// used when the default "strongest peak in 0.45–0.90" doesn't land on a stable
// band. Echinococcus: [0.45, 0.75] let a strong-but-wrong feature well above
// P26-28 (measured on a real témoin: a broad, saturated band around 0.49 of
// the profile — likely the leading edge of the same wide dark region P26-28
// itself sits in, not a separate band) outrank the real anchor purely on raw
// intensity. Raised the floor to 0.58 — comfortably below P26-28's own
// documented ~0.63 but above that 0.49 feature — while the 0.75 ceiling still
// excludes the bottom extreme P7 (~0.85).
const ANCHOR_WINDOW: Record<string, [number, number]> = {
  'LDBIO ECHINOCOCCUS WB IgG': [0.58, 0.75],
  // Trichinella: the strongest peak of the whole strip is NOT a band we read —
  // it's a high-MW band outside the 37-50 kDa reading zone (the notice: a
  // positive can show bands up to 140 kDa, only 37-50 is read), measured at
  // 0.40-0.42 of the profile with up to 1.3x the anchor's own intensity
  // (3466 vs 2625 on strip F21). That sits barely 3 points under the default
  // 0.45 floor, so a slightly taller crop would hand it the anchor and collapse
  // the whole projection. The 0.55 floor clears it by ~120 px while keeping
  // both real candidates — P50 (0.65) and P41 (0.71-0.75) — in the window,
  // where P41 wins on intensity on both strips that show the two.
  'LDBIO TRICHINELLA ES WB IgG': [0.55, 0.90],
  // Cysticercosis: the default window has three rivals for the anchor. P50-55
  // is the strongest feature of the strip (3867, more than 2x P6-8) and sits
  // at 0.39-0.40, only 5 points under the default floor; P23-26 (0.61) and P12
  // (0.76) come within 1.4x of P6-8's intensity. Restricting the window to
  // P6-8's own zone (measured at 0.820 and 0.845 on the plate's two strips)
  // keeps the anchor on the same band on both of them. If a crop ever pushes
  // P6-8 out of this window, no anchor is found and the suggestion falls back
  // to calibrateBandsFromPeaks — a degraded suggestion, which is the failure
  // we want, rather than every band silently projected off the wrong anchor.
  'LDBIO CYSTICERCOSIS WB IgG': [0.78, 0.92],
}

// How the template projection can snap to a real peak instead of trusting the
// fixed fraction blindly — see suggestBandsFromTemplate. A fraction is a
// single number measured across a handful of OTHER témoins; this témoin's
// own control→anchor span won't match those exactly, and since every
// non-anchor band's position is (fraction × THIS span), any anchor-position
// error scales up with how far the band's fraction sits from 1.0 — a band at
// fraction 1.34 amplifies a small anchor error 34% more than the anchor
// itself has. Measured on a real Echinococcus témoin: the anchor (P26-28)
// landed 68px from the fraction table's own ~63%-of-profile estimate (no
// closer real peak existed), and blindly projecting P7 from it (fraction
// 1.340) compounded that into an 863px prediction vs the real P7 peak at
// 804 — a 59px miss purely from amplification, not a wrong fraction.
const BAND_SNAP_TOLERANCE_FRAC = 0.12

// Pathologies that use suggestBandsOrderPreserving (below) instead of the
// greedy claim-based snap further down.
//
// Toxo II IgG/IgM went through FOUR attempts chasing real témoins from a
// CHRU Nancy pilot: a per-pathology tolerance, then per-band, then an
// order-violation guard, then dropping snapping entirely (pure projection)
// — each fixed one real case while breaking another, or (pure projection)
// left a band sitting in a signal-less trough instead of on any real peak.
// The common flaw: every attempt decided ONE band at a time, so a band
// could only use whichever peaks were left over after its neighbours had
// already (greedily) claimed theirs — never reconsidering that trade.
// suggestBandsOrderPreserving instead finds the assignment of ALL bands to
// peaks, respecting the template's fraction order, that minimises TOTAL
// distance across every band at once — so P45 and P40 (a close, often
// barely-resolved pair per the manufacturer's own reference image) can each
// land on one of two real neighbouring peaks instead of one of them always
// winning and the other falling back to raw. Checked against the same
// 20-case batch: 1.4px mean / 13.4px max error, same as the best
// single-band attempt, with zero order violations and (verified on the
// case that exposed every earlier attempt's flaw) no band left off every
// real peak in the image.
const ORDER_PRESERVING_PATHOLOGIES = new Set<string>([
  'LDBIO TOXO II IgG CONFIRMATION',
  'LDBIO TOXO II IgM CONFIRMATION',
])

// Cost, in px, charged for leaving a band on its raw (un-snapped) template
// projection instead of a real peak — the tie-breaker suggestBandsOrderPreserving
// uses to decide whether a so-so nearby peak is worth taking. Tuned against
// the Toxo II batch above: low enough that a peak within ~20px (P45/P40's
// typical spacing when they're a close, barely-resolved pair) is preferred
// over skipping, high enough that a distant, clearly-wrong peak (40px+ —
// the kind that used to get grabbed and block a neighbour's much better
// match) is correctly left for skip instead.
const ORDER_PRESERVING_SKIP_COST_PX = 25

/**
 * Assigns each band in `template` (sorted by ascending fraction, i.e.
 * ascending expected position) to either a real peak or its own raw
 * projection, choosing the combination that minimises TOTAL distance across
 * every band at once while keeping assigned peaks in the same order as the
 * bands themselves — a peak used by a lower-fraction band can never be
 * larger than one used by a higher-fraction band. Classic bounded
 * knapsack-style DP over (band index × peak index), tiny in practice (a
 * handful of bands, a dozen-ish peaks).
 *
 * The anchor band (fraction 1.0) is NOT included here — it's `p30` itself,
 * fixed, and excluded from the peak pool so nothing else can double-claim it.
 */
function suggestBandsOrderPreserving(
  template: Record<string, number>,
  controlLine: number,
  p30: number,
  span: number,
  allPeaks: number[],
): Map<string, number> {
  const anchorBand = Object.keys(template).find((b) => template[b] === 1)
  const bands = Object.keys(template)
    .filter((b) => b !== anchorBand)
    .sort((a, b) => template[a] - template[b])
  const projections = bands.map((b) => controlLine + template[b] * span)
  const peaks = allPeaks.filter((p) => p !== p30).sort((a, b) => a - b)
  const tolerance = BAND_SNAP_TOLERANCE_FRAC * span

  const n = bands.length
  const m = peaks.length
  const cost = (i: number, j: number) => Math.abs(peaks[j] - projections[i])

  // dp[i][j] = min total cost assigning bands[0..i) using only peaks[0..j).
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(Infinity))
  const choice: Array<Array<'skip' | 'pass' | 'take'>> = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill('skip'),
  )
  for (let j = 0; j <= m; j++) dp[0][j] = 0
  for (let i = 1; i <= n; i++) {
    for (let j = 0; j <= m; j++) {
      const skipCost = dp[i - 1][j] + ORDER_PRESERVING_SKIP_COST_PX
      const passCost = j > 0 ? dp[i][j - 1] : Infinity
      const c = j > 0 ? cost(i - 1, j - 1) : Infinity
      const takeCost = j > 0 && c <= tolerance ? dp[i - 1][j - 1] + c : Infinity
      let best = skipCost
      let how: 'skip' | 'pass' | 'take' = 'skip'
      if (passCost < best) { best = passCost; how = 'pass' }
      if (takeCost < best) { best = takeCost; how = 'take' }
      dp[i][j] = best
      choice[i][j] = how
    }
  }

  const assigned: Array<number | null> = new Array(n).fill(null)
  let i = n
  let j = m
  while (i > 0) {
    const how = choice[i][j]
    if (how === 'skip') { assigned[i - 1] = null; i -= 1 }
    else if (how === 'pass') { j -= 1 }
    else { assigned[i - 1] = j - 1; i -= 1; j -= 1 }
  }

  const out = new Map<string, number>()
  bands.forEach((band, idx) => {
    const peakIdx = assigned[idx]
    out.set(band, peakIdx != null ? peaks[peakIdx] : projections[idx])
  })
  if (anchorBand) out.set(anchorBand, p30)
  return out
}

// A sample peak landing within this fraction of the snap tolerance of its
// projected target is a near-exact positional hit — a coincidental noise peak
// snapping this tight is unlikely, so the position itself is trusted as strong
// evidence even when the peak's own signal is weak. Used in two places that
// must agree on the same definition of "tight": identifyBands' Pass 1 (relaxes
// the absolute-prominence floor for such a peak) and interpretBlot's
// major_bands/extreme_bands criteria (treats a tight-but-weak match as
// PRESENT per the manufacturer's own binary notice rule — "présence de la
// bande = positif", no intensity threshold — for 2-band tests like Schisto,
// Echinococcus and Leishmania that can never fall back on the multi-band
// similarity check a genuine low-titer positive would otherwise rely on).
const TIGHT_SNAP_FRAC = 0.3

/**
 * Smallest template fraction a `pin` may carry.
 *
 * A pin rebuilds the whole span by DIVIDING by its own fraction, so the closer
 * the pinned band sits to the control line, the more a one-pixel error in its
 * placement is multiplied across the others. At 0.475 (Cysticercosis P50-55,
 * the lowest fraction any template uses) a 3 px correction already moves the
 * farthest band by 6 px; below that the leverage stops being worth it and the
 * ordinary two-anchor projection is the safer answer.
 */
const MIN_PIN_FRACTION = 0.4

const suggestBandsFromTemplate = (
  blot: any,
  immunoblotConfig: any,
  plateOffset: number | null = null,
  /** A band the operator has placed by hand: rebuild the span around it
      instead of looking for the anchor peak. */
  pin?: { band: string; position: number } | null,
): Map<string, number> | null => {
  const template = immunoblotConfig?.pathology ? BAND_FRACTION_TEMPLATES[immunoblotConfig.pathology] : null
  if (!template || !blot?.profile?.length || !blot?.peaks?.length) return null
  // With a pin, the control line is taken from the worksheet's mounting line
  // when the analysis found one. The reason is measured, not stylistic: on the
  // Toxo G témoin of case 8549d366 the operator placed P45 at 531 and P40 at
  // 547 by hand, and the template's own fractions (0.836 and 0.863, against
  // P30 at 619) can only be satisfied by a control line at 82 and 93 — while
  // the per-strip heuristic put it at 7, on the edge of the crop rather than
  // on the printed band. Pivoting a whole pattern on a point that is 80 px out
  // spreads that error over every band; the ordinary suggestion path is left
  // exactly as it is, since its templates were calibrated against it.
  // LA LIGNE DE MONTAGE SERT AUSSI SANS ÉPINGLE.
  //
  // Elle n'était consultée qu'avec une épingle, alors que `getControlLine` —
  // la ligne effectivement DESSINÉE sur la bandelette — la passe sans
  // condition. Le gabarit se projetait donc depuis une ligne que l'opérateur
  // ne voyait nulle part : sur le cas 01e07b7b (Toxo II IgM, Nancy), 16 px
  // côté gabarit contre 71 côté écran.
  //
  // Mesuré sur les 635 bandelettes en base : 43 portent une ligne de montage,
  // dont 42 s'accrochent à un maximum local réel du profil — donc à une vraie
  // bande imprimée — et une seule retombe sur la position géométrique.
  // L'heuristique par plaque, elle, ne tombait d'accord que sur 12 des 43 ; sur
  // les 31 autres elle se posait bien plus haut, souvent à 7-50 px du bord du
  // recadrage, là où il n'y a aucune bande. 26 bandelettes voient leurs
  // positions bouger, de 6 px en médiane (85 px au pire, un Aspergillus) ;
  // aucune suggestion n'apparaît ni ne disparaît.
  const controlLine = resolveControlLinePosition(
    blot.profile, blot.peaks, plateOffset, blot.control_line_frac,
  )
  if (controlLine <= 0) return null

  // The span the whole template is projected onto. Normally it runs from the
  // control line to the anchor peak the profile itself gives up. With a pin,
  // the operator has just told us where ONE band really is, which fixes the
  // span on its own: the band sits at `fraction x span` from the control line,
  // so the span is its distance divided by its fraction. Every other band then
  // follows from the same table — that is the point of placing one band by
  // hand rather than five.
  let span: number
  const pinFraction = pin ? template[pin.band] : undefined
  if (pin && pinFraction != null) {
    if (pinFraction < MIN_PIN_FRACTION || pin.position <= controlLine) return null
    span = (pin.position - controlLine) / pinFraction
  } else {
    const win = immunoblotConfig?.pathology ? ANCHOR_WINDOW[immunoblotConfig.pathology] : undefined
    const p30 = win ? findP30Position(blot, win[0], win[1]) : findP30Position(blot)
    // Both anchors required, in the right order (control line above the anchor).
    if (p30 == null || p30 <= controlLine) return null
    span = p30 - controlLine
  }
  const anchor = controlLine + span

  if (immunoblotConfig?.pathology && ORDER_PRESERVING_PATHOLOGIES.has(immunoblotConfig.pathology)) {
    const ordered = suggestBandsOrderPreserving(template, controlLine, anchor, span, blot.peaks)
    if (ordered && pin) ordered.set(pin.band, pin.position)
    return ordered
  }

  // Claim closest-match-first, not template declaration order — two bands'
  // projections can both land nearest the SAME real peak (measured: P16 and
  // P7's projections were both closest to the one peak that was actually
  // P7, and P16 — processed first purely by object key order — grabbed it,
  // leaving P7 to fall back to its own, much worse, unsnapped projection).
  // Sorting by ascending distance-to-nearest-available-peak first lets the
  // band with the near-exact match claim it before a farther one can.
  const nearestAvailableDist = (projected: number, used: Set<number>): number => {
    let best = Infinity
    for (const peak of blot.peaks) {
      if (used.has(peak)) continue
      best = Math.min(best, Math.abs(peak - projected))
    }
    return best
  }

  // The pinned band is not up for projection: the operator has placed it, so it
  // starts out assigned, its peak is spoken for, and the order check below
  // measures every other band against it.
  const projections = Object.keys(template)
    .filter((band) => band !== pin?.band)
    .map((band) => ({ band, projected: controlLine + template[band] * span }))

  const usedPeaks = new Set<number>()
  const out = new Map<string, number>()
  if (pin && template[pin.band] != null) {
    out.set(pin.band, pin.position)
    usedPeaks.add(Math.round(pin.position))
  }
  // Re-rank after every assignment (not a one-shot sort) — claiming a peak
  // changes which peak is nearest-available for everyone still unassigned.
  const remaining = [...projections]
  while (remaining.length > 0) {
    remaining.sort(
      (a, b) => nearestAvailableDist(a.projected, usedPeaks) - nearestAvailableDist(b.projected, usedPeaks),
    )
    const { band, projected } = remaining.shift()!
    const tolerance = BAND_SNAP_TOLERANCE_FRAC * span
    const bandFraction = template[band]
    let snapped: number | null = null
    let bestDist = Infinity
    for (const peak of blot.peaks) {
      if (usedPeaks.has(peak)) continue
      const dist = Math.abs(peak - projected)
      if (dist > tolerance || dist >= bestDist) continue
      // Reject a candidate that would invert the template's own fraction
      // ordering against a band already assigned this pass — e.g. P45
      // (fraction 0.836) landing at a LARGER position than an already-
      // snapped P40 (0.863) is biologically backwards, not a coincidence of
      // both being real nearby peaks. Measured on a real témoin: P40
      // correctly snapped to peak 1032, then P45 still snapped to peak 1065
      // (> 1032) purely because it was within tolerance — nothing checked
      // that 0.836 < 0.863 should mean P45 stays BELOW P40. Falls back to
      // the raw projection instead, which is always order-consistent (it's
      // linear in the fraction, so two projections can never invert).
      const violatesOrder = Array.from(out.entries()).some(([otherBand, otherPos]) => {
        const otherFraction = template[otherBand]
        return (bandFraction < otherFraction && peak > otherPos)
          || (bandFraction > otherFraction && peak < otherPos)
      })
      if (violatesOrder) continue
      bestDist = dist
      snapped = peak
    }
    const position = snapped ?? projected
    if (snapped != null) usedPeaks.add(snapped)
    out.set(band, position)
  }
  return out
}

const calibrateBandsFromPeaks = (blot: any, immunoblotConfig: any): Map<string, number> | null => {
  if (!blot || !blot.peaks || blot.peaks.length === 0 || !immunoblotConfig) return null

  const bandPositions = new Map<string, number>()

  // ROI (Region of Interest) dynamically based on P30 position ± 300 pixels
  const { start: ROI_START, end: ROI_END } = getROIBounds(blot)

  // Filter peaks to only include those in the ROI
  const peaksInROI = blot.peaks.filter((peak: number) => peak >= ROI_START && peak <= ROI_END)

  if (peaksInROI.length === 0) return null

  // 1. Find P30 position (reference point - most intense peak between 400-500)
  const p30Position = findP30Position(blot)
  if (!p30Position) return null

  bandPositions.set('P30', p30Position)

  // 2. Filter peaks that are to the left of P30 (higher MW) and within ROI
  const peaksBeforeP30 = peaksInROI
    .filter((peak: number) => peak < p30Position)
    .sort((a: number, b: number) => a - b) // Sort left to right (position croissante)

  // 3. Map detected peaks to bands — derived from immunoblotConfig, sorted by descending MW
  // Exclude P30 and P31 (handled separately), keep only bands with MW > 31
  const bandsAboveP31 = (immunoblotConfig?.bands?.specific || [])
    .filter((b: any) => b.molecularWeight > 31)
    .sort((a: any, b: any) => b.molecularWeight - a.molecularWeight) // highest MW first (leftmost)

  // Assign left-to-right peaks to bands sorted by descending MW
  peaksBeforeP30.forEach((peakPos: number, idx: number) => {
    if (idx < bandsAboveP31.length) {
      bandPositions.set(bandsAboveP31[idx].name, peakPos)
    }
  })

  // 4. Calculate P31 position by interpolation (it's a shoulder of P30, not a distinct peak)
  // P31 is 1 kDa above P30, so very close
  // If we have at least one other band for calibration, interpolate
  if (bandPositions.size > 1) {
    // Find the closest band above P30 to use for calibration
    let refPosition = null
    let refMW = null

    const bandEntries = Array.from(bandPositions.entries())
    for (const [name, position] of bandEntries) {
      if (name !== 'P30' && position < p30Position) {
        refPosition = position
        // Find MW for this band
        const refBand = immunoblotConfig.bands.specific.find((b: any) => b.name === name)
        if (refBand) {
          refMW = refBand.molecularWeight
          break // Take the first one (closest)
        }
      }
    }

    if (refPosition && refMW) {
      // Linear interpolation between P30 (30 kDa) and reference band
      const pixelsPerKDa = (refPosition - p30Position) / (refMW - 30)
      const p31Position = p30Position + (31 - 30) * pixelsPerKDa

      // Only add if within reasonable bounds
      if (p31Position >= ROI_START && p31Position <= ROI_END) {
        bandPositions.set('P31', p31Position)
      }
    }
  }

  return bandPositions.size > 0 ? bandPositions : null
}

// Suggestion cascade: template (two-anchor) first, peak-ranking as the fallback
// when there's no template or an anchor is missing (e.g. a faint control line).
const suggestBands = (blot: any, immunoblotConfig: any): Map<string, number> | null =>
  suggestBandsFromTemplate(blot, immunoblotConfig) ?? calibrateBandsFromPeaks(blot, immunoblotConfig)

/**
 * Score a lane as a POSITIVE CONTROL candidate, reusing the two-anchor band
 * template. A real témoin shows the FULL reference pattern, so we project the
 * template (control line → P30) and count how many expected bands actually land
 * on a detected peak. The control line and P30 anchors match by construction;
 * the discriminating signal is the middle bands (P45/P40/P33/P31) — present on a
 * positive control, absent on a negative sample. This is far more specific than
 * "most intense peaks anywhere", which a noisy lane can win.
 *
 * Returns null when the template can't be applied (no template for this test or a
 * missing anchor), so the caller can fall back to a generic heuristic.
 */
export const scoreControlCandidate = (
  blot: any,
  immunoblotConfig: any,
): { matched: number; intensity: number; total: number } | null => {
  const positions = suggestBandsFromTemplate(blot, immunoblotConfig)
  if (!positions || positions.size === 0) return null
  const profile: number[] = blot?.profile ?? []
  const peaks: number[] = blot?.peaks ?? []
  if (!peaks.length) return null
  // A band counts as "present" when a detected peak sits within ~2% of the strip
  // height (min 10px) of its projected position.
  const tol = Math.max(10, profile.length * 0.02)
  let matched = 0
  let intensity = 0
  positions.forEach((pos) => {
    let bestDist = Infinity
    let bestIdx = -1
    for (const p of peaks) {
      const d = Math.abs(p - pos)
      if (d < bestDist && d <= tol) { bestDist = d; bestIdx = p }
    }
    if (bestIdx >= 0) {
      matched += 1
      intensity += profile[bestIdx] ?? 0
    }
  })
  // `total` = template band count, so the caller can threshold relative to the
  // test (a 3-band Toxocara template can't demand the same 3 hits as a 5-band Toxo one).
  return { matched, intensity, total: positions.size }
}

// ── Echinococcus species differentiation ─────────────────────────────────────
// LDBio ECHINOCOCCUS WB IgG notice vs17, § Interprétation II.
// Genus positivity = at least one extreme band (P7 and/or P26-28). The species
// is then read from the intermediate bands the operator has placed:
//   - large diffuse 17 kDa (P17-large)               → E. granulosus
//   - fine bands 16 / 17-fine / 18 kDa               → E. multilocularis
// The five profiles P1–P5 map these combinations. P4 and P5 (extreme bands only,
// no intermediate band) cannot differentiate the two species (≈30% of cases).
// Returns null when the test isn't Echinococcus (no `interpretation.profiles`)
// or when the sample isn't even genus-positive yet.
export type EchinococcusProfile = {
  profile: 'P1' | 'P2' | 'P3' | 'P4' | 'P5'
  species: 'E. granulosus' | 'E. multilocularis' | 'indéterminé'
  differentiable: boolean
  note?: string
}

export const getEchinococcusSpeciesProfile = (
  placed: Set<string>,
  config: any,
): EchinococcusProfile | null => {
  if (!config?.interpretation?.profiles) return null
  const has = (n: string) => placed.has(n)
  const hasP7 = has('P7')
  const hasHigh = has('P26-28')

  // P16/P17-fine/P17-large/P18 aren't in the pathology's official band catalogue
  // (only P7/P26-28 are — they have no témoin calibration fraction, see
  // BAND_FRACTION_TEMPLATES, so they're never offered in the "+ Ajouter une
  // bande" dropdown). The operator's only way to record them is the free-text
  // rename on a manually-placed band, so matching on the exact canonical
  // token ('P17-large') silently failed on a real case (D11, Echinococcus)
  // where the biologist typed the LDBio notice's own wording verbatim,
  // "P17 (large diffuse)" — never shown anywhere as the string to type.
  // Token-match instead of exact-match so any reasonable free-text variant
  // the notice's own vocabulary would produce still resolves.
  const normalized = Array.from(placed).map((n) => n.toLowerCase())
  const hasTokens = (...tokens: string[]) => normalized.some((n) => tokens.every((t) => n.includes(t)))
  const hasLargeDiffuse = hasTokens('p17', 'large') || hasTokens('p17', 'diffus') // E. granulosus marker
  const hasFine = hasTokens('p16') || hasTokens('p18') || hasTokens('p17', 'fine') // E. multilocularis markers

  // Not genus-positive yet. If the operator has already placed species bands,
  // surface an informative state rather than hiding silently (the notice
  // requires an extreme band 7/26-28 before a species can be concluded).
  if (!hasP7 && !hasHigh) {
    if (hasLargeDiffuse || hasFine) {
      return {
        profile: hasFine ? 'P3' : 'P2',
        species: 'indéterminé',
        differentiable: false,
        note: 'Placez une bande de genre (7 ou 26-28 kDa) pour conclure sur l’espèce.',
      }
    }
    return null
  }

  // Both species families placed → do not call; ask for a visual arbitration
  // (notice remark 3: the wide 16 kDa can be mistaken for the 17 kDa diffuse).
  if (hasLargeDiffuse && hasFine) {
    return {
      profile: 'P3',
      species: 'indéterminé',
      differentiable: false,
      note: 'Bandes des deux espèces présentes — trancher visuellement (bande 16 large vs 17 large diffuse).',
    }
  }
  if (hasFine) return { profile: 'P3', species: 'E. multilocularis', differentiable: true }
  if (hasLargeDiffuse) return { profile: 'P2', species: 'E. granulosus', differentiable: true }

  // No intermediate species band placed.
  if (hasP7 && !hasHigh) return { profile: 'P1', species: 'E. granulosus', differentiable: true }
  if (!hasP7 && hasHigh)
    return {
      profile: 'P4',
      species: 'indéterminé',
      differentiable: false,
      note: 'Bande 26-28 isolée — espèce non différenciable.',
    }
  return {
    profile: 'P5',
    species: 'indéterminé',
    differentiable: false,
    note: 'Bandes 7 + 26-28 sans bande intermédiaire — espèce non différenciable.',
  }
}

// ── Baseline subtraction ─────────────────────────────────────────────────────
// Gentle background removal using a sliding-minimum envelope (lower envelope of
// the profile) followed by box smoothing to avoid sharp artefacts.
//
// Why gentle:
//  - Window of 40 px >> band width (~10-20 px) → only slow variations are removed
//  - Box-smooth of the envelope prevents the subtraction from creating dips at
//    band positions (avoids the "negative valley" artefact)
//  - Clipped at 0 → no negative values
//
// After correction, prominence of real peaks increases while flat background
// areas go to ≈ 0, making the prominence ratio between control and sample
// more reliable.

const boxSmooth = (arr: number[], radius: number): number[] => {
  const n = arr.length
  const result = new Array<number>(n)
  let sum = 0, wCount = 0
  for (let j = 0; j <= Math.min(radius, n - 1); j++) { sum += arr[j]; wCount++ }
  for (let i = 0; i < n; i++) {
    result[i] = sum / wCount
    const removeIdx = i - radius
    const addIdx = i + radius + 1
    if (removeIdx >= 0) { sum -= arr[removeIdx]; wCount-- }
    if (addIdx < n) { sum += arr[addIdx]; wCount++ }
  }
  return result
}

// DTW-style band-to-peak alignment used when neither the témoin nor the sample
// has a usable control-line offset (so offset-projection is not an option).
//
// Inputs:
//   - calibratedBands: the user-placed bands on the témoin {position, intensity}
//   - samplePeaks: detected peaks of the sample (raw pixel positions)
//   - sampleProfile: baseline-corrected sample profile (intensities at each pixel)
//   - zoneMargin: pixel margin around the calibration zone to consider on the sample
//
// Strategy:
//   1) Restrict sample peaks to the "calibration zone" — same pixel range as the
//      témoin's calibrated bands, ± zoneMargin. This avoids matching to peaks
//      from far above/below the bands of interest.
//   2) Match top-K calibrated bands to top-K sample peaks by intensity rank.
//   3) Sort matches by témoin position; drop crossings (anchors that break
//      monotonic sample position) so the warp stays consistent.
//   4) Piecewise-linear interpolate between anchors to project each calibrated
//      band's position onto the sample's pixel coordinates.
//
// Returns Map<bandName, samplePeakPosition>.
const dtwBandsToSamplePeaks = (
  calibratedBands: Map<string, { position: number; intensity: number }>,
  samplePeaks: number[],
  sampleProfile: number[],
  zoneMargin: number = 60,
): Map<string, number> => {
  const out = new Map<string, number>()
  if (!calibratedBands.size || !samplePeaks.length || !sampleProfile.length) return out

  // Calibration zone bounds on the témoin
  const calibPositions = Array.from(calibratedBands.values()).map(d => d.position)
  const zoneMin = Math.min(...calibPositions) - zoneMargin
  const zoneMax = Math.max(...calibPositions) + zoneMargin

  // Filter sample peaks to those falling within [zoneMin, zoneMax] (clamped to profile)
  const lo = Math.max(0, Math.floor(zoneMin))
  const hi = Math.min(sampleProfile.length - 1, Math.ceil(zoneMax))
  const zonePeaks = samplePeaks.filter(p => p >= lo && p <= hi)
  if (zonePeaks.length === 0) return out

  // Rank by intensity on each side
  const bandsByIntensity = Array.from(calibratedBands.entries())
    .sort((a, b) => b[1].intensity - a[1].intensity)
  const peaksByIntensity = [...zonePeaks]
    .sort((a, b) => (sampleProfile[b] ?? 0) - (sampleProfile[a] ?? 0))

  const k = Math.min(bandsByIntensity.length, peaksByIntensity.length)
  if (k === 0) return out

  // Pair top-K, then re-sort by témoin band position
  const pairs: Array<{ bandName: string; bandPos: number; samplePos: number }> = []
  for (let i = 0; i < k; i++) {
    const [bandName, bandData] = bandsByIntensity[i]
    pairs.push({ bandName, bandPos: bandData.position, samplePos: peaksByIntensity[i] })
  }
  pairs.sort((a, b) => a.bandPos - b.bandPos)

  // Drop crossings — keep only anchors with monotonically increasing sample positions
  const monotonic: typeof pairs = []
  let lastSamplePos = -Infinity
  for (const p of pairs) {
    if (p.samplePos > lastSamplePos) {
      monotonic.push(p)
      lastSamplePos = p.samplePos
    }
  }

  // Each anchored band gets its directly matched sample peak. Bands without a
  // direct anchor are deliberately LEFT UNASSIGNED rather than interpolated /
  // snapped to a neighbour — interpolation produced the case where every band
  // collapsed onto the same single sample peak. If there's no real peak for
  // a given band, the downstream filters can correctly reject it.
  //
  // Two anchors max → at most two bands are projected. That matches the actual
  // signal on the sample instead of inventing matches.
  for (const p of monotonic) out.set(p.bandName, p.samplePos)

  return out
}

// Prominence = how much a peak rises above its surrounding local baseline.
// More robust than raw intensity: normalises for background level.
const computePeakProminence = (profile: number[], idx: number, halfWin: number = 14): number => {
  const val = profile[idx]
  if (val == null) return 0
  let leftMin = val
  let rightMin = val
  for (let i = Math.max(0, idx - halfWin); i < idx; i++) {
    if (profile[i] < leftMin) leftMin = profile[i]
  }
  for (let i = idx + 1; i < Math.min(profile.length, idx + halfWin + 1); i++) {
    if (profile[i] < rightMin) rightMin = profile[i]
  }
  return Math.max(0, val - Math.max(leftMin, rightMin))
}

const subtractBaseline = (profile: number[], halfWin?: number): number[] => {
  const n = profile.length
  // Adaptive window: on short (low-resolution) profiles a fixed ±40 px window
  // sits on the band flanks and over-subtracts, crushing faint bands. A wider
  // window reaches true background (sliding-min is monotone → never subtracts
  // MORE), so we widen it for short profiles. The formula returns 40 for
  // profiles ≥ 600 px, so long/high-res strips (Toxo/Echino ~841 px, already
  // validated) are byte-for-byte unchanged; only short profiles get a gentler
  // window (≈100 px at ~234 px).
  const hw = halfWin ?? Math.min(100, Math.max(40, Math.round(24000 / Math.max(1, n))))
  // Step 1: sliding minimum → lower envelope
  const minEnvelope = new Array<number>(n)
  for (let i = 0; i < n; i++) {
    let lo = Infinity
    for (let j = Math.max(0, i - hw); j <= Math.min(n - 1, i + hw); j++) {
      if (profile[j] < lo) lo = profile[j]
    }
    minEnvelope[i] = lo
  }
  // Step 2: smooth the envelope to avoid sharp transitions
  const smoothedBaseline = boxSmooth(minEnvelope, Math.round(hw / 2))
  // Step 3: subtract and clip
  return profile.map((v, i) => Math.max(0, v - smoothedBaseline[i]))
}

// ── Zone-local sensitive peak detection ──────────────────────────────────────
// Used by the "zone_count" interpretation mode (Toxocara): scan a NARROW pixel
// window with thresholds relative to the *window's* own max, not the whole
// strip's max. This catches faint bands inside the 24-35 kDa zone that the
// global peak detector (dominated by the strong HPM bands elsewhere) misses,
// without adding noise peaks outside the window. Returns peak positions in
// ascending order.
const detectZonePeaks = (
  profile: number[],
  start: number,
  end: number,
  opts?: { intensityFrac?: number; prominenceFrac?: number; minDist?: number; promWin?: number; debugSink?: any[] },
): number[] => {
  const lo = Math.max(1, Math.floor(start))
  const hi = Math.min(profile.length - 2, Math.ceil(end))
  if (hi - lo < 2) return []

  let zoneMax = 0
  for (let i = lo; i <= hi; i++) if (profile[i] > zoneMax) zoneMax = profile[i]
  if (zoneMax <= 0) return []

  const MIN_INTENSITY = zoneMax * (opts?.intensityFrac ?? 0.05) // 5% of zone max
  const MIN_PROMINENCE = zoneMax * (opts?.prominenceFrac ?? 0.02) // 2% of zone max
  const PROM_WIN = opts?.promWin ?? 6   // window used to measure prominence (valleys)
  const MIN_DIST = opts?.minDist ?? 6

  const peaks: Array<{ pos: number; val: number; prom: number }> = []
  // Both edges included — profile[i-1]/profile[i+1] are always in-bounds here
  // (lo = max(1, ...) so lo-1 >= 0; hi = min(length-2, ...) so hi+1 <= length-1),
  // so excluding them bought nothing. It did cost a real peak: on a real Toxocara
  // sample, the peak right at the P24 anchor's own projected position landed
  // exactly ON `hi` (the window's calibrated upper edge, anchor + margin) and was
  // silently dropped from the zone count — this is what that missing "24" was.
  for (let i = lo; i <= hi; i++) {
    const v = profile[i]
    // Local maximum on the IMMEDIATE neighbours only (catches shoulders/close
    // bumps that a strict ±3 window would reject), avoiding plateau double-count.
    const isLocalMax = v >= profile[i - 1] && v >= profile[i + 1] && (v > profile[i - 1] || v > profile[i + 1])
    if (!isLocalMax) continue
    // Prominence = height above the higher of the two surrounding valleys,
    // measured over ±PROM_WIN.
    let leftMin = v
    let rightMin = v
    for (let j = 1; j <= PROM_WIN; j++) {
      if (i - j >= 0) leftMin = Math.min(leftMin, profile[i - j])
      if (i + j < profile.length) rightMin = Math.min(rightMin, profile[i + j])
    }
    const prom = v - Math.max(leftMin, rightMin)
    const passIntensity = v >= MIN_INTENSITY
    const passProminence = prom >= MIN_PROMINENCE
    if (opts?.debugSink) {
      opts.debugSink.push({
        pos: i,
        val: Math.round(v),
        valPct: Math.round((v / zoneMax) * 100),
        prom: Math.round(prom),
        promPct: Math.round((prom / zoneMax) * 100),
        pass: passIntensity && passProminence,
        reject: !passIntensity ? 'intensity' : !passProminence ? 'prominence' : null,
      })
    }
    if (!passIntensity || !passProminence) continue
    peaks.push({ pos: i, val: v, prom })
  }

  // Enforce a minimum spacing, keeping the more intense peak on collision.
  peaks.sort((a, b) => a.pos - b.pos)
  const kept: Array<{ pos: number; val: number; prom: number }> = []
  for (const p of peaks) {
    const clashIdx = kept.findIndex((k) => Math.abs(k.pos - p.pos) < MIN_DIST)
    if (clashIdx === -1) kept.push(p)
    else if (p.val > kept[clashIdx].val) kept[clashIdx] = p
  }
  return kept.map((p) => p.pos).sort((a, b) => a - b)
}

const hoverCursorPlugin = {
  id: 'hoverCursor',
  afterDatasetsDraw(chart: any, _args: any, options: any) {
    const { hoverX, hoverKda, invertAxes } = options
    if (hoverX === null || hoverX === undefined || invertAxes) return
    const { ctx, chartArea: { top, bottom, left, right }, scales } = chart
    const x = scales.x?.getPixelForValue(hoverX)
    if (x === undefined || x < left || x > right) return

    ctx.save()
    ctx.strokeStyle = 'rgba(234, 179, 8, 0.9)'
    ctx.lineWidth = 2
    ctx.setLineDash([5, 4])
    ctx.beginPath()
    ctx.moveTo(x, top)
    ctx.lineTo(x, bottom)
    ctx.stroke()
    ctx.setLineDash([])

    // Dot at top of cursor
    ctx.fillStyle = 'rgba(234, 179, 8, 1)'
    ctx.beginPath()
    ctx.arc(x, top + 5, 4, 0, Math.PI * 2)
    ctx.fill()

    if (hoverKda !== null && hoverKda !== undefined) {
      const text = `${hoverKda} kDa`
      ctx.font = 'bold 10px sans-serif'
      const tw = ctx.measureText(text).width + 10
      const tx = Math.max(left + 2, Math.min(right - tw, x - tw / 2))
      const ry = bottom - 22
      // Rounded rect background
      const r = 4
      ctx.fillStyle = 'rgba(113, 63, 18, 0.92)'
      ctx.beginPath()
      ctx.moveTo(tx + r, ry)
      ctx.lineTo(tx + tw - r, ry)
      ctx.quadraticCurveTo(tx + tw, ry, tx + tw, ry + r)
      ctx.lineTo(tx + tw, ry + 17 - r)
      ctx.quadraticCurveTo(tx + tw, ry + 17, tx + tw - r, ry + 17)
      ctx.lineTo(tx + r, ry + 17)
      ctx.quadraticCurveTo(tx, ry + 17, tx, ry + 17 - r)
      ctx.lineTo(tx, ry + r)
      ctx.quadraticCurveTo(tx, ry, tx + r, ry)
      ctx.closePath()
      ctx.fill()
      ctx.fillStyle = 'white'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, tx + tw / 2, ry + 8.5)
    }
    ctx.restore()
  }
}

const referenceLinesPlugin = {
  id: 'referenceLines',
  afterDatasetsDraw(chart: any, args: any, options: any) {
    const immunoblotConfig = options.immunoblotConfig
    const blot = options.blot
    const invertAxes = options.invertAxes
    const positiveControlBlot = options.positiveControlBlot
    const manualBandPositions = options.manualBandPositions // Manually selected positions
    // Offset from where the positions were calibrated (positive control)
    const controlCalibrationOffset = options.controlCalibrationOffset || 0
    // Offset from the current blot being displayed
    const blotDisplayOffset = options.blotDisplayOffset || 0

    if (!immunoblotConfig || !blot) return

    const { ctx, chartArea: { top, bottom, left, right }, scales } = chart

    // Draw ROI (Region of Interest) markers - only in normal mode (not inverted)
    // ROI is dynamically calculated based on P30 position from POSITIVE CONTROL (témoin), then adjusted for display
    if (!invertAxes) {
      // Calibrated band positions (control-calibration space), if the operator
      // has placed any — the ROI follows them instead of the auto-detected P30.
      const calibratedPositions: number[] | undefined = options.manualBandPositions
        ? Array.from((options.manualBandPositions as Map<string, { position: number }>).values()).map((v) => v.position)
        : undefined
      const usingCalibrated = !!calibratedPositions && calibratedPositions.length > 0
      const roiBounds = getROIBounds(blot, positiveControlBlot, calibratedPositions)
      // Map ROI to display coords with the SAME rule as the band lines below:
      // calibrated positions live in control-calibration space (→ subtract
      // controlCalibrationOffset); the auto P30 fallback is in the displayed
      // blot's own space (→ subtract blotDisplayOffset).
      const roiOffset = usingCalibrated ? (options.controlCalibrationOffset || 0) : blotDisplayOffset
      const ROI_START = roiBounds.start - roiOffset
      const ROI_END = roiBounds.end - roiOffset

      ctx.save()
      ctx.strokeStyle = 'rgba(156, 163, 175, 0.4)' // gray-400 with opacity
      ctx.lineWidth = 1
      ctx.setLineDash([3, 3])

      // ROI start line
      const xStart = scales.x.getPixelForValue(ROI_START)
      ctx.beginPath()
      ctx.moveTo(xStart, top)
      ctx.lineTo(xStart, bottom)
      ctx.stroke()

      // ROI end line
      const xEnd = scales.x.getPixelForValue(ROI_END)
      ctx.beginPath()
      ctx.moveTo(xEnd, top)
      ctx.lineTo(xEnd, bottom)
      ctx.stroke()

      // Fill ROI zone with subtle background
      ctx.fillStyle = 'rgba(209, 213, 219, 0.1)' // gray-300 with very low opacity
      ctx.fillRect(xStart, top, xEnd - xStart, bottom - top)

      // ROI labels - positioned at middle to avoid top clutter
      const middleY = (top + bottom) / 2
      ctx.fillStyle = 'rgba(107, 114, 128, 0.7)' // gray-500
      ctx.font = '10px sans-serif'
      ctx.fillText('ROI →', xStart + 3, middleY)
      ctx.fillText('← ROI', xEnd - 35, middleY)

      ctx.restore()
    }

    // Clean profile+peaks view: skip ALL band annotations (reference lines +
    // labels) when the operator toggled them off. The profile line, its peaks
    // and the ROI zone are drawn elsewhere, so they stay visible.
    if (options.showChartBands === false) return
    const showTemoinBands = options.showTemoinBands !== false

    // Use manual band positions if available (from user selection)
    // Otherwise fall back to automatic calibration
    let bandPositions: Map<string, number> | null = null
    // Whether `bandPositions` ended up in the TÉMOIN's own raw coordinate space
    // (manual calibration is always control-space; so is the auto path when a
    // témoin exists — see below) rather than the currently-displayed blot's own.
    // Drives which offset converts it to chart space further down: getting this
    // wrong doesn't move the label's X much when the two strips' own control
    // lines happen to sit close together, but it samples the intensity curve at
    // a slightly wrong X, which can land off a peak's apex on its flank —
    // reported as the suggested band dot sitting visibly below the real peak.
    let positionsFromControl = false

    if (manualBandPositions && manualBandPositions.size > 0) {
      // Convert manual positions Map<string, {position, intensity}> to Map<string, number>
      bandPositions = new Map<string, number>()
      manualBandPositions.forEach((data: { position: number, intensity: number }, bandName: string) => {
        bandPositions!.set(bandName, data.position)
      })
      positionsFromControl = true
    } else {
      // Fallback to automatic calibration from positive control peaks
      bandPositions = positiveControlBlot
        ? suggestBands(positiveControlBlot, immunoblotConfig)
        : null
      if (bandPositions) positionsFromControl = true

      // If no positive control, try to calibrate from current blot
      if (!bandPositions) {
        bandPositions = suggestBands(blot, immunoblotConfig)
      }
    }

    // Two band sets, drawn separately so each one can be toggled:
    // the témoin's (or manual) positions, and the sample's own auto-detected ones.
    const bandSets: Array<{ positions: Map<string, number>; fromControl: boolean; visible: boolean }> = []
    if (bandPositions && bandPositions.size > 0) {
      bandSets.push({ positions: bandPositions, fromControl: positionsFromControl, visible: showTemoinBands })
    }

    // If still no calibration, can't draw lines
    if (bandSets.length === 0) {
      console.log('⚠️ No band positions calibrated')
      return
    }

    bandSets.forEach(({ positions: bandPositions, fromControl: positionsFromControl, visible }) => {
      if (!visible) return

      // PROTIS : les positions nommées librement (« 180 kDa »…) n'existent
      // pas au catalogue — bands.specific y est vide — et ces deux boucles ne
      // dessinaient QUE le catalogue : aucune annotation ne s'affichait. On
      // dessine donc le catalogue, PLUS toute position dont le nom n'y figure
      // pas. Pour IRIS, rien ne change : toutes ses positions portent des
      // noms du catalogue.
      const catalogBands: any[] = immunoblotConfig?.bands?.specific ?? []
      const catalogNames = new Set(catalogBands.map((b: any) => b.name))
      const bandList: any[] = [
        ...catalogBands,
        ...Array.from(bandPositions!.keys())
          .filter((n) => !catalogNames.has(n))
          .map((n) => ({ name: n, required: false })),
      ]

      // ── Label rows (pre-pass) — greedy stacking so band names never overlap ──
      // Sort labels by x, drop each into the lowest row whose last label ended
      // before it starts (+gap). Bands too close get pushed to a new row instead
      // of overlapping. Only meaningful for the vertical (non-inverted) chart.
      const labelRowByBand = new Map<string, number>()
      if (!invertAxes) {
        const isManual = manualBandPositions && manualBandPositions.size > 0
        ctx.font = 'bold 10px sans-serif'
        const items = bandList
          .map((band: any) => {
            const px = bandPositions!.get(band.name)
            if (px === undefined) return null
            const disp = isManual ? px - controlCalibrationOffset : px - blotDisplayOffset
            return { name: band.name, x: scales.x.getPixelForValue(disp), half: ctx.measureText(band.name).width / 2 + 5 }
          })
          .filter(Boolean)
          .sort((a: any, b: any) => a.x - b.x)
        const rowRightEdges: number[] = []
        const GAP = 4
        ;(items as Array<{ name: string; x: number; half: number }>).forEach((it) => {
          let r = rowRightEdges.findIndex((edge) => it.x - it.half > edge + GAP)
          if (r === -1) {
            r = rowRightEdges.length
          }
          rowRightEdges[r] = it.x + it.half
          labelRowByBand.set(it.name, r)
        })
      }

      bandList.forEach((band: any) => {
        // Get the calibrated position for this band
        const pixelPosition = bandPositions!.get(band.name)

        // Skip if this band wasn't detected in the calibration
        if (pixelPosition === undefined) {
          return
        }

        // Convert position from calibration space to display space. Which offset
        // undoes it depends on WHOSE raw coordinates pixelPosition is actually in
        // (positionsFromControl, set above) — not on manual-vs-auto: the auto
        // path is control-space too whenever a témoin exists (suggestBands tries
        // positiveControlBlot before falling back to the blot's own peaks), so it
        // needs controlCalibrationOffset just like manual does. Only the no-témoin
        // auto fallback (calibrated on the current blot's own peaks) is already in
        // this blot's own coordinates and needs blotDisplayOffset instead.
        //
        // Témoin-space (manual, or auto with a témoin):
        //   - absolutePosition on sample = pixelPosition - controlCalibrationOffset + blotDisplayOffset
        //   - displayPosition = (pixelPosition - controlCalibrationOffset + blotDisplayOffset) - blotDisplayOffset
        //   - displayPosition = pixelPosition - controlCalibrationOffset
        //
        // Blot's-own-space (auto, no témoin available):
        //   - absolutePosition = pixelPosition (already in blot coordinates)
        //   - displayPosition = pixelPosition - blotDisplayOffset

        // positionsFromControl covers BOTH manual calibration AND the auto path
        // when a témoin exists (suggestBands(positiveControlBlot, ...) above) —
        // both put pixelPosition in the TÉMOIN's own raw coordinate space, which
        // needs controlCalibrationOffset (the témoin's own control line) to
        // convert to chart space. Using blotDisplayOffset (the currently-
        // displayed blot's own control line) there instead — as this used to,
        // gated on isManualPosition alone — sampled the intensity curve at a
        // slightly wrong X whenever the two strips' own control lines didn't
        // sit at the exact same position, which they usually don't: reported as
        // the suggested band's dot landing visibly off a real peak's apex.
        const displayPosition = positionsFromControl
          ? pixelPosition - controlCalibrationOffset  // Témoin-space: convert from control space
          : pixelPosition - blotDisplayOffset         // Auto on the blot's own peaks: already in its space

        // Unified band-colour code, shared with the PDF report legend:
        // green = témoin · purple = détectée (auto) · blue = ajoutée manuellement.
        // Bands positioned from the témoin — the strip IS a control, or the positions
        // came from the témoin (positionsFromControl) — are témoin bands → green.
        // Bands auto-calibrated on the patient's own peaks are "détectée" → purple.
        // (Hand-added bands are blue, but they come from placedBands, not this overlay.)
        const bandFromControl = options.isControlBlot || positionsFromControl
        const bandSrcSolid = bandFromControl ? 'rgba(56, 161, 105, 0.95)' : 'rgba(128, 90, 213, 0.95)'
        const bandSrcLight = bandFromControl ? 'rgba(56, 161, 105, 0.5)'  : 'rgba(128, 90, 213, 0.5)'
        const bandSrcHex   = bandFromControl ? '#38A169' : '#805AD5'
        ctx.save()
        ctx.strokeStyle = bandSrcLight
        ctx.lineWidth = 2
        ctx.setLineDash([5, 5])

        if (invertAxes) {
          // Horizontal line when axes are inverted (Y = position)
          const yPosition = scales.y.getPixelForValue(displayPosition)
          ctx.beginPath()
          ctx.moveTo(left, yPosition)
          ctx.lineTo(right, yPosition)
          ctx.stroke()

          // Label
          ctx.fillStyle = bandSrcHex
          ctx.font = 'bold 11px sans-serif'
          ctx.fillText(`${band.name} (${band.molecularWeight} kDa)`, left + 5, yPosition - 5)
        } else {
          // Ligne verticale fine sur toute la hauteur
          const xPosition = scales.x.getPixelForValue(displayPosition)
          const targetX = Math.round(displayPosition)

          // Read intensity from the profile dataset (data is now [{x, y}, ...]).
          // Find the point whose x value matches the band's display position.
          let intensityAtPosition: number | null = null
          for (const dataset of chart.data.datasets) {
            if (!dataset.data || dataset.data.length === 0) continue
            const first: any = dataset.data[0]
            // Profile datasets are objects with x,y. Peak datasets too — but profile has many points, peak has few.
            // Skip peak datasets (smaller arrays) and prefer the profile (continuous line).
            if (first && typeof first === 'object' && 'x' in first && 'y' in first && dataset.data.length > 50) {
              // Find the point with matching x (or closest one)
              let bestDist = Infinity
              let bestY: number | null = null
              for (const pt of dataset.data as Array<{ x: number; y: number }>) {
                if (!pt || pt.y == null) continue
                const d = Math.abs(pt.x - targetX)
                if (d < bestDist) {
                  bestDist = d
                  bestY = pt.y
                  if (d === 0) break
                }
              }
              if (bestY !== null) {
                intensityAtPosition = bestY
                break
              }
            }
          }

          // Position du pic sur la courbe
          let peakY = bottom - 30 // Position par défaut
          if (intensityAtPosition !== null && intensityAtPosition !== undefined) {
            peakY = scales.y.getPixelForValue(intensityAtPosition)
          }

          // Rangée du label calculée en pré-passe (anti-chevauchement)
          const row = labelRowByBand.get(band.name) ?? 0

          // Mesurer le texte pour le fond
          ctx.font = 'bold 10px sans-serif'
          const textWidth = ctx.measureText(band.name).width
          const padding = 5
          const rectHeight = 18
          const rectWidth = textWidth + padding * 2

          // Labels en HAUT du graphique, en rangées alternées
          const topMargin = 8
          const rowSpacing = 22
          let rectX = xPosition - rectWidth / 2
          let rectY = top + topMargin + (row * rowSpacing)

          // S'assurer que le rectangle reste dans les limites horizontales
          rectX = Math.max(left + 2, Math.min(right - rectWidth - 2, rectX))

          const rectCenterX = rectX + rectWidth / 2
          const rectCenterY = rectY + rectHeight / 2
          const rectBottomY = rectY + rectHeight

          // Couleur par SOURCE (code partagé avec la légende du PDF) :
          // vert = témoin · violet = détectée (auto) · bleu = ajoutée manuellement.
          const bandColor = bandSrcSolid
          const bandColorLight = bandSrcLight

          // Ligne verticale pointillée du label jusqu'au pic (sans flèche)
          ctx.strokeStyle = bandColorLight
          ctx.lineWidth = 1.5
          ctx.setLineDash([4, 3])
          ctx.beginPath()
          ctx.moveTo(xPosition, rectBottomY + 4)
          ctx.lineTo(xPosition, peakY - 2)
          ctx.stroke()
          ctx.setLineDash([])

          // Cercle sur le pic
          ctx.beginPath()
          ctx.arc(xPosition, peakY, 3.5, 0, Math.PI * 2)
          ctx.fillStyle = bandColor
          ctx.fill()
          ctx.strokeStyle = 'white'
          ctx.lineWidth = 1.5
          ctx.stroke()

          // Fond du label avec coins arrondis
          ctx.fillStyle = bandColor
          const radius = 4
          ctx.beginPath()
          ctx.moveTo(rectX + radius, rectY)
          ctx.lineTo(rectX + rectWidth - radius, rectY)
          ctx.quadraticCurveTo(rectX + rectWidth, rectY, rectX + rectWidth, rectY + radius)
          ctx.lineTo(rectX + rectWidth, rectY + rectHeight - radius)
          ctx.quadraticCurveTo(rectX + rectWidth, rectY + rectHeight, rectX + rectWidth - radius, rectY + rectHeight)
          ctx.lineTo(rectX + radius, rectY + rectHeight)
          ctx.quadraticCurveTo(rectX, rectY + rectHeight, rectX, rectY + rectHeight - radius)
          ctx.lineTo(rectX, rectY + radius)
          ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY)
          ctx.closePath()
          ctx.fill()

          // Ombre portée légère
          ctx.shadowColor = 'rgba(0, 0, 0, 0.2)'
          ctx.shadowBlur = 4
          ctx.shadowOffsetY = 2
          ctx.fill()
          ctx.shadowColor = 'transparent'
          ctx.shadowBlur = 0
          ctx.shadowOffsetY = 0

          // Bordure blanche
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)'
          ctx.lineWidth = 1
          ctx.stroke()

          // Texte en blanc centré
          ctx.fillStyle = 'white'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.font = 'bold 11px sans-serif'
          ctx.fillText(band.name, rectCenterX, rectCenterY)
        }

        ctx.restore()
      })
    })
  }
}

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  zoomPlugin,
  referenceLinesPlugin,
  hoverCursorPlugin
)

// Animations for the comparison modal
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(128, 90, 213, 0.3); }
  50% { box-shadow: 0 0 40px rgba(128, 90, 213, 0.6); }
`

// Halo du badge « À vérifier » — orange, pour qu'un cas limite se repère
// d'un coup d'œil dans la galerie comme dans la modale.
const pulseWarn = keyframes`
  0%, 100% { box-shadow: 0 0 10px rgba(237, 137, 54, 0.45); }
  50% { box-shadow: 0 0 22px rgba(229, 62, 62, 0.65); }
`

const floatUp = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`

// Delete snap: the false-positive card dissolves — fades, shrinks and drifts up
// with a little blur — before it's actually removed.
const ghostSnap = keyframes`
  0%   { opacity: 1; transform: scale(1) translateY(0) rotate(0deg); filter: blur(0); }
  35%  { opacity: 0.85; transform: scale(1.04) translateY(-3px) rotate(-1.5deg); }
  100% { opacity: 0; transform: scale(0.82) translateY(-18px) rotate(2.5deg); filter: blur(4px); }
`

// Entrance reveal, staggered per card by mount order — gives the gallery a
// "filling up" feel right after an IRIS analysis returns instead of the whole
// grid popping in at once (the backend sends all blots in a single response,
// so this is a purely cosmetic stagger, not real per-blot progress).
const blotRevealIn = keyframes`
  0%   { opacity: 0; transform: scale(0.88) translateY(14px); }
  60%  { opacity: 1; transform: scale(1.015) translateY(-2px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`

interface BlotDetection {
  id: number
  crop_base64: string
  reconstructed_base64: string
  profile: number[]
  peaks: number[]
  x0: number
  x1: number
  y_top: number
  y_bottom: number
  num_bands: number
  image_width?: number
  image_height?: number
  /** OCR-recognised / operator-edited sample identifier (e.g. "K10"). */
  strip_id?: string | null
  /** OCR confidence for strip_id: 'sure' | 'suggested' | 'conflict' | 'edited'. */
  strip_id_status?: string | null
  /** L'identifiant a-t-il été LU sur cette bandelette (true) ou emprunté à la
   *  voisine faute d'avoir rien pu lire (false) ? `undefined` sur les cas
   *  analysés avant novembre 2026 et sur les profils dont l'identifiant vient
   *  d'ailleurs (Dynablot le lit dans le tableau du rapport). */
  strip_id_read?: boolean | null
  /** Cette détection a très probablement attrapé du fond de plaque : aucun
   *  identifiant lu en propre ET profil plat par rapport à la plaque. Avis du
   *  pipeline, jamais appliqué sans confirmation. */
  looks_like_background?: boolean | null
  /** Bordeaux/Dynablot multitest only: this strip's printed self-test name (OCR), e.g. "Aspergillus IgG" — a Dynablot report can mix several pathologies in one PDF. */
  pathology_label?: string | null
  /** Dynablot only: the SAMPLE reference printed for this strip in the report
   *  table (patient identifier, e.g. "261047344101"). Distinct from strip_id,
   *  which names the strip inside the cassette. */
  sample_label?: string | null
  /** Nancy only: where the worksheet's mounting line falls inside this crop,
   *  as a fraction of its height. The strips are glued so their control band
   *  sits on that line, so it is where the control band is expected. */
  control_line_frac?: number | null
}

interface ImmunoblotConfig {
  id: string
  name: string
  bands: {
    specific: Array<{
      id: string
      name: string
      molecularWeight: number
      zone: string
      required: boolean
      description: string
    }>
    reference: {
      minKDa: number
      maxKDa: number
      primaryZone: string
    }
  }
  interpretation: any
  displaySettings?: {
    showMolecularWeights: boolean
    showBandLabels: boolean
    highlightRequiredBands: boolean
    colorScheme: {
      requiredBand: string
      specificBand: string
      detectedBand: string
    }
  }
}

export interface BlotInterpretation {
  blotId: number
  result: string
  color: string
  numBands: number
  numPeaks: number
  identifiedBandNames: string[]
  // Positions of each identified band as a fraction of the blot image height
  // (0 = top of strip, 1 = bottom). Used by the PDF exporter to draw the
  // band lines on the cropped image preview. `manual` marks operator-added bands.
  bandLines?: Array<{ name: string; yFraction: number; manual?: boolean }>
  requiredBandId?: string | null
  hasRequiredBand?: boolean
  // Echinococcus species diagnosis (e.g. "P3 · E. multilocularis"), when the
  // test defines the P1-P5 profiles and the sample is genus-positive. Surfaced
  // in the PDF export so the report matches the on-screen species card.
  speciesLabel?: string | null
  /** Cotation semi-quantitative (Oliva 2015), quand le cas la porte —
   *  « Fortement positif · I=8 (3 bandes) ». Null partout ailleurs. */
  semiQuantLabel?: string | null
  /** Intensité de chaque bande identifiée, rapportée à la même bande sur le
   *  témoin de la plaque (sans échelle, donc comparable d'une plaque à
   *  l'autre). Figée dans l'instantané IRIS : c'est elle qui permet de
   *  stratifier l'accord entre bandes faibles et bandes franches. Vide quand
   *  aucun témoin n'est désigné. */
  bandRatios?: Record<string, number>
  // Borderline strip: verdict is NÉGATIF but the positive criteria were nearly
  // met (bands present but insufficient / required band absent / signal too weak
  // vs control). Not a verdict — just a hint to review manually.
  needsReview?: boolean
  /** Paired-comparison role (CIP-WB mother/child, J0/J+N…), flips included.
   *  Carried up to the exports, which cannot derive it: the pairing is guessed
   *  here from the row order and may have been corrected by the reader. */
  pairedRole?: 'mother' | 'child' | null
}

interface BlotGalleryProps {
  /** Le module qui affiche la galerie. PROTIS n'a pas de ligne de contrôle :
   *  les gels de recherche n'impriment aucun repère de fabricant, le marqueur
   *  jaune y désignerait un trait qui n'existe pas. */
  module?: 'iris' | 'protis'
  /** Le profil de détection DU CAS (chu-bordeaux, chu-nancy…) — pas celui du
   *  spectateur : il gate les lectures propres à un site, comme la cotation
   *  semi-quantitative Aspergillus demandée par Bordeaux
   *  (interpretation.semiQuantification.enabledProfiles). Un admin en mode
   *  shadow voit donc exactement ce que le pilote voit. */
  detectionProfile?: string | null
  blots: BlotDetection[]
  caseId?: string | null
  savedPositiveControlId?: number | null
  savedPositiveControl2Id?: number | null
  savedPositiveControl3Id?: number | null
  /** Bordeaux/Dynablot multitest only: EACH pathology owns its own independent
   *  T1/T2/T3 (a manip can need 2 témoins for the same pathology) — keyed by
   *  raw pathology_label. The 3 saved*PositiveControl*Id props above stay the
   *  single-pathology (non-multitest) case, untouched. */
  savedPositiveControlsByPathology?: Record<string, {
    positiveControlId?: number | null
    positiveControl2Id?: number | null
    positiveControl3Id?: number | null
  }> | null
  onPositiveControlChange?: (control1Id: number | null, control2Id: number | null, control3Id: number | null, pathologyLabel?: string | null) => Promise<void>
  savedBandCalibration?: Record<string, Record<string, { position: number; intensity: number }>> | null
  onBandCalibrationChange?: (controlId: number, calibration: Record<string, { position: number; intensity: number }> | null) => Promise<void>
  /** Manual override of the auto-detected control-line position, for a strip
   *  where auto-detection picks the wrong feature (e.g. the strip's own printed
   *  id text). Keyed by blot id (as a string), value = pixel position in that
   *  blot's own profile. */
  savedControlLineOverrides?: Record<string, number> | null
  onControlLineOverrideChange?: (blotId: number, position: number | null) => Promise<void>
  /** Paired-comparison couples the biologist flipped: { "<lowerId>-<higherId>": true }. */
  savedPairedRoleSwaps?: Record<string, boolean> | null
  onPairedRoleSwapChange?: (swaps: Record<string, boolean>) => void
  immunoblotConfig?: ImmunoblotConfig | null
  /** Bordeaux/Dynablot multitest only: one config per distinct raw pathology_label
   *  found on this case's strips, keyed by that raw label. While the gallery's
   *  pathology filter is active, its resolved config REPLACES `immunoblotConfig`
   *  for interpretation — see the `immunoblotConfig` useMemo below. */
  pathologyConfigMap?: Record<string, ImmunoblotConfig | null>
  /** Bordeaux/Dynablot multitest only: notifies the parent of the gallery's
   *  active pathology filter, so export can restrict to it (null = no filter,
   *  export the whole case). Fires on every change, including back to null. */
  onPathologyFilterChange?: (pathologyLabel: string | null) => void
  /** Bordeaux/Dynablot multitest only: pre-selects the pathology tab on mount
   *  (raw pathology_label, matching one of distinctPathologyLabels) — set by
   *  the parent when the case was opened FROM a pathology-scoped recent-cases
   *  list, so it opens scoped to that pathology instead of always on "Toutes". */
  initialPathologyFilter?: string | null
  onInterpretationsChange?: (interpretations: BlotInterpretation[]) => void
  /** Mode rétrospectif, lecture de paillasse en cours : chaque bandelette
   *  d'ÉCHANTILLON porte alors ses bandes à cocher, dans sa propre carte. Un
   *  second jeu de vignettes afficherait deux fois la même plaque, et c'est
   *  ici que le témoin se désigne et que les identifiants se corrigent. */
  benchReadingMode?: boolean
  benchBands?: Record<number, string[]>
  onBenchBandToggle?: (blotId: number, band: string) => void
  savedHiddenBlotIds?: number[]
  /** Incrémenté par le parent pour remettre TOUTES les bandelettes supprimées :
   *  l'effet de synchronisation ci-dessous ne fait qu'ajouter, jamais retirer. */
  restoreHiddenNonce?: number
  onHiddenBlotsChange?: (hiddenIds: number[]) => Promise<void>
  /** Bumped by the parent to (re)start the guided tour. The "Aidez-moi" tips
   *  popover now lives in the workspace's right-hand FAB dock, so its "Revoir la
   *  visite guidée" button has to reach the tour, which is owned by this component. */
  startTourSignal?: number
  /** Manual validation results (non-AI mode) — keyed by blot ID */
  manualBlotResults?: Record<number, ManualResult>
  onManualResultChange?: (blotId: number, result: ManualResult) => void
  /** Correct a strip's recognised identifier (OCR draft). */
  onStripIdChange?: (blotId: number, stripId: string | null) => void
  /** Per-blot operator comments */
  onBlotCommentChange?: (blotId: number, comment: string) => void
  /** Manually-added bands persisted on the case — restored on load so they
   *  survive a page refresh. Keyed by blot ID. */
  savedPlacedBands?: Record<number, Array<{name: string; yFraction: number}>> | null
  /** Callback to sync placed bands up for export + persistence */
  onPlacedBandsChange?: (bands: Record<number, Array<{name: string; yFraction: number}>>) => void
  /** Bands the biologist deleted, per blot: { blotId: [bandName, ...] } — for persistence */
  savedSuppressedBands?: Record<number, string[]> | null
  /** Callback to persist deleted bands so they survive a refresh */
  onSuppressedBandsChange?: (bands: Record<number, string[]>) => void
  /** Whether AI features are accessible (Premium/Enterprise plan) */
  aiAccessible?: boolean
  /** When true, all write surfaces (verdict, drag/rename of bands, controls)
   *  are locked. Used by admin-shadow mode so a supervisor can browse without
   *  mutating the pilot's data. */
  readOnly?: boolean
}

// Bounds of the actual rendered image content inside its container (accounts for
// objectFit: 'contain' letterboxing). All values are percentages of the container.
type ImageContentBounds = { topPct: number; leftPct: number; widthPct: number; heightPct: number }
const DEFAULT_IMAGE_BOUNDS: ImageContentBounds = { topPct: 0, leftPct: 0, widthPct: 100, heightPct: 100 }

// Distribute band labels to the LEFT or RIGHT of the strip so their names don't
// overlap: each label stays at its TRUE height, but when two would collide
// vertically they are pushed to opposite sides. Greedy over bands sorted by
// height; the RIGHT side is preferred (default look) and the LEFT is used when
// the right is still occupied by a nearby label.
function assignLabelSides(
  items: Array<{ name: string; top: number }>,
  minGapPct = 3.4,
): Map<string, 'left' | 'right'> {
  const out = new Map<string, 'left' | 'right'>()
  const lastTop: { left: number; right: number } = { left: -Infinity, right: -Infinity }
  ;[...items]
    .sort((a, b) => a.top - b.top)
    .forEach((it) => {
      const rightFree = it.top - lastTop.right >= minGapPct
      const leftFree = it.top - lastTop.left >= minGapPct
      let side: 'left' | 'right'
      if (rightFree) side = 'right'
      else if (leftFree) side = 'left'
      else side = lastTop.right <= lastTop.left ? 'right' : 'left'
      lastTop[side] = it.top
      out.set(it.name, side)
    })
  return out
}

// Deterministic letterbox geometry from the strip's KNOWN natural dimensions
// (blot.image_width/height) inside the fixed image card. The strip fills its
// crop (no padding — verified), so objectFit:contain just letterboxes it. This
// avoids depending on the async image-load hook, which could leave markers at
// the default full-width bounds (= card edge) if it hadn't computed yet.
// boxW/boxH are the image card's outer size; border is its CSS border width.
//
// image_width/image_height are the PRE-display-rotation (real OBB/warped
// strip) dimensions — the main-image box overlay (index.tsx) needs exactly
// that shape, but crop_base64/reconstructed_base64 (what THIS card actually
// renders) are already rotated 90° for the gallery whenever the strip is
// landscape (width > height) — see app.py. So the box this function fits
// into must use the swapped, POST-rotation shape in that case, or the
// letterbox math (and every band marker positioned from it) is wrong on the
// long axis — mild/unnoticed on Amiens's near-square strips, but a gross
// misplacement on Dynablot's ~23:1 strips (reported: markers landing far
// from the real bands on both témoin and échantillon).
const stripContentBounds = (
  imgWRaw?: number,
  imgHRaw?: number,
  boxW = 190,
  boxH = 500,
  border = 3,
): ImageContentBounds => {
  if (!imgWRaw || !imgHRaw) return DEFAULT_IMAGE_BOUNDS
  const [imgW, imgH] = imgWRaw > imgHRaw ? [imgHRaw, imgWRaw] : [imgWRaw, imgHRaw]
  const cw = boxW - 2 * border
  const ch = boxH - 2 * border
  const imgA = imgW / imgH
  const boxA = cw / ch
  let rW: number, rH: number, offX: number, offY: number
  if (imgA > boxA) { rW = cw; rH = cw / imgA; offX = 0; offY = (ch - rH) / 2 }
  else { rH = ch; rW = ch * imgA; offX = (cw - rW) / 2; offY = 0 }
  return {
    topPct: ((border + offY) / boxH) * 100,
    leftPct: ((border + offX) / boxW) * 100,
    widthPct: (rW / boxW) * 100,
    heightPct: (rH / boxH) * 100,
  }
}

export default function BlotGallery({
  module = 'iris',
  detectionProfile,
  blots,
  caseId,
  savedPositiveControlId,
  savedPositiveControl2Id,
  savedPositiveControl3Id,
  savedPositiveControlsByPathology,
  onPositiveControlChange,
  savedBandCalibration,
  onBandCalibrationChange,
  savedControlLineOverrides,
  onControlLineOverrideChange,
  savedPairedRoleSwaps,
  onPairedRoleSwapChange,
  onInterpretationsChange,
  benchReadingMode = false,
  benchBands,
  onBenchBandToggle,
  savedHiddenBlotIds,
  restoreHiddenNonce,
  onHiddenBlotsChange,
  startTourSignal = 0,
  manualBlotResults = {},
  onManualResultChange,
  onStripIdChange,
  onBlotCommentChange,
  savedPlacedBands,
  onPlacedBandsChange,
  savedSuppressedBands,
  onSuppressedBandsChange,
  aiAccessible: aiAccessibleProp,
  readOnly = false,
  immunoblotConfig: rawImmunoblotConfig,
  pathologyConfigMap,
  initialPathologyFilter = null,
  onPathologyFilterChange,
}: BlotGalleryProps) {
  const { t } = useTranslation()
  const chuConfig = useCHUConfig()
  // Use prop if provided (plan-based), otherwise fall back to CHU config
  const aiAccessible = aiAccessibleProp !== undefined ? aiAccessibleProp : isAIAccessible(chuConfig)

  // Bordeaux/Dynablot multitest: which pathology the gallery is currently
  // filtered to (see distinctPathologyLabels/isMultiPathologyCase below) —
  // declared here, ahead of the immunoblotConfig memo that reads it, so
  // filtering swaps in THAT pathology's own config below.
  const [pathologyFilter, setPathologyFilter] = useState<string | null>(initialPathologyFilter)

  // ── Config normalisation — surface species-differentiation bands ───────────
  // Historically the whole gallery only consumed `bands.specific` (the genus
  // markers: P7 + P26-28 for Echinococcus). LDBio Echinococcus also ships the
  // intermediate bands used for the E. granulosus / E. multilocularis species
  // diagnosis (P16, P17-fine, P17-large, P18… — notice vs17, § Interprétation II)
  // under `bands.intermediate`, which nothing rendered. We merge them into
  // `bands.specific` here, tagged with `bandClass` + their `species`, so every
  // downstream consumer (manual calibration selector, strip overlays, reference
  // panel, interpretation) surfaces them. Only Echinococcus defines an
  // `intermediate` array — every other test is left byte-for-byte unchanged.
  const immunoblotConfig = useMemo(() => {
    // PROTIS : les étiquettes de bandelette sont des PROJETS, pas des
    // pathologies — aucune notice par étiquette n'existe, et la mécanique
    // multitest ci-dessous remplaçait la notice PROTIS par null dès qu'un
    // filtre (ou l'ouverture d'une carte, qui scope le filtre) était actif.
    // La notice du cas vaut pour tous les projets d'un gel.
    if (module === 'protis') return rawImmunoblotConfig
    // While filtered to one pathology, use THAT pathology's own config instead
    // of the one tied to whichever pathology the case was uploaded under —
    // meaningless once a report mixes several. Falls back to the normal
    // single config when no filter is active (every non-multitest case).
    const cfg: any = (pathologyFilter && pathologyConfigMap?.[pathologyFilter] !== undefined)
      ? pathologyConfigMap[pathologyFilter]
      : rawImmunoblotConfig
    const intermediate = cfg?.bands?.intermediate
    if (!Array.isArray(intermediate) || intermediate.length === 0) return cfg
    const specific = (cfg.bands.specific || []).map((b: any) => ({
      ...b,
      bandClass: b.bandClass ?? 'genus',
    }))
    const speciesBands = intermediate
      // Bands common to both species carry no differentiating value on their own
      // (notice: "la présence isolée d'une bande intermédiaire n'est pas
      // spécifique"), but the biologist still needs to place them, so we keep
      // them — just tagged so the UI can group/deprioritise them.
      .map((b: any) => ({ ...b, bandClass: 'species', required: false }))
    return {
      ...cfg,
      bands: { ...cfg.bands, specific: [...specific, ...speciesBands] },
    }
  }, [rawImmunoblotConfig, pathologyFilter, pathologyConfigMap, module])
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const [selectedBlot, setSelectedBlot] = useState<BlotDetection | null>(null)
  const [selectedBlotIndex, setSelectedBlotIndex] = useState<number>(0)
  // The opened blot is a snapshot taken at click time — when the parent updates
  // the blots array (a strip id edited/propagated), refresh the snapshot's
  // identifier so the modal heading never shows a stale "Blot N". Returns the same
  // reference when nothing changed, so unrelated memos don't churn.
  useEffect(() => {
    setSelectedBlot((prev) => {
      if (!prev) return prev
      const fresh = blots.find((b) => b.id === prev.id)
      if (!fresh || (fresh.strip_id === prev.strip_id && fresh.strip_id_status === prev.strip_id_status)) {
        return prev
      }
      return { ...prev, strip_id: fresh.strip_id, strip_id_status: fresh.strip_id_status }
    })
  }, [blots])
  const [showRawImage, setShowRawImage] = useState<boolean>(true)
  // Guide d'analyse (config.qualityControl.analysisGuide) — built for paired-
  // comparison pathologies (CIP-WB) but never actually rendered anywhere.
  const [showAnalysisGuide, setShowAnalysisGuide] = useState<boolean>(false)
  // Refs to the rendered images — used to compute the actual content rect
  // so overlays (band lines, hover cursor) line up with the image, not the container.
  const sampleImageRef = useRef<HTMLImageElement>(null)
  const controlImageRef = useRef<HTMLImageElement>(null)
  // Bounds computed deterministically from the strip's known natural size
  // (blot.image_width/height) — reliable and synchronous, unlike the async
  // image-load hook which could leave markers at the default full-width bounds.
  const sampleImageBounds = useMemo(
    () => stripContentBounds(selectedBlot?.image_width, selectedBlot?.image_height),
    [selectedBlot],
  )
  // controlImageBounds is defined further down, once activePositiveControl exists.
  // Per-blot placed bands (manual identification by clicking on the image)
  // Key = blotId, Value = Set of matched band names (e.g. "P22", "P30")
  // Per-blot placed bands: Map<blotId, Map<bandName, yFraction>>
  // yFraction = exact click position (0–1) on the image — used for marker rendering
  // Restore manually-added bands persisted on the case (survives refresh). The
  // gallery remounts on case change (key prop), so a lazy init is enough.
  const [blotPlacedBands, setBlotPlacedBands] = useState<Map<number, Map<string, number>>>(() => {
    const m = new Map<number, Map<string, number>>()
    Object.entries(savedPlacedBands ?? {}).forEach(([blotId, arr]) => {
      if (Array.isArray(arr) && arr.length > 0) {
        m.set(Number(blotId), new Map(arr.filter((b) => b?.name).map((b) => [b.name, b.yFraction ?? 0.5])))
      }
    })
    return m
  })
  // Manual override of the auto-detected control-line position, per blot —
  // lets the operator drag the marker onto the real (often faint) manufacturer
  // band when auto-detection picks the wrong feature (e.g. the strip's own
  // printed id text, see G8 Schisto). Restored on load, same lazy-init pattern
  // as blotPlacedBands above (the gallery remounts on case change).
  const [controlLineOverrides, setControlLineOverrides] = useState<Map<number, number>>(() => {
    const m = new Map<number, number>()
    Object.entries(savedControlLineOverrides ?? {}).forEach(([blotId, pos]) => {
      if (typeof pos === 'number') m.set(Number(blotId), pos)
    })
    return m
  })
  // blotId currently being dragged, with its live (uncommitted) pixel position
  const [draggingControlLine, setDraggingControlLine] = useState<{ blotId: number; position: number } | null>(null)
  // Resolves a blot's control-line pixel position: manual override first,
  // else the auto-detected position — the SINGLE source of truth used
  // everywhere a control-line marker is drawn or a témoin↔sample alignment is
  // computed, so the card and the modal can never disagree on it again.
  // fallbackPeaks lets callers pass the strip's ORIGINAL peaks (pre sensitivity-
  // slider) so re-detected peaks never shift where a non-overridden control
  // line lands — only the manual override itself takes unconditional priority.
  // Offset agreed across this plate's strips — see plateControlLineOffset.
  // Null on a plate too small or too inconsistent to vote, where each strip
  // falls back to its own scan.
  const plateControlOffset = useMemo(() => {
    // From the raw prop rather than blotsArray, which is declared further down
    // — a median over the whole plate, hidden strips included, is unaffected.
    const arr: BlotDetection[] = Array.isArray(blots)
      ? blots
      : (() => {
          try {
            const parsed = JSON.parse(blots as unknown as string)
            return Array.isArray(parsed) ? parsed : []
          } catch {
            return []
          }
        })()
    return plateControlLineOffset(arr.map((b) => b.profile ?? []))
  }, [blots])
  const getControlLine = useCallback((blot: BlotDetection, fallbackPeaks?: number[]): number => {
    // PROTIS : PAS de ligne de contrôle — l'amer partagé, c'est LA PAGE.
    // Les pistes ont migré dans le même gel et la découpe connaît le y_top
    // absolu de chacune ; mais leurs crops ne commencent pas à la même
    // hauteur (mesuré : marqueur à 549, échantillons à ~472 — le marqueur
    // n'a pas de liseré sombre en haut). Un « offset zéro » comparait donc
    // des index décalés de ~77 px. L'offset d'une piste est l'écart de son
    // y_top au plus bas des y_top de son projet : tous les profils vivent
    // alors en coordonnée de page, et une graduation posée sur le marqueur
    // tombe au bon endroit sur chaque échantillon — géométrie mesurée, pas
    // d'heuristique.
    if (module === 'protis') {
      const proj = (blot as any).pathology_label ?? null
      let cMax = blot.y_top ?? 0
      blots.forEach((b) => {
        if (((b as any).pathology_label ?? null) === proj) cMax = Math.max(cMax, b.y_top ?? 0)
      })
      return cMax - (blot.y_top ?? 0)
    }
    if (draggingControlLine?.blotId === blot.id) return draggingControlLine.position
    // A line the reader dragged themselves always wins: a plate consensus
    // proposes, it never overrules a human correction.
    const override = controlLineOverrides.get(blot.id)
    if (typeof override === 'number') return override
    return resolveControlLinePosition(
      blot.profile ?? [],
      fallbackPeaks ?? (blot as any).peaks ?? [],
      plateControlOffset,
      (blot as any).control_line_frac,
    )
  }, [controlLineOverrides, draggingControlLine, plateControlOffset, module, blots])
  // Commits a manually-dragged control-line position: updates local state
  // immediately (so the marker doesn't snap back while the PATCH is in flight)
  // and persists it via the parent callback.
  const commitControlLineOverride = useCallback((blotId: number, position: number) => {
    setControlLineOverrides((prev) => {
      const next = new Map(prev)
      next.set(blotId, position)
      return next
    })
    onControlLineOverrideChange?.(blotId, position).catch((e) => {
      console.error('Error saving control-line override:', e)
    })
  }, [onControlLineOverrideChange])
  // Gesture state for a control-line drag in progress — set on pointer down,
  // read on every pointer move, cleared on pointer up. Kept in a ref (not
  // state) since it only drives per-event math, not rendering.
  const controlLineDragRef = useRef<{
    blotId: number
    containerRect: DOMRect
    bounds: ImageContentBounds
    profileLength: number
  } | null>(null)
  const handleControlLineDragStart = useCallback((
    e: React.PointerEvent<HTMLDivElement>,
    blot: BlotDetection,
    bounds: ImageContentBounds,
  ) => {
    if (readOnly || !blot?.profile?.length) return
    e.preventDefault()
    e.stopPropagation()
    const container = (e.currentTarget.parentElement as HTMLElement) ?? e.currentTarget
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* noop */ }
    controlLineDragRef.current = {
      blotId: blot.id,
      containerRect: container.getBoundingClientRect(),
      bounds,
      profileLength: blot.profile.length,
    }
    setDraggingControlLine({ blotId: blot.id, position: getControlLine(blot) })
  }, [readOnly, getControlLine])
  const handleControlLineDragMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = controlLineDragRef.current
    if (!d) return
    const relY = e.clientY - d.containerRect.top
    const containerRelFraction = relY / d.containerRect.height
    const imageRelFraction = d.bounds.heightPct > 0
      ? (containerRelFraction - d.bounds.topPct / 100) / (d.bounds.heightPct / 100)
      : containerRelFraction
    const clamped = Math.max(0, Math.min(1, imageRelFraction))
    const pos = Math.max(1, Math.min(d.profileLength - 1, Math.round(clamped * d.profileLength)))
    setDraggingControlLine({ blotId: d.blotId, position: pos })
  }, [])
  const handleControlLineDragEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = controlLineDragRef.current
    controlLineDragRef.current = null
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
    if (!d) return
    setDraggingControlLine((cur) => {
      if (cur && cur.blotId === d.blotId) commitControlLineOverride(cur.blotId, cur.position)
      return null
    })
  }, [commitControlLineOverride])
  // Band selected in the panel waiting to be placed on the image by a click
  const [selectedBandToPlace, setSelectedBandToPlace] = useState<string | null>(null)
  // PROTIS, mode libre : le nom de bande que le biologiste tape lui-même.
  const [freeBandName, setFreeBandName] = useState('')
  // ── Inline edit state for band markers on the sample image ─────────────
  // `bandNameOverrides[blotId][originalName] = customName` — local rename of
  // a band (e.g. "P30" → "p30 v2") that's reflected in the on-image label and
  // propagated to the placedBands record sent to the PDF export.
  const [bandNameOverrides, setBandNameOverrides] = useState<Record<number, Record<string, string>>>({})
  // Active rename target — when set, the marker label switches to an inline
  // input. Driven by a double-click on the marker badge.
  const [renamingBand, setRenamingBand] = useState<{ blotId: number; original: string } | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  // Bands the biologist has deleted from a strip (by original name). Filtered out
  // of interpretBlot's detected set → the removal propagates to the verdict, the
  // gallery card, the modal markers AND the report from this single source.
  // Seeded from persisted state so deletions survive a page reload.
  const [suppressedBands, setSuppressedBands] = useState<Map<number, Set<string>>>(() => {
    const m = new Map<number, Set<string>>()
    Object.entries(savedSuppressedBands ?? {}).forEach(([blotId, names]) => {
      if (Array.isArray(names) && names.length > 0) m.set(Number(blotId), new Set(names))
    })
    return m
  })
  // Active drag target — when set, global pointermove updates the marker's
  // yFraction so the user can slide a band vertically along the strip image.
  const [draggingBand, setDraggingBand] = useState<{ blotId: number; bandName: string } | null>(null)
  // Gesture data for a band-marker drag in progress — NOT state, so tracking
  // it doesn't re-render on every pointer event. Holds the pointer-down
  // origin and whether real movement has happened yet, so a plain click
  // (pointerdown + pointerup with no drag) never touches blotPlacedBands at
  // all — see the useEffect below, which only seeds it on the FIRST move
  // past a small threshold, not on pointerdown itself.
  const bandDragRef = useRef<{
    blotId: number
    bandName: string
    startClientY: number
    initialTopPct: number
    moved: boolean
  } | null>(null)
  // Bumped when an invalid strip-id entry is rejected, to remount the (uncontrolled)
  // Editable so it reverts to the last saved value instead of showing the bad text.
  const [stripIdEditNonce, setStripIdEditNonce] = useState(0)
  // Show/hide the detailed ratio breakdown (table + intensities + positions).
  // Default collapsed — the user only sees the similarity score, and clicks to expand.
  const [showRatioDetails, setShowRatioDetails] = useState(false)
  // Per-blot operator comments
  const [blotComments, setBlotComments] = useState<Record<number, string>>({})
  const [hiddenBlotIds, setHiddenBlotIds] = useState<Set<number>>(
    () => new Set(savedHiddenBlotIds ?? [])
  )
  // Détections qui ont très probablement attrapé du FOND DE PLAQUE plutôt
  // qu'une bandelette. Le pipeline les marque (looks_like_background) en
  // croisant deux signaux faibles : aucun identifiant lu en propre, et un
  // profil plat par rapport au reste de la plaque. Ni l'un ni l'autre ne suffit
  // — une bandelette pâle a un profil plat, une bandelette bien réelle peut
  // avoir un identifiant illisible.
  //
  // La galerie les écarte D'EMBLÉE pour que l'opérateur voie tout de suite la
  // plaque telle qu'elle devrait être, mais n'enregistre rien : le bandeau
  // montre ce qui a été retiré et attend sa décision. Une bandelette qui
  // disparaît sans trace est exactement ce que la comptabilité du flux sert à
  // détecter, on ne va pas l'introduire ici.
  const [proposedBackgroundIds, setProposedBackgroundIds] = useState<number[]>([])
  const [backgroundDecided, setBackgroundDecided] = useState(false)

  // Blots mid "ghost snap" animation — briefly on screen (dissolving) before they
  // are actually moved into hiddenBlotIds.
  const [ghostingIds, setGhostingIds] = useState<Set<number>>(new Set())
  // Ghost a false-positive blot: play the snap, then hide it for real. 👻
  const ghostBlot = (blotId: number) => {
    if (ghostingIds.has(blotId)) return
    setGhostingIds((prev) => new Set(prev).add(blotId))
    window.setTimeout(() => {
      setHiddenBlotIds((prev) => {
        const next = new Set(Array.from(prev).concat(blotId))
        onHiddenBlotsChange?.(Array.from(next))
        return next
      })
      setGhostingIds((prev) => {
        const next = new Set(prev)
        next.delete(blotId)
        return next
      })
    }, 640)
  }
  useEffect(() => {
    if (backgroundDecided) return
    const saved = new Set(savedHiddenBlotIds ?? [])
    const suspects = (blots ?? [])
      .filter((b: any) => b?.looks_like_background === true && !saved.has(b.id))
      .map((b: any) => b.id as number)
    if (!suspects.length) return
    setProposedBackgroundIds((prev) =>
      prev.length === suspects.length && prev.every((v, i) => v === suspects[i]) ? prev : suspects,
    )
    setHiddenBlotIds((prev) => {
      let changed = false
      const next = new Set(prev)
      for (const id of suspects) if (!next.has(id)) { next.add(id); changed = true }
      return changed ? next : prev
    })
  }, [blots, savedHiddenBlotIds, backgroundDecided])

  /** L'opérateur confirme : l'écartement part au serveur comme un masquage ordinaire. */
  const confirmBackgroundRemoval = () => {
    setBackgroundDecided(true)
    setHiddenBlotIds((prev) => {
      const next = new Set(prev)
      proposedBackgroundIds.forEach((id) => next.add(id))
      onHiddenBlotsChange?.(Array.from(next))
      return next
    })
    setProposedBackgroundIds([])
  }

  /** L'opérateur refuse : les détections reviennent, et on ne lui repropose pas. */
  const restoreBackgroundRemoval = () => {
    setBackgroundDecided(true)
    setHiddenBlotIds((prev) => {
      const next = new Set(prev)
      proposedBackgroundIds.forEach((id) => next.delete(id))
      return next
    })
    setProposedBackgroundIds([])
  }

  // Adopt blots hidden from OUTSIDE the gallery — e.g. dragged onto the delete
  // zone on the source image. The internal set only seeds from savedHiddenBlotIds
  // on mount, so without this an externally-deleted blot stayed visible here.
  // Union (never removes) so a blot the gallery just ghosted, still pending its
  // push-up, is never dropped.
  useEffect(() => {
    const saved = savedHiddenBlotIds ?? []
    if (!saved.length) return
    setHiddenBlotIds((prev) => {
      let changed = false
      const next = new Set(prev)
      for (const id of saved) if (!next.has(id)) { next.add(id); changed = true }
      return changed ? next : prev
    })
  }, [savedHiddenBlotIds])
  // Récupération des bandelettes supprimées, demandée depuis la confirmation de
  // relance. Un signal explicite plutôt qu'une liste vide : l'effet ci-dessus
  // n'ajoute que, et remonter la galerie pour la réinitialiser perdrait ce
  // qu'elle garde pour elle (commentaires par bandelette notamment). Le nonce
  // part de 0 et n'agit donc pas au montage.
  useEffect(() => {
    if (!restoreHiddenNonce) return
    setHiddenBlotIds(new Set())
  }, [restoreHiddenNonce])
  const [showRawControlImage, setShowRawControlImage] = useState<boolean>(true)
  const [invertAxes, setInvertAxes] = useState(false)
  const [positiveControlId, setPositiveControlId] = useState<number | null>(null)
  const [positiveControl2Id, setPositiveControl2Id] = useState<number | null>(null)
  const [positiveControl3Id, setPositiveControl3Id] = useState<number | null>(null)

  // Bordeaux/Dynablot multitest: EACH pathology's own T1/T2/T3 — see
  // savedPositiveControlsByPathology. Declared here (ahead of activeControlId
  // below, which needs it synchronously) rather than near togglePositiveControl
  // further down.
  const [positiveControlsByPathologyState, setPositiveControlsByPathologyState] = useState<
    Record<string, { positiveControlId?: number | null; positiveControl2Id?: number | null; positiveControl3Id?: number | null }>
  >({})
  useEffect(() => {
    // Re-key onto the canonical pathology id, merging any keys that were
    // saved under a slightly different raw OCR reading of the same
    // pathology (see canonicalPathologyKey) — later entries win per field,
    // which only matters if BOTH raw variants happen to have set the same
    // slot, an edge case with no real ordering to prefer anyway.
    const raw = savedPositiveControlsByPathology || {}
    const merged: typeof raw = {}
    for (const [rawKey, slot] of Object.entries(raw)) {
      const key = canonicalPathologyKey(rawKey) ?? rawKey
      merged[key] = { ...merged[key], ...slot }
    }
    setPositiveControlsByPathologyState(merged)
  }, [savedPositiveControlsByPathology])

  // Resolves slot `num` to the RIGHT storage — this pathology's own slots
  // while filtered, the legacy case-wide slots otherwise (unchanged).
  const effectiveControlId = (num: 1 | 2 | 3): number | null => {
    const key = canonicalPathologyKey(pathologyFilter)
    if (key) {
      const slot = positiveControlsByPathologyState[key] || {}
      return (num === 1 ? slot.positiveControlId : num === 2 ? slot.positiveControl2Id : slot.positiveControl3Id) ?? null
    }
    return (num === 1 ? positiveControlId : num === 2 ? positiveControl2Id : positiveControl3Id) ?? null
  }

  // The ids that currently ARE a témoin, in the RIGHT scope (this pathology's
  // own 3 slots while filtered, the legacy case-wide ones otherwise) — the
  // single source every "is this blot a témoin" check should read, instead of
  // comparing against positiveControlId/2Id/3Id directly (those are the
  // legacy slots ONLY; reading them unconditionally is what made a témoin
  // card show as a plain sample, and even compare itself against itself, once
  // multitest storage moved it out of those 3 columns).
  const activeControlIds = useMemo(
    () => [effectiveControlId(1), effectiveControlId(2), effectiveControlId(3)].filter((id): id is number => id != null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [positiveControlId, positiveControl2Id, positiveControl3Id, pathologyFilter, positiveControlsByPathologyState]
  )

  const [selectedControlForBlot, setSelectedControlForBlot] = useState<number>(1) // Which control to use (1, 2, or 3)
  const [superposed, setSuperposed] = useState(true)
  const [showTemoin, setShowTemoin] = useState(true)
  const [showBandelette, setShowBandelette] = useState(true)
  // Toggle for the band-position overlay drawn on the sample image (default on)
  const [showImageBands] = useState(true) // band overlay on the strip images (always on)
  // Toggle for the band annotations (reference lines + labels) drawn ON the
  // profile chart. Turning it off gives a clean profile+peaks view — useful
  // during calibration to place bands precisely on the peaks without the
  // annotations covering them. (default on)
  const [showChartBands, setShowChartBands] = useState(true)
  const [showTemoinBands, setShowTemoinBands] = useState(true)
  const [barMode] = useState(false)
  // Registry of every mounted profile chart (comparison, calibration control/sample,
  // side-by-side…) so the zoom controls act on whichever chart is currently on screen,
  // not only the superposed one. Destroyed instances (canvas === null) are pruned lazily.
  const chartRegistry = useRef<Set<any>>(new Set())
  const registerChart = (c: any | null) => { if (c) chartRegistry.current.add(c) }
  // Persist the current X zoom window so it survives re-renders (toggling kDa / invert /
  // sensitivity rebuilds the chart options, which otherwise wipes the plugin's zoom).
  const zoomXRef = useRef<{ min?: number; max?: number }>({})
  // Guided tour: -1 = inactive, otherwise the current step index.
  const [tourStep, setTourStep] = useState<number>(-1)
  const tourAutoStartedRef = useRef(false)
  const [imageRenderKey, setImageRenderKey] = useState(0) // Force image re-render
  const [displayedControlImage, setDisplayedControlImage] = useState<string | null>(null)
  const [peakSensitivity, setPeakSensitivity] = useState<number>(30) // 0-100%, detects more peaks at higher values (default 30% for balanced detection)

  // Manual band selection for positive control — dynamic from immunoblotConfig
  // Sorted by descending MW (highest first = leftmost on profile) and excluding non-specific bands
  const BAND_NAMES: string[] = useMemo(() => {
    const specific = immunoblotConfig?.bands?.specific
    if (specific && specific.length > 0) {
      return [...specific]
        .sort((a: any, b: any) => b.molecularWeight - a.molecularWeight)
        .map((b: any) => b.name)
    }
    // A LOADED config with a genuinely empty band list (paired-comparison
    // pathologies like CIP-WB have no named bands at all — see
    // pairedComparisonPartners) means exactly that: no bands. Only fall back
    // to the IgG defaults when the config itself hasn't loaded yet.
    if (immunoblotConfig) return []
    return ['P45', 'P40', 'P33', 'P31', 'P30']
  }, [immunoblotConfig])
  type BandName = string

  // Required bands (blocking for interpretation) vs optional (improve accuracy but not blocking)
  const REQUIRED_BAND_NAMES: string[] = useMemo(() => {
    const specific = immunoblotConfig?.bands?.specific
    if (!specific) return []
    return [...specific]
      .filter((b: any) => b.required === true)
      .sort((a: any, b: any) => b.molecularWeight - a.molecularWeight)
      .map((b: any) => b.name)
  }, [immunoblotConfig])

  const OPTIONAL_BAND_NAMES: string[] = useMemo(() => {
    const specific = immunoblotConfig?.bands?.specific
    if (!specific) return BAND_NAMES
    return [...specific]
      .filter((b: any) => b.required !== true)
      .sort((a: any, b: any) => b.molecularWeight - a.molecularWeight)
      .map((b: any) => b.name)
  }, [immunoblotConfig, BAND_NAMES])

  // Calibration sequence: required bands first (so early save is possible), then optional
  const CALIBRATION_BAND_ORDER: string[] = useMemo(() => {
    return [...REQUIRED_BAND_NAMES, ...OPTIONAL_BAND_NAMES]
  }, [REQUIRED_BAND_NAMES, OPTIONAL_BAND_NAMES])

  // Reference band = lowest MW band (last in descending-MW sorted list), e.g. P30 for Toxo, P7 for Echino
  const REFERENCE_BAND = useMemo(() => BAND_NAMES[BAND_NAMES.length - 1] ?? 'P30', [BAND_NAMES])
  const [bandSelectionMode, setBandSelectionMode] = useState(false)
  // When true, clicking a peak only updates the current band and exits — no auto-advance.
  // Used when the user clicks an existing band badge to correct just that one.
  const [singleBandEditMode, setSingleBandEditMode] = useState(false)
  const [currentBandToSelect, setCurrentBandToSelect] = useState<BandName | null>(null)
  const [selectedBandPositions, setSelectedBandPositions] = useState<Map<BandName, { position: number, intensity: number }>>(new Map())
  // Bands the operator has placed BY HAND in this session. Placing one band
  // re-projects the rest from it (see handleBandPeakClick); these are the ones
  // that must never be moved by that re-projection — a position the operator
  // set is an observation, not a suggestion.
  const [handPlacedBands, setHandPlacedBands] = useState<Set<BandName>>(new Set())
  const [hoverState, setHoverState] = useState<{ relFraction: number; rawProfileIdx: number; chartX: number } | null>(null)
  const [showKdaScale, setShowKdaScale] = useState(false)

  // Resolve current active control ID from state (before activePositiveControl memo is available)
  const activeControlId = useMemo(() => {
    switch (selectedControlForBlot) {
      case 2: return effectiveControlId(2)
      case 3: return effectiveControlId(3)
      default: return effectiveControlId(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedControlForBlot, positiveControlId, positiveControl2Id, positiveControl3Id, pathologyFilter, positiveControlsByPathologyState])

  // Synchronously derived band positions for the active control
  // This avoids the race condition where useEffect updates selectedBandPositions AFTER render,
  // causing one frame where activePositiveControl is T2 but selectedBandPositions still has T1's data
  // Does the operator have a SAVED manual calibration for the active control?
  // Kept separate from the positions so the UI can tell "suggested" from
  // "operator-confirmed" even though both feed the verdict.
  const hasManualCalibration = useMemo(() => {
    if (!savedBandCalibration || !activeControlId) return false
    const cc = savedBandCalibration[String(activeControlId)]
    if (cc && typeof cc === 'object' && Object.keys(cc).some((k) => BAND_NAMES.includes(k))) return true
    return Object.keys(savedBandCalibration).some((k) => BAND_NAMES.includes(k))
  }, [savedBandCalibration, activeControlId, BAND_NAMES])

  const effectiveBandPositions = useMemo((): Map<BandName, { position: number, intensity: number }> => {
    // During band selection, use the live state (user is actively clicking peaks)
    if (bandSelectionMode) return selectedBandPositions

    // 1) Operator's saved manual calibration for the active control.
    if (savedBandCalibration && activeControlId) {
      const controlCalibration = savedBandCalibration[String(activeControlId)]
      if (controlCalibration && typeof controlCalibration === 'object'
          && Object.keys(controlCalibration).some((k) => BAND_NAMES.includes(k))) {
        const map = new Map<BandName, { position: number, intensity: number }>()
        Object.entries(controlCalibration).forEach(([band, data]) => {
          if (BAND_NAMES.includes(band)) map.set(band, data as { position: number, intensity: number })
        })
        if (map.size > 0) return map
      }
      // Old flat format { P45: {...}, P30: {...} } — use for any control.
      if (Object.keys(savedBandCalibration).some((k) => BAND_NAMES.includes(k))) {
        const map = new Map<BandName, { position: number, intensity: number }>()
        Object.entries(savedBandCalibration).forEach(([band, data]) => {
          if (BAND_NAMES.includes(band) && data && typeof data === 'object' && 'position' in data) {
            map.set(band, data as unknown as { position: number, intensity: number })
          }
        })
        if (map.size > 0) return map
      }
    }

    // 2) No manual calibration → fall back to the auto-suggestion as the working
    // reference, so a good suggestion drives the verdict without forcing the
    // operator to re-place every band (option A). Flagged "suggested" for the UI;
    // the per-blot "Valider" stays the clinical sign-off. Control blot resolved
    // from `blots` (activePositiveControl / blotsArray aren't defined this early).
    // `blots` can arrive as a JSON STRING — a bare Array.isArray check returned []
    // on those, leaving the reference empty and the verdict "en attente" despite a
    // good suggestion. Parse it exactly like blotsArray does.
    const blotList: any[] = Array.isArray(blots)
      ? blots
      : (typeof blots === 'string'
          ? (() => { try { const p = JSON.parse(blots); return Array.isArray(p) ? p : [] } catch { return [] } })()
          : [])
    const ctrlBlot = blotList.find((b: any) => b.id === activeControlId)
    if (ctrlBlot && immunoblotConfig) {
      const suggested = suggestBands(ctrlBlot, immunoblotConfig)
      if (suggested && suggested.size > 0) {
        const map = new Map<BandName, { position: number, intensity: number }>()
        suggested.forEach((position, name) => {
          if (!BAND_NAMES.includes(name)) return
          const idx = Math.max(0, Math.min((ctrlBlot.profile?.length ?? 1) - 1, Math.round(position)))
          map.set(name as BandName, { position, intensity: ctrlBlot.profile?.[idx] ?? 0 })
        })
        if (map.size > 0) return map
      }
    }

    return new Map()
  }, [savedBandCalibration, activeControlId, bandSelectionMode, selectedBandPositions, BAND_NAMES, blots, immunoblotConfig])

  // True when the bands currently driving the verdict come from the auto-suggestion
  // rather than an operator calibration — used to mark them "suggéré" in the UI.
  const bandsAreSuggested = !bandSelectionMode && !hasManualCalibration && effectiveBandPositions.size > 0

  // Positions drawn on the profile chart. While the operator has picked a band to
  // (re)calibrate, hide that band's existing marker so its previous position
  // doesn't clutter the profile while they place it — it reappears at the new
  // spot as soon as a peak is clicked (calibration exits on click). Badges and
  // checkmarks keep using effectiveBandPositions, so only the chart is affected.
  const chartBandPositions = useMemo(() => {
    if (bandSelectionMode && currentBandToSelect && effectiveBandPositions.has(currentBandToSelect)) {
      const m = new Map(effectiveBandPositions)
      m.delete(currentBandToSelect)
      return m
    }
    return effectiveBandPositions
  }, [effectiveBandPositions, bandSelectionMode, currentBandToSelect])

  // Restore band calibration for the active control when it changes or when saved data loads
  // Also handles migration from old flat format { P45: {...} } to new per-control format { "1": { P45: {...} } }
  useEffect(() => {
    if (savedBandCalibration && activeControlId) {
      // Try per-control format FIRST: { "controlId": { P45: {...}, P30: {...} } }
      const controlCalibration = savedBandCalibration[String(activeControlId)]
      if (controlCalibration && typeof controlCalibration === 'object') {
        const hasValidBands = Object.keys(controlCalibration).some(k => BAND_NAMES.includes(k))
        if (hasValidBands) {
          const map = new Map<BandName, { position: number, intensity: number }>()
          Object.entries(controlCalibration).forEach(([band, data]) => {
            if (BAND_NAMES.includes(band)) {
              map.set(band, data as { position: number, intensity: number })
            }
          })
          if (map.size > 0) {
            setSelectedBandPositions(map)
            return
          }
        }
      }

      // Fallback: old flat format { P45: {...}, P30: {...} } — use for any control
      const keys = Object.keys(savedBandCalibration)
      const isOldFormat = keys.some(k => BAND_NAMES.includes(k))
      if (isOldFormat) {
        const map = new Map<BandName, { position: number, intensity: number }>()
        Object.entries(savedBandCalibration).forEach(([band, data]) => {
          if (BAND_NAMES.includes(band) && data && typeof data === 'object' && 'position' in data) {
            map.set(band, data as unknown as { position: number, intensity: number })
          }
        })
        if (map.size > 0) {
          setSelectedBandPositions(map)
          return
        }
      }
    }
    // No calibration for this control — reset
    if (!bandSelectionMode) {
      setSelectedBandPositions(new Map())
      setHandPlacedBands(new Set())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedBandCalibration, activeControlId, BAND_NAMES, bandSelectionMode])

  // Ensure blots is an array
  const blotsArray: BlotDetection[] = useMemo(() => {
    let arr: BlotDetection[] = []
    if (!blots) return arr
    if (Array.isArray(blots)) arr = blots
    else if (typeof blots === 'string') {
      try {
        const parsed = JSON.parse(blots)
        arr = Array.isArray(parsed) ? parsed : []
      } catch (_e) {
        arr = []
      }
    }
    return arr.filter((b) => !hiddenBlotIds.has(b.id))
  }, [blots, hiddenBlotIds])

  /**
   * One rule, stated once: an identifier is unique WITHIN a search.
   *
   * On a standard plate every strip is one patient, no label is set, and the
   * rule degenerates to "unique on the plate" — two strips sharing an id means
   * one is misread or mistyped, and attributing a result to the wrong patient
   * is the one failure here with a clinical consequence.
   *
   * On a Dynablot multitest plate (the only path that fills `pathology_label`)
   * one sample is run across several self-tests, so the same id legitimately
   * reappears from one search to the next — including on two different
   * Aspergillus strips, which are two searches. Twice inside the SAME search
   * stays impossible.
   */
  const stripIdKey = (blot: BlotDetection) => {
    const sid = (blot.strip_id ?? '').trim().toUpperCase()
    // A bare letter ("K", propagated plate-wide by design) is not an id yet.
    if (!/^[A-Z]\d+$/.test(sid)) return null
    return `${((blot.pathology_label ?? '') as string).trim().toUpperCase()}|${sid}`
  }

  /**
   * Keys held by more than one visible strip — what drives the `conflict`
   * marker, which until now nothing ever produced. Derived from the list
   * rather than stored on each strip, so it is always current: correcting
   * either twin clears the flag on both, with no state to invalidate.
   */
  const duplicateStripIdKeys = useMemo(() => {
    const counts = new Map<string, number>()
    blotsArray.forEach((b) => {
      const key = stripIdKey(b)
      if (key) counts.set(key, (counts.get(key) ?? 0) + 1)
    })
    return new Set(
      Array.from(counts.entries())
        .filter(([, n]) => n > 1)
        .map(([key]) => key),
    )
  }, [blotsArray])

  /** Does this strip share its id with another strip of the same search? */
  const hasStripIdConflict = useCallback(
    (blot: BlotDetection) => {
      const key = stripIdKey(blot)
      return key != null && duplicateStripIdKeys.has(key)
    },
    [duplicateStripIdKeys],
  )

  /**
   * The strip of the same search already carrying `candidate`, if any — what
   * makes a hand-entered id impossible to save.
   */
  const stripCarryingId = useCallback(
    (candidate: string, blot: BlotDetection) => {
      const key = stripIdKey({ ...blot, strip_id: candidate })
      if (!key) return undefined
      return blotsArray.find((b) => b.id !== blot.id && stripIdKey(b) === key)
    },
    [blotsArray],
  )

  // Staggered entrance: freeze each blot's delay rank at the order it had the
  // first time the gallery mounted (i.e. right when the analysis result
  // landed), so later actions — filtering, deleting a card, which shift
  // array indices — never re-trigger or re-order the reveal animation. A
  // blot id not in this map (added later via "add-blot") simply gets no
  // entrance animation.
  const entranceOrderRef = useRef<Map<number, number> | null>(null)
  if (entranceOrderRef.current === null) {
    entranceOrderRef.current = new Map(blotsArray.map((b, i) => [b.id, i]))
  }
  const [entranceDone, setEntranceDone] = useState(false)
  useEffect(() => {
    const maxRank = Math.min((entranceOrderRef.current?.size ?? 0) - 1, 15)
    const totalMs = Math.max(0, maxRank) * 70 + 420 + 150
    const timer = setTimeout(() => setEntranceDone(true), totalMs)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Bordeaux/Dynablot multitest: distinct pathology names OCR-read across this
  // case's strips (see pathology_label). Only worth surfacing a filter/section
  // headers when there's genuinely more than one — a normal single-pathology
  // upload reads the same label on every strip and shouldn't show either.
  const distinctPathologyLabels = useMemo(() => {
    const seen = new Set<string>()
    const ordered: string[] = []
    blotsArray.forEach((b) => {
      const label = (b as any).pathology_label as string | null | undefined
      if (label && !seen.has(label)) {
        seen.add(label)
        ordered.push(label)
      }
    })
    return ordered
  }, [blotsArray])
  const isMultiPathologyCase = distinctPathologyLabels.length > 1

  // ── Gap-filling strip-id suggestions ────────────────────────────────────────
  // Dynablot strips are laid out top-to-bottom in strict numeric order within
  // each self-test block (I1, I2, I3… for Aspergillus on a real case) — so
  // when OCR fails on ONE strip (garbled read, e.g. "C" instead of "I8") but
  // gets every OTHER strip in that block right, the missing number is not a
  // guess: it's whatever rank that strip holds in the row order, deduced from
  // its confidently-read neighbours. Scoped per pathology_label (multitest) —
  // or the whole case for a single-pathology upload, where every blot shares
  // (or lacks) the same label and this naturally becomes one group.
  const gapFilledStripIdSuggestions = useMemo(() => {
    const result = new Map<number, string>()
    const groups = new Map<string, BlotDetection[]>()
    blotsArray.forEach((b) => {
      const label = ((b as any).pathology_label as string | null | undefined) ?? '__default__'
      const arr = groups.get(label) ?? []
      arr.push(b)
      groups.set(label, arr)
    })
    groups.forEach((group) => {
      // Order along the plate. A Dynablot report stacks its strips as rows, so
      // they read top to bottom; Nancy and Amiens mount them side by side as
      // lanes whose y_top differs only by a few px of mounting stagger, so they
      // read left to right. Sorting a side-by-side plate by row ranked it
      // essentially at random and the inferred sequence came out wrong — a real
      // Nancy plate reading I10/I11 proposed I8 and I9 for the two strips next
      // to them. Pick the axis the strips actually spread along.
      const xs = group.map((b) => b.x0 ?? 0)
      const ys = group.map((b) => b.y_top ?? 0)
      const spreadX = Math.max(...xs) - Math.min(...xs)
      const spreadY = Math.max(...ys) - Math.min(...ys)
      const sorted = [...group].sort((a, b) =>
        spreadX >= spreadY
          ? (a.x0 ?? 0) - (b.x0 ?? 0)
          : (a.y_top ?? 0) - (b.y_top ?? 0),
      )
      const known = sorted.map((b, i) => {
        const status = (b as any).strip_id_status
        const sid = (b as any).strip_id as string | undefined
        if ((status === 'sure' || status === 'edited') && sid) {
          const m = STRIP_ID_SEQ_RE.exec(sid.trim())
          if (m) return { rank: i + 1, prefix: m[1], num: parseInt(m[2], 10) }
        }
        return null
      })
      const knownEntries = known.filter((k): k is { rank: number; prefix: string; num: number } => k !== null)
      // Need at least 2 confident anchors, ALL sharing one letter prefix, AND
      // rank - number must be the SAME constant offset for every one of them
      // — a plain run of consecutive numbers, whatever it starts at. NOT
      // necessarily rank===num: a Dynablot multitest numbers strips
      // CONTINUOUSLY across the whole sheet, not per pathology block — a
      // Toxo IgG group can start at K10 (continuing IgM's own K6-K12), so its
      // OWN row rank 1 is number 10, not 1. Measured on a real case: K10(rank
      // 1)/K12(rank 3)/K13(rank 4)/K14(rank 5) all share offset 9 — missing
      // rank 2 is therefore K11, not K2. Any mismatch (mixed prefixes, a non-
      // sequential numbering scheme) means we can't safely infer a gap, so
      // the group is left alone entirely.
      if (knownEntries.length < 2) return
      const prefix = knownEntries[0].prefix
      const offset = knownEntries[0].num - knownEntries[0].rank
      const isSequential = knownEntries.every((k) => k.prefix === prefix && (k.num - k.rank) === offset)
      if (!isSequential) return
      sorted.forEach((b, i) => {
        const status = (b as any).strip_id_status
        if (status === 'sure' || status === 'edited') return
        result.set(b.id, `${prefix}${i + 1 + offset}`)
      })
    })
    return result
  }, [blotsArray])

  // ── Per-pathology context, independent of the active filter ────────────────
  // Every memo above (immunoblotConfig, activePositiveControl,
  // effectiveBandPositions, BAND_NAMES, REQUIRED_BAND_NAMES) is scoped to
  // whatever `pathologyFilter` currently is — correct while filtered to ONE
  // pathology, but "Toutes" (pathologyFilter === null) mixes blots from every
  // pathology in the same grid, and those memos fall back to the single
  // legacy case-wide témoin slots — the WRONG témoin/config for every
  // pathology except whichever one happens to occupy those slots. That's why
  // a card read POSITIF filtered but NÉGATIF in "Toutes": the verdict was
  // computed against a different pathology's témoin entirely.
  // This resolves the same 5 values for EVERY distinct pathology on the case
  // up front, keyed by canonical pathology id, so the "Toutes" card loop can
  // pass the right one to interpretBlot/identifyBands per blot — via their
  // new optional `ctx` param — instead of relying on the filter-scoped memos.
  // Those memos, and every other existing caller, are untouched.
  const pathologyContexts = useMemo(() => {
    const map = new Map<string, {
      config: any
      control1Id: number | null
      control2Id: number | null
      control3Id: number | null
      activeControl: BlotDetection | undefined
      effectiveBandPositions: Map<string, { position: number; intensity: number }>
      BAND_NAMES: string[]
      REQUIRED_BAND_NAMES: string[]
    }>()
    distinctPathologyLabels.forEach((label) => {
      const key = canonicalPathologyKey(label)
      if (!key || map.has(key)) return

      const rawCfg: any = (pathologyConfigMap?.[label] !== undefined) ? pathologyConfigMap[label] : rawImmunoblotConfig
      // Same species-band normalisation as the immunoblotConfig memo above.
      const intermediate = rawCfg?.bands?.intermediate
      const config = (Array.isArray(intermediate) && intermediate.length > 0)
        ? {
            ...rawCfg,
            bands: {
              ...rawCfg.bands,
              specific: [
                ...(rawCfg.bands.specific || []).map((b: any) => ({ ...b, bandClass: b.bandClass ?? 'genus' })),
                ...intermediate.map((b: any) => ({ ...b, bandClass: 'species', required: false })),
              ],
            },
          }
        : rawCfg

      const specific = config?.bands?.specific
      // Same distinction as the BAND_NAMES memo above: a loaded config with a
      // genuinely empty band list (paired-comparison pathologies) means no
      // bands, not "not loaded yet" — only default to IgG's shape when there
      // is no config at all.
      const BAND_NAMES: string[] = (specific && specific.length > 0)
        ? [...specific].sort((a: any, b: any) => b.molecularWeight - a.molecularWeight).map((b: any) => b.name)
        : config ? [] : ['P45', 'P40', 'P33', 'P31', 'P30']
      const REQUIRED_BAND_NAMES: string[] = specific
        ? [...specific].filter((b: any) => b.required === true).sort((a: any, b: any) => b.molecularWeight - a.molecularWeight).map((b: any) => b.name)
        : []

      const slot = positiveControlsByPathologyState[key] || {}
      const control1Id = slot.positiveControlId ?? null
      const control2Id = slot.positiveControl2Id ?? null
      const control3Id = slot.positiveControl3Id ?? null
      const activeControl = blotsArray.find((b) => b.id === control1Id)

      const bandPositions = new Map<string, { position: number; intensity: number }>()
      if (activeControl && config) {
        // 1) Operator's saved manual calibration for this pathology's témoin.
        const controlCalibration = savedBandCalibration?.[String(control1Id)]
        if (controlCalibration && typeof controlCalibration === 'object'
            && Object.keys(controlCalibration).some((k) => BAND_NAMES.includes(k))) {
          Object.entries(controlCalibration).forEach(([band, data]) => {
            if (BAND_NAMES.includes(band)) bandPositions.set(band, data as { position: number; intensity: number })
          })
        }
        // 2) No manual calibration → fall back to auto-suggestion, same
        // cascade as the effectiveBandPositions memo above.
        if (bandPositions.size === 0) {
          const suggested = suggestBands(activeControl, config)
          if (suggested && suggested.size > 0) {
            suggested.forEach((position, name) => {
              if (!BAND_NAMES.includes(name)) return
              const idx = Math.max(0, Math.min((activeControl.profile?.length ?? 1) - 1, Math.round(position)))
              bandPositions.set(name, { position, intensity: activeControl.profile?.[idx] ?? 0 })
            })
          }
        }
      }

      map.set(key, { config, control1Id, control2Id, control3Id, activeControl, effectiveBandPositions: bandPositions, BAND_NAMES, REQUIRED_BAND_NAMES })
    })
    return map
  }, [distinctPathologyLabels, pathologyConfigMap, rawImmunoblotConfig, positiveControlsByPathologyState, blotsArray, savedBandCalibration])

  // ── Paired-comparison pairing (CIP-WB mère/enfant, J0/J+N, sérum/humeur
  // aqueuse) ───────────────────────────────────────────────────────────────
  // Some pathologies (config.interpretation.mode === 'paired_comparison',
  // e.g. LDBIO TOXOPLASMA CIP-WB) never use a manufacturer témoin at all —
  // the kit compares two SAMPLE strips to each other. On a real Dynablot
  // case (b1c28a42), such a pathology's strips print as consecutive pairs in
  // row order (A1-A4 IgG = 2 couples of 2) — same y_top row order
  // gapFilledStripIdSuggestions groups by, just paired up two at a time
  // instead of read individually. First strip of each pair = reference
  // (mère / J0 / sérum), second = compared (enfant / J+N / humeur aqueuse) —
  // per the operator's own read of how these reports are laid out.
  // Global across all pathologies on the case (not pathologyFilter-scoped)
  // since blot ids are unique case-wide and this never depends on which
  // pathology is currently filtered.
  /**
   * A couple's identity, independent of which member currently holds which
   * role — so flipping it twice returns to the guess instead of drifting.
   */
  const pairKeyOf = (a: BlotDetection, b: BlotDetection) =>
    `${Math.min(a.id, b.id)}-${Math.max(a.id, b.id)}`

  const pairedComparisonPartners = useMemo(() => {
    const result = new Map<number, { partner: BlotDetection; role: 'reference' | 'compared' }>()
    const groups = new Map<string, BlotDetection[]>()
    blotsArray.forEach((b) => {
      const label = (b as any).pathology_label as string | null | undefined
      if (!label) return
      const cfg: any = pathologyConfigMap?.[label]
      if (cfg?.interpretation?.mode !== 'paired_comparison') return
      const arr = groups.get(label) ?? []
      arr.push(b)
      groups.set(label, arr)
    })
    groups.forEach((group) => {
      const sorted = [...group].sort((a, b) => (a.y_top ?? 0) - (b.y_top ?? 0))
      for (let i = 0; i + 1 < sorted.length; i += 2) {
        // Row order only GUESSES who is the mother. A plate mounted the other
        // way round is flipped by the reader, couple by couple — the guess
        // decides where the differences and the verdict are reported, so it
        // needs a remedy rather than a re-analysis.
        const swapped = (savedPairedRoleSwaps ?? {})[pairKeyOf(sorted[i], sorted[i + 1])] === true
        const reference = swapped ? sorted[i + 1] : sorted[i]
        const compared = swapped ? sorted[i] : sorted[i + 1]
        result.set(reference.id, { partner: compared, role: 'reference' })
        result.set(compared.id, { partner: reference, role: 'compared' })
      }
      // An odd strip left over (uneven count) is deliberately left unset —
      // interpretBlot's paired-mode branch reports "non appariée" instead of
      // guessing a partner.
    })
    return result
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blotsArray, pathologyConfigMap, savedPairedRoleSwaps])

  /**
   * Flip the couple `blot` belongs to. Toggling the same couple twice drops the
   * key entirely, so the stored map only ever holds the couples that really
   * contradict the row-order guess.
   */
  const togglePairedRoles = useCallback(
    (blot: BlotDetection) => {
      const info = pairedComparisonPartners.get(blot.id)
      if (!info) return
      const key = pairKeyOf(blot, info.partner)
      const next = { ...(savedPairedRoleSwaps ?? {}) }
      if (next[key]) delete next[key]
      else next[key] = true
      onPairedRoleSwapChange?.(next)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pairedComparisonPartners, savedPairedRoleSwaps, onPairedRoleSwapChange],
  )

  // Drop a stale filter selection (e.g. after re-analysis changes the labels).
  useEffect(() => {
    if (pathologyFilter && !distinctPathologyLabels.includes(pathologyFilter)) {
      setPathologyFilter(null)
    }
  }, [pathologyFilter, distinctPathologyLabels])

  // Let the parent scope export to the active filter (see onPathologyFilterChange).
  useEffect(() => {
    onPathologyFilterChange?.(pathologyFilter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathologyFilter])

  // PROTIS : un projet peut poser PLUSIEURS plaques sur une page
  // (A.platensis, G.sulfuraria) — plate_label les nomme, et ce sous-filtre
  // les sépare une fois le projet choisi. Remis à zéro quand le projet change.
  const [plateFilter, setPlateFilter] = useState<string | null>(null)
  useEffect(() => { setPlateFilter(null) }, [pathologyFilter])
  const displayedBlotsArray = useMemo(
    () => blotsArray.filter((b) =>
      (!pathologyFilter || (b as any).pathology_label === pathologyFilter)
      && (!plateFilter || (b as any).plate_label === plateFilter)),
    [blotsArray, pathologyFilter, plateFilter]
  )

  // Positive-control HINT — never an auto-pick. Band pattern alone is only ~45%
  // right for the single best lane (a strongly positive patient shows the same
  // bands as the control), but the true témoin sits in the top 2 ~82% of the
  // time. So we rank by template score and surface the best 1–2 lanes as a
  // "témoin probable ?" badge the operator confirms with a click. Requiring
  // matched ≥ 3 means a real band pattern (P40/P38/P33…), not just the P30 anchor,
  // which keeps clearly-negative lanes out of the hint. Built from blotsArray so
  // it inherits the same JSON-parsing/hidden-filtering (raw `blots` may be a
  // stringified payload — calling .filter on it directly would throw).
  const controlCandidateIds = useMemo(() => {
    // Le rapport Dynablot NOMME son propre témoin : la colonne échantillon de
    // la bandelette porte « C+… » (vérifié sur les plaques Bordeaux réelles :
    // « C+AS-026 » sur le contrôle Aspergillus, y compris sur les plaques où
    // personne n'avait désigné de témoin). Une référence imprimée vaut mieux
    // que n'importe quel score de bandes — ces pistes passent en tête des
    // suggestions. Elles ne sont jamais sélectionnées toutes seules : la
    // désignation reste un clic du biologiste.
    const printed = blotsArray
      .filter((b: any) => /(^|[^a-z0-9])c\+/i.test(String(b.sample_label ?? '')))
      .map((b: any) => b.id)
    const scored = blotsArray
      .map((b: any) => ({ id: b.id, s: scoreControlCandidate(b, immunoblotConfig) }))
      // Require a real band pattern, scaled to the template: ~60% of its bands,
      // min 2. Toxo (5 bands) → 3; Toxocara (3 bands) → 2. This keeps clearly-
      // negative lanes out without demanding a perfect match on short templates.
      .filter((x): x is { id: number; s: { matched: number; intensity: number; total: number } } =>
        !!x.s && x.s.matched >= Math.max(2, Math.ceil(x.s.total * 0.6)))
      .sort((a, b) => (b.s.matched - a.s.matched) || (b.s.intensity - a.s.intensity))
    return Array.from(new Set([...printed, ...scored.slice(0, 2).map((x) => x.id)]))
  }, [blotsArray, immunoblotConfig])

  // No sync needed — parent uses key prop to force remount on case/reanalysis change

  // Reset when case changes (caseId changes). NOT positiveControlsByPathologyState:
  // that one's own prop-sync effect is declared BEFORE this one (it has to run
  // ahead of activeControlId, further up), so on first mount this effect would
  // fire straight after it and wipe the just-loaded per-pathology témoins back
  // to {} — every témoin looking unselected until manually re-picked, exactly
  // as reported. Harmless to drop here: switching cases already remounts this
  // whole component (parent uses a key prop), and that effect's own sync
  // handles a case with no saved témoins yet (→ {}) correctly on its own.
  useEffect(() => {
    console.log('🔄 Case changed, resetting positive control state')
    setPositiveControlId(null)
    setPositiveControl2Id(null)
    setPositiveControl3Id(null)
  }, [caseId])

  // Load saved positive controls from props (priority #1)
  useEffect(() => {
    console.log('🔄 Loading saved controls from props:', {
      savedPositiveControlId,
      savedPositiveControl2Id,
      savedPositiveControl3Id
    })

    if (savedPositiveControlId !== undefined && savedPositiveControlId !== null) {
      setPositiveControlId(savedPositiveControlId)
    } else if (savedPositiveControlId === null) {
      // Explicitly null means no saved control
      setPositiveControlId(null)
    } else {
      // undefined means still loading
      console.log('⏳ Saved positive control 1 not yet loaded (undefined)')
    }

    if (savedPositiveControl2Id !== undefined && savedPositiveControl2Id !== null) {
      setPositiveControl2Id(savedPositiveControl2Id)
    } else if (savedPositiveControl2Id === null) {
      setPositiveControl2Id(null)
    } else {
      console.log('⏳ Saved positive control 2 not yet loaded (undefined)')
    }

    if (savedPositiveControl3Id !== undefined && savedPositiveControl3Id !== null) {
      setPositiveControl3Id(savedPositiveControl3Id)
    } else if (savedPositiveControl3Id === null) {
      setPositiveControl3Id(null)
    } else {
      console.log('⏳ Saved positive control 3 not yet loaded (undefined)')
    }
  }, [savedPositiveControlId, savedPositiveControl2Id, savedPositiveControl3Id])

  // REMOVED: Auto-suggestion is now done only once after initial analysis in parent component
  // This prevents re-suggesting every time the case is opened

  // Function to calculate peaks count IN ROI for a given sensitivity (without state dependency)
  // positiveControl: optional positive control blot to use for ROI calculation (P30 reference)
  const calculatePeaksInROIForSensitivity = (blot: BlotDetection, sensitivity: number, positiveControl?: BlotDetection | null): number => {
    if (!blot || !blot.profile || blot.profile.length === 0) return 0

    // Dynamic ROI bounds based on P30 position from POSITIVE CONTROL (témoin) ± 300 pixels
    const { start: ROI_START, end: ROI_END } = getROIBounds(blot, positiveControl)

    const originalPeaks = new Set(blot.peaks || [])
    if (sensitivity === 0) {
      // Count only original peaks in ROI
      return (blot.peaks || []).filter(p => p >= ROI_START && p <= ROI_END).length
    }

    const profile = blot.profile
    const maxIntensity = Math.max(...profile)
    if (maxIntensity === 0) {
      return (blot.peaks || []).filter(p => p >= ROI_START && p <= ROI_END).length
    }

    const sensitivityFactor = Math.pow(1 - sensitivity / 100, 2)
    const minIntensityThreshold = maxIntensity * sensitivityFactor * 0.15
    const windowSize = Math.max(3, Math.floor(3 + sensitivityFactor * 9))
    const minProminence = maxIntensity * sensitivityFactor * 0.05
    const minDistance = Math.max(5, Math.floor(5 + sensitivityFactor * 10))

    const allPeaks: number[] = []

    for (let i = windowSize; i < profile.length - windowSize; i++) {
      const currentValue = profile[i]
      if (currentValue < minIntensityThreshold) continue

      let isLocalMax = true
      let leftMin = currentValue
      let rightMin = currentValue

      for (let j = 1; j <= windowSize; j++) {
        if (profile[i - j] >= currentValue || profile[i + j] >= currentValue) {
          isLocalMax = false
          break
        }
        leftMin = Math.min(leftMin, profile[i - j])
        rightMin = Math.min(rightMin, profile[i + j])
      }

      if (!isLocalMax) continue

      const prominence = currentValue - Math.max(leftMin, rightMin)
      if (prominence < minProminence) continue

      const tooClose = allPeaks.some(p => Math.abs(p - i) < minDistance)
      if (tooClose) continue

      allPeaks.push(i)
    }

    const combinedPeaks = [...Array.from(originalPeaks), ...allPeaks]
    const sortedPeaks = Array.from(new Set(combinedPeaks)).sort((a, b) => a - b)

    // Apply horizontal distance filter
    const MIN_HORIZONTAL_DISTANCE = 15
    const filteredPeaks: number[] = []

    for (const peakPos of sortedPeaks) {
      const closeExistingIdx = filteredPeaks.findIndex(
        existingPos => Math.abs(existingPos - peakPos) < MIN_HORIZONTAL_DISTANCE
      )

      if (closeExistingIdx !== -1) {
        const existingPos = filteredPeaks[closeExistingIdx]
        if (profile[peakPos] > profile[existingPos]) {
          filteredPeaks[closeExistingIdx] = peakPos
        }
      } else {
        filteredPeaks.push(peakPos)
      }
    }

    // Return only peaks within ROI
    return filteredPeaks.filter(p => p >= ROI_START && p <= ROI_END).length
  }

  // Find optimal sensitivity to get at least minPeaks in ROI
  // positiveControl: optional positive control blot to use for ROI calculation (P30 reference)
  const findOptimalSensitivity = (blot: BlotDetection, minPeaks: number = 5, positiveControl?: BlotDetection | null): number => {
    // Test sensitivity values from 0 to 100 in steps of 5
    for (let sens = 0; sens <= 100; sens += 5) {
      const peakCount = calculatePeaksInROIForSensitivity(blot, sens, positiveControl)
      if (peakCount >= minPeaks) {
        return sens
      }
    }
    return 100 // Max sensitivity if we can't reach minPeaks
  }

  // Note: Auto-adjust sensitivity useEffect is defined later, after activePositiveControl is available

  const handleBlotClick = (blot: BlotDetection, index: number) => {
    // PENDANT LA SAISIE, LA FICHE DÉTAILLÉE NE S'OUVRE PAS.
    //
    // Masquer la vignette sur la carte ne suffisait pas : la fiche rouvrait la
    // bandelette en pleine taille, avec son profil, ses pics et — pour le
    // témoin, qui n'est pas voilé — ses repères de bandes. C'est là que
    // l'aveugle se perdait pour de bon. On ferme la porte plutôt que de
    // flouter pièce par pièce : il n'y a rien à y faire tant qu'on saisit, et
    // la calibration du témoin se fait après le verrouillage.
    if (benchReadingMode) {
      toast({
        title: 'Saisie de paillasse en cours',
        description:
          'La fiche détaillée reste fermée tant que votre lecture n’est pas verrouillée. Reportez les bandes vues sur la membrane, puis verrouillez : la plaque s’ouvre entièrement, témoin compris.',
        status: 'info',
        duration: 6000,
        isClosable: true,
      })
      return
    }
    // Drop any zoom window so the new profile starts at full range (otherwise the
    // previous blot's zoom carries over and the profiles look superimposed).
    zoomXRef.current = {}
    // Opening a card from "Toutes" (no filter) must land on the SAME modal a
    // biologist would get by filtering to that pathology first, then clicking
    // it — otherwise the modal (comparison view, band calibration, ratio
    // panel…) would keep computing against the legacy case-wide témoin/config
    // instead of this blot's own pathology, same bug as the gallery grid card
    // (see pathologyContexts) but for every OTHER piece of UI the grid fix
    // doesn't reach. Scoping the filter here reuses that already-correct path
    // wholesale instead of threading ctx through the entire component.
    const blotPathology = (blot as any).pathology_label as string | null | undefined
    if (!pathologyFilter && blotPathology) {
      setPathologyFilter(blotPathology)
    }
    // Open from the live array so a just-edited/propagated strip id is reflected
    // immediately (the caller's object can be a pre-edit snapshot).
    setSelectedBlot(blotsArray.find((b) => b.id === blot.id) ?? blot)
    setSelectedBlotIndex(index)
    setInvertAxes(false)
    setShowRawImage(true)
    setShowRawControlImage(true)
    onOpen()
  }

  // ── Band placement by image click (non-premium manual identification) ──────
  // Given a Y fraction (0–1) on the blot image, returns the nearest reference
  // band name from the immunoblot config.
  // If calibrated positions are available (effectiveBandPositions), uses them.
  // Otherwise distributes bands evenly by descending MW (top = highest kDa).

  // Paired-comparison pathologies (CIP-WB): M is always shown on the left and
  // E always on the right regardless of which one is `selectedBlot` (see the
  // row-reverse flip + pairInfoSelected-driven labels/colours below) — so
  // toggling `selectedBlot` between the 2 members of the SAME couple is a
  // visual no-op: both images stay exactly where they were. "Next/previous"
  // must instead move between COUPLES (measured: reported as "navigation
  // shows the same images" when it only toggled within one couple).
  const getCoupleNavInfo = (blot: BlotDetection | null) => {
    if (!blot) return null
    const label = (blot as any).pathology_label as string | null | undefined
    if (!label) return null
    const cfg: any = pathologyConfigMap?.[label]
    if (cfg?.interpretation?.mode !== 'paired_comparison') return null
    const group = blotsArray
      .filter((b) => (b as any).pathology_label === label)
      .sort((a, b) => (a.y_top ?? 0) - (b.y_top ?? 0))
    const couples: Array<{ reference: BlotDetection; compared: BlotDetection }> = []
    for (let i = 0; i + 1 < group.length; i += 2) {
      // Couple membership comes from row order, but WHICH of the two is the
      // child comes from pairedComparisonPartners — a flipped couple would
      // otherwise land the arrows back on the mother, where no difference is
      // ever drawn.
      const flipped = pairedComparisonPartners.get(group[i].id)?.role === 'compared'
      couples.push({
        reference: flipped ? group[i + 1] : group[i],
        compared: flipped ? group[i] : group[i + 1],
      })
    }
    const coupleIndex = couples.findIndex((c) => c.reference.id === blot.id || c.compared.id === blot.id)
    if (coupleIndex < 0) return null
    return { couples, coupleIndex }
  }

  const handlePreviousBlot = () => {
    const nav = getCoupleNavInfo(selectedBlot)
    if (nav) {
      if (nav.coupleIndex <= 0) return
      // Land on the CHILD (compared), not the mother: the sample-image overlay
      // (difference tick marks, kDa chips) is wired to whichever blot is
      // `selectedBlot` itself, never to the partner/control-slot image — landing
      // on the mother left the differences invisible while browsing couples with
      // the arrows, even though they showed fine when opened directly from the
      // child's own gallery card.
      const target = nav.couples[nav.coupleIndex - 1].compared
      const targetIndex = displayedBlotsArray.findIndex((b) => b.id === target.id)
      if (targetIndex < 0) return
      zoomXRef.current = {}
      setSelectedBlot(target)
      setSelectedBlotIndex(targetIndex)
      setShowRawImage(true)
      setShowRawControlImage(true)
      setSelectedBandToPlace(null)
      return
    }
    if (selectedBlotIndex > 0) {
      const newIndex = selectedBlotIndex - 1
      zoomXRef.current = {}
      setSelectedBlot(displayedBlotsArray[newIndex])
      setSelectedBlotIndex(newIndex)
      setShowRawImage(true)
      setShowRawControlImage(true)
      setSelectedBandToPlace(null)
    }
  }

  /**
   * After a validation, jump straight to the next strip that still needs one —
   * skipping controls, hidden strips and blots already signed off. Keeps the
   * routine flow going instead of making the operator hunt for the next one.
   * Returns false when nothing is left to validate.
   */
  const goToNextBlotToValidate = (fromIndex: number, alsoSkipIds?: number[]): boolean => {
    const controlIds = new Set(
      activeControlIds
    )
    const hidden = new Set(hiddenBlotIds ?? [])
    // Paired-comparison pathologies (CIP-WB): the partner just got validated in
    // this same click (see the "Valider" handler), but manualBlotResults is a
    // stale closure until the next render — skip it explicitly instead of
    // landing on a strip that's actually already done.
    const alsoSkip = new Set(alsoSkipIds ?? [])
    // Le tour complet, et pas seulement ce qui suit.
    //
    // La recherche ne regardait qu'en avant : valider la dernière bandelette —
    // ou n'importe laquelle après avoir sauté les précédentes — ne trouvait
    // plus rien et annonçait « Toutes les bandelettes sont validées » alors
    // qu'il en restait en amont. Le tour repart donc du début une fois la fin
    // atteinte, et ne conclut qu'après avoir tout vu.
    //
    // L'index de départ est sauté explicitement : c'est la bandelette qu'on
    // vient de valider, et `manualBlotResults` est encore la fermeture d'avant
    // le rendu — on y reviendrait en boucle en la croyant sans verdict.
    for (let step = 1; step <= displayedBlotsArray.length; step++) {
      const i = (fromIndex + step) % displayedBlotsArray.length
      if (i === fromIndex) continue
      const b = displayedBlotsArray[i]
      if (!b || controlIds.has(b.id) || hidden.has(b.id) || alsoSkip.has(b.id)) continue
      if (manualBlotResults[b.id] != null) continue
      zoomXRef.current = {}
      setSelectedBlot(b)
      setSelectedBlotIndex(i)
      setShowRawImage(true)
      setShowRawControlImage(true)
      setSelectedBandToPlace(null)
      return true
    }
    return false
  }

  const handleNextBlot = () => {
    const nav = getCoupleNavInfo(selectedBlot)
    if (nav) {
      if (nav.coupleIndex >= nav.couples.length - 1) return
      // See handlePreviousBlot: land on the CHILD so the difference overlay
      // (tied to selectedBlot's own image) actually renders while browsing.
      const target = nav.couples[nav.coupleIndex + 1].compared
      const targetIndex = displayedBlotsArray.findIndex((b) => b.id === target.id)
      if (targetIndex < 0) return
      zoomXRef.current = {}
      setSelectedBlot(target)
      setSelectedBlotIndex(targetIndex)
      setShowRawImage(true)
      setShowRawControlImage(true)
      setSelectedBandToPlace(null)
      return
    }
    if (selectedBlotIndex < displayedBlotsArray.length - 1) {
      const newIndex = selectedBlotIndex + 1
      zoomXRef.current = {}
      setSelectedBlot(displayedBlotsArray[newIndex])
      setSelectedBlotIndex(newIndex)
      setShowRawImage(true)
      setShowRawControlImage(true)
      setSelectedBandToPlace(null)
    }
  }

  // Keyboard navigation + validation shortcuts
  useEffect(() => {
    if (!isOpen) return

    const isControlBlot = selectedBlot &&
      (activeControlIds.includes(selectedBlot.id))

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore when typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea') return

      // Navigation
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        handlePreviousBlot()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        handleNextBlot()
      } else if (e.key === 'Escape') {
        onClose()

      // Validation shortcuts — non-premium, sample blots only. Gated on a
      // témoin existing, same as the ManualResultSelector buttons themselves —
      // otherwise P/D/N still validated a strip with nothing to compare against.
      } else if (!aiAccessible && selectedBlot && !isControlBlot && activePositiveControl) {
        const hasEquivocal = !!immunoblotConfig?.interpretation?.equivocal
        const placedBands = new Set(blotPlacedBands.get(selectedBlot.id)?.keys() ?? [])
        const { allowed: positifOk } = checkPositifAllowed(
          placedBands,
          immunoblotConfig?.interpretation as any ?? null,
          immunoblotConfig?.bands?.specific as any ?? []
        )
        if ((e.key === '1' || e.key.toLowerCase() === 'p') && positifOk) {
          e.preventDefault()
          onManualResultChange?.(selectedBlot.id, 'POSITIF')
        } else if (hasEquivocal && (e.key === '2' || e.key.toLowerCase() === 'd')) {
          // DOUTEUX only if the test defines an equivocal interpretation
          e.preventDefault()
          onManualResultChange?.(selectedBlot.id, 'DOUTEUX')
        } else if (e.key === '3' || e.key.toLowerCase() === 'n') {
          e.preventDefault()
          onManualResultChange?.(selectedBlot.id, 'NÉGATIF')
        } else if (e.key === 'Enter' && manualBlotResults[selectedBlot.id] != null) {
          // Advance to next blot once validated
          e.preventDefault()
          handleNextBlot()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedBlotIndex, displayedBlotsArray.length, onClose, aiAccessible, selectedBlot, manualBlotResults, positiveControlId, positiveControl2Id, positiveControl3Id])

  const togglePositiveControl = async (blotId: number, controlNumber: 1 | 2 | 3 = 1) => {
    const currentId = effectiveControlId(controlNumber)
    const newValue = currentId === blotId ? null : blotId

    const pathologyKey = canonicalPathologyKey(pathologyFilter)
    if (pathologyKey) {
      // Bordeaux/Dynablot multitest: this pathology's OWN slots, independent
      // of every other pathology's — see savedPositiveControlsByPathology.
      // Keyed by the CANONICAL id, not the raw OCR label (see
      // canonicalPathologyKey), so it stays the same slot across reanalyses
      // even if the OCR'd label text itself comes back slightly different.
      const prevSlot = positiveControlsByPathologyState[pathologyKey] || {}
      const newSlot = {
        positiveControlId: controlNumber === 1 ? newValue : (prevSlot.positiveControlId ?? null),
        positiveControl2Id: controlNumber === 2 ? newValue : (prevSlot.positiveControl2Id ?? null),
        positiveControl3Id: controlNumber === 3 ? newValue : (prevSlot.positiveControl3Id ?? null),
      }

      if (onPositiveControlChange && caseId) {
        try {
          await onPositiveControlChange(newSlot.positiveControlId, newSlot.positiveControl2Id, newSlot.positiveControl3Id, pathologyKey)
        } catch (error) {
          console.error('❌ Error saving positive controls:', error)
          toast({
            title: t('blotAnalysis.gallery.errorSavingControls'),
            description: t('blotAnalysis.gallery.unableToSaveControls'),
            status: 'error',
            duration: 3000,
          })
          return
        }
      } else {
        console.warn('⚠️ Not saving to backend:', { hasCallback: !!onPositiveControlChange, caseId })
      }

      setPositiveControlsByPathologyState((prev) => ({ ...prev, [pathologyKey]: newSlot }))
      return
    }

    // Calculate the new state for all 3 controls BEFORE updating state
    // This ensures we use the correct values when saving to backend
    const newControl1Id = controlNumber === 1 ? newValue : positiveControlId
    const newControl2Id = controlNumber === 2 ? newValue : positiveControl2Id
    const newControl3Id = controlNumber === 3 ? newValue : positiveControl3Id

    // Save to backend if callback is provided and we have a caseId
    if (onPositiveControlChange && caseId) {
      try {
        await onPositiveControlChange(newControl1Id, newControl2Id, newControl3Id)
      } catch (error) {
        console.error('❌ Error saving positive controls:', error)
        toast({
          title: t('blotAnalysis.gallery.errorSavingControls'),
          description: t('blotAnalysis.gallery.unableToSaveControls'),
          status: 'error',
          duration: 3000,
        })
        return
      }
    } else {
      console.warn('⚠️ Not saving to backend:', { hasCallback: !!onPositiveControlChange, caseId })
    }

    // Update local state AFTER successful save
    switch (controlNumber) {
      case 1: setPositiveControlId(newValue); break
      case 2: setPositiveControl2Id(newValue); break
      case 3: setPositiveControl3Id(newValue); break
    }
    // No toast: the T+ marker appearing/disappearing on the strip is the feedback.
  }

  // Get positive control blots — resolved through effectiveControlId so a
  // Bordeaux/Dynablot multitest filter transparently swaps in THIS
  // pathology's own slots instead of the legacy case-wide ones.
  const positiveControl = useMemo(() =>
    blotsArray.find(b => b.id === effectiveControlId(1)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blotsArray, positiveControlId, pathologyFilter, positiveControlsByPathologyState]
  )

  const positiveControl2 = useMemo(() =>
    blotsArray.find(b => b.id === effectiveControlId(2)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blotsArray, positiveControl2Id, pathologyFilter, positiveControlsByPathologyState]
  )

  const positiveControl3 = useMemo(() =>
    blotsArray.find(b => b.id === effectiveControlId(3)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blotsArray, positiveControl3Id, pathologyFilter, positiveControlsByPathologyState]
  )

  // Get the active positive control for the selected blot. The manual
  // T1/T2/T3 toggle (selectedControlForBlot) picks WHICH slot to use — a
  // manip can have 2+ témoins for the same pathology (weak/strong,
  // replicates). No pathology-matching needed here anymore: positiveControl/
  // 2/3 above already resolve through effectiveControlId, so while filtered
  // they can only ever hold THIS pathology's own témoins in the first place
  // (Bordeaux/Dynablot multitest — see savedPositiveControlsByPathology).
  // Paired-comparison pathologies (CIP-WB): no manufacturer témoin exists to
  // pick, so the whole existing "témoin" concept — this memo, and every bit
  // of UI built on it (comparison modal, charts, labels) — is simply fed the
  // selected blot's own PAIR PARTNER (mère/J0/sérum) instead. Nothing else
  // changes: the rest of the file still just reads `activePositiveControl`
  // exactly as it always has, unchanged, per the operator's ask to keep the
  // usual "témoin" modal rather than build a parallel one.
  const pairInfoSelected = selectedBlot ? pairedComparisonPartners.get(selectedBlot.id) : undefined
  /**
   * PROTIS — le rôle d'une piste, lu sur son libellé (normalisé : l'OCR rend
   * « rotéines total es » ou « Protéines s totales »).
   */
  const protisLaneRole = (stripId: unknown): 'marker' | 'pbs' | 'total' | null => {
    const n = String(stripId ?? '').toLowerCase().normalize('NFD').replace(/[^a-z0-9]/g, '')
    if (!n) return null
    if (n.includes('marqueur') || n.includes('marker')) return 'marker'
    if (n.includes('pbs') || n.includes('temoinnegatif') || n === 'blanc') return 'pbs'
    if (n.includes('total')) return 'total'
    return null
  }
  /** La référence affichée dans la comparaison : marqueur (échelle, défaut),
   *  PBS (spécificité) ou protéines totales (carte de l'extrait / charge). */
  const [protisRefRole, setProtisRefRole] = useState<'marker' | 'pbs' | 'total'>('marker')
  const protisRefWord = protisRefRole === 'marker' ? 'Marqueur' : protisRefRole === 'pbs' ? 'Témoin PBS' : 'Prot. totales'
  /** La piste du rôle demandé, dans la même plaque (la plus proche en x). */
  const protisLaneOfRole = (role: 'marker' | 'pbs' | 'total'): BlotDetection | null => {
    const sel: any = selectedBlot
    if (!sel) return null
    const proj = sel.pathology_label ?? null
    const candidates = blots.filter((b) =>
      ((b as any).pathology_label ?? null) === proj
      && protisLaneRole((b as any).strip_id) === role
      && b.id !== sel.id)
    if (!candidates.length) return null
    return candidates.sort((a, b) => Math.abs(a.x0 - sel.x0) - Math.abs(b.x0 - sel.x0))[0]
  }
  /** Le marqueur de la plaque — il porte les graduations, quelle que soit la
   *  référence affichée. */
  const protisPlateMarker = module === 'protis'
    ? (protisLaneRole((selectedBlot as any)?.strip_id) === 'marker' ? selectedBlot : protisLaneOfRole('marker'))
    : null

  const activePositiveControl = useMemo(() => {
    // PROTIS : pas de témoin positif sur un gel — c'est le MARQUEUR DE MASSE
    // de la même plaque (même projet) qui sert de référence de comparaison
    // dans la modale : il porte l'échelle à laquelle les bandes se lisent.
    if (module === 'protis') {
      if (!selectedBlot) return null
      // La piste ouverte ne se compare pas à elle-même : si elle EST la
      // référence demandée, rien à montrer.
      if (protisLaneRole((selectedBlot as any).strip_id) === protisRefRole) return null
      return protisLaneOfRole(protisRefRole)
    }
    if (pairInfoSelected) return pairInfoSelected.partner
    switch (selectedControlForBlot) {
      case 1: return positiveControl
      case 2: return positiveControl2
      case 3: return positiveControl3
      default: return positiveControl
    }
  }, [module, blots, selectedBlot, protisRefRole, pairInfoSelected, selectedControlForBlot, positiveControl, positiveControl2, positiveControl3])

  // Bordeaux/Dynablot multitest: which of THIS pathology's own 3 slots (see
  // savedPositiveControlsByPathology) a quick "set as témoin" action (the
  // CTA/hint below, not the explicit T1/T2/T3 buttons) should claim — the
  // first free one, so it ADDS a témoin rather than replacing an existing
  // one (a manip can need 2 for the same pathology). Falls back to slot 1
  // when all 3 are already taken. Outside a filter, always slot 1 —
  // unchanged from before.
  const resolveControlSlot = (): 1 | 2 | 3 => {
    if (!pathologyFilter) return 1
    if (effectiveControlId(1) == null) return 1
    if (effectiveControlId(2) == null) return 2
    if (effectiveControlId(3) == null) return 3
    return 1
  }

  // Deterministic letterbox bounds for the control strip (see sampleImageBounds).
  const controlImageBounds = useMemo(
    () => stripContentBounds(activePositiveControl?.image_width, activePositiveControl?.image_height),
    [activePositiveControl],
  )

  // Auto-suggested band positions — computed from the active control's detected peaks.
  // Used as a STARTING POINT when no manual calibration exists yet, so the user can
  // correct just one or two bands instead of placing every band from scratch.
  const suggestedBandPositions = useMemo((): Map<BandName, { position: number; intensity: number }> => {
    if (!activePositiveControl || !immunoblotConfig) return new Map()
    const positions = suggestBands(activePositiveControl, immunoblotConfig)
    if (!positions || positions.size === 0) return new Map()
    const map = new Map<BandName, { position: number; intensity: number }>()
    const profile = activePositiveControl.profile
    positions.forEach((position, name) => {
      if (!BAND_NAMES.includes(name)) return
      const idx = Math.max(0, Math.min(profile.length - 1, Math.round(position)))
      const intensity = profile[idx] ?? 0
      map.set(name, { position, intensity })
    })
    return map
  }, [activePositiveControl, immunoblotConfig, BAND_NAMES])

  // Use saved calibration if available, otherwise fall back to auto-suggested positions.
  // This is what the UI badges + single-band correction should operate on.
  const displayedBandPositions = useMemo(() => {
    // While calibrating, show ONLY the bands actually placed so far — never the
    // auto-suggested fallback. Otherwise "Tout recalibrer" (which empties the
    // calibration) would immediately re-show the suggested bands, looking like
    // the old bands never cleared.
    if (bandSelectionMode) return effectiveBandPositions
    return effectiveBandPositions.size > 0 ? effectiveBandPositions : suggestedBandPositions
  }, [effectiveBandPositions, suggestedBandPositions, bandSelectionMode])

  // Compact species pill for the selected SAMPLE blot (Echinococcus) — just the
  // species name, meant to sit inline next to the result pill above the strip.
  // Returns null for controls, non-Echinococcus tests, or a sample with no
  // species-differentiating band placed yet.
  // Justification complète de la cotation semi-quantitative, au survol : la
  // note de chaque bande avec son intensité relative au témoin, puis le calcul
  // I vs 2n en toutes lettres — le biologiste doit pouvoir REFAIRE le calcul
  // de tête, pas juste lire un verdict.
  const semiQuantTooltip = (sq: any) => (
    <Box fontSize="xs" lineHeight="1.7" py={1.5} px={0.5}>
      <Text fontWeight="800" mb={1}>
        Cotation semi-quantitative — Oliva et al. 2015
      </Text>
      {sq.notes.map((x: any) => (
        <Text key={x.band}>
          {x.band} : note <b>{x.note}</b>/4 · intensité {Math.round(x.ratio * 100)} % du témoin
        </Text>
      ))}
      <Text mt={1} fontWeight="700">
        I = {sq.notes.map((x: any) => x.note).join(' + ')} = {sq.I} {sq.strength === 'strong' ? '>' : '≤'} 2 × {sq.n} bandes = {2 * sq.n}
      </Text>
      <Text>→ {sq.label.toLowerCase()} selon la règle n/2n du papier.</Text>
      <Text mt={1} color="gray.300" fontStyle="italic">
        Suggestion calculée sur les bandes auto-identifiées — le verdict reste le vôtre.
      </Text>
    </Box>
  )

  const renderSpeciesDiagnosis = () => {
    if (!selectedBlot) return null
    const isControl =
      activeControlIds.includes(selectedBlot.id)
    if (isControl) return null
    // Union both band sources: manual placement (non-premium `blotPlacedBands`)
    // and the AI auto-identified bands (premium `selectedBlotIdentifiedBands`),
    // so the species diagnosis works in both validation modes.
    const placed = new Set<string>([
      ...Array.from(blotPlacedBands.get(selectedBlot.id)?.keys() ?? []),
      ...selectedBlotIdentifiedBands,
    ])
    const sp = getEchinococcusSpeciesProfile(placed, immunoblotConfig)
    if (!sp) return null
    // Only show once a species is actually named (skip the indéterminé states,
    // which would just add noise next to the result pill).
    if (!sp.differentiable) return null
    return (
      <Text fontSize="sm" fontWeight="700" fontStyle="italic" color="purple.600" whiteSpace="nowrap">
        {sp.species}
      </Text>
    )
  }

  // ── Band placement helpers — declared after activePositiveControl ─────────

  const findBandAtPosition = useCallback((relFraction: number, profile: number[]): string | null => {
    if (!immunoblotConfig?.bands?.specific?.length) return null
    const bands = [...immunoblotConfig.bands.specific].sort(
      (a: any, b: any) => b.molecularWeight - a.molecularWeight
    )
    if (effectiveBandPositions.size > 0 && activePositiveControl) {
      const controlOffset = getControlLine(activePositiveControl)
      const clickIdx = Math.round(relFraction * profile.length)
      let closest: string | null = null
      let minDist = Infinity
      effectiveBandPositions.forEach((data, bandName) => {
        const dist = Math.abs((data.position - controlOffset) - clickIdx)
        if (dist < minDist && dist < profile.length * 0.12) { minDist = dist; closest = bandName }
      })
      return closest
    }
    const idx = Math.round(relFraction * (bands.length - 1))
    return bands[Math.max(0, Math.min(bands.length - 1, idx))]?.name ?? null
  }, [immunoblotConfig, effectiveBandPositions, activePositiveControl, getControlLine])

  const getBandYFraction = useCallback((bandName: string, profile: number[]): number | null => {
    if (!immunoblotConfig?.bands?.specific?.length) return null
    if (effectiveBandPositions.size > 0 && activePositiveControl) {
      const controlOffset = getControlLine(activePositiveControl)
      const data = effectiveBandPositions.get(bandName)
      if (!data) return null
      return (data.position - controlOffset) / profile.length
    }
    const bands = [...immunoblotConfig.bands.specific].sort((a: any, b: any) => b.molecularWeight - a.molecularWeight)
    const idx = bands.findIndex((b: any) => b.name === bandName)
    if (idx < 0) return null
    return idx / Math.max(1, bands.length - 1)
  }, [immunoblotConfig, effectiveBandPositions, activePositiveControl, getControlLine])

  // Build the placedBands export payload — applies per-blot rename overrides
  // so the PDF report shows the labels the operator actually wants. Centralised
  // here so togglePlacedBand / drag / rename all go through the same shape.
  const buildPlacedBandsRecord = useCallback((source: Map<number, Map<string, number>>, overrides: Record<number, Record<string, string>>) => {
    const record: Record<number, Array<{name: string; yFraction: number}>> = {}
    source.forEach((bandMap, id) => {
      record[id] = Array.from(bandMap.entries()).map(([origName, yFraction]) => ({
        name: overrides[id]?.[origName] ?? origName,
        yFraction,
      }))
    })
    return record
  }, [])

  // Serialise the suppressed-bands map to the persisted shape { blotId: [name, ...] }.
  const buildSuppressedRecord = useCallback((source: Map<number, Set<string>>) => {
    const record: Record<number, string[]> = {}
    source.forEach((names, id) => { if (names.size > 0) record[id] = Array.from(names) })
    return record
  }, [])

  // yFraction = actual click Y (0–1); if omitted falls back to calibrated position
  // PROTIS : à l'OUVERTURE du marqueur, si une échelle connue compte autant
  // de graduations que de pics détectés, elles se posent toutes seules — la
  // suggestion automatique du témoin IRIS, appliquée à l'échelle. Une seule
  // fois par marqueur et par session : si le biologiste les efface, on ne
  // les lui réimpose pas.
  const autoPlacedMarkersRef = useRef<Set<number>>(new Set())
  useEffect(() => {
    if (module !== 'protis' || !selectedBlot || readOnly) return
    if (!/marqueur|marker/i.test(String((selectedBlot as any).strip_id ?? ''))) return
    // Diagnostic volontairement bavard : ce chemin a déjà échoué en silence
    // deux fois (notice en retard, mauvais module par l'adresse).
    const say = (why: string, extra?: unknown) =>
      console.info(`🧪 PROTIS auto-graduations [blot ${selectedBlot.id}]: ${why}`, extra ?? '')
    if (autoPlacedMarkersRef.current.has(selectedBlot.id)) { say('déjà posées cette session'); return }
    if ((blotPlacedBands.get(selectedBlot.id)?.size ?? 0) > 0) { say('des bandes existent déjà'); return }
    const profLen: number = ((selectedBlot as any).profile ?? []).length
    const peaks: number[] = (((selectedBlot as any).peaks ?? []) as number[]).slice().sort((a, b) => a - b)
    if (!profLen) { say('profil absent'); return }

    // D'ABORD les graduations LUES SUR LA FEUILLE (OCR des chiffres imprimés,
    // avec leur hauteur) : c'est l'échelle du cas, quelle qu'elle soit. La
    // position lue est celle du chiffre — on l'aimante au pic le plus proche
    // quand il est tout près, c'est la bande qui fait foi.
    const read = ((selectedBlot as any).graduations ?? []) as Array<{ kda: number; y_frac: number }>
    if (read.length >= 2) {
      say(`pose de ${read.length} graduations lues sur la feuille`)
      autoPlacedMarkersRef.current.add(selectedBlot.id)
      const entries: Array<[string, number]> = read.map((g) => {
        let y = g.y_frac
        const nearest = peaks.length
          ? peaks.reduce((best, pk) => (Math.abs(pk / profLen - g.y_frac) < Math.abs(best / profLen - g.y_frac) ? pk : best), peaks[0])
          : null
        if (nearest !== null && Math.abs(nearest / profLen - g.y_frac) < 0.02) y = nearest / profLen
        return [`${g.kda} kDa`, y]
      })
      placeBandsBatch(selectedBlot.id, entries)
      return
    }

    // Repli : les échelles connues de la notice, si l'une compte exactement
    // autant de graduations que de pics détectés.
    if (peaks.length < 2) { say('pics absents et rien de lu', { peaks: peaks.length }); return }
    const ladders = (immunoblotConfig as any)?.interpretation?.ladderProfile?.ladder?.knownLadders ?? []
    const matching = ladders.find((l: any) => (l.values?.length ?? 0) === peaks.length)
    if (!matching) {
      say('rien de lu sur la feuille, et aucune échelle de la notice ne compte autant de graduations que de pics', {
        pics: peaks.length,
        echelles: ladders.map((l: any) => l.values?.length),
        configChargee: Boolean(immunoblotConfig),
      })
      return
    }
    say(`pose de ${matching.values.length} graduations (${matching.label ?? ''})`)
    autoPlacedMarkersRef.current.add(selectedBlot.id)
    const values = (matching.values as number[]).slice().sort((x, y) => y - x)
    placeBandsBatch(selectedBlot.id, values.map((v, i) => [`${v} kDa`, peaks[i] / profLen] as [string, number]))
    // La notice arrive en différé : sans elle dans les dépendances, ouvrir le
    // marqueur avant son chargement laissait l'effet muet pour de bon.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, selectedBlot?.id, readOnly, immunoblotConfig])

  // PROTIS : pose PLUSIEURS bandes d'un coup — les suggestions en placent
  // dix, et dix persistances séparées feraient dix PATCH pour un geste.
  const placeBandsBatch = useCallback((blotId: number, entries: Array<[string, number]>) => {
    if (!entries.length) return
    setBlotPlacedBands(prev => {
      const next = new Map(prev)
      const cur = new Map(next.get(blotId) ?? [])
      entries.forEach(([name, y]) => cur.set(name, y))
      next.set(blotId, cur)
      if (onPlacedBandsChange) onPlacedBandsChange(buildPlacedBandsRecord(next, bandNameOverrides))
      return next
    })
  }, [onPlacedBandsChange, buildPlacedBandsRecord, bandNameOverrides])

  const togglePlacedBand = useCallback((blotId: number, bandName: string, yFraction?: number) => {
    setBlotPlacedBands(prev => {
      const next = new Map(prev)
      const cur = new Map(next.get(blotId) ?? [])
      if (cur.has(bandName)) {
        cur.delete(bandName)
      } else {
        cur.set(bandName, yFraction ?? 0.5)
        // Re-placing a band that was previously deleted un-suppresses it, so a
        // deletion is never a dead-end — the band can always be brought back.
        setSuppressedBands(sPrev => {
          if (!sPrev.get(blotId)?.has(bandName)) return sPrev
          const sNext = new Map(sPrev)
          const sCur = new Set(sNext.get(blotId) ?? [])
          sCur.delete(bandName)
          if (sCur.size === 0) sNext.delete(blotId)
          else sNext.set(blotId, sCur)
          if (onSuppressedBandsChange) onSuppressedBandsChange(buildSuppressedRecord(sNext))
          return sNext
        })
      }
      next.set(blotId, cur)
      if (onPlacedBandsChange) {
        onPlacedBandsChange(buildPlacedBandsRecord(next, bandNameOverrides))
      }
      return next
    })
  }, [onPlacedBandsChange, buildPlacedBandsRecord, bandNameOverrides, onSuppressedBandsChange, buildSuppressedRecord])

  // Delete a band from a strip entirely. Works for both an auto-detected band and
  // a hand-placed one: the manual placement (if any) is dropped AND the original
  // name is marked suppressed, so interpretBlot filters it out of the detected set.
  // That single filter makes the deletion propagate everywhere — verdict, gallery
  // card, modal markers and the exported report — and it also updates the parent's
  // placedBands record so the PDF export stays in sync.
  const suppressBand = useCallback((blotId: number, bandName: string) => {
    setBlotPlacedBands(prev => {
      if (!prev.get(blotId)?.has(bandName)) return prev
      const next = new Map(prev)
      const cur = new Map(next.get(blotId) ?? [])
      cur.delete(bandName)
      if (cur.size === 0) next.delete(blotId)
      else next.set(blotId, cur)
      if (onPlacedBandsChange) onPlacedBandsChange(buildPlacedBandsRecord(next, bandNameOverrides))
      return next
    })
    setSuppressedBands(prev => {
      const next = new Map(prev)
      const cur = new Set(next.get(blotId) ?? [])
      cur.add(bandName)
      next.set(blotId, cur)
      if (onSuppressedBandsChange) onSuppressedBandsChange(buildSuppressedRecord(next))
      return next
    })
  }, [onPlacedBandsChange, buildPlacedBandsRecord, bandNameOverrides, onSuppressedBandsChange, buildSuppressedRecord])

  // Display name resolver — falls back to the original band name when no
  // rename was set for that (blotId, name) pair.
  const getBandDisplayName = useCallback((blotId: number, original: string) =>
    bandNameOverrides[blotId]?.[original] ?? original, [bandNameOverrides])

  // Apply a band rename — empty / equal to original removes the override.
  // Rename a band by MOVING it to the new name (keyed by name in blotPlacedBands),
  // not by aliasing the old name. The old alias approach relabelled *every* future
  // band of `original` — so after renaming a P31 to P30, re-adding a real P31 got
  // hijacked back to "P30". Here the band physically moves under `trimmed`:
  //   • blotPlacedBands: place `trimmed` at the band's current Y, drop `original`
  //     (option B — replace any band already named `trimmed`);
  //   • suppress the now-vacated AI marker for `original` so it doesn't linger —
  //     re-adding `original` later un-suppresses it (see togglePlacedBand);
  //   • clear any leftover name alias for `original`.
  // `currentFraction` is the band's on-image Y (0–1); the caller passes the marker's
  // placed/AI position so an AI-only band materialises at the right spot.
  const applyBandRename = useCallback((blotId: number, original: string, raw: string, currentFraction?: number) => {
    const trimmed = raw.trim()

    // Overrides with `original`'s alias dropped (rename no longer uses aliases).
    const overridesNext = (() => {
      if (!bandNameOverrides[blotId]?.[original]) return bandNameOverrides
      const n = { ...bandNameOverrides }
      const bm = { ...n[blotId] }
      delete bm[original]
      if (Object.keys(bm).length === 0) delete n[blotId]
      else n[blotId] = bm
      return n
    })()
    setBandNameOverrides(overridesNext)

    // Revert / no-op: just drop the alias and re-sync.
    if (!trimmed || trimmed === original) {
      if (onPlacedBandsChange) onPlacedBandsChange(buildPlacedBandsRecord(blotPlacedBands, overridesNext))
      return
    }

    // Move the band under the new name (option B: overwrite an existing `trimmed`).
    const cur = new Map(blotPlacedBands.get(blotId) ?? [])
    const y = cur.get(original) ?? currentFraction ?? 0.5
    cur.delete(original)
    cur.set(trimmed, Math.max(0, Math.min(1, y)))
    const placedNext = new Map(blotPlacedBands)
    placedNext.set(blotId, cur)
    setBlotPlacedBands(placedNext)

    // Hide the vacated AI marker for `original`; make sure the target isn't hidden.
    const sup = new Map(suppressedBands)
    const supCur = new Set(sup.get(blotId) ?? [])
    supCur.add(original)
    supCur.delete(trimmed)
    if (supCur.size === 0) sup.delete(blotId)
    else sup.set(blotId, supCur)
    setSuppressedBands(sup)
    if (onSuppressedBandsChange) onSuppressedBandsChange(buildSuppressedRecord(sup))

    if (onPlacedBandsChange) onPlacedBandsChange(buildPlacedBandsRecord(placedNext, overridesNext))
  }, [bandNameOverrides, blotPlacedBands, suppressedBands, onPlacedBandsChange, buildPlacedBandsRecord, onSuppressedBandsChange, buildSuppressedRecord])

  // Move a placed band to a new vertical position on the image.
  // Snapshot is taken in the drag-start handler (not here) so the undo
  // restores the position BEFORE the drag began, not after each pointermove.
  const updateBandYFraction = useCallback((blotId: number, bandName: string, newY: number) => {
    setBlotPlacedBands(prev => {
      if (!prev.get(blotId)?.has(bandName)) return prev
      const next = new Map(prev)
      const cur = new Map(next.get(blotId) ?? [])
      cur.set(bandName, Math.max(0, Math.min(1, newY)))
      next.set(blotId, cur)
      if (onPlacedBandsChange) {
        onPlacedBandsChange(buildPlacedBandsRecord(next, bandNameOverrides))
      }
      return next
    })
  }, [onPlacedBandsChange, buildPlacedBandsRecord, bandNameOverrides])

  // Global pointer tracking for band drag — translates mouse Y to a yFraction
  // (0–1 relative to the sample image bounds) and pushes it through the
  // updateBandYFraction callback. We attach listeners only while a drag is
  // active to avoid wasted handlers in idle state.
  useEffect(() => {
    if (!draggingBand) return
    const handleMove = (e: PointerEvent) => {
      const drag = bandDragRef.current
      // A few px of travel — same threshold as the box-drag pattern in
      // index.tsx — before treating this as a genuine drag rather than the
      // pointer settling after a click. Only THEN seed blotPlacedBands (if
      // this band had no entry yet), so a plain click never creates one.
      if (drag && !drag.moved) {
        if (Math.abs(e.clientY - drag.startClientY) < 3) return
        drag.moved = true
        setBlotPlacedBands(prev => {
          if (prev.get(drag.blotId)?.has(drag.bandName)) return prev
          const initialY = Math.max(0, Math.min(1, drag.initialTopPct / 100))
          const next = new Map(prev)
          const cur = new Map(next.get(drag.blotId) ?? [])
          cur.set(drag.bandName, initialY)
          next.set(drag.blotId, cur)
          if (onPlacedBandsChange) onPlacedBandsChange(buildPlacedBandsRecord(next, bandNameOverrides))
          return next
        })
      }
      const img = sampleImageRef.current
      if (!img) return
      const rect = img.getBoundingClientRect()
      const yFrac = (e.clientY - rect.top) / rect.height
      updateBandYFraction(draggingBand.blotId, draggingBand.bandName, yFrac)
      // Mirror the strip-hover readout so the profile cursor + kDa track the band
      // in real time — lets the biologist line the band up on its peak while
      // dragging, instead of guessing and checking after the fact.
      if (selectedBlot) {
        const b = sampleImageBounds
        const imageRelFraction = b && b.heightPct > 0
          ? (yFrac - b.topPct / 100) / (b.heightPct / 100)
          : yFrac
        if (imageRelFraction >= 0 && imageRelFraction <= 1) {
          const rawProfileIdx = Math.round(imageRelFraction * selectedBlot.profile.length)
          const offset = getControlLine(selectedBlot)
          setHoverState({ relFraction: imageRelFraction, rawProfileIdx, chartX: rawProfileIdx - offset })
        }
      }
    }
    const handleUp = () => { bandDragRef.current = null; setDraggingBand(null); setHoverState(null) }
    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)
    document.body.style.cursor = 'ns-resize'
    document.body.style.userSelect = 'none'
    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [draggingBand, updateBandYFraction, selectedBlot, sampleImageBounds, getControlLine, bandNameOverrides, buildPlacedBandsRecord, onPlacedBandsChange])

  /**
   * Le panneau des graduations/bandes posées (PROTIS), rendu DES DEUX
   * côtés : la colonne manuelle (non-premium) ET à droite des profils en
   * mode IA — il était né dans la première, invisible pour un admin.
   */
  const protisGraduationsPanel = module === 'protis' && selectedBlot && (() => {
                          const placed = blotPlacedBands.get(selectedBlot.id) ?? new Map<string, number>()
                          const isMarkerLane = /marqueur|marker/i.test(String((selectedBlot as any).strip_id ?? ''))
                          if (!isMarkerLane) return null
                          let names: string[]
                          if (isMarkerLane) {
                            const ladders = (immunoblotConfig as any)?.interpretation?.ladderProfile?.ladder?.knownLadders ?? []
                            const peaks: number[] = ((selectedBlot as any).peaks ?? [])
                            const readVals: number[] = (((selectedBlot as any).graduations ?? []) as Array<{ kda: number }>).map((g) => g.kda)
                            const overlap = (l: any) => ((l.values ?? []) as number[]).filter((v) => placed.has(`${v} kDa`)).length
                            const scale = placed.size
                              ? ladders.slice().sort((a: any, b: any) => overlap(b) - overlap(a))[0]
                              : ladders.find((l: any) => (l.values?.length ?? 0) === peaks.length) ?? ladders[0]
                            // Les graduations LUES sur la feuille d'abord — l'échelle du cas —
                            // puis celles de la notice qui n'y figurent pas.
                            const scaleVals: number[] = ((scale?.values ?? []) as number[]).filter((v) => !readVals.includes(v))
                            names = [...readVals, ...scaleVals].sort((a, b) => b - a).map((v) => `${v} kDa`)
                            Array.from(placed.keys()).forEach((n) => { if (!names.includes(n)) names.push(n) })
                          } else {
                            names = Array.from(placed.keys())
                          }
                          console.info('🧪 panneau graduations:', { isMarkerLane, names: names.length, placed: placed.size })
                          if (!names.length) return null
                          return (
                            <Box w="full" bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200" p={3}>
                              <VStack align="center" spacing={2}>
                                <Text fontSize="xs" fontWeight="bold" color="gray.600" textAlign="center">
                                  {isMarkerLane ? 'Marqueur de masse' : 'Bandes de la piste'}
                                </Text>
                                <HStack spacing={2} justify="center">
                                  <Text fontSize="2xs" color="gray.400" fontStyle="italic">
                                    {isMarkerLane ? "Graduations de l'échelle" : 'Bandes posées'}
                                  </Text>
                                  {placed.size > 0 && (
                                    <Badge colorScheme="blue" fontSize="2xs" borderRadius="full" px={2}>
                                      {placed.size} placée{placed.size > 1 ? 's' : ''}
                                    </Badge>
                                  )}
                                </HStack>
                                <Text fontSize="2xs" color={selectedBandToPlace ? 'blue.500' : 'gray.400'} textAlign="center" fontWeight={selectedBandToPlace ? 'semibold' : 'normal'}>
                                  {selectedBandToPlace
                                    ? `→ Cliquez un pic du profil ou l'image pour ${placed.has(selectedBandToPlace) ? 'repositionner' : 'placer'} ${selectedBandToPlace}`
                                    : "Sélectionnez une case, puis cliquez le profil ou l'image"}
                                </Text>
                                <Flex wrap="wrap" gap={1.5} justify="center" w="full">
                                  {names.map((n) => {
                                    const isPlaced = placed.has(n)
                                    const isSelected = selectedBandToPlace === n
                                    return (
                                      <HStack
                                        key={n}
                                        spacing={2}
                                        px={2}
                                        py={1}
                                        borderRadius="lg"
                                        bg={isSelected ? 'yellow.100' : isPlaced ? 'blue.50' : 'transparent'}
                                        border="1px solid"
                                        borderColor={isSelected ? 'yellow.400' : isPlaced ? 'blue.200' : 'transparent'}
                                        cursor="pointer"
                                        onClick={() => setSelectedBandToPlace(isSelected ? null : n)}
                                        transition="all 0.15s"
                                        _hover={{ bg: isSelected ? 'yellow.200' : 'gray.100' }}
                                      >
                                        <Box w={2.5} h={2.5} borderRadius="full" flexShrink={0} border="2px solid"
                                          bg={isSelected ? 'yellow.500' : isPlaced ? 'blue.500' : 'gray.300'}
                                          borderColor={isSelected ? 'yellow.400' : isPlaced ? 'blue.300' : 'gray.200'}
                                        />
                                        <Text fontSize="xs" flex={1}
                                          fontWeight={isSelected || isPlaced ? 'bold' : 'normal'}
                                          color={isSelected ? 'yellow.800' : isPlaced ? 'blue.700' : 'gray.500'}
                                        >
                                          {n}
                                        </Text>
                                        {isSelected && <Text fontSize="2xs" color="yellow.700" fontWeight="bold">→</Text>}
                                        {isPlaced && !isSelected && <Icon as={MdCheckCircle} boxSize={3} color="blue.500" />}
                                      </HStack>
                                    )
                                  })}
                                </Flex>
                              </VStack>
                            </Box>
                          )
                        })()

  const handleSampleImageClickModal = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (selectedBlot && selectedBandToPlace) {
      // Place the currently selected band at the exact click Y position.
      // Works in both modes — in AI mode this lets the biologist add a band the
      // detector missed on an "à revoir" strip, which then counts in the verdict.
      const rect = e.currentTarget.getBoundingClientRect()
      const relY = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height))
      if (module === 'protis') {
        // Sémantique « poser OU déplacer » : cliquer une case déjà posée
        // puis l'image REPOSITIONNE la graduation — le toggle d'IRIS, lui,
        // l'aurait supprimée. Et sur le marqueur, la pose enchaîne sur la
        // graduation suivante, comme la calibration IRIS.
        placeBandsBatch(selectedBlot.id, [[selectedBandToPlace, relY]])
        setSelectedBandToPlace(nextMarkerGraduation(selectedBandToPlace))
        return
      }
      togglePlacedBand(selectedBlot.id, selectedBandToPlace, relY)
      // Deselect after placing
      setSelectedBandToPlace(null)
    } else if (aiAccessible) {
      setShowRawImage(v => !v)
    }
    // If non-premium but no band selected: do nothing (cursor shows intent)
  }, [aiAccessible, selectedBlot, selectedBandToPlace, togglePlacedBand, module, placeBandsBatch])

  // Force image re-render when active control changes
  useEffect(() => {
    if (activePositiveControl) {
      const newImage = `data:image/png;base64,${activePositiveControl.reconstructed_base64}`
      console.log('📸 Setting new control image:', {
        controlId: activePositiveControl.id,
        imageLength: activePositiveControl.reconstructed_base64?.length
      })
      setDisplayedControlImage(newImage)
      setImageRenderKey(prev => prev + 1)
    } else {
      setDisplayedControlImage(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePositiveControl?.id, activePositiveControl?.reconstructed_base64])

  // Auto-adjust sensitivity when selected blot changes (using activePositiveControl for ROI)
  useEffect(() => {
    if (selectedBlot) {
      const optimalSensitivity = findOptimalSensitivity(selectedBlot, 5, activePositiveControl)
      setPeakSensitivity(optimalSensitivity)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlot?.id, activePositiveControl?.id])

  // Save calibration to backend and exit selection mode
  const finishCalibration = async (positions: Map<BandName, { position: number; intensity: number }>) => {
    setBandSelectionMode(false)
    setCurrentBandToSelect(null)

    const calibrationObject: Record<string, { position: number; intensity: number }> = {}
    positions.forEach((data, band) => { calibrationObject[band] = data })

    if (onBandCalibrationChange && activePositiveControl) {
      try {
        await onBandCalibrationChange(activePositiveControl.id, calibrationObject)
        const requiredDone = REQUIRED_BAND_NAMES.length === 0 || REQUIRED_BAND_NAMES.every(b => positions.has(b))
        const calibratedNames = Array.from(positions.keys()).join(', ')
        toast({
          title: requiredDone ? t('blotAnalysis.calibration.saved') : t('blotAnalysis.calibration.savedPartial'),
          description: t('blotAnalysis.calibration.savedDetail', { count: positions.size, names: calibratedNames }),
          status: 'success',
          duration: 3000,
        })
      } catch (error) {
        console.error('Error saving band calibration:', error)
        toast({ title: t('blotAnalysis.common.error'), description: t('blotAnalysis.calibration.saveFailed'), status: 'error', duration: 3000 })
      }
    }
  }

  // Handle manual band selection - click on a peak to assign it to current band.
  // Unified single-band model: each chart click saves ONE band and exits selection
  // mode. To calibrate the next band the user clicks its pastille — no auto-advance.
  // PROTIS : une case armée (selectedBandToPlace) se pose aussi DEPUIS LE
  // PROFIL — clic sur un pic (prioritaire) ou n'importe quelle position de la
  // courbe, par la même tuyauterie que la calibration du témoin IRIS.
  // Le marqueur ouvert joue le rôle du témoin : il porte sa livrée VERTE
  // (carte, pastille, courbe) au lieu du violet « échantillon ».
  const isProtisMarkerSelected = module === 'protis' && !!selectedBlot
    && /marqueur|marker/i.test(String((selectedBlot as any)?.strip_id ?? ''))
  const protisChartArmed = module === 'protis' && !!selectedBandToPlace
  // Sur le MARQUEUR, le flux est séquentiel comme la calibration IRIS : chaque
  // pose arme d'elle-même la graduation suivante non posée (180 → 10), et le
  // geste s'arrête tout seul à la dernière.
  const nextMarkerGraduation = (justPlaced: string): string | null => {
    if (!selectedBlot || !/marqueur|marker/i.test(String((selectedBlot as any).strip_id ?? ''))) return null
    const ladders = (immunoblotConfig as any)?.interpretation?.ladderProfile?.ladder?.knownLadders ?? []
    const values: number[] = Array.from(new Set(ladders.flatMap((l: any) => (l.values ?? []) as number[])))
    const placedNow = new Set<string>([...Array.from(blotPlacedBands.get(selectedBlot.id)?.keys() ?? []), justPlaced])
    for (const v of values.sort((a, b) => b - a)) {
      if (!placedNow.has(`${v} kDa`)) return `${v} kDa`
    }
    return null
  }
  const handleProtisChartPlace = (position: number) => {
    const len = selectedBlot?.profile?.length
    if (!len || !selectedBandToPlace || !selectedBlot) return
    placeBandsBatch(selectedBlot.id, [[selectedBandToPlace, position / len]])
    setSelectedBandToPlace(nextMarkerGraduation(selectedBandToPlace))
  }

  // ── Les bandes posées, dessinées SUR le profil (comme la calibration
  // IRIS) : le graphe sait déjà annoter des positions nommées, on lui donne
  // les nôtres. Position en index de profil, intensité lue sur la courbe.
  const protisChartBands = useMemo(() => {
    if (module !== 'protis') return undefined
    // Dans la COMPARAISON, ce sont les graduations DU MARQUEUR qui annotent
    // les profils — l'échelle vaut pour toute la plaque, c'est tout son
    // sens. Le marqueur ouvert seul annote les siennes.
    const src: any = protisPlateMarker ?? selectedBlot
    if (!src?.profile?.length) return undefined
    const placed = blotPlacedBands.get(src.id)
    if (!placed?.size) return undefined
    const len = src.profile.length
    const m = new Map<string, { position: number; intensity: number }>()
    placed.forEach((y, name) => {
      const pos = Math.max(0, Math.min(len - 1, Math.round(y * len)))
      m.set(name, { position: pos, intensity: src.profile[pos] ?? 0 })
    })
    return m
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, selectedBlot?.id, protisPlateMarker?.id, blotPlacedBands])

  const handleBandPeakClick = async (peakPosition: number, intensity: number) => {
    if (!bandSelectionMode || !currentBandToSelect) return

    const newMap = new Map(selectedBandPositions)
    // One peak = one band. If another band is already sitting on this exact
    // peak, drop it so the band being placed REPLACES it, instead of stacking
    // two labels on the same position.
    const clicked = Math.round(peakPosition)
    for (const [band, data] of Array.from(newMap.entries())) {
      if (band !== currentBandToSelect && Math.round(data.position) === clicked) {
        newMap.delete(band)
      }
    }
    newMap.set(currentBandToSelect, { position: peakPosition, intensity })

    // Carry the other bands with the one just placed.
    //
    // The template holds each band as a fraction of the control line → anchor
    // span, so a single correctly placed band fixes that span and every other
    // band follows from the same table. Correcting one band by hand is then
    // worth five, which is the whole point of the gesture.
    //
    // Only bands the operator has NOT placed themselves move: their own
    // placements are observations and stay put, including the one being set
    // now. A band whose re-projection snaps onto a real peak takes the peak,
    // exactly as the first suggestion does.
    const placedByHand = new Set(handPlacedBands).add(currentBandToSelect)
    setHandPlacedBands(placedByHand)
    let carried = 0
    if (activePositiveControl && immunoblotConfig) {
      const reprojected = suggestBandsFromTemplate(
        activePositiveControl, immunoblotConfig, null,
        { band: currentBandToSelect, position: peakPosition },
      )
      const profile: number[] = activePositiveControl.profile ?? []
      reprojected?.forEach((position, band) => {
        if (!BAND_NAMES.includes(band) || placedByHand.has(band as BandName)) return
        const idx = Math.max(0, Math.min(profile.length - 1, Math.round(position)))
        const previous = newMap.get(band as BandName)
        if (previous && Math.round(previous.position) === Math.round(position)) return
        newMap.set(band as BandName, { position, intensity: profile[idx] ?? 0 })
        carried += 1
      })
    }
    setSelectedBandPositions(newMap)

    // Persist the new calibration map (one band updated, others preserved)
    const calibrationObject: Record<string, { position: number; intensity: number }> = {}
    newMap.forEach((data, band) => { calibrationObject[band] = data })
    if (onBandCalibrationChange && activePositiveControl) {
      try {
        await onBandCalibrationChange(activePositiveControl.id, calibrationObject)
      } catch (e) {
        console.error('Error saving band calibration:', e)
        toast({
          title: t('blotAnalysis.common.error'),
          description: t('blotAnalysis.calibration.saveFailed'),
          status: 'error',
          duration: 3000,
        })
      }
    }
    toast({
      title: t('blotAnalysis.calibration.bandCalibrated', { band: currentBandToSelect }),
      description: carried > 0
        ? t('blotAnalysis.calibration.carriedOthers', { count: carried })
        : t('blotAnalysis.calibration.positionPx', { px: Math.round(peakPosition) }),
      status: 'success',
      duration: carried > 0 ? 3500 : 2000,
    })
    // Exit selection mode after each click (user clicks next pastille if needed)
    setBandSelectionMode(false)
    setSingleBandEditMode(false)
    setCurrentBandToSelect(null)
  }

  // Calibrate by clicking directly on the CONTROL image (like clicking the peak
  // on the chart, and like "ajouter une bande" on samples). Converts the click
  // height (fraction of the strip) to a raw profile position, then reuses the
  // same placement logic. Returns true when it consumed the click.
  const handleControlImageCalibrationClick = (e: React.MouseEvent<HTMLDivElement>): boolean => {
    if (readOnly || !bandSelectionMode || !currentBandToSelect || !selectedBlot) return false
    const len = selectedBlot.profile?.length ?? 0
    if (!len) return false
    const rect = e.currentTarget.getBoundingClientRect()
    const relY = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height))
    const pos = Math.max(0, Math.min(len - 1, Math.round(relY * len)))
    const intensity = selectedBlot.profile[pos] ?? 0
    handleBandPeakClick(pos, intensity)
    return true
  }

  // Skip the current optional band and move to the next one
  const skipCurrentOptionalBand = async () => {
    if (!currentBandToSelect || REQUIRED_BAND_NAMES.includes(currentBandToSelect)) return
    const currentIndex = CALIBRATION_BAND_ORDER.indexOf(currentBandToSelect)
    if (currentIndex < CALIBRATION_BAND_ORDER.length - 1) {
      const nextBand = CALIBRATION_BAND_ORDER[currentIndex + 1]
      setCurrentBandToSelect(nextBand)
    } else {
      // Last band was optional and skipped — finish with what we have
      await finishCalibration(selectedBandPositions)
    }
  }

  // Start band selection — always single-band mode.
  //   - { singleBand: 'P30' }  → edit that specific band
  //   - { resetAll: true }      → wipe existing calibration, then arm next uncalibrated band
  //   - (no options)            → pick the first uncalibrated band automatically
  // The user always sees ONE pastille at a time. To calibrate the next band they
  // click its pastille — there is no more sequential "auto-advance" flow.
  const startBandSelection = (options?: { singleBand?: BandName; resetAll?: boolean }) => {
    if (CALIBRATION_BAND_ORDER.length === 0) return

    // Pre-fill state with existing saved calibration OR auto-suggested positions,
    // unless resetAll is true (full reset).
    const baseline = effectiveBandPositions.size > 0 ? effectiveBandPositions : suggestedBandPositions
    const seedPositions = options?.resetAll ? new Map() : new Map(baseline)
    setSelectedBandPositions(seedPositions)
    if (options?.resetAll) setHandPlacedBands(new Set())

    // "Tout recalibrer" — wipe the SAVED calibration too, not just local state,
    // so the old bands don't linger (and don't reappear if the user backs out
    // or ends up placing fewer bands than before). We start from a truly clean
    // slate; each subsequent placement re-persists the cumulative map.
    if (options?.resetAll && onBandCalibrationChange && activePositiveControl) {
      // null → the parent deletes this control's calibration entry entirely.
      onBandCalibrationChange(activePositiveControl.id, null).catch((e) => {
        console.error('Error clearing band calibration:', e)
      })
    }

    // Pick which band to calibrate now
    const targetBand =
      options?.singleBand
      ?? CALIBRATION_BAND_ORDER.find((b) => !seedPositions.has(b))
      ?? CALIBRATION_BAND_ORDER[0]

    setBandSelectionMode(true)
    setSingleBandEditMode(true)
    setCurrentBandToSelect(targetBand)

    const mw = immunoblotConfig?.bands?.specific?.find((b: any) => b.name === targetBand)?.molecularWeight ?? ''
    const isRequired = REQUIRED_BAND_NAMES.includes(targetBand)
    toast({
      title: t('blotAnalysis.calibration.calibrateBand', { band: targetBand }),
      description: t('blotAnalysis.calibration.clickPeak', { band: targetBand, mw: mw ? ` (${mw} kDa)` : '', required: isRequired ? t('blotAnalysis.calibration.requiredBandSuffix') : '' }),
      status: 'info',
      duration: 3000,
    })
  }

  // Calibrate button click: a fresh calibration starts immediately, but "Tout
  // recalibrer" wipes the placed bands — ask for confirmation first.
  const handleCalibrateButtonClick = () => {
    if (effectiveBandPositions.size > 0) {
      const ok = window.confirm(
        t('blotAnalysis.calibration.confirmReset'),
      )
      if (!ok) return
      startBandSelection({ resetAll: true })
    } else {
      startBandSelection()
    }
  }

  // Navigate to the active control blot and start calibration
  const navigateToControlAndCalibrate = () => {
    if (!activePositiveControl) return
    const controlIndex = displayedBlotsArray.findIndex(b => b.id === activePositiveControl.id)
    if (controlIndex === -1) return
    setSelectedBlot(displayedBlotsArray[controlIndex])
    setSelectedBlotIndex(controlIndex)
    setTimeout(() => startBandSelection(), 100)
  }

  // ── Guided tour ───────────────────────────────────────────────────────────
  // Onboards new users: pick the témoin (T+) → calibrate its bands → REC/RAW →
  // zoom tools. Steps 2-4 live inside the control's modal, which the tour opens
  // automatically once a témoin is set. AI mode only.
  const tourSteps: TourStep[] = useMemo(() => {
    const step = (key: string, target: string, placement: TourStep['placement'], bulletCount: number): TourStep => ({
      key, target, placement,
      title: t(`blotAnalysis.tourSteps.${key}.title`),
      body: t(`blotAnalysis.tourSteps.${key}.body`),
      bullets: Array.from({ length: bulletCount }, (_, i) => t(`blotAnalysis.tourSteps.${key}.b${i + 1}`)),
    })
    return [
      step('extraction', '[data-tour="show-blots-toggle"]', 'left', 4),
      step('control', '[data-tour="control-toggles"]', 'bottom', 2),
      step('bands', '[data-tour="bands-panel"]', 'left', 3),
      step('recraw', '[data-tour="rec-raw"]', 'top', 2),
      step('kda', '[data-tour="tool-kda"]', 'bottom', 2),
      step('zoom', '[data-tour="zoom-tools"]', 'bottom', 2),
      step('sensitivity', '[data-tour="tool-sensitivity"]', 'bottom', 2),
    ]
  }, [t])

  const anyTourControlId = activeControlIds[0] ?? null
  // The tips popover lives in the workspace's FAB dock (another component), so it
  // asks for the tour by bumping this counter. Ignore the initial 0 so the tour
  // doesn't auto-open on mount.
  useEffect(() => {
    if (startTourSignal > 0) setTourStep(0)
  }, [startTourSignal])
  const tourNext = () => setTourStep((s) => Math.min(s + 1, tourSteps.length - 1))
  const tourPrev = () => setTourStep((s) => Math.max(0, s - 1))
  const endTour = () => {
    setTourStep(-1)
    try { localStorage.setItem('blotGuidedTourDone', '1') } catch { /* ignore */ }
  }

  // Auto-start once for first-time users on a fresh AI-mode case. Never for
  // a read-only viewer (admin-shadow, or a no-account guest via a shared
  // link — see GuestCaseAccess.tsx): every step of this tour points at a
  // write action (set témoin, reposition, re-analyse…) that's disabled or
  // absent for them, so it just confuses instead of helping.
  useEffect(() => {
    if (tourAutoStartedRef.current || !aiAccessible || blotsArray.length === 0 || readOnly) return
    let done = false
    try { done = localStorage.getItem('blotGuidedTourDone') === '1' } catch { /* ignore */ }
    if (done) return
    tourAutoStartedRef.current = true
    const id = setTimeout(() => setTourStep(0), 600) // let the gallery paint first
    return () => clearTimeout(id)
  }, [aiAccessible, blotsArray.length])

  // Control step → bands step as soon as a témoin is designated. (Step 0 is now
  // the "verify extraction" step, so the control step lives at index 1.)
  useEffect(() => {
    if (tourStep === 1 && anyTourControlId != null) setTourStep(2)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyTourControlId])

  // Modal state has to follow the tour BOTH ways.
  //   - steps ≥ 2 (calibration) happen inside the control's modal → open it;
  //   - steps 0–1 point at the gallery (extraction toggle, T+ buttons), which the
  //     size="full" modal covers completely → going back must close it again,
  //     otherwise the spotlight lands on a target hidden behind the modal.
  // tourStep < 0 means the tour is closed: leave the user's modal alone.
  useEffect(() => {
    if (tourStep < 0) return
    if (tourStep < 2) {
      if (isOpen) onClose()
      return
    }
    if (anyTourControlId == null) return
    if (!isOpen || selectedBlot?.id !== anyTourControlId) {
      const idx = displayedBlotsArray.findIndex((b) => b.id === anyTourControlId)
      if (idx >= 0) handleBlotClick(displayedBlotsArray[idx], idx)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourStep])

  // Cancel band selection (discards in-progress calibration)
  const cancelBandSelection = () => {
    setBandSelectionMode(false)
    setSingleBandEditMode(false)
    setCurrentBandToSelect(null)
    setSelectedBandPositions(new Map())
    setHandPlacedBands(new Set())
  }

  // Cache aligned profiles for all blots (peak-anchored alignment — matches the strongest
  // sample peak to the strongest control peak, etc., so a sample with only P30 still ends
  // up with its P30 lined up on the control's P30).
  const dtwProfileCache = useMemo(() => {
    const cache = new Map<number, number[]>()
    if (!activePositiveControl || effectiveBandPositions.size === 0) return cache

    const controlOffset = getControlLine(activePositiveControl)
    const shiftedControl = activePositiveControl.profile.slice(controlOffset)
    const shiftedControlPeaks = activePositiveControl.peaks
      .filter(p => p >= controlOffset)
      .map(p => p - controlOffset)
    if (shiftedControl.length === 0) return cache

    for (const blot of blots) {
      if (blot.id === activePositiveControl.id) continue
      const sampleOffset = getControlLine(blot)
      const shiftedSample = blot.profile.slice(sampleOffset)
      const shiftedSamplePeaks = blot.peaks
        .filter(p => p >= sampleOffset)
        .map(p => p - sampleOffset)
      if (shiftedSample.length > 0) {
        cache.set(
          blot.id,
          alignProfilesByPeakAnchors(shiftedControl, shiftedSample, shiftedControlPeaks, shiftedSamplePeaks),
        )
      }
    }
    return cache
  }, [blots, activePositiveControl, effectiveBandPositions, getControlLine])

  // DTW-aligned sample profile for the currently selected blot (from cache)
  const dtwAlignedSampleProfile = useMemo((): number[] | null => {
    if (!selectedBlot || !activePositiveControl) return null
    if (selectedBlot.id === activePositiveControl.id) return null
    return dtwProfileCache.get(selectedBlot.id) || null
  }, [selectedBlot, activePositiveControl, dtwProfileCache])

  // Cache baseline-corrected profiles for all blots + control.
  // Computed once per set of blots; downstream analysis uses corrected profiles
  // so that slowly varying background is removed before peak detection.
  const baselineCorrectedCache = useMemo(() => {
    const cache = new Map<number, number[]>()
    for (const blot of (Array.isArray(blots) ? blots : [])) {
      cache.set(blot.id, subtractBaseline(blot.profile))
    }
    if (activePositiveControl) {
      cache.set(activePositiveControl.id, subtractBaseline(activePositiveControl.profile))
    }
    return cache
  }, [blots, activePositiveControl])

  // Calculate manual ratios based on selected band positions.
  // Reference band: required band for Toxo (P30), most intense calibrated band otherwise.
  const manualBandRatios = useMemo(() => {
    if (effectiveBandPositions.size < 1) return null

    // Pick reference band: required band if defined, otherwise most intense calibrated band
    let refBandName: string = REFERENCE_BAND
    if (REQUIRED_BAND_NAMES.length > 0) {
      refBandName = REQUIRED_BAND_NAMES[REQUIRED_BAND_NAMES.length - 1] // lowest MW required (e.g. P30)
    } else {
      let maxIntensity = 0
      effectiveBandPositions.forEach((data, bandName) => {
        if (data.intensity > maxIntensity) { maxIntensity = data.intensity; refBandName = bandName }
      })
    }

    const refData = effectiveBandPositions.get(refBandName)
    if (!refData) return null

    const ratios = new Map<string, number>()
    const intensities = new Map<string, number>()
    const positions = new Map<string, number>()

    effectiveBandPositions.forEach((data, bandName) => {
      if (data.intensity <= 100) return
      intensities.set(bandName, data.intensity)
      positions.set(bandName, data.position)
      if (bandName !== refBandName && data.intensity > 0 && refData.intensity > 0) {
        ratios.set(`${refBandName}/${bandName}`, refData.intensity / data.intensity)
      }
    })

    return { ratios, intensities, positions, refBandName }
  }, [effectiveBandPositions, REFERENCE_BAND, REQUIRED_BAND_NAMES])

  // Compare manual ratios with sample blot (all tests — reference band varies per test)
  const manualRatioComparison = useMemo(() => {
    if (!manualBandRatios || !selectedBlot || !selectedBlot.profile) return null
    if (effectiveBandPositions.size < 2) return null

    // Don't compare if the selected blot IS the positive control (comparing with itself)
    const isSelectedBlotTheControl = activePositiveControl && selectedBlot.id === activePositiveControl.id
    if (isSelectedBlotTheControl) {
      // Return control-only data for display, but mark as "self-comparison"
      return {
        controlIntensities: manualBandRatios.intensities,
        controlPositions: manualBandRatios.positions,
        controlRatios: manualBandRatios.ratios,
        sampleIntensities: manualBandRatios.intensities,
        samplePositions: manualBandRatios.positions,
        sampleRatios: manualBandRatios.ratios,
        ratioDeltas: new Map<string, { control: number, sample: number, delta: number, percentDiff: number }>(),
        overallSimilarity: null as number | null,
        isConsistent: true,
        isSelfComparison: true,
        refBandName: manualBandRatios.refBandName,
      }
    }

    // For each manually selected band position on the control,
    // find the intensity at the same position on the sample
    const sampleIntensities = new Map<string, number>()
    const samplePositions = new Map<string, number>()
    const sampleRatios = new Map<string, number>()

    // Get control offset for alignment
    const controlOffset = activePositiveControl
      ? getControlLine(activePositiveControl)
      : 0

    // Step 1: Read sample intensities at the calibrated band positions.
    //
    // Strategy: project each calibrated band's position onto the SAMPLE'S RAW
    // pixel coords, then snap to the nearest detected sample peak (the same peaks
    // the operator sees on the chart). The intensity is read from the SAME
    // baseline-corrected profile the chart displays — so the number in the panel
    // matches the height of the peak the operator can see. Bands with no
    // detected peak nearby (signal is just background) are rejected.
    const sampleOffset = getControlLine(selectedBlot)
    const correctedSampleProfile = baselineCorrectedCache.get(selectedBlot.id)
      ?? subtractBaseline(selectedBlot.profile)
    // findControlLinePosition returns 0 when no peak ≥ threshold sits in the
    // top 100 px. Without a real control line we cannot trust offset projection.
    const sampleHasControlLine = sampleOffset > 0
    const controlHasControlLine = controlOffset > 0
    const samplePeaksRaw = Array.isArray(selectedBlot.peaks) ? selectedBlot.peaks : []
    const SNAP_TOLERANCE = Math.max(20, Math.floor(selectedBlot.profile.length * 0.05))

    // Build band → sample-peak mapping. Two regimes:
    //   1) Both sides have a real control line → offset projection + snap
    //   2) At least one side lacks a control line → DTW-style alignment using
    //      the calibrated bands and the sample peaks in the same zone
    //      (rank-by-intensity matching + monotonic anchor filtering).
    const bandToSamplePeak = new Map<string, number>()
    if (sampleHasControlLine && controlHasControlLine) {
      // Greedy 1:1 assignment: collect every band↔peak pair within tolerance, then
      // assign the closest pairs first, never reusing a peak or a band. Without this
      // two tightly-packed bands (Toxo P40/P45, P30/P31) both snap to the same
      // detected peak and get shown at an identical position/intensity.
      const pairs: Array<{ bandName: string; peak: number; d: number }> = []
      effectiveBandPositions.forEach((data, bandName) => {
        const naive = Math.round(data.position - controlOffset + sampleOffset)
        for (const p of samplePeaksRaw) {
          const d = Math.abs(p - naive)
          if (d <= SNAP_TOLERANCE) pairs.push({ bandName, peak: p, d })
        }
      })
      pairs.sort((a, b) => a.d - b.d)
      // A shoulder (P31) only maps once its anchor (P30) claimed its own distinct
      // peak — so a single doublet peak stays P30, never P31. Anchors/normal bands
      // are assigned first (closest-first), then shoulders. Toxo IgG & IgM.
      const SHOULDER_ANCHOR: Record<string, string> = { P31: 'P30' }
      const takenBands = new Set<string>()
      const takenPeaks = new Set<number>()
      const assignPairs = (shoulders: boolean) => {
        for (const pr of pairs) {
          if ((pr.bandName in SHOULDER_ANCHOR) !== shoulders) continue
          if (takenBands.has(pr.bandName) || takenPeaks.has(pr.peak)) continue
          if (shoulders && !takenBands.has(SHOULDER_ANCHOR[pr.bandName])) continue
          bandToSamplePeak.set(pr.bandName, pr.peak)
          takenBands.add(pr.bandName)
          takenPeaks.add(pr.peak)
        }
      }
      assignPairs(false)
      assignPairs(true)
    } else if (samplePeaksRaw.length > 0) {
      const dtwMap = dtwBandsToSamplePeaks(effectiveBandPositions, samplePeaksRaw, correctedSampleProfile)
      dtwMap.forEach((samplePos, bandName) => {
        bandToSamplePeak.set(bandName, samplePos)
      })
    }

    const rawSampleReadings = new Map<string, { idx: number, intensity: number }>()
    bandToSamplePeak.forEach((snapped, bandName) => {
      if (snapped < 0 || snapped >= correctedSampleProfile.length) return

      // Acceptance gate: prominence ≥ 30 on the corrected profile (same threshold
      // as identifyBands) — so the ratio panel and the image badge/overlay always
      // agree on which bands are present.
      const prominence = computePeakProminence(correctedSampleProfile, snapped)
      if (prominence < 30) return
      const intensity = correctedSampleProfile[snapped]
      if (intensity <= 0) return
      rawSampleReadings.set(bandName, { idx: snapped, intensity })
    })

    const refBandName = manualBandRatios.refBandName
    // Ratio coherence check (only for tests with a required reference band like Toxo):
    // we keep the RATIO-of-ratios check (controlRatio vs sampleRatio) — that's the
    // diagnostic signal that proves the strip pattern matches the calibration.
    // We dropped the symmetric absolute-intensity-diff cap: a strong positive
    // sample legitimately has higher titer (= brighter bands) than the calibrated
    // control. Capping it was rejecting genuine strong positives.
    const useRatioCheck = REQUIRED_BAND_NAMES.length > 0
    const rawSampleRef = useRatioCheck ? rawSampleReadings.get(refBandName) : null
    const controlRefData = useRatioCheck ? effectiveBandPositions.get(refBandName) : null
    const MAX_RATIO_DIFF = 0.75

    rawSampleReadings.forEach(({ idx, intensity }, bandName) => {
      if (useRatioCheck && bandName !== refBandName
          && rawSampleRef && controlRefData && controlRefData.intensity > 0) {
        const controlBandData = effectiveBandPositions.get(bandName)
        if (controlBandData && controlBandData.intensity > 0) {
          const controlRatio = controlRefData.intensity / controlBandData.intensity
          const sampleRatio = rawSampleRef.intensity / intensity
          const ratioDiff = Math.abs(controlRatio - sampleRatio) / controlRatio
          if (ratioDiff > MAX_RATIO_DIFF) return
        }
      }
      sampleIntensities.set(bandName, intensity)
      samplePositions.set(bandName, idx)
    })

    // Calculate sample ratios using the chosen reference band
    const sampleRef = sampleIntensities.get(refBandName)
    if (sampleRef && sampleRef > 0) {
      sampleIntensities.forEach((intensity, bandName) => {
        if (bandName !== refBandName && intensity > 0) {
          sampleRatios.set(`${refBandName}/${bandName}`, sampleRef / intensity)
        }
      })
    }

    // Compare ratios
    const ratioDeltas = new Map<string, { control: number, sample: number, delta: number, percentDiff: number }>()
    let totalDiff = 0
    let comparisons = 0

    manualBandRatios.ratios.forEach((controlRatio, ratioName) => {
      const sampleRatio = sampleRatios.get(ratioName)
      if (sampleRatio !== undefined) {
        const delta = Math.abs(controlRatio - sampleRatio)
        const percentDiff = (delta / controlRatio) * 100
        ratioDeltas.set(ratioName, { control: controlRatio, sample: sampleRatio, delta, percentDiff })
        totalDiff += percentDiff
        comparisons++
      }
    })

    // Similarity reliability threshold:
    //   - Tests with ≥ 3 bands → need ≥ 2 ratio comparisons to be meaningful
    //     (one ratio alone is too easy to satisfy and produces misleading scores).
    //   - Tests with only 2 bands (e.g. LDBIO Toxocara → B24, B35) → max possible
    //     comparisons = 1, so accept it. Otherwise similarity would always be N/A.
    const totalTestBands = BAND_NAMES.length
    const minComparisons = totalTestBands <= 2 ? 1 : 2
    const hasEnough = comparisons >= minComparisons
    const avgDiff = hasEnough ? totalDiff / comparisons : 100
    const overallSimilarity = hasEnough ? Math.max(0, 100 - avgDiff) : null
    const isConsistent = hasEnough ? avgDiff <= 25 : false

    return {
      controlIntensities: manualBandRatios.intensities,
      controlPositions: manualBandRatios.positions,
      controlRatios: manualBandRatios.ratios,
      sampleIntensities,
      samplePositions,
      sampleRatios,
      ratioDeltas,
      overallSimilarity,
      isConsistent,
      isSelfComparison: false,
      refBandName,
    }
  }, [manualBandRatios, selectedBlot, activePositiveControl, effectiveBandPositions, dtwAlignedSampleProfile, REQUIRED_BAND_NAMES.length, BAND_NAMES.length, baselineCorrectedCache, getControlLine])

  // Detect additional peaks based on sensitivity threshold
  // peakSensitivity: 0 = only original detected peaks, 100 = detect all local maxima (even weak ones)
  // Uses exponential scaling so mid-range (50%) is more useful
  const getEnhancedPeaks = useMemo(() => {
    return (blot: BlotDetection | null | undefined): number[] => {
      if (!blot || !blot.profile || blot.profile.length === 0) return []

      // Start with original detected peaks
      const originalPeaks = new Set(blot.peaks || [])

      // If sensitivity is 0, return only original peaks (filtered by min intensity)
      const MIN_PEAK_INTENSITY = 100
      if (peakSensitivity === 0) return (blot.peaks || []).filter(pos => blot.profile[pos] > MIN_PEAK_INTENSITY)

      // Find all local maxima in the profile
      const profile = blot.profile
      const maxIntensity = Math.max(...profile)
      if (maxIntensity === 0) return blot.peaks || []

      // Use exponential scaling for more progressive sensitivity
      // sensitivityFactor: 0% → 1.0, 50% → 0.25, 100% → 0
      const sensitivityFactor = Math.pow(1 - peakSensitivity / 100, 2)

      // Threshold for considering a point as a potential peak
      // At 0%: 15% of max, at 50%: ~4% of max, at 100%: 0%
      const minIntensityThreshold = maxIntensity * sensitivityFactor * 0.15

      // Window size for local maximum detection (smaller = more sensitive)
      // At 0%: 12, at 50%: 5, at 100%: 3
      const windowSize = Math.max(3, Math.floor(3 + sensitivityFactor * 9))

      // Minimum prominence (peak must be this much higher than surrounding valleys)
      // At 0%: 5% of max, at 50%: ~1.25% of max, at 100%: 0%
      const minProminence = maxIntensity * sensitivityFactor * 0.05

      // Minimum distance between peaks
      // At 0%: 15, at 50%: 8, at 100%: 5
      const minDistance = Math.max(5, Math.floor(5 + sensitivityFactor * 10))

      const allPeaks: number[] = []

      // Scan profile for local maxima
      for (let i = windowSize; i < profile.length - windowSize; i++) {
        const currentValue = profile[i]

        // Skip if below minimum threshold
        if (currentValue < minIntensityThreshold) continue

        // Check if this is a local maximum within the window
        let isLocalMax = true
        let leftMin = currentValue
        let rightMin = currentValue

        for (let j = 1; j <= windowSize; j++) {
          if (profile[i - j] >= currentValue || profile[i + j] >= currentValue) {
            isLocalMax = false
            break
          }
          leftMin = Math.min(leftMin, profile[i - j])
          rightMin = Math.min(rightMin, profile[i + j])
        }

        if (!isLocalMax) continue

        // Check prominence (how much higher than surrounding valleys)
        const prominence = currentValue - Math.max(leftMin, rightMin)
        if (prominence < minProminence) continue

        // Check if too close to an existing peak (avoid duplicates)
        const tooClose = allPeaks.some(p => Math.abs(p - i) < minDistance)
        if (tooClose) continue

        allPeaks.push(i)
      }

      // Combine original peaks with newly detected ones
      const combinedPeaks = [...Array.from(originalPeaks), ...allPeaks]

      // Sort by position
      const sortedPeaks = Array.from(new Set(combinedPeaks)).sort((a, b) => a - b)

      // Final filter: remove peaks that are too close horizontally
      // Keep only the most intense peak when two are too close
      const MIN_HORIZONTAL_DISTANCE = 15 // Minimum pixels between peaks
      const filteredPeaks: number[] = []

      for (const peakPos of sortedPeaks) {
        // Check if this peak is too close to an already accepted peak
        const closeExistingIdx = filteredPeaks.findIndex(
          existingPos => Math.abs(existingPos - peakPos) < MIN_HORIZONTAL_DISTANCE
        )

        if (closeExistingIdx !== -1) {
          // Too close to existing peak - keep the more intense one
          const existingPos = filteredPeaks[closeExistingIdx]
          if (profile[peakPos] > profile[existingPos]) {
            filteredPeaks[closeExistingIdx] = peakPos // Replace with more intense
          }
          // Otherwise keep existing (do nothing)
        } else {
          filteredPeaks.push(peakPos)
        }
      }

      // Remove peaks with intensity <= 100 (too weak to be meaningful)
      return filteredPeaks
        .filter(pos => profile[pos] > MIN_PEAK_INTENSITY)
        .sort((a, b) => a - b)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peakSensitivity])

  // Enhanced peaks for selected blot (original + newly detected based on sensitivity)
  const enhancedSelectedPeaks = useMemo(() => {
    return selectedBlot ? getEnhancedPeaks(selectedBlot) : []
  }, [selectedBlot, getEnhancedPeaks])

  // Create a virtual blot with enhanced peaks for display
  const selectedBlotWithEnhancedPeaks = useMemo((): BlotDetection | null => {
    if (!selectedBlot) return null
    return {
      ...selectedBlot,
      peaks: enhancedSelectedPeaks,
      num_bands: enhancedSelectedPeaks.length
    }
  }, [selectedBlot, enhancedSelectedPeaks])

  // Simulate positive/negative based on number of peaks in dynamic ROI (using positive control's P30)
  const getBlotStatus = (blot: BlotDetection): 'positive' | 'negative' => {
    const { start: ROI_START, end: ROI_END } = getROIBounds(blot, activePositiveControl)
    const peaksInRange = blot.peaks.filter((p) => p >= ROI_START && p <= ROI_END).length
    return peaksInRange >= 3 ? 'positive' : 'negative'
  }

  // Calibrate position to molecular weight (kDa)
  // Assumes linear relationship between position and molecular weight


  // ── Peak-prominence helpers ──────────────────────────────────────────────────
  // Prominence = how much a peak rises above its surrounding local baseline.
  // More robust than raw intensity: normalises for background level.
  const getPeakProminence = (profile: number[], idx: number, halfWin: number = 14): number => {
    const val = profile[idx]
    const left = profile.slice(Math.max(0, idx - halfWin), idx)
    const right = profile.slice(idx + 1, Math.min(profile.length, idx + halfWin + 1))
    const leftMin = left.length > 0 ? Math.min(...left) : val
    const rightMin = right.length > 0 ? Math.min(...right) : val
    // Prominence above the HIGHER of the two local minima (conservative estimate)
    return Math.max(0, val - Math.max(leftMin, rightMin))
  }

  // Find the best local maximum (highest prominence) within ±tolerance of a target index.
  const findBestPeakNear = (
    profile: number[], target: number, tolerance: number = 10
  ): { idx: number; intensity: number; prominence: number } | null => {
    let best: { idx: number; intensity: number; prominence: number } | null = null
    const lo = Math.max(1, target - tolerance)
    const hi = Math.min(profile.length - 2, target + tolerance)
    for (let i = lo; i <= hi; i++) {
      if (profile[i] > profile[i - 1] && profile[i] >= profile[i + 1]) {
        const prom = getPeakProminence(profile, i)
        if (!best || prom > best.prominence) {
          best = { idx: i, intensity: profile[i], prominence: prom }
        }
      }
    }
    return best
  }

  // ── Paired-comparison (CIP-WB): compare two SAMPLE strips directly, no
  // témoin at all — see pairedComparisonPartners. Deliberately does NOT
  // reuse alignProfilesWithDTW/alignProfilesByPeakAnchors: those are built
  // to smooth ordinary gel-run distance drift between a témoin and its
  // matched sample for a cosmetic overlay only (never fed into a verdict —
  // see SHOW_RATIO_COMPARISON). This test's entire diagnostic principle is
  // "does the compared strip have a peak the reference doesn't" — rank-order
  // warping could match away exactly the extra peak that makes the couple
  // positive. Instead: align each strip only by its OWN control line
  // (getAlignedProfile, no warping), then match compared-strip peaks to
  // reference-strip peaks with the same fixed-tolerance snap used everywhere
  // else in this file (BAND_SNAP_TOLERANCE_FRAC).
  //
  // Known limitation (flagged, not solved here): the notice excludes bands
  // ≥120 kDa (too compressed near the loading well to resolve) and the IgM
  // Heat Shock Protein artifact (~37 kDa) from this comparison. Neither zone
  // can be located in pixels today — this test has no named/calibrated band
  // to build a kDa↔pixel mapping from (unlike Toxo/Aspergillus's P30 etc.),
  // and reading the kit's own printed colour ladder would need a whole new
  // detection feature. Left for a follow-up; a real difference found very
  // near the strip's top (loading well) should be read with that in mind.
  const comparePairedStrips = (
    referenceBlot: BlotDetection,
    comparedBlot: BlotDetection,
  ): {
    differences: Array<{ position: number; kind: 'extra' | 'reinforced'; prominence: number; ratio?: number }>
    isUniformReinforcement: boolean
  } => {
    const refAligned = getAlignedProfile(referenceBlot)
    const cmpAligned = getAlignedProfile(comparedBlot)
    const correctedRef = subtractBaseline(refAligned.profile)
    const correctedCmp = subtractBaseline(cmpAligned.profile)

    // Printed-ID exclusion — see idZoneRawEnd above. The control-line search
    // window (CONTROL_LINE_SEARCH_FRAC = 0.35, see findControlLinePosition)
    // sits ENTIRELY inside that zone, so the aligned profile's own offset
    // does NOT reliably clear the ID ink — and since mother and child always
    // carry DIFFERENT ids, any ID-ink signal that leaked into the comparison
    // would read as a fake "extra band" no couple could ever pass. Mother
    // and child are read independently here (their control lines can land
    // at different raw offsets), so each gets its own margin translated
    // into its own aligned coordinate space.
    const refIdZoneEnd = Math.max(0, idZoneRawEnd(referenceBlot) - refAligned.offset)
    const cmpIdZoneEnd = Math.max(0, idZoneRawEnd(comparedBlot) - cmpAligned.offset)

    const MIN_PROMINENCE = 100
    const MIN_SEPARATION = 10
    const REINFORCEMENT_RATIO = 3
    const tolerance = Math.max(10, Math.floor(Math.min(correctedRef.length, correctedCmp.length) * BAND_SNAP_TOLERANCE_FRAC))

    // Real local maxima on each strip's OWN corrected profile (not the
    // upstream `peaks` list — that's detected on the raw unaligned profile
    // and can miss faint-but-real bumps, same reasoning as Pass 2/3 above).
    const findRealPeaks = (profile: number[]): number[] => {
      const found: number[] = []
      for (let i = 1; i < profile.length - 1; i++) {
        if (profile[i] > profile[i - 1] && profile[i] >= profile[i + 1]) {
          if (getPeakProminence(profile, i) >= MIN_PROMINENCE) found.push(i)
        }
      }
      found.sort((a, b) => a - b)
      const collapsed: number[] = []
      for (const idx of found) {
        const last = collapsed[collapsed.length - 1]
        if (last !== undefined && idx - last < MIN_SEPARATION) {
          if (getPeakProminence(profile, idx) > getPeakProminence(profile, last)) collapsed[collapsed.length - 1] = idx
        } else {
          collapsed.push(idx)
        }
      }
      return collapsed
    }

    const cmpPeaks = findRealPeaks(correctedCmp).filter((idx) => idx >= cmpIdZoneEnd)
    const refPeaks = findRealPeaks(correctedRef).filter((idx) => idx >= refIdZoneEnd)

    const differences: Array<{ position: number; kind: 'extra' | 'reinforced'; prominence: number; ratio?: number }> = []
    const matchedRatios: number[] = []
    const usedRefPeaks = new Set<number>()

    cmpPeaks.forEach((cIdx) => {
      let bestIdx: number | null = null
      let bestDist = Infinity
      for (const rIdx of refPeaks) {
        if (usedRefPeaks.has(rIdx)) continue
        const dist = Math.abs(rIdx - cIdx)
        if (dist <= tolerance && dist < bestDist) { bestIdx = rIdx; bestDist = dist }
      }
      if (bestIdx === null) {
        differences.push({ position: cIdx + cmpAligned.offset, kind: 'extra', prominence: getPeakProminence(correctedCmp, cIdx) })
        return
      }
      usedRefPeaks.add(bestIdx)
      const cmpProm = getPeakProminence(correctedCmp, cIdx)
      const refProm = getPeakProminence(correctedRef, bestIdx)
      if (refProm > 0) {
        const ratio = cmpProm / refProm
        matchedRatios.push(ratio)
        if (ratio >= REINFORCEMENT_RATIO) {
          differences.push({ position: cIdx + cmpAligned.offset, kind: 'reinforced', prominence: cmpProm, ratio })
        }
      }
    })

    // Hemoconcentration guard: if EVERY matched band (at least 2, so there's
    // something to compare against) shows a similarly elevated ratio, it's a
    // uniform intensity shift, not a positivity signal — the notice is
    // explicit this must be read as negative, unlike an ISOLATED reinforced
    // band among otherwise-equal bands.
    const reinforcedCount = differences.filter(d => d.kind === 'reinforced').length
    const isUniformReinforcement = matchedRatios.length >= 2 && reinforcedCount === matchedRatios.length

    return {
      differences: isUniformReinforcement ? differences.filter(d => d.kind === 'extra') : differences,
      isUniformReinforcement,
    }
  }

  // Identify bands using peak-to-peak comparison with the control (témoin).
  //
  // Strategy: project each calibrated control band into the SAMPLE's raw pixel
  // coords (using both control lines as anchors), then snap to the nearest
  // detected sample peak. The intensity / prominence is read from the SAME
  // baseline-corrected profile that the chart displays — so the bands shown on
  // the image match the bands the operator can see on the chart, and match the
  // numbers in the ratio panel. No DTW-derived phantom peaks.
  const identifyBands = (
    blot: BlotDetection,
    config?: ImmunoblotConfig | null,
    ctx?: {
      activePositiveControl?: BlotDetection
      effectiveBandPositions?: Map<string, { position: number; intensity: number }>
    },
  ): Array<{
    peakIndex: number
    position: number
    intensity: number
    prominence: number
    estimatedMW: number
    matchedBand: string | null
    bandMW: number | null
    // How far (px) the accepted peak sat from its PROJECTED target, and the
    // snap window it was accepted within — null in rank-matching mode, where
    // there's no target to measure a distance against. Lets a caller tell a
    // near-exact match from one that only just cleared the tolerance ceiling
    // (see BORDERLINE_SNAP_RATIO in the major_bands criterion below).
    snapDistance?: number | null
    snapTolerance?: number | null
  }> => {
    if (!config) return []
    // Per-pathology override — the "Toutes" gallery grid calls this per
    // blot's OWN pathology instead of the single filter-scoped closure
    // values below (see pathologyContexts). Every other caller omits ctx,
    // so behaviour there is byte-for-byte unchanged.
    const ctxActivePositiveControl = ctx && 'activePositiveControl' in ctx ? ctx.activePositiveControl : activePositiveControl
    const ctxEffectiveBandPositions = ctx?.effectiveBandPositions ?? effectiveBandPositions

    const controlOffset = ctxActivePositiveControl
      ? getControlLine(ctxActivePositiveControl)
      : 0
    const sampleOffset = getControlLine(blot)

    // Baseline-corrected sample profile — same source as the chart.
    const correctedSample = baselineCorrectedCache.get(blot.id)
      ?? subtractBaseline(blot.profile)
    const correctedControlProfile = ctxActivePositiveControl
      ? (baselineCorrectedCache.get(ctxActivePositiveControl.id) ?? subtractBaseline(ctxActivePositiveControl.profile))
      : null

    // Use the ORIGINAL detected peaks from the AI pipeline — not the sensitivity-
    // slider-enhanced ones — so band identification is stable and matches the
    // ratio panel and image overlay (which both work off the original peaks).
    const blotsArr = Array.isArray(blots) ? blots : []
    const originalBlot = blotsArr.find(b => b.id === blot.id) ?? blot
    const samplePeaksRaw = Array.isArray(originalBlot.peaks) ? originalBlot.peaks : []

    // Short-circuit: the blot we're interpreting IS the active positive control.
    // The user has placed bands directly on it — those positions ARE the truth,
    // no projection / snapping needed. Returning the calibrated bands as-is also
    // prevents the control from being reported as NÉGATIF when its own control
    // line falls below the intensity threshold (which would block the regular
    // identifyBands path that requires controlOffset > 0).
    if (
      ctxActivePositiveControl
      && blot.id === ctxActivePositiveControl.id
      && ctxEffectiveBandPositions.size > 0
    ) {
      const results: Array<{
        peakIndex: number
        position: number
        intensity: number
        prominence: number
        estimatedMW: number
        matchedBand: string | null
        bandMW: number | null
      }> = []
      ctxEffectiveBandPositions.forEach((data, bandName) => {
        const idx = Math.max(0, Math.min(correctedSample.length - 1, Math.round(data.position)))
        const refBand = config.bands.specific.find((b: any) => b.name === bandName)
        results.push({
          peakIndex: idx,
          position: idx,
          intensity: data.intensity,
          prominence: getPeakProminence(correctedSample, idx),
          estimatedMW: refBand?.molecularWeight ?? 0,
          matchedBand: bandName,
          bandMW: refBand?.molecularWeight ?? null,
        })
      })
      results.sort((a, b) => a.position - b.position)
      return results
    }

    // ── Zone-count mode (Toxocara) ───────────────────────────────────────────
    // Count every distinct peak inside the calibrated kDa window instead of
    // snapping to fixed named bands. The window is bounded by the anchor bands
    // the operator calibrated on the témoin (P24 & P35), projected onto the
    // sample. A zone-local sensitive scan picks up faint bands the global peak
    // detector misses. ≥2 distinct peaks → the minimum_bands criterion fires.
    if ((config as any)?.interpretation?.mode === 'zone_count' && ctxEffectiveBandPositions.size >= 2) {
      const zc = (config as any).interpretation.zoneCount ?? {}
      const margin = zc.marginPx ?? 10

      const projectPos = (controlPos: number) =>
        (controlOffset > 0 && sampleOffset > 0)
          ? controlPos - controlOffset + sampleOffset
          : controlPos

      // Anchors (P24 / P35) projected onto the sample, sorted by position, used
      // both to bound the window and to interpolate an estimated kDa per peak.
      const anchors = Array.from(ctxEffectiveBandPositions.entries())
        .map(([name, data]) => ({
          mw: config.bands.specific.find((b: any) => b.name === name)?.molecularWeight ?? 0,
          pos: projectPos(data.position),
        }))
        .sort((a, b) => a.pos - b.pos)

      const zStart = anchors[0].pos - margin
      const zEnd = anchors[anchors.length - 1].pos + margin

      const estimateMW = (pos: number): number => {
        const a = anchors[0]
        const b = anchors[anchors.length - 1]
        if (b.pos === a.pos) return a.mw
        return Math.round((a.mw + (pos - a.pos) * (b.mw - a.mw) / (b.pos - a.pos)) * 10) / 10
      }

      // ── Noise gate vs the positive control ──────────────────────────────────
      // detectZonePeaks thresholds are RELATIVE to the window's OWN max, so on a
      // NEGATIVE sample (only baseline noise in the 24-35 zone) faint noise bumps
      // still clear "5% of a tiny max" and get counted → false POSITIF with several
      // "bands". Anchor the gate to the CONTROL instead: a genuine band must reach a
      // meaningful fraction of the témoin's own band prominence in the same zone
      // (same 20% rule isBandSignificant uses for fixed-band tests, which zone_count
      // labels "~XX kDa" bypass). Pure noise sits well below it.
      const ZONE_MIN_PROMINENCE_RATIO = 0.20
      let minZoneProm = 0
      if (correctedControlProfile) {
        const ctrlPositions = Array.from(ctxEffectiveBandPositions.values())
          .map((d) => d.position)
          .sort((a, b) => a - b)
        const ctrlZonePeaks = detectZonePeaks(
          correctedControlProfile,
          ctrlPositions[0] - margin,
          ctrlPositions[ctrlPositions.length - 1] + margin,
        )
        const ctrlProms = ctrlZonePeaks
          .map((p) => getPeakProminence(correctedControlProfile, p))
          .filter((v) => v > 0)
          .sort((a, b) => a - b)
        // MEDIAN control zone-band prominence = robust "real band" reference (a lone
        // very strong band doesn't over-inflate it, a near-threshold one doesn't sink it).
        const refProm = ctrlProms.length > 0 ? ctrlProms[Math.floor(ctrlProms.length / 2)] : 0
        minZoneProm = refProm * ZONE_MIN_PROMINENCE_RATIO
      }

      const zonePeaks = detectZonePeaks(correctedSample, zStart, zEnd)
        .filter((pos) => getPeakProminence(correctedSample, pos) >= minZoneProm)

      // The notice only names 6 bands (P24/26/28/30/32/35) — but estimateMW is a
      // pure two-point LINEAR interpolation between just the window's two extreme
      // anchors, which distorts intermediate positions (measured: a real peak
      // sitting right at P32's own calibrated spot came out "~34 kDa" — a value
      // that doesn't correspond to any band in the notice, since true migration
      // isn't perfectly linear over an 11 kDa span). Snap to the nearest NAMED
      // band's exact kDa when close, so the label is always one the notice
      // actually defines instead of an arbitrary decimal.
      const namedBandMWs = (config.bands?.specific ?? [])
        .map((b: any) => b.molecularWeight)
        .filter((mw: any): mw is number => typeof mw === 'number')
      const SNAP_TO_NAMED_MW_TOLERANCE = 3

      // Label each counted peak by its kDa (readable identification within the
      // zone). Uniqueness is enforced so the count stays correct even if two
      // peaks round to the same kDa.
      const seenLabels = new Map<string, number>()
      return zonePeaks.map((pos) => {
        const rawMw = estimateMW(pos)
        let mw = rawMw
        let bestDist = Infinity
        for (const namedMw of namedBandMWs) {
          const dist = Math.abs(namedMw - rawMw)
          if (dist < bestDist) { bestDist = dist; mw = namedMw }
        }
        if (bestDist > SNAP_TO_NAMED_MW_TOLERANCE) mw = rawMw
        let label = `~${Math.round(mw)} kDa`
        const n = (seenLabels.get(label) ?? 0) + 1
        seenLabels.set(label, n)
        if (n > 1) label = `${label} (${n})`
        return {
          peakIndex: pos,
          position: pos,
          intensity: correctedSample[pos] ?? 0,
          prominence: getPeakProminence(correctedSample, pos),
          estimatedMW: mw,
          matchedBand: label,
          bandMW: null,
        }
      })
    }

    // Map band names to their target index in the sample.
    // Two regimes:
    //   1) Both témoin and sample have a real control line (offsets > 0) →
    //      project each band by offset shift, snap to nearest real peak.
    //   2) At least one side lacks a control line → fallback to RANK-BY-POSITION
    //      matching: calibrated bands sorted by their position on the témoin (MW
    //      order) ↔ detected sample peaks sorted by position. Each band gets the
    //      n-th sample peak. Prominence / intensity gates still apply so we don't
    //      blindly accept noise peaks.
    let referenceBandPositions: Map<string, number> | null = null
    let useRankMatching = false

    if (ctxEffectiveBandPositions.size > 0) {
      referenceBandPositions = new Map<string, number>()
      if (controlOffset > 0 && sampleOffset > 0) {
        ctxEffectiveBandPositions.forEach((data, bandName) => {
          referenceBandPositions!.set(bandName, data.position - controlOffset + sampleOffset)
        })
      } else if (samplePeaksRaw.length > 0) {
        // No control line on at least one side → DTW-style fallback using the
        // calibrated bands of the témoin and the sample's peaks in the same zone.
        useRankMatching = true
        const dtwMap = dtwBandsToSamplePeaks(ctxEffectiveBandPositions, samplePeaksRaw, correctedSample)
        if (dtwMap.size === 0) return []
        dtwMap.forEach((samplePos, bandName) => {
          referenceBandPositions!.set(bandName, samplePos)
        })
      } else {
        return []  // no peaks to match against
      }
    } else {
      referenceBandPositions = ctxActivePositiveControl
        ? suggestBands(ctxActivePositiveControl, config)
        : suggestBands(blot, config)
    }

    if (!referenceBandPositions) return []

    // Acceptance gates — tightened to reduce false positives where any noise peak
    // within the snap window was being accepted as a real band.
    // Sample peak must be:
    //   - prominent enough on its own (≥ MIN_ABSOLUTE_PROMINENCE on corrected profile)
    //   - at least MIN_PROMINENCE_RATIO of the control's prominence at the same band
    //   - intensity ≥ MIN_INTENSITY_RATIO of the control's calibrated intensity
    //     (catches the case where a snap lands on a tiny local max that has decent
    //     prominence but is still far below the corresponding control band)
    const MIN_PROMINENCE_RATIO = 0.25
    const MIN_ABSOLUTE_PROMINENCE = 150
    const MIN_INTENSITY_RATIO = 0.20
    // Snap window — was 5%, now 3% so neighbouring bands cannot snap to the same
    // peak (Toxo bands P40/P45 are only ~15 px apart on these strips).
    const SAMPLE_SNAP_TOLERANCE = Math.max(10, Math.floor(correctedSample.length * 0.03))

    const results: Array<{
      peakIndex: number
      position: number
      intensity: number
      prominence: number
      estimatedMW: number
      matchedBand: string | null
      bandMW: number | null
      snapDistance?: number | null
      snapTolerance?: number | null
    }> = []

    // Prevent the same sample peak from being matched to multiple calibrated
    // bands — toxo bands like P40/P45 are very close on the strip and could
    // otherwise both snap onto the same noise peak in between.
    const usedPeaks = new Set<number>()

    // ── TEMP DIAGNOSTIC ────────────────────────────────────────────────────
    // Log per-band acceptance trace to help diagnose why bands are dropped.
    // Toggle off with localStorage.setItem('blotDiag', '0') in the console.
    // Remove this whole block once we're happy with the rules.
    const DIAG = typeof window !== 'undefined'
      && window.localStorage?.getItem('blotDiag') !== '0'
      && selectedBlot && blot.id === selectedBlot.id
    const diagLog = (band: string, status: string, extras: Record<string, unknown>) => {
      if (!DIAG) return
      // eslint-disable-next-line no-console
      console.log(`[BAND-DIAG blot#${blot.id} ${band}]`, status, extras)
    }
    if (DIAG) {
      // eslint-disable-next-line no-console
      console.log(`[BAND-DIAG blot#${blot.id}] mode=${useRankMatching ? 'rank-by-intensity' : 'offset-snap'}  controlOffset=${controlOffset}  sampleOffset=${sampleOffset}  samplePeaks=[${samplePeaksRaw.join(',')}]  calibrationZone=[${Math.min(...Array.from(ctxEffectiveBandPositions.values()).map(d => d.position))}..${Math.max(...Array.from(ctxEffectiveBandPositions.values()).map(d => d.position))}]`)
    }

    // Snap ANCHORS before their SHOULDERS. P31 is a ~1 kDa satellite of P30, well
    // inside the snap tolerance, so on a single doublet peak both compete for it.
    // Iterating the anchor first lets it claim the shared peak (usedPeaks then blocks
    // the shoulder), and a shoulder is only accepted if its anchor was — so a lone
    // doublet peak is labelled P30, never P31 (the confusion this fixes). Legitimate
    // shoulders with their own proportional signal are still recovered in Pass 2,
    // which is already anchored on P30. Applies to Toxo IgG & IgM (both doublet P31/P30).
    const SHOULDER_ANCHOR: Record<string, string> = { P31: 'P30' }
    const acceptedBands = new Set<string>()
    // Assign the band whose target sits CLOSEST to a real peak first, so a band with
    // a near-exact peak claims it before a farther band snaps to it greedily. Fixes
    // the P40/P45 collision (K20: peak@934 is P40's at d=3, but P45 was grabbing it
    // at d=27 first, starving P40) and naturally orders P30 (d≈0) before its P31
    // shoulder. The shoulder DEPENDENCY below still guards the P30/P31 pair.
    const nearestPeakDist = (target: number): number => {
      const t = Math.round(target)
      let best = Infinity
      for (const p of samplePeaksRaw) { const d = Math.abs(p - t); if (d < best) best = d }
      return best
    }
    const orderedBands = Array.from(referenceBandPositions.entries())
      .sort((a, b) => nearestPeakDist(a[1]) - nearestPeakDist(b[1]))
    orderedBands.forEach(([bandName, sampleTargetIdx]) => {
      if (bandName in SHOULDER_ANCHOR && !acceptedBands.has(SHOULDER_ANCHOR[bandName])) {
        diagLog(bandName, 'reject:shoulder-without-anchor', { anchor: SHOULDER_ANCHOR[bandName] })
        return
      }
      let snapped: number | null = null
      // Distance (px) between the projected target and the peak it snapped to —
      // null in rank-matching mode (no target, so no distance to report).
      let snapDistance: number | null = null

      if (useRankMatching) {
        // In rank-matching mode, the value IS the assigned sample peak directly.
        snapped = Math.round(sampleTargetIdx)
        if (snapped < 1 || snapped >= correctedSample.length - 1) {
          diagLog(bandName, 'reject:rank-out-of-profile', { snapped, profileLen: correctedSample.length })
          return
        }
      } else {
        const target = Math.round(sampleTargetIdx)
        if (target < 1 || target >= correctedSample.length - 1) {
          diagLog(bandName, 'reject:target-out-of-profile', { target, profileLen: correctedSample.length })
          return
        }

        // Snap to the nearest REAL detected sample peak (from blot.peaks) — these
        // are the peaks the operator sees on the chart. Reject if none is near.
        let bestDist = Infinity
        for (const p of samplePeaksRaw) {
          if (usedPeaks.has(p)) continue
          const d = Math.abs(p - target)
          if (d < bestDist && d <= SAMPLE_SNAP_TOLERANCE) { bestDist = d; snapped = p }
        }
        if (snapped === null) {
          diagLog(bandName, 'reject:no-peak-in-snap-window', {
            target,
            tolerance: SAMPLE_SNAP_TOLERANCE,
            availablePeaks: samplePeaksRaw.filter(p => !usedPeaks.has(p)),
          })
          return
        }
        snapDistance = bestDist
      }

      // Measure prominence at the snapped position in the corrected profile.
      const samplePeak = findBestPeakNear(correctedSample, snapped, 6)
      if (!samplePeak) {
        diagLog(bandName, 'reject:no-local-max-near-snap', { snapped })
        return
      }

      // A peak landing THIS close to its projected target is very likely the
      // real band even when weak — a coincidental noise peak snapping this
      // tight is unlikely. Measured on a real case (Leishmania C9, major_bands
      // test with no required-band anchor to fall back on): P16 snapped 5px
      // from a 28px tolerance (18%) yet its prominence (84) sat under
      // MIN_ABSOLUTE_PROMINENCE and its ratios vs the témoin (0.18/0.18) just
      // missed MIN_PROMINENCE_RATIO/MIN_INTENSITY_RATIO (0.25/0.20) — a real
      // low-titer band dropped by a hard gate before it ever reached
      // isBandSignificant's own similarity-aware weak-signal handling below.
      // Only a lower absolute-prominence floor stays enforced here (still
      // rejects flat background); the relative-to-control judgment is left to
      // isBandSignificant/the "à vérifier" equivocal path downstream instead
      // of being decided twice, once here with no fallback and again there.
      // (TIGHT_SNAP_FRAC is module-level — interpretBlot's major_bands/
      // extreme_bands criteria rely on the exact same threshold, see there.)
      const isTightSnap = snapDistance != null && snapDistance <= SAMPLE_SNAP_TOLERANCE * TIGHT_SNAP_FRAC
      const minAbsProminenceForBand = isTightSnap ? MIN_ABSOLUTE_PROMINENCE * 0.5 : MIN_ABSOLUTE_PROMINENCE

      if (samplePeak.prominence < minAbsProminenceForBand) {
        diagLog(bandName, 'reject:prominence-too-low', {
          snapped,
          prominence: Math.round(samplePeak.prominence),
          minRequired: minAbsProminenceForBand,
          isTightSnap,
        })
        return
      }

      // Compare with control prominence + intensity at the calibrated position
      // — skipped for a tight snap (see above): isBandSignificant judges that
      // relative strength next, with a similarity fallback this hard gate lacks.
      const controlBandData = ctxEffectiveBandPositions.get(bandName)
      if (controlBandData && correctedControlProfile && !isTightSnap) {
        const controlPeakPos = Math.round(controlBandData.position)
        const controlProminence = getPeakProminence(correctedControlProfile, controlPeakPos)
        const promRatio = controlProminence > 0 ? samplePeak.prominence / controlProminence : null
        if (controlProminence > 0 && samplePeak.prominence / controlProminence < MIN_PROMINENCE_RATIO) {
          diagLog(bandName, 'reject:prominence-ratio-too-low', {
            snapped,
            samplePromin: Math.round(samplePeak.prominence),
            ctrlPromin: Math.round(controlProminence),
            ratio: promRatio !== null ? promRatio.toFixed(2) : null,
            minRatio: MIN_PROMINENCE_RATIO,
          })
          return
        }

        // Same scale as the prominence-ratio check just above: the témoin side
        // must read from correctedControlProfile too, not controlBandData.intensity
        // (RAW — ctxEffectiveBandPositions stores it straight from the profile,
        // see pathologyContexts/effectiveBandPositions). Comparing a CORRECTED
        // sample value against a RAW témoin one compares two different scales —
        // measured on a real case (Leishmania C2): P14 snapped to a genuine,
        // strong, clean peak (corrected prominence 280 — nowhere near noise)
        // yet still got rejected here, 0.15 vs the 0.20 floor, purely because
        // the témoin's raw intensity at that position (~3444) runs far higher
        // than its own corrected value would (baseline is elevated there).
        const controlIntensity = correctedControlProfile[controlPeakPos] ?? 0
        const sampleIntensity = correctedSample[samplePeak.idx] ?? 0
        const intRatio = controlIntensity > 0 ? sampleIntensity / controlIntensity : null
        // Even corrected-vs-corrected, C2's P14 still only reached 0.16 (vs the
        // 0.20 floor) — a real, absolute overall-brightness difference between
        // that scan and the témoin's, not noise: its promRatio (1.26) agrees
        // with the témoin almost exactly, since prominence (a peak's rise above
        // ITS OWN local background) is already scale-robust in a way raw
        // intensity never is. So a comfortably-clearing prominence ratio (≥2×
        // its own floor) is trusted on its own, without also demanding the
        // absolute intensity independently agree. Checked against every band
        // examined this session across 4 pathologies (Aspergillus, Toxo,
        // Schisto, Leishmania) before adding this: only C2's P14 was ever
        // affected — every other band that reaches this gate already clears
        // MIN_INTENSITY_RATIO on its own, so this never masks a real rejection
        // elsewhere, it only rescues this specific pattern.
        const promRatioComfortable = promRatio !== null && promRatio >= MIN_PROMINENCE_RATIO * 2
        if (controlIntensity > 0 && intRatio !== null && intRatio < MIN_INTENSITY_RATIO && !promRatioComfortable) {
          diagLog(bandName, 'reject:intensity-ratio-too-low', {
            snapped,
            sampleIntensity: Math.round(sampleIntensity),
            ctrlIntensity: Math.round(controlIntensity),
            ratio: intRatio !== null ? intRatio.toFixed(2) : null,
            minRatio: MIN_INTENSITY_RATIO,
          })
          return
        }
        // NOTE: we intentionally do NOT cap the upper bound of the sample/control
        // intensity ratio. A strong positive sample has a HIGHER titer than the
        // calibrated control, so its band can legitimately be brighter — that's
        // not an error, it's clinical reality. The lower bound (MIN_INTENSITY_RATIO)
        // is enough to reject phantom matches with no real signal.
      }
      diagLog(bandName, isTightSnap ? 'ACCEPT:tight-snap' : 'ACCEPT', {
        snapped,
        position: samplePeak.idx,
        prominence: Math.round(samplePeak.prominence),
        intensity: Math.round(correctedSample[samplePeak.idx] ?? 0),
      })

      usedPeaks.add(snapped)
      acceptedBands.add(bandName)

      const refBand = config.bands.specific.find((b: any) => b.name === bandName)
      // Position + intensity now in sample raw pixel coords, intensity from the
      // baseline-corrected curve so it matches the chart.
      results.push({
        peakIndex: samplePeak.idx,
        position: samplePeak.idx,
        intensity: correctedSample[samplePeak.idx] ?? 0,
        prominence: samplePeak.prominence,
        estimatedMW: refBand?.molecularWeight ?? 0,
        matchedBand: bandName,
        bandMW: refBand?.molecularWeight ?? null,
        snapDistance,
        snapTolerance: useRankMatching ? null : SAMPLE_SNAP_TOLERANCE,
      })
    })

    // ── Pass 2: control-anchored recovery of weak/shoulder bands ──────────────
    // Originally scoped to tests with a required reference band (Toxo II
    // IgG/IgM), whose strips pack P30/P31/P33 within ~55 px — the weak 31/33
    // are often unresolved SHOULDERS of the dominant P30, real signal but no
    // local maximum, so pass 1 (which needs a detected peak) drops them.
    // Generalised to EVERY test: the reference doesn't need to be a NAMED
    // required band, just whichever band pass 1 already confirmed with the
    // HIGHEST intensity on THIS sample — that's still a genuine, sample-
    // specific anchor to judge a fainter band's plausibility against, and
    // every test has one the moment pass 1 finds anything at all. Without
    // this, a real but faint band with NO detected peak anywhere nearby
    // (not even one failing the prominence floor — see the tight-snap
    // relaxation above, which only helps when pass 1 has SOME peak to snap
    // to) was silently dropped on tests with no required band. Measured on a
    // real case (Aspergillus I12): P22's target had no peak within the snap
    // window at all, yet a genuine crest sat right there — 51% of the
    // sample's own P30 (peak-anchored) intensity, vs 90% on the témoin
    // (delta 0.43, comfortably inside tolerance).
    // Here we MEASURE the corrected profile at each still-missing calibrated
    // position and accept the band only if BOTH:
    //   (a) its intensity is a meaningful fraction of the sample's own anchor, AND
    //   (b) its intensity ratio to that anchor matches the témoin's own ratio.
    // Anchored on pass 1 finding SOMETHING first → a true negative (nothing
    // detected at all) can never gain phantom bands. Offset mode only (rank
    // mode assigns differently).
    if (!useRankMatching && correctedControlProfile && results.length > 0) {
      const matchedNames = new Set(results.map((r) => r.matchedBand))
      // Positions already taken (pass 1 + earlier anchored bands) so two bands can
      // never be reported at the same spot (e.g. P40/P45 collapsing onto one crest).
      const usedPositions = results.map((r) => r.position)
      const MIN_BAND_SEPARATION = 10
      const sampleRef = results.reduce((best, r) => (!best || r.intensity > best.intensity ? r : best), null as typeof results[number] | null)
      const controlRef = sampleRef ? ctxEffectiveBandPositions.get(sampleRef.matchedBand!) : undefined
      // Read the témoin's OWN intensity at a calibrated position from its
      // BASELINE-CORRECTED profile, not the raw one ctxEffectiveBandPositions
      // stores — sampleRatio's numerator (localMax) is already corrected, so
      // comparing it against a RAW témoin ratio compares two different scales.
      // Measured on a real case (Aspergillus I12): raw témoin intensities gave
      // controlRatio=1.16 for P22 (vs sample 0.24, delta 0.80 — rejected, just
      // over the 0.7 tolerance) purely because P22's raw baseline sat higher
      // than P30's; corrected, the delta drops back into a coherent range.
      const correctedControlAt = (position: number): number => {
        const idx = Math.max(0, Math.min(correctedControlProfile.length - 1, Math.round(position)))
        return correctedControlProfile[idx] ?? 0
      }
      const controlRefIntensity = controlRef ? correctedControlAt(controlRef.position) : 0
      if (sampleRef && sampleRef.intensity > 0 && controlRef && controlRefIntensity > 0) {
        // Floor as a fraction of the SAMPLE's OWN anchor (P30), NOT of the control
        // band in absolute. A faint positive patient has every band scaled down vs
        // the strong témoin, so an absolute vs-control floor wrongly drops its real
        // shoulders (measured on K7: P31 is 42% of its own P30 — a clear band — yet
        // only 20% of the strong control's P31). 0.05 keeps P31 (0.42) and P33 (0.08)
        // while cutting near-flat flanks (P40/P45 at 0.035/0.011). Tunable.
        const ANCHORED_MIN_SAMPLE_RATIO = 0.05
        // |sampleRatio-controlRatio|/controlRatio. 0.80 (was 0.70, was 0.60) so a
        // patient band that is proportionally weaker than the strong control still
        // passes — K20's P33 sat at delta 0.63 (sample 0.07 vs control 0.19 of
        // P30). Raised again after generalising this pass beyond Toxo (see above):
        // on a real case (Aspergillus I12), P22/P16 sat at delta 0.73/0.75 — real,
        // visible-on-the-raw-profile bands that 0.70 was dropping. Clinical
        // sensitivity knob — validate with Anne.
        const ANCHORED_RATIO_TOLERANCE = 0.8
        referenceBandPositions.forEach((sampleTargetIdx, bandName) => {
          if (matchedNames.has(bandName)) return
          const controlBand = ctxEffectiveBandPositions.get(bandName)
          if (!controlBand || controlBand.intensity <= 0) return
          const target = Math.round(sampleTargetIdx)
          if (target < 3 || target >= correctedSample.length - 3) return
          // Crest of the corrected profile around the predicted position — a
          // shoulder still has a local crest even if it isn't a global local max.
          // ±18 (was ±6, then ±12): measured on real cases (Aspergillus I24,
          // then Toxo II's P30/P31 doublet — CHRU Nancy pilot), a genuine
          // shoulder can sit this far from its projected target when its
          // immediate neighbour band's own peak is even closer to the target
          // than the shoulder itself, forcing the search past that neighbour
          // to find it. Still gated by the two checks below (a real crest,
          // not already claimed) so widening doesn't let a pure slope point
          // through.
          const CREST_SEARCH_RADIUS = 18
          // Scan OUTWARD from the target, distance 0/±1/±2/…, and take the
          // FIRST genuine local max that ISN'T within MIN_BAND_SEPARATION of
          // an already-claimed position — not the tallest point anywhere in
          // the window (a wide window on a strip with a much taller
          // neighbour band nearby always has "a tallest point", its rising
          // slope, even where there's no separate crest of its own), and not
          // just the FIRST local max regardless of whose it is (that first
          // local max can BE the neighbour's own peak, when the neighbour
          // sits closer to the target than the real shoulder does — measured
          // on a real case, Toxo II K6: P31's target landed 9px from P30's
          // own peak but the real P31 shoulder was 14px away on the other
          // side; stopping at the first local max found P30's peak, then
          // rejected it as a collision instead of continuing outward to find
          // P31's own). Verified against 19 real Toxo II cases (ground truth
          // = the position the operator placed by hand after pass 2 failed):
          // recovers all of them pass 2 previously rejected as either "no
          // real crest" or "collision", never worse than before on any case,
          // and isolated shoulders (no neighbour within MIN_BAND_SEPARATION
          // of the target at all, e.g. Aspergillus I24) behave identically.
          let localMax = 0
          let localIdx = target
          outer: for (let d = 0; d <= CREST_SEARCH_RADIUS; d++) {
            for (const i of d === 0 ? [target] : [target - d, target + d]) {
              if (i < 1 || i > correctedSample.length - 2) continue
              if (usedPositions.some((p) => Math.abs(i - p) < MIN_BAND_SEPARATION)) continue
              const v = correctedSample[i]
              const isLocalMax = v >= correctedSample[i - 1] && v >= correctedSample[i + 1]
                && (v > correctedSample[i - 1] || v > correctedSample[i + 1])
              if (isLocalMax) { localMax = v; localIdx = i; break outer }
            }
          }
          if (localMax <= 0) return
          // A true local prominence (higher than its OWN immediate
          // neighbours, not just higher than farther-out points still riding
          // a slope) proves this is a separate band. Measured on a real case
          // (Aspergillus I10, one real peak, otherwise flat): the "P18-20"
          // crest sat on a perfectly monotonic decay from that one peak —
          // prominence ≈0, correctly rejected here (previously accepted as a
          // phantom band with prominence hardcoded to 0, which fed
          // isBandSignificant rather than gating Pass 2 itself).
          const recoveredProminence = getPeakProminence(correctedSample, localIdx)
          if (recoveredProminence <= 0) {
            diagLog(bandName, 'reject:anchored-no-real-crest', { target, localIdx, localMax: Math.round(localMax) })
            return
          }
          // Never duplicate a position already assigned to another band.
          if (usedPositions.some((p) => Math.abs(p - localIdx) < MIN_BAND_SEPARATION)) {
            diagLog(bandName, 'reject:anchored-collision', { target, localIdx, usedPositions })
            return
          }
          const sampleRatio = localMax / sampleRef.intensity
          if (sampleRatio < ANCHORED_MIN_SAMPLE_RATIO) {
            diagLog(bandName, 'reject:anchored-intensity-too-low', {
              target, sampleIntensity: Math.round(localMax), sampleP30: Math.round(sampleRef.intensity), ratioVsOwnP30: sampleRatio.toFixed(2),
            })
            return
          }
          const controlRatio = correctedControlAt(controlBand.position) / controlRefIntensity
          const ratioDelta = controlRatio > 0 ? Math.abs(sampleRatio - controlRatio) / controlRatio : Infinity
          if (ratioDelta > ANCHORED_RATIO_TOLERANCE) {
            diagLog(bandName, 'reject:anchored-ratio-incoherent', {
              target, sampleRatio: sampleRatio.toFixed(2), controlRatio: controlRatio.toFixed(2), delta: ratioDelta.toFixed(2), tol: ANCHORED_RATIO_TOLERANCE,
            })
            return
          }
          const refBand = config.bands.specific.find((b: any) => b.name === bandName)
          // Store the MEASURED prominence (not a hardcoded 0) so this band can
          // clear isBandSignificant's normal ratio check like any Pass-1 band,
          // rather than depending entirely on the (2+ band, ≥2-comparison)
          // similarity fallback to ever count as significant — measured on a
          // real case (Aspergillus I12): P18-20/P30 alone already satisfied
          // minimum_bands, so the verdict never reached the "weak signal" path
          // that would have surfaced P22/P16 differently; they just silently
          // weren't in the list positiveResult renders.
          results.push({
            peakIndex: localIdx,
            position: localIdx,
            intensity: localMax,
            prominence: recoveredProminence,
            estimatedMW: refBand?.molecularWeight ?? 0,
            matchedBand: bandName,
            bandMW: refBand?.molecularWeight ?? null,
          })
          matchedNames.add(bandName)
          usedPositions.push(localIdx)
          diagLog(bandName, 'ACCEPT:control-anchored', {
            target, position: localIdx, sampleIntensity: Math.round(localMax),
            sampleRatio: sampleRatio.toFixed(2), controlRatio: controlRatio.toFixed(2),
          })
        })
      }
    }

    // ── Pass 3: anchor-free single-band recovery ──────────────────────────────
    // Pass 2 needs an ALREADY-CONFIRMED band on this sample to self-reference
    // against — on a sample where Pass 1 found NOTHING at all, it can't run,
    // even if a real band sits almost exactly on its projected target. Measured
    // on a real case (Aspergillus I4): P30's target landed 3px from a genuine
    // local crest (prominence 129, ~11% of the profile's own max — real, but
    // below the backend's own 20% peak-detection floor, so it never became a
    // detected peak at all) — yet with zero other confirmed bands, Pass 2 had
    // no reference to run against.
    // This is inherently riskier than Pass 2: with no corroborating band on the
    // SAME sample, nothing else on the strip confirms the sample's own signal
    // scale, so a direct sample-vs-témoin intensity ratio is the only check
    // available (no self-referencing ratio-of-ratios coherence check). Guarded
    // tightly to compensate: only when NOTHING else matched at all, and only
    // for a real crest landing almost exactly (≤8px) on its projected target —
    // a coincidental noise bump landing that precisely is unlikely.
    if (results.length === 0 && !useRankMatching && correctedControlProfile) {
      const ANCHOR_FREE_MAX_DIST = 8
      // Lower than Pass 1/2's floors (0.20/0.05-ish) because the position match
      // required here (≤8px) is already the primary evidence, not the
      // intensity — measured on real cases: Aspergillus I4's genuine P30 crest
      // sat at 0.14 of the témoin's own corrected intensity; Toxo K12's own
      // genuine P30 (clean, symmetric, prominence 166 — nowhere near the
      // ANCHOR_FREE_MIN_PROMINENCE floor below, which is what actually catches
      // noise, see K7/K9) sat at 0.090, just under the previous 0.10 floor.
      // 0.08 keeps both while still cutting near-zero background (a
      // coincidental bump at <8% of the témoin, landing within 8px of the
      // target purely by chance, stays implausible).
      const ANCHOR_FREE_MIN_INTENSITY_RATIO = 0.08
      // The `prominence <= 0` check below only proves the crest isn't PURELY a
      // slope point — it says nothing about SIZE, and a near-flat region can
      // have a tiny positive wobble (measurement noise) that's still ≤0 in
      // theory but rounds to a few units in practice. Measured on real cases
      // (Toxo K7/K9): a "P33" crest at prominence 13.6/20.0 passed this pass
      // with no other signal on the whole strip to corroborate it — visibly
      // just noise on the raw-profile chart, nowhere near the real P30 case
      // that motivated this pass (Aspergillus I4, prominence 129). Matches
      // the tight-snap relaxation's own floor (half of Pass 1's normal 150) —
      // same principle: a strong position match still needs a REAL crest,
      // not just "not literally flat".
      const ANCHOR_FREE_MIN_PROMINENCE = 75
      const ANCHOR_FREE_MIN_SEPARATION = 10
      const anchorFreeUsedPositions: number[] = []
      const correctedControlAt2 = (position: number): number => {
        const idx = Math.max(0, Math.min(correctedControlProfile.length - 1, Math.round(position)))
        return correctedControlProfile[idx] ?? 0
      }
      referenceBandPositions.forEach((sampleTargetIdx, bandName) => {
        const controlBand = ctxEffectiveBandPositions.get(bandName)
        if (!controlBand || controlBand.intensity <= 0) return
        const target = Math.round(sampleTargetIdx)
        if (target < 3 || target >= correctedSample.length - 3) return
        let localMax = 0
        let localIdx = target
        for (let i = Math.max(1, target - ANCHOR_FREE_MAX_DIST); i <= Math.min(correctedSample.length - 2, target + ANCHOR_FREE_MAX_DIST); i++) {
          if (correctedSample[i] > localMax) { localMax = correctedSample[i]; localIdx = i }
        }
        if (localMax <= 0) return
        const prominence = getPeakProminence(correctedSample, localIdx)
        if (prominence < ANCHOR_FREE_MIN_PROMINENCE) {
          diagLog(bandName, 'reject:anchor-free-no-real-crest', { target, localIdx, prominence: Math.round(prominence), minRequired: ANCHOR_FREE_MIN_PROMINENCE })
          return
        }
        // Never duplicate a position already assigned to another band THIS pass.
        if (anchorFreeUsedPositions.some((p) => Math.abs(p - localIdx) < ANCHOR_FREE_MIN_SEPARATION)) {
          diagLog(bandName, 'reject:anchor-free-collision', { target, localIdx, anchorFreeUsedPositions })
          return
        }
        const controlIntensity = correctedControlAt2(controlBand.position)
        if (controlIntensity <= 0) return
        const intensityRatio = localMax / controlIntensity
        if (intensityRatio < ANCHOR_FREE_MIN_INTENSITY_RATIO) {
          diagLog(bandName, 'reject:anchor-free-intensity-too-low', {
            target, localIdx, sampleIntensity: Math.round(localMax), controlIntensity: Math.round(controlIntensity), ratio: intensityRatio.toFixed(2),
          })
          return
        }
        const refBand = config.bands.specific.find((b: any) => b.name === bandName)
        results.push({
          peakIndex: localIdx,
          position: localIdx,
          intensity: localMax,
          prominence,
          estimatedMW: refBand?.molecularWeight ?? 0,
          matchedBand: bandName,
          bandMW: refBand?.molecularWeight ?? null,
        })
        anchorFreeUsedPositions.push(localIdx)
        diagLog(bandName, 'ACCEPT:anchor-free', {
          target, localIdx, sampleIntensity: Math.round(localMax), ratio: intensityRatio.toFixed(2),
        })
      })
    }

    results.sort((a, b) => a.position - b.position)
    return results
  }

  // Interpret blot result based on immunoblot config
  const interpretBlotCore = (
    blot: BlotDetection,
    ctx?: {
      config?: ImmunoblotConfig | null
      activePositiveControl?: BlotDetection
      effectiveBandPositions?: Map<string, { position: number; intensity: number }>
      BAND_NAMES?: string[]
      REQUIRED_BAND_NAMES?: string[]
    },
  ): {
    result: string
    conclusion: string
    color: string
    identifiedBands?: Array<{
      peakIndex: number
      position: number
      intensity?: number
      estimatedMW: number
      matchedBand: string | null
      bandMW: number | null
    }>
    hasP30?: boolean
    requiredBandId?: string | null
    hasRequiredBand?: boolean
  } => {
    // Per-pathology override — see identifyBands' own ctx and
    // pathologyContexts. Every existing caller omits ctx, so the modal /
    // comparison view / filtered grid are byte-for-byte unchanged; only the
    // "Toutes" gallery grid passes one, per blot, so each card is
    // interpreted against ITS OWN pathology instead of the single
    // filter-scoped closure values below.
    const ctxConfig = ctx && 'config' in ctx ? ctx.config : immunoblotConfig
    const ctxActivePositiveControl = ctx && 'activePositiveControl' in ctx ? ctx.activePositiveControl : activePositiveControl
    const ctxEffectiveBandPositions = ctx?.effectiveBandPositions ?? effectiveBandPositions
    const ctxBandNames = ctx?.BAND_NAMES ?? BAND_NAMES
    const ctxRequiredBandNames = ctx?.REQUIRED_BAND_NAMES ?? REQUIRED_BAND_NAMES

    // ── Paired-comparison pathologies (CIP-WB) never have a témoin at all —
    // branch out before the témoin gates below, which would otherwise block
    // forever on "Sélectionner un témoin positif" for a pathology that has
    // none. See pairedComparisonPartners/comparePairedStrips.
    // ── PROTIS (ladder_profile) : PAS DE VERDICT. Un gel de recherche se
    // lit en profil — bandes posées, masses estimées — jamais en
    // POSITIF/NÉGATIF. Sa notice ne déclare aucun critère, et la suite de
    // cette fonction suppose interpretation.positive.criteria : au premier
    // affichage d'une notice ladder_profile, plantage à chaque rendu.
    if ((ctxConfig as any)?.interpretation?.mode === 'ladder_profile') {
      return {
        result: 'Profil',
        conclusion: 'Lecture en profil : bandes et masses estimées, sans verdict.',
        color: 'gray',
        identifiedBands: [],
      }
    }

    if ((ctxConfig as any)?.interpretation?.mode === 'paired_comparison') {
      const pairInfo = pairedComparisonPartners.get(blot.id)
      if (!pairInfo) {
        return {
          result: 'À vérifier',
          conclusion: 'Bandelette non appariée — cette pathologie fonctionne par couples de 2 bandelettes (référence + comparée) ; vérifier le nombre de bandelettes détectées.',
          color: 'orange',
          identifiedBands: [],
        }
      }
      const referenceBlot = pairInfo.role === 'reference' ? blot : pairInfo.partner
      const comparedBlot = pairInfo.role === 'compared' ? blot : pairInfo.partner
      const { differences, isUniformReinforcement } = comparePairedStrips(referenceBlot, comparedBlot)
      // No named-band catalogue exists for this test (the notice compares
      // profiles, not fixed reference bands), but the config still declares
      // the usable molecular-weight window (bands.reference.min/maxKDa —
      // e.g. 15-120 for CIP-WB). Two-point log-linear estimate, same method
      // as computeKda elsewhere: the ID-zone boundary (idZoneRawEnd — see
      // above, and comparePairedStrips' own exclusion) anchors maxKDa (it's
      // where the "usable <120kDa" zone starts), the strip's far end anchors
      // minKDa. `d.position` is always in the COMPARED strip's own raw
      // coordinates (see comparePairedStrips — both 'extra' and 'reinforced'
      // differences are pushed with `cmpAligned.offset`). Approximate, not a
      // band-matched calibration — labelled "~" throughout for that reason.
      const kdaRef = (ctxConfig as any)?.bands?.reference
      const maxKDa = kdaRef?.maxKDa ?? 120
      const minKDa = kdaRef?.minKDa ?? 15
      const posLo = idZoneRawEnd(comparedBlot)
      const posHi = Math.max(posLo + 1, comparedBlot.profile.length)
      const estimateKda = (rawPos: number): number => {
        const clamped = Math.min(Math.max(rawPos, posLo), posHi)
        const t = (clamped - posLo) / (posHi - posLo)
        const slope = Math.log(minKDa / maxKDa)
        return Math.round(maxKDa * Math.exp(slope * t) * 10) / 10
      }
      const identifiedBandsOut = differences.map(d => ({
        peakIndex: d.position,
        position: d.position,
        intensity: d.prominence,
        estimatedMW: estimateKda(d.position),
        matchedBand: null,
        bandMW: null,
      }))
      const pairInterpretation = (ctxConfig as any).interpretation
      if (differences.length > 0) {
        const describe = (d: typeof differences[number]) => {
          const kda = estimateKda(d.position)
          const kind = d.kind === 'extra' ? 'bande supplémentaire' : `renforcement isolé (×${d.ratio?.toFixed(1)})`
          return `${kind} vers ~${kda} kDa`
        }
        const kinds = differences.map(describe).join(' ; ')
        return {
          result: pairInterpretation.positive.result,
          conclusion: `${pairInterpretation.positive.conclusion} (${differences.length} différence(s) : ${kinds})`,
          color: 'green',
          identifiedBands: identifiedBandsOut,
        }
      }
      return {
        result: pairInterpretation.negative.result,
        conclusion: isUniformReinforcement
          ? `${pairInterpretation.negative.conclusion} (renforcement uniforme de toutes les bandes détecté — probable hémoconcentration, non retenu comme critère de positivité)`
          : pairInterpretation.negative.conclusion,
        color: 'red',
        identifiedBands: identifiedBandsOut,
      }
    }

    // Was `positiveControlId !== null` — only ever checked slot 1's raw
    // existence, regardless of whether that's even the slot in play
    // (selectedControlForBlot) or, for Bordeaux/Dynablot multitest, whether
    // it belongs to the CURRENT pathology at all. That let a Toxocara strip
    // sail past this gate on the strength of a Schisto témoin sitting in
    // slot 1, then fall through to "négatif" below for lack of any real
    // témoin to match against — looking like a verdict when it was really
    // "no témoin yet". ctxActivePositiveControl already resolves the correct
    // slot AND the pathology match, so basing the gate on it fixes both.
    const hasPositiveControl = ctxActivePositiveControl != null

    // For tests with required bands (Toxo): block until the required band is calibrated.
    // For tests with no required bands (Echino, Schisto, Aspergillus): a positive control
    // is enough — auto-calibration from the control profiles will be used.
    // The required bands must be PRESENT in the working reference — which is the
    // operator's manual calibration when they calibrated, or the auto-suggestion
    // otherwise (option A). ctxEffectiveBandPositions already resolves that fallback,
    // so checking it (instead of savedBandCalibration directly) lets a good
    // suggestion satisfy the gate without forcing manual placement.
    const hasRequiredCalibration = ctxRequiredBandNames.length === 0
      || ctxRequiredBandNames.every(b => ctxEffectiveBandPositions.has(b as BandName))

    if (!hasPositiveControl || !hasRequiredCalibration) {
      return {
        result: t('blotAnalysis.interpretation.pending', 'En attente'),
        conclusion: !hasPositiveControl
          ? t('blotAnalysis.interpretation.pendingControl', 'Sélectionner un témoin positif')
          : t('blotAnalysis.interpretation.calibrateRequired', { names: ctxRequiredBandNames.join(', ') }),
        color: 'gray',
        identifiedBands: [],
      }
    }

    if (!ctxConfig) {
      const status = getBlotStatus(blot)
      return {
        result: status === 'positive' ? t('blotAnalysis.interpretation.positive') : t('blotAnalysis.interpretation.negative'),
        conclusion: status === 'positive' ? t('blotAnalysis.interpretation.positiveBlotDetected') : t('blotAnalysis.interpretation.negativeBlot'),
        color: status === 'positive' ? 'green' : 'red'
      }
    }

    const { interpretation } = ctxConfig
    // Ceinture générique : une notice sans critères de positivité (un mode
    // futur, une notice en cours d'écriture) ne doit jamais faire planter le
    // rendu — elle ne rend simplement pas de verdict.
    if (!(interpretation as any)?.positive?.criteria) {
      return {
        result: '—',
        conclusion: 'Notice sans critères de lecture par bandes : pas de verdict.',
        color: 'gray',
        identifiedBands: [],
      }
    }
    // Bands the biologist explicitly deleted on this strip are removed from the
    // detected set here, at the single source every surface reads (verdict, card,
    // modal markers, report). Deleting a band therefore un-counts it everywhere.
    const suppressedForBlot = suppressedBands.get(blot.id)
    const identifiedBands = identifyBands(blot, ctxConfig, ctx)
      .filter(band => !(band.matchedBand != null && suppressedForBlot?.has(band.matchedBand)))
    const identifiedSpecificBands = identifiedBands.filter(band => band.matchedBand !== null)

    // ── Per-blot similarity via peak prominence ratios ───────────────────────────
    // For each pair of calibrated bands, compare control_prominence/other_prominence
    // against sample_prominence/other_prominence. Prominence-based ratios are
    // invariant to absolute titer level — only the relative peak pattern matters.
    const computeLocalSimilarity = (): number | null => {
      if (ctxEffectiveBandPositions.size < 2) return null

      const isControlBlot = ctxActivePositiveControl && blot.id === ctxActivePositiveControl.id
      const blotDtwProfile = !isControlBlot ? (dtwProfileCache.get(blot.id) || null) : null
      const controlOffset = ctxActivePositiveControl
        ? getControlLine(ctxActivePositiveControl) : 0
      const sampleOffset = getControlLine(blot)

      // Use baseline-corrected profiles for prominence comparisons
      const correctedSample = blotDtwProfile
        ? subtractBaseline(blotDtwProfile)
        : (baselineCorrectedCache.get(blot.id) ?? subtractBaseline(blot.profile))
      const correctedCtrl = ctxActivePositiveControl
        ? (baselineCorrectedCache.get(ctxActivePositiveControl.id) ?? subtractBaseline(ctxActivePositiveControl.profile))
        : correctedSample

      // Reference band = most prominent in corrected control
      let refBandName = ''
      let maxCtrlProminence = 0
      ctxEffectiveBandPositions.forEach((d, name) => {
        const prom = getPeakProminence(correctedCtrl, Math.round(d.position))
        if (prom > maxCtrlProminence) { maxCtrlProminence = prom; refBandName = name }
      })
      if (!refBandName) return null

      const refCtrl = ctxEffectiveBandPositions.get(refBandName)!
      const refTargetIdx = blotDtwProfile
        ? Math.round(refCtrl.position - controlOffset)
        : Math.round(refCtrl.position - controlOffset + sampleOffset)
      const refSamplePeak = findBestPeakNear(correctedSample, refTargetIdx, 10)
      if (!refSamplePeak || refSamplePeak.prominence < 20) return null

      let totalDiff = 0, comparisons = 0
      const refCtrlProminence = getPeakProminence(correctedCtrl, Math.round(refCtrl.position))

      ctxEffectiveBandPositions.forEach((data, bandName) => {
        if (bandName === refBandName) return
        const ctrlProminence = getPeakProminence(correctedCtrl, Math.round(data.position))
        if (ctrlProminence <= 0 || refCtrlProminence <= 0) return
        const targetIdx = blotDtwProfile
          ? Math.round(data.position - controlOffset)
          : Math.round(data.position - controlOffset + sampleOffset)
        const samplePeak = findBestPeakNear(correctedSample, targetIdx, 10)
        if (!samplePeak || samplePeak.prominence < 20) return
        const controlRatio = refCtrlProminence / ctrlProminence
        const sampleRatio = refSamplePeak.prominence / samplePeak.prominence
        if (controlRatio > 0) {
          totalDiff += Math.abs(controlRatio - sampleRatio) / controlRatio * 100
          comparisons++
        }
      })
      // Similarity is only statistically meaningful with at least 2 ratio comparisons.
      // With a single comparison (2 calibrated bands → 1 ratio), the score is too
      // easily inflated — two background readings can happen to be proportional.
      if (comparisons < 2) return null
      return Math.max(0, 100 - totalDiff / comparisons)
    }

    const localSimilarity = computeLocalSimilarity()

    // ── Band significance check ───────────────────────────────────────────────────
    // identifyBands already filters by MIN_PROMINENCE_RATIO (10%). Here we apply a
    // stricter threshold using peak prominence, with a fallback to similarity for
    // low-titer true positives whose peaks are faint but proportionally correct.
    const MIN_PROMINENCE_RATIO_SIGNIFICANT = 0.20
    const SIMILARITY_THRESHOLD = 60
    const correctedCtrlForSig = ctxActivePositiveControl
      ? (baselineCorrectedCache.get(ctxActivePositiveControl.id) ?? subtractBaseline(ctxActivePositiveControl.profile))
      : null
    const isBandSignificant = (bandName: string, sampleProminence: number): boolean => {
      const controlData = ctxEffectiveBandPositions.get(bandName)
      if (!controlData || !correctedCtrlForSig) return true
      const ctrlProminence = getPeakProminence(correctedCtrlForSig, Math.round(controlData.position))
      if (ctrlProminence <= 0) return true
      if (sampleProminence / ctrlProminence >= MIN_PROMINENCE_RATIO_SIGNIFICANT) return true
      return localSimilarity !== null && localSimilarity >= SIMILARITY_THRESHOLD
    }
    // Once the biologist ENDORSES the strip by hand-placing at least one band, the
    // strip is reviewed: every band already DETECTED/displayed on it counts as
    // confirmed too — no need to re-place a band that's already shown on the card.
    // With NO manual placement, the strict "significant vs control" rule stands, so
    // fully-automatic verdicts are unchanged. Suppressed (deleted) bands never count.
    const hasHandPlacedBand = (blotPlacedBands.get(blot.id)?.size ?? 0) > 0
    const significantSpecificBands = hasHandPlacedBand
      ? identifiedSpecificBands.filter(b => !suppressedForBlot?.has(b.matchedBand!))
      : identifiedSpecificBands.filter(b => isBandSignificant(b.matchedBand!, b.prominence ?? 0))
    const significantBandNames = Array.from(new Set(significantSpecificBands.map(b => b.matchedBand!))).sort()
    const allDetectedBandNames = Array.from(new Set(identifiedSpecificBands.map(b => b.matchedBand!))).sort()

    // Hand-placed bands the detector missed entirely (not in the detected set above)
    // still count as confirmed.
    const placedBandNames = Array.from(blotPlacedBands.get(blot.id)?.keys() ?? [])
      .filter((n) => ctxBandNames.includes(n) && !significantBandNames.includes(n) && !suppressedForBlot?.has(n))
    const confirmedBandNames = [...significantBandNames, ...placedBandNames].sort()
    const confirmedCount = confirmedBandNames.length

    // ── Borderline-snap check — shared across every criterion below ─────────────
    // A matched band's target is projected from the témoin by an offset shift (see
    // identifyBands); the peak it snaps to can sit anywhere inside the tolerance
    // window, right up to the ceiling. A match riding that edge isn't the confident
    // hit a verdict resting on a SINGLE band implies — same badge/logic for every
    // test (Echino's extreme_bands, Schisto's major_bands, Toxo/Aspergillus's
    // minimum_bands+required_band), not just the one it was first noticed on.
    // Measured on a real case (G11, Schisto): the only qualifying match sat at
    // 27px of a 27px tolerance (100% of the ceiling) — accepted as a clean POSITIF
    // with nothing to tell it apart from a 2px near-exact match.
    const BORDERLINE_SNAP_RATIO = 0.6
    const isBorderlineSnap = (b: typeof identifiedSpecificBands[number]): boolean =>
      b.snapDistance != null && b.snapTolerance != null && b.snapTolerance > 0
        && (b.snapDistance / b.snapTolerance) > BORDERLINE_SNAP_RATIO
    // Hand-placed bands are never borderline — the biologist pinned their exact
    // position, there's no projected-target distance to question.
    const solidSpecificBands = significantSpecificBands.filter(b => !isBorderlineSnap(b))
    const solidBandNames = Array.from(new Set(solidSpecificBands.map(b => b.matchedBand!))).sort()
    const solidConfirmedBandNames = [...solidBandNames, ...placedBandNames].sort()
    const solidConfirmedCount = solidConfirmedBandNames.length
    const borderlineSpecificBands = significantSpecificBands.filter(isBorderlineSnap)
    const borderlineSnapPercent = (bands: typeof borderlineSpecificBands): number =>
      bands.length > 0 ? Math.round((bands[0].snapDistance! / bands[0].snapTolerance!) * 100) : 0

    // ── Tight-snap-but-weak check — for extreme_bands/major_bands (Echino,
    // Schisto, Leishmania) only. Their notices state a pure PRESENCE rule
    // ("présence de la bande = positif", no intensity threshold), but a band
    // can fail isBandSignificant's ratio check purely on weak signal — and
    // with only 2 reference bands, the similarity fallback that would rescue
    // a genuine low-titer positive on a richer test (Toxo, Aspergillus) can
    // never engage (needs ≥ 2 ratio comparisons, i.e. ≥ 3 reference bands).
    // A near-exact positional match (same TIGHT_SNAP_FRAC identifyBands used
    // to accept it past the absolute-prominence floor in the first place) is
    // trusted as genuine presence per the notice, flagged "à vérifier" like
    // every other borderline path — never a silent POSITIF.
    const isTightSnapBand = (b: typeof identifiedSpecificBands[number]): boolean =>
      b.snapDistance != null && b.snapTolerance != null && b.snapTolerance > 0
        && (b.snapDistance / b.snapTolerance) <= TIGHT_SNAP_FRAC
    const tightSnapPercent = (bands: typeof identifiedSpecificBands): number =>
      bands.length > 0 && bands[0].snapTolerance ? Math.round((bands[0].snapDistance! / bands[0].snapTolerance!) * 100) : 0

    // Helpers — band lists per result type:
    // POSITIF → significantSpecificBands only (confirmed)
    // borderline → identifiedSpecificBands (show what was seen, even weak, for info)
    // NÉGATIF → [] (nothing clinically relevant to show)
    const positiveResult = (bandNames: string[]) => ({
      result: interpretation.positive.result,
      conclusion: `${interpretation.positive.conclusion} (${t('blotAnalysis.interpretation.uniqueBandsIdentified', { count: bandNames.length, bands: bandNames.join(', ') })})`,
      color: 'green' as const,
      // Show EVERY detected band (significant or not), same as negativeResult/
      // equivocalResult already do — a POSITIF verdict reached via one solid
      // band must not hide another real, weaker one the AI also found right
      // next to it (measured on a real case, Leishmania C2: verdict reaches
      // POSITIF on P14 alone, but P16 — genuinely detected via Pass 2, just
      // below the 20% significance floor with no similarity fallback possible
      // on a 2-band test — vanished from the card AND the modal). bandNames
      // (used in the conclusion sentence above) still reflects only the
      // CONFIRMED/significant bands — this only widens what's rendered as
      // markers/chips, not what the verdict counts as diagnostic.
      identifiedBands: identifiedSpecificBands,
      hasP30: ctxRequiredBandNames.length === 0 || significantSpecificBands.some(b => b.matchedBand === ctxRequiredBandNames[ctxRequiredBandNames.length - 1]),
      requiredBandId: ctxRequiredBandNames[ctxRequiredBandNames.length - 1] ?? null,
      hasRequiredBand: true,
      needsReview: false,
    })
    const negativeResult = (detail?: string) => ({
      result: interpretation.negative.result,
      conclusion: detail ? `${interpretation.negative.conclusion} (${detail})` : interpretation.negative.conclusion,
      color: 'red' as const,
      // Carry the bands actually detected on the strip (even weak / insufficient) so
      // they stay visible in the modal, the gallery card AND the report — a NÉGATIF
      // verdict must not silently hide what was seen. The verdict is unchanged; these
      // bands are informational. When nothing was detected this is [] → no change.
      identifiedBands: identifiedSpecificBands,
      hasP30: false,
      requiredBandId: ctxRequiredBandNames[ctxRequiredBandNames.length - 1] ?? null,
      hasRequiredBand: false,
      needsReview: false,
    })
    // The LDBIO Western Blot verdict is BINARY (POSITIF / NÉGATIF). The
    // "équivoque" of the notice refers to the screening test (ELISA/Dye Test)
    // the WB confirms, NOT the WB's own result. So a strip that does not meet
    // the positive criteria (too few bands, required band absent, signal too
    // weak) is NÉGATIF — never "équivoque". We still list the weak bands seen,
    // for information, in the conclusion detail. These borderline strips also
    // carry needsReview=true so the UI can flag them "à revoir" (a hint for the
    // biologist to check manually) WITHOUT reintroducing an équivoque verdict.
    const equivocalResult = (reason: string) => ({
      result: interpretation.negative.result,
      conclusion: `${interpretation.negative.conclusion} (${reason})`,
      color: 'red' as string,
      identifiedBands: identifiedSpecificBands,
      hasP30: false,
      requiredBandId: ctxRequiredBandNames[ctxRequiredBandNames.length - 1] ?? null,
      hasRequiredBand: false,
      needsReview: true,
    })


    // ── Criterion: extreme_bands (Echinococcus) ──────────────────────────────────
    if (interpretation.positive.criteria.some((c: any) => c.type === 'extreme_bands')) {
      if (solidSpecificBands.length >= 1) return positiveResult(significantBandNames)
      if (borderlineSpecificBands.length >= 1) {
        const simStr = localSimilarity !== null ? `, similarité ${Math.round(localSimilarity)}%` : ''
        return {
          ...equivocalResult(`à vérifier`),
          result: interpretation.positive.result,
          conclusion: `${interpretation.positive.conclusion} (à vérifier — ${borderlineSpecificBands.map(b => b.matchedBand).join(', ')} détectée(s) mais en limite de tolérance de correspondance (~${borderlineSnapPercent(borderlineSpecificBands)}%${simStr})`,
          color: 'green' as string,
          hasP30: true,
          hasRequiredBand: true,
        }
      }
      const tightSnapSpecificBands = identifiedSpecificBands.filter(
        b => !significantSpecificBands.includes(b) && isTightSnapBand(b)
      )
      if (tightSnapSpecificBands.length >= 1) {
        const simStr = localSimilarity !== null ? `, similarité ${Math.round(localSimilarity)}%` : ''
        return {
          ...equivocalResult(`à vérifier`),
          result: interpretation.positive.result,
          conclusion: `${interpretation.positive.conclusion} (à vérifier — ${tightSnapSpecificBands.map(b => b.matchedBand).join(', ')} détectée(s), position quasi parfaite mais signal faible vs témoin (~${tightSnapPercent(tightSnapSpecificBands)}% de la tolérance)${simStr})`,
          color: 'green' as string,
          hasP30: true,
          hasRequiredBand: true,
        }
      }
      if (identifiedSpecificBands.length >= 1) {
        const simStr = localSimilarity !== null ? `, similarité ${Math.round(localSimilarity)}%` : ''
        return equivocalResult(`signal trop faible vs témoin (< ${20}%${simStr})`)
      }
      return negativeResult()
    }

    // ── Criterion: major_bands (Schistosoma) ─────────────────────────────────────
    // POSITIF = P30-34 OR P22-24 present AND with sufficient intensity vs control.
    if (interpretation.positive.criteria.some((c: any) => c.type === 'major_bands')) {
      const sortedByMW = [...ctxConfig.bands.specific].sort((a: any, b: any) => b.molecularWeight - a.molecularWeight)
      const majorBandNames = sortedByMW.slice(0, 2).map((b: any) => b.name)

      const detectedMajor = identifiedSpecificBands.filter(b => majorBandNames.includes(b.matchedBand ?? ''))
      // isBandSignificant expects a PROMINENCE (it divides by the control's own
      // prominence — see its doc above), not an intensity. Passing b.intensity
      // here compared two different quantities: intensity is always far bigger
      // than prominence for the same peak, so the ratio cleared the 20% floor
      // almost regardless of real strength — measured on a real case (Leishmania
      // C9): P16's prominence-ratio vs témoin was 0.18 (correctly below floor),
      // but its intensity-ratio was 1.09, so the bug called it "significant" and
      // drove a clean POSITIF, while `significantSpecificBands` elsewhere in this
      // function (used for the actual band list/markers) correctly excluded it
      // via the SAME check done right — the two disagreed, so the verdict said
      // POSITIF while nothing was drawn to justify it.
      const significantMajor = detectedMajor.filter(b => isBandSignificant(b.matchedBand!, b.prominence ?? 0))
      const solidMajor = significantMajor.filter(b => !isBorderlineSnap(b))
      const borderlineMajor = significantMajor.filter(isBorderlineSnap)

      if (solidMajor.length >= 1) return positiveResult(significantBandNames)
      if (borderlineMajor.length >= 1) {
        const simStr = localSimilarity !== null ? `, similarité ${Math.round(localSimilarity)}%` : ''
        return {
          ...equivocalResult(`à vérifier`),
          result: interpretation.positive.result,
          conclusion: `${interpretation.positive.conclusion} (à vérifier — ${borderlineMajor.map(b => b.matchedBand).join(', ')} détectée(s) mais en limite de tolérance de correspondance (~${borderlineSnapPercent(borderlineMajor)}%${simStr})`,
          color: 'green' as string,
          hasP30: true,
          hasRequiredBand: true,
        }
      }
      const tightSnapMajor = detectedMajor.filter(b => !significantMajor.includes(b) && isTightSnapBand(b))
      if (tightSnapMajor.length >= 1) {
        const simStr = localSimilarity !== null ? `, similarité ${Math.round(localSimilarity)}%` : ''
        return {
          ...equivocalResult(`à vérifier`),
          result: interpretation.positive.result,
          conclusion: `${interpretation.positive.conclusion} (à vérifier — ${tightSnapMajor.map(b => b.matchedBand).join(', ')} détectée(s), position quasi parfaite mais signal faible vs témoin (~${tightSnapPercent(tightSnapMajor)}% de la tolérance)${simStr})`,
          color: 'green' as string,
          hasP30: true,
          hasRequiredBand: true,
        }
      }
      if (detectedMajor.length >= 1) {
        const simStr = localSimilarity !== null ? `, similarité ${Math.round(localSimilarity)}%` : ''
        return equivocalResult(
          `${detectedMajor.map(b => b.matchedBand).join(', ')} détectée(s) mais signal trop faible vs témoin (< ${20}%${simStr})`
        )
      }
      if (identifiedSpecificBands.length > 0) {
        return equivocalResult(`bandes mineures seulement (${allDetectedBandNames.join(', ')}) — ${majorBandNames.join(' ou ')} absentes`)
      }
      return negativeResult()
    }

    // ── Criterion: minimum_bands (Aspergillus) ──────────────────────────────────
    // ── Criterion: minimum_bands + required_band (Toxo IgG / Toxo IgM) ──────────
    const requiredBandId = interpretation.positive.criteria
      .find((c: any) => c.type === 'required_band')?.bandId ?? null
    const hasRequiredBand = requiredBandId
      ? confirmedBandNames.includes(requiredBandId)
      : true
    const hasRequiredBandAtAll = requiredBandId
      ? (identifiedBands.some(b => b.matchedBand === requiredBandId) || placedBandNames.includes(requiredBandId))
      : true

    // Once the biologist has actively endorsed the strip by hand-placing at least
    // one band, a required band that is PRESENT BUT WEAK (detected, yet below the
    // vs-control significance ratio) is accepted as satisfied: the manual placement
    // is the clinical confirmation the ratio test was hedging on. With no manual
    // placement the stricter "confirmed (significant or hand-placed)" rule stands,
    // so fully-automatic verdicts are unchanged.
    const biologistEndorsed = hasHandPlacedBand
    const requiredBandSatisfied = hasRequiredBand || (biologistEndorsed && hasRequiredBandAtAll)

    const minBandsCriterion = interpretation.positive.criteria.find((c: any) => c.type === 'minimum_bands')
    const minBands = minBandsCriterion?.value ?? 1

    if (confirmedCount >= minBands && requiredBandSatisfied) {
      // Same solid-vs-borderline split as the other criteria above: if the
      // threshold (or the required band) is only reached by counting a
      // borderline-snap match, this isn't the confident hit a silent POSITIF
      // implies — flag it "à vérifier" instead, same badge as a weak-signal one.
      // Deliberately NOT "à valider": that word is already the biologist's own
      // manual-validation button/badge elsewhere in the UI — reusing it here
      // read as if this were the same action, when it's really "look closer
      // before you validate", a different and more cautionary signal.
      const solidHasRequiredBand = requiredBandId ? solidConfirmedBandNames.includes(requiredBandId) : true
      const solidRequiredBandSatisfied = solidHasRequiredBand || (biologistEndorsed && hasRequiredBandAtAll)
      if (solidConfirmedCount >= minBands && solidRequiredBandSatisfied) {
        return {
          ...positiveResult(confirmedBandNames),
          hasP30: requiredBandSatisfied,
          requiredBandId,
          hasRequiredBand: requiredBandSatisfied,
        }
      }
      const simStr = localSimilarity !== null ? `, similarité ${Math.round(localSimilarity)}%` : ''
      return {
        ...equivocalResult(`à vérifier`),
        result: interpretation.positive.result,
        conclusion: `${interpretation.positive.conclusion} (à vérifier — correspondance en limite de tolérance (~${borderlineSnapPercent(borderlineSpecificBands)}%) : ${confirmedBandNames.join(', ')}${simStr})`,
        color: 'green' as string,
        hasP30: requiredBandSatisfied,
        requiredBandId,
        hasRequiredBand: requiredBandSatisfied,
      }
    }

    // Enough specific bands detected AND the required band is present, but they
    // are faint vs the control → likely a weak POSITIF. Keep a POSITIF verdict
    // but flag needsReview so the biologist double-checks ("POSITIF à vérifier").
    //
    // Two distinct ways this can happen — checked with OR, not just the
    // aggregate count: (a) too few SIGNIFICANT bands overall (the original
    // check), or (b) enough significant bands to clear minBands on their
    // own, but the REQUIRED band itself is the weak one and gets excluded
    // from "confirmed" by isBandSignificant's 20% floor, while OTHER bands
    // (not required) are independently strong enough to reach minBands
    // without it. (a) alone misses (b) entirely: significantSpecificBands
    // can already be >= minBands from those other bands, so the primary
    // POSITIF branch above fails on requiredBandSatisfied alone, and this
    // fallback's old aggregate-count condition never fired either — falling
    // through to équivoque/négatif despite the strip showing minBands solid
    // bands. Measured on two real cases (Toxo II IgM, K14 and K18): P30
    // detected at the right position (tight snap, 2-9px), but its own
    // prominence sat at 9-16% of the témoin's — under the 20% floor — while
    // P40 (K14) or others were independently well above it.
    if (identifiedSpecificBands.length >= minBands && hasRequiredBandAtAll
      && (significantSpecificBands.length < minBands || !requiredBandSatisfied)) {
      const simStr = localSimilarity !== null ? `, similarité ${Math.round(localSimilarity)}%` : ''
      return {
        ...equivocalResult(`à vérifier`),
        result: interpretation.positive.result,
        conclusion: `${interpretation.positive.conclusion} (à vérifier — signal faible vs témoin (< 20%${simStr}) : ${allDetectedBandNames.join(', ')})`,
        color: 'green' as string,
        hasP30: hasRequiredBandAtAll,
        hasRequiredBand: hasRequiredBandAtAll,
      }
    }

    if (identifiedSpecificBands.length === 0) return negativeResult()

    const reason = !hasRequiredBand && requiredBandId
      ? `${requiredBandId} absente`
      : t('blotAnalysis.interpretation.onlyBandsIdentified', { count: significantSpecificBands.length })

    if (!hasRequiredBand && requiredBandId && significantSpecificBands.length <= 1) {
      return negativeResult(`${requiredBandId} absente`)
    }

    return equivocalResult(reason)
  }

  // ── Cotation semi-quantitative (Oliva et al., J Clin Microbiol 2015) ──────
  // La lecture historique du WB Aspergillus au CHU Bordeaux : chaque bande
  // spécifique présente reçoit une note de 1 à 4 face au témoin positif de la
  // plaque (ratio d'intensités corrigées, bins de la config), I = somme des
  // notes, puis I ≤ 2n → faiblement positif, I > 2n → fortement positif.
  // Gatée par le PROFIL DU CAS (interpretation.semiQuantification.enabledProfiles),
  // pas par le spectateur : seuls les cas des sites qui la demandent la portent,
  // et un admin en shadow voit la même chose que le pilote. Les notes ne se
  // calculent que sur les bandes auto-identifiées : une bande posée à la main
  // n'a pas de mesure — la cotation reste une suggestion, jamais un verdict.
  type SemiQuant = {
    label: string
    strength: 'weak' | 'strong'
    n: number
    I: number
    notes: Array<{ band: string; note: number; ratio: number }>
  }

  /**
   * Intensité de chaque bande identifiée, RAPPORTÉE à la même bande sur le
   * témoin de la plaque.
   *
   * C'est le rapport, et non l'intensité brute, qui se compare d'une plaque à
   * l'autre : une valeur absolue dépend du scan, de l'éclairage et du lot de
   * réactifs, alors que le rapport s'en affranchit — les deux mesures ont
   * subi la même chaîne. C'est la grandeur que la cotation d'Oliva note de 1
   * à 4, et c'est celle qu'il faut figer dans l'instantané pour pouvoir, plus
   * tard, stratifier l'accord entre bandes faibles et bandes franches.
   */
  const bandRatiosOf = (
    interp: ReturnType<typeof interpretBlotCore>,
    ctx?: Parameters<typeof interpretBlotCore>[1],
  ): Record<string, number> => {
    const ctrl = ctx && 'activePositiveControl' in ctx ? ctx.activePositiveControl : activePositiveControl
    const effPositions = ctx?.effectiveBandPositions ?? effectiveBandPositions
    if (!ctrl?.profile?.length || !effPositions?.size) return {}
    const ctrlCorrected = baselineCorrectedCache.get(ctrl.id) ?? subtractBaseline(ctrl.profile)
    const out: Record<string, number> = {}
    for (const b of interp.identifiedBands ?? []) {
      if (!b.matchedBand || out[b.matchedBand] !== undefined) continue
      const ctrlPos = effPositions.get(b.matchedBand)?.position
      if (ctrlPos == null || typeof b.intensity !== 'number') continue
      const ci = ctrlCorrected[Math.round(ctrlPos)] ?? 0
      if (ci > 0) out[b.matchedBand] = Number((b.intensity / ci).toFixed(3))
    }
    return out
  }

  const computeSemiQuant = (
    interp: ReturnType<typeof interpretBlotCore>,
    ctx?: Parameters<typeof interpretBlotCore>[1],
  ): SemiQuant | null => {
    const cfg: any = ctx && 'config' in ctx ? ctx.config : immunoblotConfig
    const sq = cfg?.interpretation?.semiQuantification
    // Diagnostic (même interrupteur que BAND-DIAG : localStorage blotDiag='0'
    // pour couper) — dit quelle porte rejette, sur les seules recherches qui
    // portent une cotation.
    const sqDiag = (status: string, extras?: Record<string, unknown>) => {
      if (typeof window === 'undefined' || window.localStorage?.getItem('blotDiag') === '0') return
      if (String(cfg?.pathology ?? '').toUpperCase().includes('ASPERGILLUS')) {
        // eslint-disable-next-line no-console
        console.log('[SEMIQUANT]', status, { detectionProfile, result: interp.result, ...extras })
      }
    }
    // Pas de bloc semiQuantification dans la config = la cotation n'est pas
    // ACTIVÉE sur cet environnement (c'est l'interrupteur du déploiement :
    // tant que la migration n'a pas posé le bloc, le code reste éteint).
    // État normal, donc silencieux — pas un rejet à diagnostiquer.
    if (!sq) return null
    if (!detectionProfile) { sqDiag('reject:detectionProfile-absent'); return null }
    if (!Array.isArray(sq.enabledProfiles) || !sq.enabledProfiles.includes(detectionProfile)) {
      sqDiag('reject:profil-non-active', { enabledProfiles: sq.enabledProfiles }); return null
    }
    // Cotable : verdict POSITIF, ou cas limite (needsReview — IRIS rend alors
    // « NÉGATIF (à vérifier) » avec les bandes vues quand même dans
    // identifiedBands). Oliva cote dès que 2 bandes spécifiques sont
    // présentes : un équivoque IRIS à 2 bandes faibles est précisément son
    // « faiblement positif ». Seul le négatif franc reste sans cotation.
    const positive = String(interp.result).includes('POSITIF')
    const borderline = (interp as any).needsReview === true
    if (!positive && !borderline) { sqDiag('reject:verdict-negatif-franc'); return null }
    const ctrl = ctx && 'activePositiveControl' in ctx ? ctx.activePositiveControl : activePositiveControl
    const effPositions = ctx?.effectiveBandPositions ?? effectiveBandPositions
    if (!ctrl?.profile?.length) { sqDiag('reject:pas-de-temoin'); return null }
    if (!effPositions?.size) { sqDiag('reject:positions-temoin-vides'); return null }
    const wanted = new Set<string>(sq.specificBands ?? [])
    const bins: number[] = Array.isArray(sq.gradeBins) && sq.gradeBins.length === 3
      ? sq.gradeBins
      : [0.4, 0.7, 1.0]
    // Même rapport d'intensités que celui figé dans l'instantané : une seule
    // définition, donc la note affichée et la stratification de l'évaluation
    // ne peuvent pas diverger.
    const notes: SemiQuant['notes'] = Object.entries(bandRatiosOf(interp, ctx))
      .filter(([band]) => wanted.has(band))
      .map(([band, ratio]) => ({ band, ratio, note: 1 + bins.filter((t) => ratio > t).length }))
    const n = notes.length
    if (n < (sq.minBands ?? 2)) {
      sqDiag('reject:moins-de-2-bandes-notees', {
        notes,
        identifiees: (interp.identifiedBands ?? []).map((b) => b.matchedBand),
        positionsTemoin: Array.from(effPositions.keys()),
      })
      return null
    }
    const I = notes.reduce((s, x) => s + x.note, 0)
    const strength: 'weak' | 'strong' = I <= 2 * n ? 'weak' : 'strong'
    const label = strength === 'weak'
      ? (sq.labels?.weak ?? 'Faiblement positif')
      : (sq.labels?.strong ?? 'Fortement positif')
    return { label, strength, n, I, notes }
  }

  // L'enveloppe que tout le monde appelle : le verdict du cœur, plus la
  // cotation quand le cas la porte. Les appelants existants sont inchangés.
  const interpretBlot = (
    blot: BlotDetection,
    ctx?: Parameters<typeof interpretBlotCore>[1],
  ): ReturnType<typeof interpretBlotCore> & { semiQuant?: SemiQuant | null } => {
    const interp = interpretBlotCore(blot, ctx)
    const semiQuant = computeSemiQuant(interp, ctx)
    return semiQuant ? { ...interp, semiQuant } : interp
  }

  // Memoize interpretation result for the selected blot — avoids recalculating on every render
  //
  // Must resolve the SAME per-blot pathology ctx as the "Toutes" gallery card
  // (blotCtx below, mirroring the grid's own resolution) — otherwise opening a
  // card from the unfiltered grid recomputes the verdict against whatever
  // pathology happens to be the filter-scoped closure's current témoin/config
  // instead of the blot's OWN, and the modal can show a different verdict
  // than the card that opened it (seen on K14: POSITIF in the modal, NÉGATIF
  // on the card, same blot).
  /**
   * La bandelette ouverte est-elle encore sous le voile ?
   *
   * Pendant la lecture de paillasse, IRIS n'est servi que pour les témoins.
   * Le profil d'un échantillon arrive vide, et une interprétation faite sur
   * un profil vide rend « NÉGATIF » — pas parce qu'IRIS a conclu, mais parce
   * qu'il n'a rien reçu. L'afficher ferait lire une absence comme un résultat,
   * et pire : ferait entrer ce résultat dans l'œil du biologiste avant qu'il
   * n'ait saisi sa propre lecture, ce que tout le dispositif cherche à éviter.
   */
  // Reconnu au PROFIL VIDE, et non à la liste des témoins : c'est le serveur
  // qui décide ce qu'il voile, et sur une plaque multitest sans filtre de
  // pathologie cette liste ne contient pas les témoins rangés par recherche.
  // Lire la conséquence plutôt que la redériver évite de se tromper là où les
  // deux divergent.
  const selectedBlotVeiled = Boolean(
    benchReadingMode && selectedBlot && !(selectedBlot.profile?.length),
  )

  const selectedBlotInterpretation = useMemo(() => {
    if (!selectedBlotWithEnhancedPeaks) return null
    const blotPathologyLabel = (selectedBlotWithEnhancedPeaks as any).pathology_label as string | null | undefined
    const blotCtx = (!pathologyFilter && blotPathologyLabel)
      ? pathologyContexts.get(canonicalPathologyKey(blotPathologyLabel) ?? '')
      : undefined
    return interpretBlot(selectedBlotWithEnhancedPeaks, blotCtx)
  // pairedComparisonPartners: interpretBlot reads it to decide which strip of a
  // couple carries the differences. Flipping a couple leaves every other
  // dependency identical (the partner object itself does not change), so
  // without it the verdict stayed on the strip it was computed for.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlotWithEnhancedPeaks, immunoblotConfig, effectiveBandPositions, activePositiveControl, dtwAlignedSampleProfile, blotPlacedBands, suppressedBands, pathologyFilter, pathologyContexts, pairedComparisonPartners])

  // Get identified band names for selected blot
  const selectedBlotIdentifiedBands = useMemo(() => {
    const auto = (selectedBlotInterpretation?.identifiedBands ?? [])
      .filter(b => b.matchedBand !== null)
      .map(b => b.matchedBand!)
    // Include bands the biologist placed by hand so the list under the image and
    // everywhere else reflects them too.
    const placed = selectedBlot
      ? Array.from(blotPlacedBands.get(selectedBlot.id)?.keys() ?? []).filter(n => BAND_NAMES.includes(n))
      : []
    return Array.from(new Set([...auto, ...placed]))
  }, [selectedBlotInterpretation, selectedBlot, blotPlacedBands, BAND_NAMES])

  // Get identified band names for control blot
  // Calculate interpretations for ALL blots and report to parent for export
  const allBlotInterpretations = useMemo((): BlotInterpretation[] => {
    return blotsArray.map(blot => {
      // Multitest: this export loop covers EVERY blot on the case regardless
      // of the CURRENT UI filter, so it needs each blot's OWN pathology
      // context unconditionally — not just while unfiltered (see
      // pathologyContexts). Without this, exporting while e.g. Schisto is
      // filtered would compute every OTHER pathology's blots (Aspergillus,
      // Toxo…) against Schisto's témoin/config in the PDF/report.
      const blotLabel = (blot as any).pathology_label as string | null | undefined
      const blotCtx = blotLabel ? pathologyContexts.get(canonicalPathologyKey(blotLabel) ?? '') : undefined
      const bandNamesForBlot = blotCtx?.BAND_NAMES ?? BAND_NAMES
      const interp = interpretBlot(blot, blotCtx)
      // Bands shown in the export/report = EXACTLY the bands the verdict identified
      // (interpretBlot.identifiedBands). No fallback to a fresh identifyBands() call:
      // negativeResult() deliberately returns [] (nothing clinically relevant to
      // show), so re-deriving bands here would surface bands the gallery card and the
      // verdict do NOT count — the exact card-vs-report mismatch we must avoid. Weak
      // but genuinely-seen bands still appear on borderline strips via equivocalResult
      // (identifiedBands = identifiedSpecificBands, needsReview = "à revoir").
      const specificBands = interp.identifiedBands?.filter(b => b.matchedBand !== null) ?? []
      // Paired comparison (CIP-WB): a difference is NOT a matched band — it has
      // no name in the config, only a kDa estimate, so `matchedBand` is null by
      // construction (see interpretBlot's paired branch). The filter above
      // therefore dropped every difference on its way to the exports: the PDF
      // showed the child's strip with no marks at all, and the band list was
      // empty, while the screen displayed them. Same label as the gallery chip
      // so the report and the card read alike.
      const pairedDifferences =
        pairedComparisonPartners.get(blot.id)?.role === 'compared'
          ? (interp.identifiedBands ?? []).filter(
              (b: any) => b.matchedBand === null && (b.estimatedMW ?? 0) > 0,
            )
          : []
      // Unique identified band names, including bands the biologist placed by hand
      // (so the gallery, badges and PDF band list all reflect them).
      const placedNames = Array.from(blotPlacedBands.get(blot.id)?.keys() ?? [])
        .filter(n => bandNamesForBlot.includes(n) && !suppressedBands.get(blot.id)?.has(n))
      const identifiedBandNames = Array.from(new Set([
        ...specificBands.map(b => b.matchedBand!),
        ...pairedDifferences.map((d: any) => `+~${d.estimatedMW} kDa`),
        ...placedNames,
      ]))
      // Per-band y-fraction (0 = top, 1 = bottom) used by the PDF exporter to
      // draw band lines on the strip image. position is in raw sample pixel
      // coords after our DTW/offset alignment in identifyBands.
      const profileLen = blot.profile?.length ?? 0
      const seenForLines = new Set<string>()
      const bandLines: Array<{ name: string; yFraction: number; manual?: boolean }> = []
      if (profileLen > 0) {
        for (const b of specificBands) {
          if (!b.matchedBand || seenForLines.has(b.matchedBand)) continue
          seenForLines.add(b.matchedBand)
          const yFraction = Math.max(0, Math.min(1, b.position / profileLen))
          bandLines.push({ name: b.matchedBand, yFraction, manual: false })
        }
        // The differences get their own lines, so the exported image carries
        // the same marks as the one on screen.
        for (const d of pairedDifferences as any[]) {
          const name = `+~${d.estimatedMW} kDa`
          if (seenForLines.has(name)) continue
          seenForLines.add(name)
          bandLines.push({
            name,
            yFraction: Math.max(0, Math.min(1, d.position / profileLen)),
            manual: false,
          })
        }
      }
      // Manually-placed bands → their own lines (click position). Sourced from
      // blotPlacedBands so they stay in sync with the interpretation the PDF export
      // reads (unlike the separate export-only placedBands record, which may lag).
      // A band present in blotPlacedBands was placed / renamed / added / repositioned
      // by the biologist → it is MANUAL (blue), everywhere (this feeds the PDF, and the
      // gallery + image markers share the same "in placedBands = blue" rule). Keyed on
      // the PLACEMENT, not the band name: an added P40 stays blue even when the
      // auto-detector also has a "P40" elsewhere. A purely auto-detected band left
      // untouched keeps manual:false (violet).
      const placedForLines = blotPlacedBands.get(blot.id)
      if (placedForLines) {
        placedForLines.forEach((yFraction, name) => {
          const yf = Math.max(0, Math.min(1, yFraction))
          const existing = bandLines.findIndex((l) => l.name === name)
          if (existing >= 0) bandLines[existing] = { ...bandLines[existing], yFraction: yf, manual: true }
          else bandLines.push({ name, yFraction: yf, manual: true })
        })
      }
      // Echinococcus species diagnosis for sample blots — same logic as the
      // on-screen card, so the PDF can carry it. Uses manual + auto-identified
      // bands; null for controls, non-Echinococcus tests, or indéterminé cases.
      const isControlBlot = blotCtx
        ? [blotCtx.control1Id, blotCtx.control2Id, blotCtx.control3Id].includes(blot.id)
        : activeControlIds.includes(blot.id)
      let speciesLabel: string | null = null
      if (!isControlBlot) {
        const placedForSpecies = new Set<string>([
          ...Array.from(blotPlacedBands.get(blot.id)?.keys() ?? []),
          ...identifiedBandNames,
        ])
        const sp = getEchinococcusSpeciesProfile(placedForSpecies, immunoblotConfig)
        if (sp && sp.differentiable) speciesLabel = `${sp.profile} · ${sp.species}`
      }
      return {
        blotId: blot.id,
        result: interp.result,
        color: interp.color,
        numBands: identifiedBandNames.length,  // Number of identified specific bands
        numPeaks: blot.peaks?.length || 0,     // Number of peaks detected
        identifiedBandNames,
        bandLines,
        speciesLabel,
        // La cotation ne vaut que pour un échantillon : un témoin comparé à
        // lui-même sortirait « fortement positif » par construction.
        semiQuantLabel: !isControlBlot && (interp as any).semiQuant
          ? `${(interp as any).semiQuant.label} · I=${(interp as any).semiQuant.I} (${(interp as any).semiQuant.n} bandes)`
          : null,
        bandRatios: bandRatiosOf(interp, blotCtx),
        needsReview: (interp as any).needsReview ?? false,
        // 'reference'/'compared' are internal wording; exports speak clinically.
        pairedRole: (() => {
          const role = pairedComparisonPartners.get(blot.id)?.role
          return role ? (role === 'reference' ? 'mother' : 'child') : null
        })(),
      }
    })
  // pairedComparisonPartners: this list is what the exports read (see
  // onInterpretationsChange → ExportButton). Omitting it meant a flipped
  // couple showed the new verdict on screen while the PDF, the CSV and the
  // JSON kept the one computed before the flip.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blotsArray, immunoblotConfig, effectiveBandPositions, activePositiveControl, dtwProfileCache, blotPlacedBands, suppressedBands, aiAccessible, positiveControlId, positiveControl2Id, positiveControl3Id, activeControlIds, pathologyContexts, BAND_NAMES, pairedComparisonPartners])

  // Report interpretations to parent whenever they change
  useEffect(() => {
    if (onInterpretationsChange && allBlotInterpretations.length > 0) {
      onInterpretationsChange(allBlotInterpretations)
    }
  }, [allBlotInterpretations, onInterpretationsChange])

  const computeKda = (chartX: number): number | null => {
    if (!immunoblotConfig || !showKdaScale || effectiveBandPositions.size < 2) return null
    const controlOffset = activePositiveControl
      ? getControlLine(activePositiveControl)
      : 0
    const points: Array<{ pos: number; kDa: number }> = []
    effectiveBandPositions.forEach((data, bandName) => {
      const band = immunoblotConfig.bands.specific.find((b: any) => b.name === bandName)
      if (band?.molecularWeight) {
        points.push({ pos: data.position - controlOffset, kDa: band.molecularWeight })
      }
    })
    if (points.length < 2) return null
    points.sort((a, b) => a.pos - b.pos)
    const p1 = points[0], p2 = points[points.length - 1]
    if (Math.abs(p2.pos - p1.pos) < 1) return null
    const slope = (Math.log(p2.kDa) - Math.log(p1.kDa)) / (p2.pos - p1.pos)
    const kda = p1.kDa * Math.exp(slope * (chartX - p1.pos))
    return isFinite(kda) && kda > 0 ? Math.round(kda * 10) / 10 : null
  }

  const handleImageMouseMove = (
    e: React.MouseEvent<HTMLDivElement>,
    blot: BlotDetection,
    imageBounds?: ImageContentBounds,
  ) => {
    const profile = blot.profile
    const rect = e.currentTarget.getBoundingClientRect()
    const relY = e.clientY - rect.top
    // Normalise mouse Y to image-content space (accounts for objectFit: contain letterbox).
    // Without bounds, fall back to container-relative.
    let imageRelFraction: number
    if (imageBounds && imageBounds.heightPct > 0) {
      const containerRelFraction = relY / rect.height
      imageRelFraction = (containerRelFraction - imageBounds.topPct / 100) / (imageBounds.heightPct / 100)
      // Cursor outside the actual image content (top/bottom letterbox) → no hover state
      if (imageRelFraction < 0 || imageRelFraction > 1) {
        setHoverState(null)
        return
      }
    } else {
      imageRelFraction = Math.max(0, Math.min(1, relY / rect.height))
    }
    const rawProfileIdx = Math.round(imageRelFraction * profile.length)
    const offset = getControlLine(blot)
    setHoverState({ relFraction: imageRelFraction, rawProfileIdx, chartX: rawProfileIdx - offset })
  }

  const handleImageMouseLeave = () => setHoverState(null)

  // Prepare chart data for a blot.
  // FULL profile is shown (no slicing) with X = position − controlOffset, so the
  // control line of every blot lands at X=0 and earlier content sits at negative X.
  const getChartData = (blot: BlotDetection, title: string, color: string, originalPeaks?: number[]) => {
    // Use originalPeaks for offset calculation so sensitivity slider never shifts the profile baseline.
    const controlOffset = getControlLine(blot, originalPeaks)
    // Use baseline-corrected profile for display (full length, no slice)
    const corrected = baselineCorrectedCache.get(blot.id) ?? subtractBaseline(blot.profile)
    const validPeaks = blot.peaks.filter(p => p >= 0 && p < corrected.length)

    if (invertAxes) {
      // Inverted axes: X = intensity, Y = position (relative to control line)
      return {
        datasets: [
          {
            label: title,
            data: corrected.map((intensity, i) => ({ x: intensity, y: i - controlOffset })),
            borderColor: color,
            backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.2)'),
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.1,
            showLine: true,
          },
          {
            label: t('blotAnalysis.comparison.peaksBands'),
            data: validPeaks.map(p => ({ x: corrected[p], y: p - controlOffset })),
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.8)',
            pointRadius: 5,
            pointHoverRadius: 7,
            pointStyle: 'circle',
            showLine: false,
          },
        ],
      }
    }

    // Standard axes: X = position (relative to control line), Y = intensity
    return {
      datasets: [
        {
          label: title,
          data: corrected.map((intensity, i) => ({ x: i - controlOffset, y: intensity })),
          borderColor: color,
          backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.2)'),
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: t('blotAnalysis.comparison.peaksBands'),
          data: validPeaks.map(p => ({ x: p - controlOffset, y: corrected[p] })),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.8)',
          pointRadius: 3.5,
          pointHoverRadius: 5,
          pointStyle: 'circle',
          showLine: false,
        },
      ],
    }
  }

  // Prepare overlayed chart data with both blots (aligned on control lines, optionally with DTW)
  const getOverlayedChartData = (control: BlotDetection, sample: BlotDetection, useDTW: boolean = false, visibleTemoin: boolean = true, visibleBandelette: boolean = true, sampleOriginalPeaks?: number[]) => {
    // Find control line positions and shift both profiles
    // Always use original peaks for offset calculation — enhanced peaks (from sensitivity slider)
    // must never affect profile alignment, only peak marker display.
    const controlOffset = getControlLine(control)
    const sampleOffset = getControlLine(sample, sampleOriginalPeaks)

    // Use baseline-corrected profiles for display (FULL — no slicing)
    const correctedControl = baselineCorrectedCache.get(control.id) ?? subtractBaseline(control.profile)
    const correctedSample = baselineCorrectedCache.get(sample.id) ?? subtractBaseline(sample.profile)

    const validControlPeaks = control.peaks.filter(p => p >= 0 && p < correctedControl.length)
    const validSamplePeaks = (sampleOriginalPeaks ?? sample.peaks).filter(
      p => p >= 0 && p < correctedSample.length,
    )

    // X coordinates relative to the control line: X=0 is the control line of EACH blot.
    // This puts both control and sample on a common axis with the control line aligned.
    // Negative X = content above the control line (top of the strip).
    const controlData = correctedControl.map((intensity, i) => ({ x: i - controlOffset, y: intensity }))
    let sampleData = correctedSample.map((intensity, i) => ({ x: i - sampleOffset, y: intensity }))

    const controlPeakPts = validControlPeaks.map(p => ({ x: p - controlOffset, y: correctedControl[p] }))
    let samplePeakPts = validSamplePeaks.map(p => ({ x: p - sampleOffset, y: correctedSample[p] }))

    // useDTW kept as legacy param — peak-anchored visual alignment is no longer needed
    // since both profiles already align at X=0 via their respective control offsets.
    void useDTW

    // No-control-line fallback: when one or both strips lack a real control line,
    // pixel-offset alignment is meaningless. Use DTW-style band-to-peak alignment
    // to warp the sample profile so each calibrated band sits at the same X as
    // the corresponding témoin band on the chart.
    //
    // Safeguards against the classic DTW pathologies the user flagged earlier:
    //   - Max displacement filter: drop anchors that would shift the X axis too
    //     much vs. the original sample (controls how aggressively the warp moves).
    //   - Slope clamp [SLOPE_MIN, SLOPE_MAX]: drop anchors that would compress or
    //     stretch the profile too violently between two consecutive anchors.
    //   - Linear interpolation of intensity (not nearest-neighbour) + plateau
    //     smoothing on the warped output: avoids fake flat peaks introduced
    //     when many reference indices end up reading the same sample index.
    const noControlLine = controlOffset <= 0 || sampleOffset <= 0
    if (noControlLine && effectiveBandPositions.size > 0 && validSamplePeaks.length > 0) {
      const bandSamplePositions = dtwBandsToSamplePeaks(
        effectiveBandPositions,
        validSamplePeaks,
        correctedSample,
      )
      if (bandSamplePositions.size > 0) {
        const MAX_DISPLACEMENT = Math.floor(correctedSample.length * 0.20)  // 20% of profile
        const SLOPE_MIN = 0.6   // sample compressed to 60% of témoin spacing
        const SLOPE_MAX = 1.7   // sample stretched to 170% of témoin spacing

        // Build anchor pairs (témoin pos ↔ sample pos), drop crazy displacements.
        const rawAnchors: Array<[number, number]> = []
        effectiveBandPositions.forEach((data, bandName) => {
          const samplePos = bandSamplePositions.get(bandName)
          if (samplePos === undefined) return
          if (Math.abs(samplePos - data.position) > MAX_DISPLACEMENT) return
          rawAnchors.push([data.position, samplePos])
        })
        rawAnchors.sort((a, b) => a[0] - b[0])

        // Slope filter: drop anchors that produce out-of-band slopes vs. neighbours.
        const anchors: Array<[number, number]> = []
        for (let i = 0; i < rawAnchors.length; i++) {
          if (anchors.length === 0) { anchors.push(rawAnchors[i]); continue }
          const [rPrev, sPrev] = anchors[anchors.length - 1]
          const [rCur, sCur] = rawAnchors[i]
          const dr = rCur - rPrev
          const ds = sCur - sPrev
          if (dr <= 0 || ds <= 0) continue
          const slope = ds / dr
          if (slope < SLOPE_MIN || slope > SLOPE_MAX) continue
          anchors.push(rawAnchors[i])
        }

        if (anchors.length > 0) {
          // Identity boundary anchors — extrapolation slope = 1 (no shift at the
          // ends), so regions outside the calibration zone aren't moved violently.
          const fullAnchors: Array<[number, number]> = []
          if (anchors[0][0] > 0) {
            const startShift = anchors[0][1] - anchors[0][0]
            fullAnchors.push([0, Math.max(0, startShift)])
          }
          fullAnchors.push(...anchors)
          const lastAnchor = anchors[anchors.length - 1]
          const refEnd = correctedControl.length - 1
          const sampleEnd = correctedSample.length - 1
          if (lastAnchor[0] < refEnd) {
            const extrapolated = Math.min(sampleEnd, lastAnchor[1] + (refEnd - lastAnchor[0]))
            fullAnchors.push([refEnd, extrapolated])
          }

          // Piecewise-linear warp with linear intensity interpolation between
          // adjacent sample samples (instead of nearest neighbour) — smoother curve.
          const warped = new Array<number>(correctedControl.length).fill(0)
          let seg = 0
          for (let r = 0; r < correctedControl.length; r++) {
            while (seg < fullAnchors.length - 2 && r > fullAnchors[seg + 1][0]) seg++
            const [rL, sL] = fullAnchors[seg]
            const [rR, sR] = fullAnchors[seg + 1]
            const tt = rR === rL ? 0 : (r - rL) / (rR - rL)
            const sFloat = sL + tt * (sR - sL)
            const sLo = Math.max(0, Math.min(sampleEnd, Math.floor(sFloat)))
            const sHi = Math.max(0, Math.min(sampleEnd, sLo + 1))
            const frac = sFloat - sLo
            // Linear interpolation between two adjacent sample samples
            warped[r] = (1 - frac) * (correctedSample[sLo] ?? 0) + frac * (correctedSample[sHi] ?? 0)
          }

          // Plateau smoothing: detect runs of nearly-constant values and
          // re-interpolate between the surrounding non-plateau samples. This
          // erases the fake flat peaks the user was flagging.
          const plateauEpsilon = 0.001 * (correctedSample.reduce((m, v) => Math.max(m, v), 0) || 1)
          const smoothed = [...warped]
          for (let i = 1; i < warped.length - 1; i++) {
            if (Math.abs(warped[i] - warped[i - 1]) < plateauEpsilon
                && Math.abs(warped[i] - warped[i + 1]) < plateauEpsilon) {
              // i is on a plateau — find the surrounding non-plateau samples
              let leftIdx = i - 1, leftVal = warped[i - 1]
              while (leftIdx > 0 && Math.abs(warped[leftIdx] - warped[i]) < plateauEpsilon) {
                leftIdx--
              }
              leftVal = warped[leftIdx]
              let rightIdx = i + 1, rightVal = warped[i + 1]
              while (rightIdx < warped.length - 1 && Math.abs(warped[rightIdx] - warped[i]) < plateauEpsilon) {
                rightIdx++
              }
              rightVal = warped[rightIdx]
              if (rightIdx > leftIdx) {
                const ttt = (i - leftIdx) / (rightIdx - leftIdx)
                smoothed[i] = leftVal + ttt * (rightVal - leftVal)
              }
            }
          }

          // Plot the warped sample at the SAME X as the témoin (X = i - controlOffset).
          sampleData = smoothed.map((intensity, i) => ({ x: i - controlOffset, y: intensity }))
          // Sample peak markers now sit at the calibrated band positions
          // (= same X as témoin), so the visual correspondence is clear.
          samplePeakPts = anchors.map(([bandPos, _samplePos]) => ({
            x: bandPos - controlOffset,
            y: smoothed[Math.round(bandPos)] ?? 0,
          }))
        }
      }
    }

    const datasets: any[] = []
    if (invertAxes) {
      // Inverted: X = intensity, Y = position
      if (visibleTemoin) {
        datasets.push({
          label: module === 'protis' ? `Marqueur (${blotLabel(control)})` : t('blotAnalysis.comparison.positiveControlBlot', { id: blotLabel(control) }),
          data: controlData.map(({ x, y }) => ({ x: y, y: x })),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          borderWidth: 2.5,
          pointRadius: 0,
          tension: 0.1,
          showLine: true,
          fill: true,
          order: 2,
        })
        datasets.push({
          label: module === 'protis' ? 'Pics marqueur' : t('blotAnalysis.comparison.controlPeaks'),
          data: controlPeakPts.map(({ x, y }) => ({ x: y, y: x })),
          borderColor: 'rgb(255, 140, 0)',
          backgroundColor: 'rgba(255, 140, 0, 0.9)',
          pointRadius: 5,
          pointHoverRadius: 7,
          pointStyle: 'triangle',
          showLine: false,
          order: 0,
        })
      }
      if (visibleBandelette) {
        datasets.push({
          label: t('blotAnalysis.comparison.sampleBlot', { id: blotLabel(sample) }),
          data: sampleData.map(({ x, y }) => ({ x: y, y: x })),
          borderColor: 'rgb(168, 85, 247)',
          backgroundColor: 'rgba(168, 85, 247, 0.08)',
          borderWidth: 2.5,
          pointRadius: 0,
          tension: 0.1,
          showLine: true,
          fill: true,
          borderDash: [6, 3],
          order: 1,
        })
        datasets.push({
          label: t('blotAnalysis.comparison.samplePeaks'),
          data: samplePeakPts.map(({ x, y }) => ({ x: y, y: x })),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.9)',
          pointRadius: 3,
          pointHoverRadius: 4.5,
          pointStyle: 'circle',
          showLine: false,
          order: 0,
        })
      }
      return { datasets }
    }

    // Standard: X = position (relative to control line), Y = intensity
    if (visibleTemoin) {
      datasets.push({
        label: module === 'protis' ? `Marqueur (${blotLabel(control)})` : t('blotAnalysis.comparison.positiveControlBlot', { id: blotLabel(control) }),
        data: controlData,
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.15)',
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0.1,
        fill: true,
        order: 2,
      })
      datasets.push({
        label: module === 'protis' ? 'Pics marqueur' : t('blotAnalysis.comparison.controlPeaks'),
        data: controlPeakPts,
        borderColor: 'rgb(255, 140, 0)',
        backgroundColor: 'rgba(255, 140, 0, 0.9)',
        pointRadius: 7,
        pointHoverRadius: 9,
        pointStyle: 'triangle',
        showLine: false,
        order: 0,
      })
    }
    if (visibleBandelette) {
      datasets.push({
        label: t('blotAnalysis.comparison.sampleBlot', { id: blotLabel(sample) }),
        data: sampleData,
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.08)',
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0.1,
        fill: true,
        borderDash: [6, 3],
        order: 1,
      })
      datasets.push({
        label: t('blotAnalysis.comparison.samplePeaks'),
        data: samplePeakPts,
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.9)',
        pointRadius: 4,
        pointHoverRadius: 6,
        pointStyle: 'circle',
        showLine: false,
      })
    }
    return { datasets }
  }

  // Apply fn to every currently-mounted chart, pruning any that were destroyed.
  const forEachChart = (fn: (c: any) => void) => {
    chartRegistry.current.forEach((c) => {
      if (!c || !c.canvas) { chartRegistry.current.delete(c); return }
      try { fn(c) } catch { /* chart mid-teardown — ignore */ }
    })
  }
  const captureZoomX = (chart: any) => {
    const sx = chart?.scales?.x
    if (sx) zoomXRef.current = { min: sx.min, max: sx.max }
  }
  const resetZoom = () => {
    zoomXRef.current = {}
    forEachChart((c) => {
      // The x-scale min/max are fed from zoomXRef, and the plugin's own
      // "original limits" can be a zoomed window — so resetZoom() alone snaps
      // back to that window. Clear the plugin state first, THEN force the axis
      // to auto-range and update → true full range.
      try { c.resetZoom?.('none') } catch { /* ignore */ }
      if (c.options?.scales?.x) {
        c.options.scales.x.min = undefined
        c.options.scales.x.max = undefined
      }
      c.update?.('none')
    })
  }
  // Horizontal-only zoom (X = migration position is the meaningful axis). Capturing the
  // resulting window into zoomXRef lets getChartOptions re-inject it on the next render.
  const zoomIn = () => {
    forEachChart((c) => { c.zoom?.({ x: 1.25 }); captureZoomX(c) })
  }
  const zoomOut = () => {
    forEachChart((c) => { c.zoom?.({ x: 0.8 }); captureZoomX(c) })
  }

  const getChartOptions = (blot?: BlotDetection, positiveControlBlot?: BlotDetection, onPeakClick?: (position: number, intensity: number) => void, manualBandPositions?: Map<string, { position: number, intensity: number }>): any => ({
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event: any, elements: any[], chart: any) => {
      if (!onPeakClick || !(bandSelectionMode || protisChartArmed) || !blot) return

      const controlOffset = getControlLine(blot)

      // First, check if a peak point was clicked (dataset index 1 is peaks)
      // Data is now in {x, y} object format where x = position − controlOffset, y = intensity.
      const peakElement = elements.find((el: any) => el.datasetIndex === 1)
      if (peakElement) {
        const dataIndex = peakElement.index
        const dataset = chart.data.datasets[peakElement.datasetIndex]
        const point = dataset.data[dataIndex] as { x: number; y: number } | null
        if (point && typeof point === 'object' && 'x' in point && 'y' in point) {
          const originalPosition = Math.round(point.x + controlOffset)
          if (originalPosition >= 0 && originalPosition < blot.profile.length) {
            onPeakClick(originalPosition, point.y)
            return
          }
        }
      }

      // No peak clicked → click anywhere on the chart to select a position.
      const nativeEvent = event.native as MouseEvent
      if (!nativeEvent) return

      const rect = chart.canvas.getBoundingClientRect()
      const canvasX = nativeEvent.clientX - rect.left

      const xScale = chart.scales.x
      if (xScale) {
        // xScale.getValueForPixel returns the X value (here: position − controlOffset, can be negative)
        const xValue = Math.round(xScale.getValueForPixel(canvasX))
        const originalPosition = xValue + controlOffset
        if (originalPosition >= 0 && originalPosition < blot.profile.length) {
          const intensity = blot.profile[originalPosition]
          if (intensity !== undefined) {
            onPeakClick(originalPosition, intensity)
          }
        }
      }
    },
    layout: {
      padding: {
        top: 20, // Réduit car les labels sont maintenant à hauteur des pics
        left: 40, // Augmenté pour les labels à gauche (P31, P40...)
        right: 40, // Augmenté pour les labels à droite (P30, P33, P45...)
        bottom: 10,
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
        },
      },
      title: {
        display: false, // Désactivé pour ne pas se superposer aux labels des bandes en haut
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
            modifierKey: 'ctrl',
          },
          pinch: {
            enabled: true,
          },
          mode: 'xy',
          // Keep the X zoom window in sync with our ref (wheel/pinch + buttons) so it
          // can be re-applied after any re-render instead of snapping back to full range.
          onZoomComplete: ({ chart }: any) => captureZoomX(chart),
        },
        pan: {
          enabled: true,
          mode: 'xy',
          onPanComplete: ({ chart }: any) => captureZoomX(chart),
        },
        limits: {
          y: { min: 'original', max: 'original' },
          x: { min: 'original', max: 'original' },
        },
      },
      referenceLines: {
        blot,
        immunoblotConfig,
        invertAxes,
        positiveControlBlot,
        // The strip being drawn is a control → its bands use the "témoin" (green)
        // colour, matching the PDF legend (green = témoin · purple = détectée · blue = manuelle).
        isControlBlot: blot ? activeControlIds.includes(blot.id) : false,
        showChartBands, // When false, the band reference lines/labels are not drawn (clean profile+peaks)
        showTemoinBands, // Témoin (green) band lines
        manualBandPositions, // Manually selected band positions (overrides auto-calibration)
        // Offset from the control (where positions were calibrated)
        controlCalibrationOffset: positiveControlBlot
          ? getControlLine(positiveControlBlot)
          : (blot ? getControlLine(blot) : 0),
        // Offset from the current blot being displayed
        blotDisplayOffset: blot ? getControlLine(blot) : 0
      },
      hoverCursor: {
        hoverX: hoverState?.chartX ?? null,
        hoverKda: hoverState ? computeKda(hoverState.chartX) : null,
        invertAxes,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grace: '15%', // Ajoute 15% de marge au-dessus du max pour les labels des bandes
        title: {
          display: true,
          text: invertAxes ? t('blotAnalysis.comparison.position') : t('blotAnalysis.comparison.intensity'),
          font: {
            size: 12,
            weight: 'bold',
          },
        },
      },
      x: {
        // Always linear so the axis can render negative positions (content above
        // the control line of each blot, which is at X=0).
        type: 'linear',
        // Re-apply the persisted zoom window (undefined = auto/full range). This is what
        // makes the current zoom survive a re-render triggered by a toolbar button.
        min: zoomXRef.current.min,
        max: zoomXRef.current.max,
        title: {
          display: true,
          text: invertAxes ? t('blotAnalysis.comparison.intensity') : t('blotAnalysis.comparison.position'),
          font: {
            size: 12,
            weight: 'bold',
          },
        },
      },
    },
  })

  const assignedControlIds = activeControlIds

  const sampleBlots = useMemo(
    () => blotsArray.filter((blot) => !assignedControlIds.includes(blot.id)),
    [assignedControlIds, blotsArray]
  )

  // pathologyLabel: the blot's OWN pathology_label — required to show the
  // right témoin badge in "Toutes" (pathologyFilter === null), where
  // effectiveControlId alone only knows about the legacy case-wide slots
  // (wrong témoin for every pathology except whichever occupies those).
  const getCardTheme = useCallback((blotId: number, pathologyLabel?: string | null) => {
    const inAllView = !pathologyFilter && pathologyLabel != null
    const ctxForBlot = inAllView ? pathologyContexts.get(canonicalPathologyKey(pathologyLabel) ?? '') : null
    if (blotId === effectiveControlId(1) || (ctxForBlot && blotId === ctxForBlot.control1Id)) {
      return {
        borderColor: 'green.300',
        hoverBorderColor: 'green.400',
        bg: 'linear-gradient(180deg, #f0fff4 0%, #ffffff 100%)',
        accentBg: 'green.50',
        accentColor: 'green.600',
        pillScheme: 'green',
        controlNumber: 1,
      }
    }

    if (blotId === effectiveControlId(2) || (ctxForBlot && blotId === ctxForBlot.control2Id)) {
      return {
        borderColor: 'blue.300',
        hoverBorderColor: 'blue.400',
        bg: 'linear-gradient(180deg, #ebf8ff 0%, #ffffff 100%)',
        accentBg: 'blue.50',
        accentColor: 'blue.600',
        pillScheme: 'blue',
        controlNumber: 2,
      }
    }

    if (blotId === effectiveControlId(3) || (ctxForBlot && blotId === ctxForBlot.control3Id)) {
      return {
        borderColor: 'red.300',
        hoverBorderColor: 'red.400',
        bg: 'linear-gradient(180deg, #fff5f5 0%, #ffffff 100%)',
        accentBg: 'red.50',
        accentColor: 'red.600',
        pillScheme: 'red',
        controlNumber: 3,
      }
    }

    if (!aiAccessible && manualBlotResults[blotId] === 'POSITIF') {
      return {
        borderColor: 'green.200',
        hoverBorderColor: 'green.300',
        bg: 'linear-gradient(180deg, #f7fff9 0%, #ffffff 100%)',
        accentBg: 'green.50',
        accentColor: 'green.600',
        pillScheme: 'green',
        controlNumber: null,
      }
    }

    if (!aiAccessible && manualBlotResults[blotId] === 'DOUTEUX') {
      return {
        borderColor: 'orange.200',
        hoverBorderColor: 'orange.300',
        bg: 'linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)',
        accentBg: 'orange.50',
        accentColor: 'orange.600',
        pillScheme: 'orange',
        controlNumber: null,
      }
    }

    if (!aiAccessible && manualBlotResults[blotId] === 'NÉGATIF') {
      return {
        borderColor: 'red.200',
        hoverBorderColor: 'red.300',
        bg: 'linear-gradient(180deg, #fff8f8 0%, #ffffff 100%)',
        accentBg: 'red.50',
        accentColor: 'red.600',
        pillScheme: 'red',
        controlNumber: null,
      }
    }

    return {
      borderColor: 'gray.200',
      hoverBorderColor: 'purple.300',
      bg: 'linear-gradient(180deg, #ffffff 0%, #fafbff 100%)',
      accentBg: 'purple.50',
      accentColor: 'purple.600',
      pillScheme: 'purple',
      controlNumber: null,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiAccessible, manualBlotResults, positiveControlId, positiveControl2Id, positiveControl3Id, pathologyFilter, positiveControlsByPathologyState, pathologyContexts])

  if (!blotsArray || blotsArray.length === 0) {
    return (
      <Box
        borderRadius="2xl"
        border="1px solid"
        borderColor="gray.200"
        bg="linear-gradient(180deg, #ffffff 0%, #fafbff 100%)"
        p={{ base: 5, md: 6 }}
        boxShadow="sm"
      >
        <VStack spacing={3} align="stretch">
          <HStack spacing={3}>
            <Box
              w={10}
              h={10}
              borderRadius="xl"
              bg="purple.50"
              color="purple.600"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={MdLayers} boxSize={5} />
            </Box>
            <VStack align="start" spacing={0}>
              <Heading size="md">
                {t('blotAnalysis.gallery.detectedBlots')}
              </Heading>
              <Text color="gray.600" fontSize="sm">
                {t('blotAnalysis.gallery.noBlotDetected')}
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </Box>
    )
  }

  return (
    <>
      <Box
        borderRadius="2xl"
        border="1px solid"
        borderColor="gray.200"
        bg="linear-gradient(180deg, #ffffff 0%, #fafbff 100%)"
        p={{ base: 5, md: 6 }}
        boxShadow="sm"
      >
        <VStack spacing={5} align="stretch">
          <Flex justify="space-between" align={{ base: 'start', md: 'center' }} gap={4} wrap="wrap">
            <HStack align="start" spacing={3}>
              <Box
                w={11}
                h={11}
                borderRadius="xl"
                bg="purple.50"
                color="purple.600"
                border="1px solid"
                borderColor="purple.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Icon as={MdLayers} boxSize={5} />
              </Box>
              <VStack align="start" spacing={1}>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme="purple" variant="subtle" fontSize="sm" px={3} py={1} borderRadius="full">
                    {pathologyFilter ? `${displayedBlotsArray.length} / ${blotsArray.length}` : blotsArray.length}{' '}
                    {t('blotAnalysis.gallery.blot')}{blotsArray.length > 1 ? 's' : ''}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Cliquez sur une bandelette pour la valider
                </Text>
              </VStack>
            </HStack>

            <HStack spacing={2} flexWrap="wrap">
              {(() => {
                // Bordeaux/Dynablot multitest: scope the count to the active
                // pathology filter — "23/39 VALIDÉS" for Aspergillus, not the
                // whole 41-strip case, so a fully-validated pathology shows as
                // done even while others in the same case aren't yet.
                const scopedSampleBlots = pathologyFilter
                  ? sampleBlots.filter((b) => (b as any).pathology_label === pathologyFilter)
                  : sampleBlots
                const visibleSamples = scopedSampleBlots.filter((b) => !hiddenBlotIds.has(b.id))
                if (visibleSamples.length === 0) return null
                const validated = visibleSamples.filter((b) => manualBlotResults[b.id] != null).length
                const allDone = validated === visibleSamples.length
                return (
                  <Badge
                    variant={allDone ? 'solid' : 'subtle'}
                    colorScheme={allDone ? 'green' : 'orange'}
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontWeight="bold"
                    display="inline-flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Icon as={MdCheckCircle} boxSize={3} /> {validated}/{visibleSamples.length} VALIDÉS
                  </Badge>
                )
              })()}
            </HStack>
          </Flex>

          {isMultiPathologyCase && (
            <>
            <HStack spacing={2} flexWrap="wrap">
              <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Filtrer :
              </Text>
              <Badge
                as="button"
                onClick={() => setPathologyFilter(null)}
                cursor="pointer"
                variant={pathologyFilter === null ? 'solid' : 'outline'}
                colorScheme="purple"
                borderRadius="full"
                px={3}
                py={1}
                fontSize="2xs"
              >
                Toutes ({blotsArray.length})
              </Badge>
              {distinctPathologyLabels.map((label) => {
                const count = blotsArray.filter((b) => (b as any).pathology_label === label).length
                const active = pathologyFilter === label
                // A Dynablot report can carry a self-test we have no config
                // for — either unknown to the catalog, or listed but not yet
                // released (see pathologyConfigMap / resolvePathologyLabel).
                // Its strips are detected and displayed, but nothing can be
                // interpreted: no bands, no verdict. Until now the tab looked
                // exactly like a supported one, and the absence of result read
                // as a failure of the analysis rather than as a test we do not
                // cover yet.
                const unsupported = module === 'iris'
                  && !!pathologyConfigMap && pathologyConfigMap[label] === null
                return (
                  <Tooltip
                    key={label}
                    label={unsupported ? t('blotAnalysis.gallery.pathologyUnsupportedHint') : ''}
                    isDisabled={!unsupported}
                    hasArrow
                    openDelay={200}
                  >
                    <Badge
                      as="button"
                      onClick={() => setPathologyFilter(active ? null : label)}
                      cursor="pointer"
                      variant={active ? 'solid' : 'outline'}
                      colorScheme={unsupported ? 'gray' : 'purple'}
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="2xs"
                    >
                      {label} ({count})
                      {unsupported && (
                        <Box as="span" ml={1.5} color={active ? 'orange.200' : 'orange.500'}>
                          ⚠ {t('blotAnalysis.gallery.pathologyUnsupported')}
                        </Box>
                      )}
                    </Badge>
                  </Tooltip>
                )
              })}
            </HStack>
            {/* PROTIS : les blocs du projet choisi (A.platensis…), en
                sous-filtres. N'apparaît que s'il y a plus d'un bloc nommé. */}
            {module === 'protis' && pathologyFilter && (() => {
              const blocks = Array.from(new Set(
                blotsArray
                  .filter((b) => (b as any).pathology_label === pathologyFilter)
                  .map((b) => (b as any).plate_label)
                  .filter((v): v is string => !!v),
              ))
              if (blocks.length < 2) return null
              return (
                <HStack spacing={2} mt={2} flexWrap="wrap">
                  <Text fontSize="2xs" color="gray.500" fontWeight="600">Blocs :</Text>
                  <Badge
                    as="button"
                    onClick={() => setPlateFilter(null)}
                    cursor="pointer"
                    variant={plateFilter === null ? 'solid' : 'outline'}
                    colorScheme="cyan"
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontSize="2xs"
                  >
                    Tous
                  </Badge>
                  {blocks.map((block) => (
                    <Badge
                      key={block}
                      as="button"
                      onClick={() => setPlateFilter(plateFilter === block ? null : block)}
                      cursor="pointer"
                      variant={plateFilter === block ? 'solid' : 'outline'}
                      colorScheme="cyan"
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="2xs"
                    >
                      {block}
                    </Badge>
                  ))}
                </HStack>
              )
            })()}
            </>
          )}

          {proposedBackgroundIds.length > 0 && (
            <Box
              borderWidth="1px"
              borderColor="orange.200"
              bg="orange.50"
              borderRadius="xl"
              p={3}
              mb={4}
            >
              <HStack align="flex-start" spacing={3}>
                <Icon as={LuTriangleAlert} color="orange.500" boxSize={4} mt={0.5} />
                <Box flex="1" minW={0}>
                  <Text fontSize="sm" fontWeight="700" color="orange.800">
                    {proposedBackgroundIds.length} détection{proposedBackgroundIds.length > 1 ? 's' : ''} écartée{proposedBackgroundIds.length > 1 ? 's' : ''} : fond de plaque probable
                  </Text>
                  <Text fontSize="xs" color="orange.800" mt={0.5}>
                    Aucun identifiant n’a été lu dessus, et leur profil est plat par rapport au
                    reste de la plaque. Les deux ensemble : ce n’est probablement pas une
                    bandelette. Rien n’est enregistré tant que vous n’avez pas confirmé.
                  </Text>
                  <HStack spacing={3} mt={2} align="flex-end" flexWrap="wrap">
                    {proposedBackgroundIds.map((id) => {
                      const b: any = (blots ?? []).find((x: any) => x.id === id)
                      return (
                        <VStack key={id} spacing={1}>
                          <Box
                            w="26px"
                            h="120px"
                            overflow="hidden"
                            borderWidth="1px"
                            borderColor="orange.300"
                            borderRadius="sm"
                            bg="white"
                          >
                            {b?.crop_base64 && (
                              <Image
                                src={`data:image/png;base64,${b.crop_base64}`}
                                alt={`Détection ${id}`}
                                objectFit="cover"
                                objectPosition="top"
                                w="100%"
                                h="100%"
                              />
                            )}
                          </Box>
                          <Text fontSize="2xs" color="orange.700">#{id}</Text>
                        </VStack>
                      )
                    })}
                  </HStack>
                </Box>
                <VStack spacing={2} align="stretch">
                  <Button size="xs" colorScheme="orange" onClick={confirmBackgroundRemoval}>
                    Confirmer
                  </Button>
                  <Button size="xs" variant="outline" colorScheme="orange" onClick={restoreBackgroundRemoval}>
                    Les rétablir
                  </Button>
                </VStack>
              </HStack>
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, sm: 2, xl: 3, '2xl': 4 }} spacing={5}>
            {displayedBlotsArray.map((blot, index) => {
              // "Toutes": resolve THIS blot's own pathology context (config,
              // témoin, band positions/names) instead of the filter-scoped
              // closure values — see pathologyContexts above for why.
              const blotPathologyLabel = (blot as any).pathology_label as string | null | undefined
              const blotCtx = (!pathologyFilter && blotPathologyLabel)
                ? pathologyContexts.get(canonicalPathologyKey(blotPathologyLabel) ?? '')
                : undefined
              // "Toutes" can't unambiguously assign a témoin: togglePositiveControl
              // would write the legacy case-wide slot instead of this card's own
              // pathology. Filter to one pathology first — see the T+ buttons and hint.
              const disabledInAllView = !pathologyFilter && isMultiPathologyCase
              // Paired-comparison pathologies (CIP-WB mère/enfant) never use a
              // témoin at all — the T1/T2/T3 picker is meaningless for them,
              // filtered or not, so it's disabled unconditionally rather than
              // only in "Toutes". See pairedComparisonPartners/comparePairedStrips.
              const cardConfigForControlGate = blotCtx?.config ?? (pathologyFilter ? immunoblotConfig : null)
              const isPairedModeCard = (cardConfigForControlGate as any)?.interpretation?.mode === 'paired_comparison'
              // readOnly: a guest cannot designate a control — it would change the
              // interpretation shown to them while the server refuses the write.
              const controlPickingDisabled = readOnly || disabledInAllView || isPairedModeCard || module === 'protis'
              // Shown top-right over the image, opposite the delete icon — a
              // spot that's free once the témoin picker is hidden for this
              // pathology. Kept away from the strip-id row entirely: an
              // earlier version sat next to the strip-id editor/gap-fill
              // badge there and was reported as cluttering/interfering with it.
              const pairInfo = isPairedModeCard ? pairedComparisonPartners.get(blot.id) : undefined
              const cardBandNames = blotCtx?.BAND_NAMES ?? BAND_NAMES
              const blotResult = interpretBlot(blot, blotCtx)
              const detectedBands = blotResult.identifiedBands?.filter((band) => band.matchedBand !== null) || []
              // Include hand-placed bands so the gallery card reflects them too.
              const placedForCard = Array.from(blotPlacedBands.get(blot.id)?.keys() ?? []).filter(n => cardBandNames.includes(n))
              const aiUniqueBands = Array.from(new Set([...detectedBands.map((band) => band.matchedBand), ...placedForCard])).sort()
              const manualResult = manualBlotResults[blot.id]
              const cardTheme = getCardTheme(blot.id, blotPathologyLabel)
              const isControl = cardTheme.controlNumber !== null
              // Pendant la lecture de paillasse, tout ce qui vient d'IRIS est
              // voilé sur les ÉCHANTILLONS : afficher son verdict ou son
              // compte de bandes y reviendrait à montrer un vide pour un
              // résultat. Le témoin reste entier, il sert à calibrer.
              //
              // Reconnu au profil vide — c'est le serveur qui voile, et sur
              // une plaque multitest la liste des témoins du filtre courant ne
              // dit pas tout.
              const hideIrisState = benchReadingMode && !(blot.profile?.length)
              // LE TÉMOIN NE LIVRE PAS SES BANDES NON PLUS.
              //
              // Il n'est pas voilé — il doit rester entier pour se désigner et se
              // calibrer — mais afficher ses bandes détectées revient à publier le
              // gabarit positionnel de la plaque : où tombent P26-28 et P7. L'œil
              // le reporte ensuite sur les échantillons, et l'aveugle est percé
              // par la bande plutôt que par l'image. Observé sur un cas réel
              // (Echinococcus, témoin D13 : « 2 BANDES · P26-28 · P7 » affiché en
              // pleine saisie). Le décompte et les pastilles disparaissent donc
              // pour TOUTES les bandelettes tant qu'il reste une saisie en cours.
              const hideBandReadout = benchReadingMode
              // For positive controls, show the MANUALLY CALIBRATED bands (all of them) instead of
              // the AI-filtered "significant" bands — otherwise the user's 5-band calibration shows
              // as only 3 because the intensity threshold drops weaker bands.
              const calibratedForControl = isControl && savedBandCalibration?.[String(blot.id)]
                ? Object.keys(savedBandCalibration[String(blot.id)]).filter(k => cardBandNames.includes(k)).sort()
                : null
              // Show whatever the AI actually detected, same as the modal — a
              // biologist-validated NÉGATIF does NOT hide it there (negativeResult()
              // deliberately carries identifiedSpecificBands as informational: "a
              // NÉGATIF verdict must not silently hide what was seen"). Force-
              // clearing it here ONLY on the card used to hide a genuinely stale
              // AI band from an old verdict, but it just as easily hides a REAL
              // band the AI later learns to detect (measured on a real case,
              // Aspergillus I4: a manual NÉGATIF from before Pass 3 existed made
              // the newly-recovered P30 invisible on the card while the modal
              // showed it plainly) — the same card-vs-modal mismatch this was
              // meant to prevent, just in the other direction.
              // PROTIS : les bandes d'une carte sont celles que le biologiste a
              // POSÉES (ou acceptées des suggestions) — il n'y a ni catalogue ni
              // calibration de témoin pour en nommer d'autres. Sans cette branche,
              // la carte disait « 0 bande » après une pose.
              const uniqueBands: string[] = module === 'protis'
                ? Array.from(blotPlacedBands.get(blot.id)?.keys() ?? [])
                : (calibratedForControl && calibratedForControl.length > 0)
                ? calibratedForControl
                : (aiUniqueBands as string[])
              // Paired-comparison pathologies have no named bands at all
              // (matchedBand is always null — see comparePairedStrips) —
              // uniqueBands would always read "0 bandes" even on a POSITIF
              // verdict, which looks contradictory. Count differences
              // instead, unnamed.
              const pairedDifferencesCount = isPairedModeCard ? (blotResult.identifiedBands?.length ?? 0) : 0
              const isGhosting = ghostingIds.has(blot.id)
              // A patient strip whose identifier is missing or half-read ("I" with
              // no number) blocks every export (see isCompleteStripId). Flag it right
              // on the card so the biologist sees which strip to complete without
              // having to open the export menu. Controls never carry a patient id.
              // PROTIS : les identifiants sont des NOMS lus sur la planche
              // (PBS, ECH, Marqueur de masse…) — exiger un numéro n'a pas de sens.
              const stripIdIncomplete = module === 'iris' && !isControl && !isCompleteStripId((blot as any).strip_id)
              // Bordeaux/Dynablot multitest: a report can mix several self-tests in
              // one PDF (see pathology_label, OCR-read per strip). blotsArray keeps
              // its natural report-row order, and same-pathology strips already come
              // as one contiguous block in that order — so a section header only
              // needs to fire when the label changes from the previous card, no
              // sorting/regrouping of the array itself required.
              const pathologyLabel = (blot as any).pathology_label as string | null | undefined
              const prevPathologyLabel = index > 0 ? ((displayedBlotsArray[index - 1] as any).pathology_label as string | null | undefined) : undefined
              const showPathologyHeader = isMultiPathologyCase && !!pathologyLabel && pathologyLabel !== prevPathologyLabel
              const entranceRank = entranceOrderRef.current?.get(blot.id)
              const showEntrance = !entranceDone && entranceRank !== undefined
              const entranceAnimation = showEntrance
                ? `${blotRevealIn} 0.42s cubic-bezier(0.16, 1, 0.3, 1) ${Math.min(entranceRank, 15) * 70}ms both`
                : undefined

              return (
                <React.Fragment key={blot.id}>
                {showPathologyHeader && (
                  <Box gridColumn="1 / -1" pt={index > 0 ? 3 : 0}>
                    <HStack spacing={2} pb={1} borderBottom="2px solid" borderColor="purple.200">
                      <Icon as={MdLayers} boxSize={4} color="purple.500" />
                      <Text fontSize="sm" fontWeight="700" color="purple.700" textTransform="uppercase" letterSpacing="wide">
                        {pathologyLabel}
                      </Text>
                    </HStack>
                  </Box>
                )}
                <Card
                  position="relative"
                  cursor={isGhosting ? 'default' : 'pointer'}
                  onClick={() => { if (!isGhosting) handleBlotClick(blot, index) }}
                  transition="all 0.22s ease"
                  _hover={isGhosting ? undefined : {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 20px 40px -24px rgba(76, 81, 191, 0.45)',
                    borderColor: stripIdIncomplete ? 'orange.400' : cardTheme.hoverBorderColor,
                  }}
                  borderWidth="1px"
                  borderColor={stripIdIncomplete ? 'orange.300' : cardTheme.borderColor}
                  borderLeftWidth={!isControl && aiAccessible ? '4px' : '1px'}
                  borderLeftColor={!isControl && aiAccessible ? (manualResult != null ? 'green.400' : 'orange.400') : cardTheme.borderColor}
                  bg={cardTheme.bg}
                  borderRadius="xl"
                  overflow="hidden"
                  // Anneau orange : la carte d'un cas limite (needsReview) se repère
                  // depuis l'autre bout de la galerie, tant qu'elle n'est pas validée.
                  boxShadow={(blotResult as any).needsReview && manualResult == null && aiAccessible
                    ? '0 0 0 2px rgba(237, 137, 54, 0.55), 0 6px 18px rgba(237, 137, 54, 0.28)'
                    : 'sm'}
                  pointerEvents={isGhosting ? 'none' : undefined}
                  animation={isGhosting ? `${ghostSnap} 0.64s ease forwards` : entranceAnimation}
                >
                  <CardBody p={0}>
                    <VStack spacing={0} align="stretch">
                      <Box px={4} pt={4} pb={3}>
                        <HStack justify="flex-start" align="start" spacing={3}>
                          <HStack spacing={3} align="start">
                            <Box
                              w={9}
                              h={9}
                              borderRadius="lg"
                              bg={cardTheme.accentBg}
                              color={cardTheme.accentColor}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                            >
                              <Icon as={MdBarChart} boxSize={4} />
                            </Box>
                            <VStack align="start" spacing={0} minW={0}>
                              {/* Recognised strip identifier (OCR) replaces "Blot N"
                                  when available. Status marker: ✓ confident,
                                  ~ suggested/low-confidence, ⚠ conflict to check.
                                  Falls back to "Blot N" when no ID was read. */}
                              {(() => {
                                const sid = (blot as any).strip_id as string | undefined
                                const st = (blot as any).strip_id_status
                                // A shared id outranks every other status: it is the
                                // one OCR/typing failure that can mis-attribute a
                                // result, so it must be visible even on a strip the
                                // reader just typed by hand.
                                const marker = (st === 'conflict' || hasStripIdConflict(blot as any))
                                  ? { c: 'orange.500', t: t('blotAnalysis.gallery.idConflict') }
                                  : st === 'sure' ? { c: 'green.500', t: t('blotAnalysis.gallery.idSure') }
                                  : st === 'edited' ? { c: 'blue.500', t: t('blotAnalysis.gallery.idEdited') }
                                  : { c: 'gray.400', t: t('blotAnalysis.gallery.idSuggested') }
                                return (
                                  <>
                                    <HStack spacing={1.5} align="center" onClick={(e) => e.stopPropagation()}>
                                      {/* Paired-comparison role (CIP-WB): which strip is the
                                          mother and which the child is otherwise only legible
                                          once the comparison is open, while it decides where
                                          the differences and the verdict are reported. */}
                                      {(() => {
                                        const role = pairedComparisonPartners.get(blot.id)?.role
                                        if (!role) return null
                                        const isMother = role === 'reference'
                                        // Clickable: the couple is flipped from the card
                                        // itself, without opening the comparison — the
                                        // role is read here, so it is corrected here.
                                        return (
                                          <Tooltip
                                            label={t(readOnly
                                              ? (isMother
                                                ? 'blotAnalysis.gallery.motherStrip'
                                                : 'blotAnalysis.gallery.childStrip')
                                              : (isMother
                                                ? 'blotAnalysis.gallery.motherStripSwap'
                                                : 'blotAnalysis.gallery.childStripSwap'))}
                                            hasArrow
                                            openDelay={200}
                                          >
                                            <Badge
                                              as={readOnly ? undefined : 'button'}
                                              type={readOnly ? undefined : 'button'}
                                              onClick={readOnly ? undefined : (e: React.MouseEvent) => {
                                                e.stopPropagation()
                                                togglePairedRoles(blot)
                                              }}
                                              colorScheme={isMother ? 'purple' : 'green'}
                                              variant="solid"
                                              fontSize="2xs"
                                              fontWeight="bold"
                                              px={1.5}
                                              borderRadius="full"
                                              flexShrink={0}
                                              textTransform="none"
                                              cursor={readOnly ? 'default' : 'pointer'}
                                              _hover={readOnly ? undefined : { opacity: 0.8 }}
                                            >
                                              {t(isMother
                                                ? 'blotAnalysis.gallery.motherShort'
                                                : 'blotAnalysis.gallery.childShort')}
                                            </Badge>
                                          </Tooltip>
                                        )
                                      })()}
                                      {/* Editable — click to correct the OCR draft; falls back to
                                          a "add" placeholder when nothing was recognised. */}
                                      <Editable
                                        // Read-only guest: the server refuses every write on a
                                        // viewer share, so letting the field be edited only
                                        // produced a correction that vanished on reload.
                                        isDisabled={readOnly}
                                        // Uncontrolled input: defaultValue only applies at mount, so
                                        // key on the value to remount when it changes from data
                                        // (a propagated letter, or the saved id) — otherwise the card
                                        // keeps showing the stale draft and suggestions never appear.
                                        // The nonce also remounts it after a rejected entry, reverting
                                        // the shown text to the last saved id.
                                        key={`sid-${blot.id}-${sid ?? ''}-${stripIdEditNonce}`}
                                        defaultValue={sid || ''}
                                        placeholder="＋ ID"
                                        submitOnBlur
                                        onSubmit={(val) => {
                                          const v = val.trim()
                                          if (v === (sid || '')) return
                                          // Reject malformed ids (digit-only "123", symbols "T+", …)
                                          // before they ever reach the DB; a letter + up to 3 digits
                                          // ("K", "K10") is the only accepted shape.
                                          if (!isValidStripIdDraft(v)) {
                                            toast({
                                              title: t('blotAnalysis.gallery.idInvalid'),
                                              description: t('blotAnalysis.gallery.idInvalidDetail'),
                                              status: 'warning',
                                              duration: 5000,
                                            })
                                            setStripIdEditNonce((n) => n + 1)
                                            return
                                          }
                                          // An id another strip of the same search
                                          // already carries is refused outright: two
                                          // strips sharing one identifier attribute a
                                          // result to the wrong patient, and nothing
                                          // downstream can tell which was meant. The
                                          // entry reverts to the last saved id (nonce
                                          // remount), so an id is freed by correcting
                                          // the other strip first. On a Dynablot
                                          // multitest plate the same id stays allowed
                                          // from one search to the next — see
                                          // `stripIdKey`.
                                          const twin = stripCarryingId(v, blot as any)
                                          if (twin) {
                                            toast({
                                              title: t('blotAnalysis.gallery.idDuplicate', {
                                                id: v.trim().toUpperCase(),
                                              }),
                                              description: t('blotAnalysis.gallery.idDuplicateDetail'),
                                              status: 'error',
                                              duration: 6000,
                                              isClosable: true,
                                            })
                                            setStripIdEditNonce((n) => n + 1)
                                            return
                                          }
                                          onStripIdChange?.(blot.id, v || null)
                                        }}
                                        fontSize="sm"
                                        fontWeight="bold"
                                        color={sid ? 'gray.800' : 'gray.400'}
                                      >
                                        <Tooltip label={t('blotAnalysis.gallery.idEditHint')} hasArrow openDelay={400}>
                                          <EditablePreview whiteSpace="nowrap" cursor="text" _hover={{ bg: 'gray.50' }} px={1} borderRadius="sm" />
                                        </Tooltip>
                                        <EditableInput w="72px" px={1} />
                                      </Editable>
                                      {sid && (
                                        <Tooltip label={marker.t} hasArrow openDelay={200}>
                                          <Box w="7px" h="7px" borderRadius="full" bg={marker.c} flexShrink={0} />
                                        </Tooltip>
                                      )}
                                      {stripIdIncomplete && (
                                        <Tooltip label={t('blotAnalysis.gallery.idMissingNumber')} hasArrow openDelay={200}>
                                          <Badge colorScheme="orange" variant="solid" fontSize="2xs" fontWeight="bold" px={1.5} borderRadius="full" flexShrink={0} textTransform="none">
                                            ⚠ N° manquant
                                          </Badge>
                                        </Tooltip>
                                      )}
                                      {/* Deduced from the row order of this strip's confidently-read
                                          neighbours (see gapFilledStripIdSuggestions) — a much better
                                          guess than OCR's own garbled read when it failed on exactly
                                          this one strip. One click applies it; never auto-applied. */}
                                      {(() => {
                                        const gapSuggestion = gapFilledStripIdSuggestions.get(blot.id)
                                        if (!gapSuggestion || gapSuggestion === sid) return null
                                        return (
                                          <Tooltip label={`Déduit de la séquence des bandelettes voisines — cliquez pour appliquer "${gapSuggestion}"`} hasArrow openDelay={200}>
                                            <Badge
                                              as="button"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                onStripIdChange?.(blot.id, gapSuggestion)
                                              }}
                                              colorScheme="purple"
                                              variant="outline"
                                              fontSize="2xs"
                                              fontWeight="bold"
                                              px={1.5}
                                              borderRadius="full"
                                              flexShrink={0}
                                              textTransform="none"
                                              cursor="pointer"
                                              _hover={{ bg: 'purple.50' }}
                                            >
                                              → {gapSuggestion} ?
                                            </Badge>
                                          </Tooltip>
                                        )
                                      })()}
                                    </HStack>
                                    {/* Second line = secondary identity. The sample
                                        reference lives here rather than beside the
                                        identifier: that row already carries the status
                                        dot, the "missing number" warning and the
                                        sequence suggestion, which appear exactly when
                                        the id needs attention — the moment the card has
                                        the least room to spare. Here the reference is
                                        always visible and never competes. */}
                                    <HStack spacing={1.5} align="center" minW={0} maxW="100%">
                                      <Text fontSize="2xs" color="gray.400" flexShrink={0}>Blot {blot.id}</Text>
                                      {(blot as any).sample_label && (
                                        <Tooltip
                                          label={t('blotAnalysis.gallery.sampleRef', { ref: (blot as any).sample_label })}
                                          hasArrow
                                          openDelay={200}
                                        >
                                          <Badge
                                            colorScheme="blue"
                                            variant="subtle"
                                            fontSize="2xs"
                                            fontWeight="bold"
                                            px={1.5}
                                            borderRadius="full"
                                            textTransform="none"
                                            minW={0}
                                            overflow="hidden"
                                            whiteSpace="nowrap"
                                            sx={{ textOverflow: 'ellipsis' }}
                                          >
                                            {(blot as any).sample_label}
                                          </Badge>
                                        </Tooltip>
                                      )}
                                    </HStack>
                                  </>
                                )
                              })()}
                            </VStack>
                          </HStack>
                        </HStack>

                        {/* Verdict + validation on their own row, left-aligned, so they
                            sit under the identifier instead of being pushed to the far
                            right edge of a header row that has no free space. */}
                        {/* Masqué pendant la lecture de paillasse : IRIS est voilé, donc
                            « à valider » ne désigne rien à faire et « 0 bande » n'est pas
                            un résultat — c'est l'absence de résultat, par construction.
                            Le témoin, lui, est visible et garde ses mentions. */}
                        {!hideIrisState && (
                        <HStack spacing={2} align="center" mt={2} flexWrap="wrap">
                          {isControl ? (
                            <Badge
                              colorScheme={cardTheme.pillScheme}
                              variant="subtle"
                              fontSize="xs"
                              px={2.5}
                              py={1}
                              borderRadius="full"
                            >
                              {t('blotAnalysis.gallery.control')} {cardTheme.controlNumber}
                            </Badge>
                          ) : manualResult ? (
                            // The biologist's own validated verdict always wins over the raw
                            // AI badge below — showing the AI's "POSITIF" next to a "Validé"
                            // pill after the biologist corrected it to NÉGATIF (aiAccessible
                            // path used to do exactly that, silently) made it look like the
                            // positive reading had been validated when the opposite had.
                            <Badge
                              colorScheme={
                                manualResult === 'POSITIF' ? 'green' :
                                manualResult === 'DOUTEUX' ? 'orange' : 'red'
                              }
                              variant="subtle"
                              fontSize="xs"
                              fontWeight="bold"
                              px={2.5}
                              py={1}
                              borderRadius="full"
                            >
                              {manualResult}
                            </Badge>
                          ) : !aiAccessible && module === 'iris' ? (
                            <Badge
                              colorScheme="gray"
                              fontSize="xs"
                              variant="outline"
                              px={2.5}
                              py={1}
                              borderRadius="full"
                            >
                              {t('blotAnalysis.gallery.toValidate')}
                            </Badge>
                          ) : effectiveBandPositions.size > 0 ? (
                            <Badge
                              colorScheme={blotResult.color}
                              variant="subtle"
                              fontSize="xs"
                              fontWeight="bold"
                              px={2.5}
                              py={1}
                              borderRadius="full"
                            >
                              {blotResult.result}
                            </Badge>
                          ) : null}
                          {/* Cas limite (needsReview) — affiché aussi en galerie, et de façon
                              voyante : critères presque atteints (signal faible vs témoin,
                              accrochage en limite de tolérance). Il s'éteint dès que le
                              biologiste a validé, pour ne pas doublonner l'état de validation. */}
                          {(blotResult as any).needsReview && manualResult == null && aiAccessible && effectiveBandPositions.size > 0 && (
                            <Tooltip label={t('blotAnalysis.clinical.borderlineTooltip')} hasArrow placement="top">
                              <Badge
                                bgGradient="linear(to-r, orange.400, red.500)"
                                color="white"
                                fontSize="2xs"
                                px={2}
                                py={1}
                                borderRadius="full"
                                fontWeight="bold"
                                cursor="help"
                                whiteSpace="nowrap"
                                animation={`${pulseWarn} 1.8s ease-in-out infinite`}
                              >
                                ⚠ {t('blotAnalysis.clinical.toCheck')}
                              </Badge>
                            </Tooltip>
                          )}
                          {/* Cotation semi-quantitative (Oliva 2015) — seulement quand le
                              cas la porte (profil Bordeaux + config Aspergillus), jamais
                              sur un témoin. Suggestion, pas un verdict. */}
                          {!isControl && (blotResult as any).semiQuant && aiAccessible && (
                            <Tooltip
                              label={semiQuantTooltip((blotResult as any).semiQuant)}
                              hasArrow
                              placement="top"
                              maxW="340px"
                            >
                              <Badge
                                colorScheme={(blotResult as any).semiQuant.strength === 'strong' ? 'purple' : 'cyan'}
                                variant="solid"
                                fontSize="2xs"
                                px={2}
                                py={1}
                                borderRadius="full"
                                fontWeight="bold"
                                cursor="help"
                                whiteSpace="nowrap"
                              >
                                {(blotResult as any).semiQuant.label} · I={(blotResult as any).semiQuant.I}
                              </Badge>
                            </Tooltip>
                          )}
                          {/* Biologist validation status — colour code (green = validated).
                              IRIS seulement : une piste PROTIS ne porte pas de verdict. */}
                          {module === 'iris' && !isControl && aiAccessible && (
                            <Badge
                              colorScheme={manualResult != null ? 'green' : 'orange'}
                              variant={manualResult != null ? 'solid' : 'outline'}
                              fontSize="2xs"
                              px={2}
                              py={1}
                              borderRadius="full"
                              fontWeight="bold"
                              display="inline-flex"
                              alignItems="center"
                              gap={1}
                              whiteSpace="nowrap"
                            >
                              <Icon as={MdCheckCircle} boxSize={2.5} /> {manualResult != null ? t('blotAnalysis.gallery.validated') : t('blotAnalysis.gallery.toValidate')}
                            </Badge>
                          )}
                        </HStack>
                        )}

                        <HStack mt={3} spacing={1.5} flexWrap="wrap">
                          {/* The differences are a property of the CHILD specifically
                              (that's who carries the extra/reinforced bands — the
                              mother is the baseline by definition, comparePairedStrips
                              always reports 0 for her) — only show the count + kDa
                              chips on the "compared" card, never on "reference". */}
                          {isPairedModeCard ? (
                            pairInfo?.role === 'compared' && (
                              <Badge colorScheme="teal" variant="subtle" fontSize="2xs" px={2} borderRadius="full">
                                {pairedDifferencesCount} différence{pairedDifferencesCount !== 1 ? 's' : ''}
                              </Badge>
                            )
                          ) : (
                            !hideBandReadout && (
                              <Badge colorScheme="teal" variant="subtle" fontSize="2xs" px={2} borderRadius="full">
                                {`${uniqueBands.length} ${uniqueBands.length !== 1 ? t('blotAnalysis.gallery.bandsLabel') : t('blotAnalysis.gallery.bandLabel')}`}
                              </Badge>
                            )
                          )}
                          {/* Shown directly as chips, same visible treatment as the
                              auto-detected purple bands below — not tucked behind a
                              hover, per the operator's own convention for this app. No
                              named-band catalogue exists for this test, so the label is
                              a kDa estimate (bands.reference.min/maxKDa, two-point
                              log-linear — see interpretBlot's paired branch), not a
                              matched band name. "+" marks it as gained vs the mother. */}
                          {isPairedModeCard && pairInfo?.role === 'compared' && (blotResult.identifiedBands ?? []).map((d, dIndex) => (
                            <Badge
                              key={dIndex}
                              bg="purple.100"
                              color="purple.700"
                              fontSize="2xs"
                              px={2}
                              borderRadius="full"
                            >
                              +~{d.estimatedMW} kDa
                            </Badge>
                          ))}
                          {!isPairedModeCard && !hideBandReadout && uniqueBands.map((bandName, bandIndex) => {
                            // Unified band-colour code, shared with the PDF report
                            // legend: green = control (témoin), purple = auto-detected,
                            // blue = manually added. Required bands keep a bold "*"
                            // rather than a colour so the source code stays unambiguous.
                            const bandIsManual = placedForCard.includes(bandName)
                            const scheme = isControl ? 'green' : bandIsManual ? 'blue' : 'purple'
                            const isRequired = REQUIRED_BAND_NAMES.includes(bandName)
                            return (
                              <Badge
                                key={bandIndex}
                                bg={`${scheme}.100`}
                                color={`${scheme}.700`}
                                fontSize="2xs"
                                px={2}
                                borderRadius="full"
                                fontWeight={isRequired ? 'bold' : 'normal'}
                              >
                                {bandName}{isRequired ? '*' : ''}
                              </Badge>
                            )
                          })}
                        </HStack>
                      </Box>

                      <Box px={4}>
                        <Box
                          position="relative"
                          borderRadius="xl"
                          overflow="hidden"
                          bg="white"
                          border="1px solid"
                          borderColor="blackAlpha.100"
                          w="100%"
                          aspectRatio={1.1}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Box
                            position="absolute"
                            top={2}
                            left={2}
                            right={2}
                            zIndex={1}
                          >
                            <HStack justify="space-between" align="start">
                              <HStack spacing={1}>
                                <Tooltip label={t('blotAnalysis.gallery.deleteGhost')} hasArrow>
                                  <IconButton
                                    aria-label={t('blotAnalysis.gallery.deleteBlot')}
                                    icon={<Icon as={MdDelete} />}
                                    size="xs"
                                    variant="solid"
                                    bg="whiteAlpha.900"
                                    color="red.500"
                                    _hover={{ bg: 'white', color: 'red.600' }}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      ghostBlot(blot.id)
                                    }}
                                  />
                                </Tooltip>
                                {/* Compact star group — icon-only pill so the buttons
                                    no longer cover the top of the blot image. The
                                    number ID is shown via the tooltip and as a tiny
                                    superscript inside the active state. Paired-
                                    comparison pathologies (CIP-WB) have no témoin
                                    concept at all — hidden entirely for them, not just
                                    disabled, per explicit feedback that a greyed-out
                                    button still read as a button and cluttered the
                                    strip-id row. */}
                                {module === 'iris' && !isPairedModeCard && (
                                <>
                                <HStack spacing={1} data-tour="control-toggles">
                                {([
                                  { num: 1, activeId: blotCtx ? blotCtx.control1Id : effectiveControlId(1), gradient: 'linear(135deg, #16a34a, #22c55e)', solidColor: 'green.500', glow: 'rgba(34, 197, 94, 0.5)' },
                                  { num: 2, activeId: blotCtx ? blotCtx.control2Id : effectiveControlId(2), gradient: 'linear(135deg, #2563eb, #3b82f6)', solidColor: 'blue.500', glow: 'rgba(59, 130, 246, 0.5)' },
                                  { num: 3, activeId: blotCtx ? blotCtx.control3Id : effectiveControlId(3), gradient: 'linear(135deg, #dc2626, #ef4444)', solidColor: 'red.500', glow: 'rgba(239, 68, 68, 0.5)' },
                                ] as const).map(({ num, activeId, gradient, solidColor, glow }) => {
                                  const isActive = blot.id === activeId
                                  // T1/T2/T3 stay 3 independent slots — a manip can genuinely need
                                  // 2 témoins for the same pathology (weak/strong, replicates).
                                  // Bordeaux/Dynablot multitest: each pathology has its OWN T1/T2/T3
                                  // (effectiveControlId/togglePositiveControl resolve to
                                  // savedPositiveControlsByPathology while filtered), so a
                                  // different pathology's témoin can never occupy this same slot —
                                  // no cross-pathology collision to guard against here.
                                  // "Toutes" (no filter) and paired-comparison
                                  // pathologies (no témoin concept at all) both
                                  // disable this picker — controlPickingDisabled
                                  // (defined above, per card) guards both: see there.
                                  return (
                                    <Tooltip
                                      key={num}
                                      label={isPairedModeCard
                                        ? t('blotAnalysis.gallery.pairedNoControl')
                                        : disabledInAllView
                                          ? t('blotAnalysis.gallery.filterToDesignate')
                                          : (isActive ? t('blotAnalysis.gallery.removeControl', { num }) : t('blotAnalysis.gallery.setControl', { num }))}
                                      hasArrow
                                      openDelay={200}
                                    >
                                      <Box
                                        as="button"
                                        disabled={controlPickingDisabled}
                                        cursor={controlPickingDisabled ? 'not-allowed' : 'pointer'}
                                        opacity={controlPickingDisabled ? 0.4 : 1}
                                        onClick={(e: React.MouseEvent) => {
                                          e.stopPropagation()
                                          if (controlPickingDisabled) return
                                          togglePositiveControl(blot.id, num as 1 | 2 | 3)
                                        }}
                                        position="relative"
                                        h="20px"
                                        w="20px"
                                        borderRadius="full"
                                        display="inline-flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        bgGradient={isActive ? gradient : undefined}
                                        bg={isActive ? undefined : 'whiteAlpha.900'}
                                        color={isActive ? 'white' : solidColor}
                                        border="1.5px solid"
                                        borderColor={isActive ? 'transparent' : solidColor}
                                        boxShadow={isActive ? `0 3px 8px ${glow}` : '0 1px 2px rgba(0,0,0,0.08)'}
                                        transition="all 0.15s cubic-bezier(0.4, 0, 0.2, 1)"
                                        _hover={controlPickingDisabled ? undefined : {
                                          transform: 'scale(1.12)',
                                          boxShadow: `0 4px 10px ${glow}`,
                                        }}
                                        _active={controlPickingDisabled ? undefined : { transform: 'scale(0.95)' }}
                                        sx={{ backdropFilter: 'saturate(180%) blur(4px)' }}
                                      >
                                        <Text
                                          fontSize="9px"
                                          fontWeight="800"
                                          lineHeight="1"
                                          letterSpacing="-0.3px"
                                          sx={isActive ? { filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))' } : undefined}
                                        >
                                          T+
                                        </Text>
                                        {/* Superscript number visible only when active */}
                                        {isActive && (
                                          <Box
                                            position="absolute"
                                            top="-3px"
                                            right="-3px"
                                            minW="11px"
                                            h="11px"
                                            px="2px"
                                            borderRadius="full"
                                            bg="white"
                                            color={solidColor}
                                            fontSize="8px"
                                            fontWeight="bold"
                                            lineHeight="11px"
                                            textAlign="center"
                                            boxShadow="0 1px 2px rgba(0,0,0,0.15)"
                                          >
                                            {num}
                                          </Box>
                                        )}
                                      </Box>
                                    </Tooltip>
                                  )
                                })}
                                </HStack>
                                {/* Positive-control HINT (not an auto-pick): shown on
                                    the best 1–2 candidate lanes while no témoin is set.
                                    One click designates it as T+1. Disappears once any
                                    T+ is chosen. Wording flags it as a guess to verify. */}
                                {!controlPickingDisabled
                                  && (pathologyFilter
                                    ? ![positiveControl, positiveControl2, positiveControl3].some((c) => (c as any)?.pathology_label === pathologyFilter)
                                    : positiveControlId == null && blot.id !== positiveControl2Id && blot.id !== positiveControl3Id)
                                  && controlCandidateIds.includes(blot.id) && (
                                  <Tooltip
                                    label={t('blotAnalysis.gallery.controlCandidate')}
                                    hasArrow
                                    openDelay={200}
                                  >
                                    <Box
                                      as="button"
                                      onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation()
                                        togglePositiveControl(blot.id, resolveControlSlot())
                                      }}
                                      display="inline-flex"
                                      alignItems="center"
                                      gap="3px"
                                      h="20px"
                                      pl="5px"
                                      pr="7px"
                                      borderRadius="full"
                                      bg="whiteAlpha.900"
                                      color="orange.600"
                                      border="1.5px dashed"
                                      borderColor="orange.400"
                                      boxShadow="0 1px 2px rgba(0,0,0,0.08)"
                                      transition="all 0.15s"
                                      _hover={{ bg: 'orange.400', color: 'white', borderColor: 'orange.400', transform: 'scale(1.05)' }}
                                      _active={{ transform: 'scale(0.96)' }}
                                      sx={{
                                        backdropFilter: 'saturate(180%) blur(4px)',
                                        animation: 'temoinHint 1.8s ease-in-out infinite',
                                        '@keyframes temoinHint': {
                                          '0%, 100%': { boxShadow: '0 0 0 0 rgba(251,146,60,0.5)' },
                                          '50%': { boxShadow: '0 0 0 4px rgba(251,146,60,0)' },
                                        },
                                      }}
                                    >
                                      <Icon as={MdStar} boxSize="11px" />
                                      <Text fontSize="9px" fontWeight="800" lineHeight="1" whiteSpace="nowrap">
                                        Témoin&nbsp;?
                                      </Text>
                                    </Box>
                                  </Tooltip>
                                )}
                                </>
                              )}
                            </HStack>
                              {pairInfo && (
                                <Tooltip
                                  label={t('blotAnalysis.gallery.pairTooltip', {
                                    role: pairInfo.role === 'reference' ? t('blotAnalysis.gallery.pairReference') : t('blotAnalysis.gallery.pairCompared'),
                                    partner: (pairInfo.partner as any).strip_id || `#${pairInfo.partner.id}`,
                                  })}
                                  hasArrow
                                  openDelay={200}
                                >
                                  <Text
                                    fontSize="9px"
                                    fontWeight="700"
                                    color="teal.600"
                                    bg="whiteAlpha.900"
                                    px={2}
                                    py="2px"
                                    borderRadius="full"
                                    whiteSpace="nowrap"
                                    boxShadow="0 1px 2px rgba(0,0,0,0.08)"
                                  >
                                    ↔ {(pairInfo.partner as any).strip_id || `#${pairInfo.partner.id}`}
                                  </Text>
                                </Tooltip>
                              )}
                            </HStack>
                          </Box>

                          {(() => {
                            // LA LECTURE SE FAIT SUR LA MEMBRANE, PAS À L'ÉCRAN.
                            //
                            // Pendant la saisie, montrer la bandelette invite à la
                            // relire sur le scan — et à « découvrir » en cochant une
                            // bande faible qu'on n'avait pas notée à la paillasse.
                            // L'image disparaît donc : ce qu'on transcrit vient du
                            // support, pas du pixel.
                            //
                            // L'identifiant, lui, n'est pas une source de biais : il
                            // ne porte aucune information de bande, et c'est par lui
                            // que la saisie s'attribue à la bonne bandelette. Quand
                            // l'OCR en est sûr, son texte suffit. Quand il doute, on
                            // montre la ZONE DE L'IDENTIFIANT seule — le haut de la
                            // bandelette, au-dessus de la zone de lecture — pour que
                            // l'attribution reste vérifiable sans exposer les bandes.
                            const idTrusted = ['sure', 'edited'].includes(
                              String((blot as any).strip_id_status ?? ''),
                            ) && isCompleteStripId((blot as any).strip_id)
                            if (hideIrisState && idTrusted) {
                              return (
                                <Box w="100%" py={6} px={4} textAlign="center">
                                  <Icon as={LuEyeOff} boxSize={5} color="purple.300" mb={2} />
                                  <Text fontSize="xs" color="purple.700" fontWeight="600">
                                    Bandelette masquée pendant la saisie
                                  </Text>
                                  <Text fontSize="2xs" color="gray.500" mt={1}>
                                    Reportez ce que vous avez vu sur la membrane. L’image
                                    s’affichera au verrouillage.
                                  </Text>
                                </Box>
                              )
                            }
                            if (hideIrisState) {
                              return (
                                <Box w="100%" textAlign="center">
                                  {/* La zone d'identification, à l'échelle de la bandelette.
                                      Une bandelette fait 1 de large pour 26 de
                                      haut : étalée sur toute la largeur de la
                                      carte, elle se réduisait à un filet d'une
                                      dizaine de pixels dans un cadre vide. Le
                                      cadre épouse donc la forme réelle de la
                                      bandelette, étroit et haut.

                                      La fenêtre couvre les 25 % supérieurs :
                                      mesuré sur les crops Nancy, le texte
                                      imprimé tombe entre 5 % et 25 % de la
                                      hauteur. On s'arrête là volontairement,
                                      tout ce qui suit ce sont les bandes et
                                      elles doivent rester voilées. */}
                                  <Box
                                    mx="auto"
                                    w="38px"
                                    h="240px"
                                    overflow="hidden"
                                    borderWidth="1px"
                                    borderColor="blackAlpha.200"
                                    borderRadius="sm"
                                    bg="blackAlpha.50"
                                  >
                                    <Image
                                      src={`data:image/png;base64,${blot.crop_base64}`}
                                      alt={`Identifiant de la bandelette ${blot.id}`}
                                      objectFit="cover"
                                      objectPosition="top"
                                      w="100%"
                                      h="100%"
                                    />
                                  </Box>
                                  <Text fontSize="2xs" color="orange.600" fontWeight="600" mt={1}>
                                    Identifiant incertain : zone d’identification seule
                                  </Text>
                                </Box>
                              )
                            }
                            return (
                              <Image
                                src={`data:image/png;base64,${blot.crop_base64}`}
                                alt={`Blot ${blot.id}`}
                                objectFit="contain"
                                w="100%"
                                h="100%"
                                maxH="300px"
                                p={3}
                              />
                            )
                          })()}
                        </Box>
                      </Box>

                      {/* Lecture de paillasse : les bandes vues à l'œil, sur la
                          carte de la bandelette elle-même. Le témoin en est
                          exclu — c'est un contrôle, pas un échantillon à
                          mesurer — et il se désigne juste au-dessus. */}
                      {benchReadingMode && !isControl && (
                        <Box
                          mt={3}
                          px={4}
                          py={3}
                          borderTop="1px solid"
                          borderColor="purple.100"
                          bg="purple.50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Text fontSize="2xs" fontWeight="700" color="purple.700" mb={1.5} textTransform="uppercase" letterSpacing="wide">
                            Bandes vues à l’œil
                          </Text>
                          <HStack spacing={3} wrap="wrap">
                            {cardBandNames.map((band: string) => (
                              <Checkbox
                                key={band}
                                size="sm"
                                colorScheme="purple"
                                isChecked={benchBands?.[blot.id]?.includes(band) ?? false}
                                onChange={() => onBenchBandToggle?.(blot.id, band)}
                              >
                                <Text fontSize="xs">{band}</Text>
                              </Checkbox>
                            ))}
                          </HStack>
                        </Box>
                      )}

                      <HStack
                        px={4}
                        py={3}
                        mt={3}
                        justify="space-between"
                        borderTop="1px solid"
                        borderColor="blackAlpha.100"
                      >
                        <Text fontSize="xs" fontWeight="medium" color="gray.600">
                          {t('blotAnalysis.gallery.openDetail')}
                        </Text>
                        <Icon as={MdChevronRight} color="gray.400" boxSize={4} />
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
                </React.Fragment>
              )
            })}
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Comparison Modal - Enhanced UI */}
      <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="slideInBottom">
        <ModalOverlay
          bg="blackAlpha.700"
          backdropFilter="blur(10px)"
        />
        <ModalContent
          bg="transparent"
          maxW="98vw"
          h="98vh"
          m={2}
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.5)"
        >
          {/* Glassmorphism Header — teal/cyan to match the main blot-analysis module */}
          <ModalHeader
            bgGradient="linear(135deg, teal.600 0%, teal.500 50%, cyan.400 100%)"
            backgroundSize="200% 200%"
            animation={`${gradientShift} 8s ease infinite`}
            py={3}
            px={4}
            position="sticky"
            top={0}
            zIndex={10}
            borderTopRadius="2xl"
            boxShadow="0 4px 30px rgba(20, 184, 166, 0.3)"
          >
            <VStack spacing={3} w="full">
              {/* Première ligne: Titre centré + Navigation + Boutons de contrôle */}
              <Flex w="full" align="center" justify="space-between">
                {/* Titre et badge centrés */}
                <Flex flex="1" justify="center" align="center" gap={4}>
                  <HStack spacing={3}>
                    <Box
                      p={2}
                      bg="whiteAlpha.200"
                      borderRadius="lg"
                      backdropFilter="blur(10px)"
                    >
                      <Icon as={MdLayers} color="white" boxSize={5} />
                    </Box>
                    <Heading
                      size="md"
                      color="white"
                      fontWeight="bold"
                      letterSpacing="tight"
                      textShadow="0 2px 10px rgba(0,0,0,0.3)"
                    >
                      {immunoblotConfig?.name ? pathologyDisplayName(immunoblotConfig.name) : t('blotAnalysis.comparison.blotsComparison')}
                    </Heading>
                  </HStack>

                  {selectedBlot && (() => {
                    // Paired-comparison pathologies (CIP-WB): navigate between
                    // COUPLES, not between the 2 members of one couple — see
                    // getCoupleNavInfo.
                    const nav = getCoupleNavInfo(selectedBlot)
                    return (
                    <HStack spacing={2}>
                      {/* Navigation gauche */}
                      <Tooltip label={t('blotAnalysis.comparison.previousBlot')} hasArrow>
                        <IconButton
                          aria-label={t('blotAnalysis.comparison.previousBlot')}
                          icon={<MdChevronLeft size={22} />}
                          onClick={handlePreviousBlot}
                          isDisabled={nav ? nav.coupleIndex <= 0 : selectedBlotIndex === 0}
                          bg="whiteAlpha.200"
                          color="white"
                          _hover={{ bg: 'whiteAlpha.300', transform: 'scale(1.05)' }}
                          _active={{ bg: 'whiteAlpha.400' }}
                          _disabled={{ opacity: 0.3, cursor: 'not-allowed', transform: 'none' }}
                          size="sm"
                          borderRadius="lg"
                          transition="all 0.2s ease"
                        />
                      </Tooltip>

                      {/* Badge avec numéro - avec effet pulse */}
                      <Badge
                        bg="white"
                        color="teal.700"
                        fontSize="md"
                        px={4}
                        py={1.5}
                        borderRadius="full"
                        fontWeight="bold"
                        boxShadow="0 4px 15px rgba(0,0,0,0.2)"
                        animation={`${floatUp} 3s ease-in-out infinite`}
                      >
                        {nav
                          ? `COUPLE ${nav.coupleIndex + 1} / ${nav.couples.length}`
                          : `BLOT ${selectedBlotIndex + 1} / ${displayedBlotsArray.length}`}
                      </Badge>

                      {/* Flip the couple on display. Sits with the couple counter
                          rather than next to one of the two strips: the action is
                          about the pair, and the roles are only GUESSED from the
                          printed row order — a plate mounted the other way round
                          put the differences and the verdict on the mother. */}
                      {nav && !readOnly && selectedBlot && (
                        <Tooltip label={t('blotAnalysis.comparison.swapPairedRolesHint')} hasArrow>
                          <Button
                            size="sm"
                            leftIcon={<Icon as={MdSwapHoriz} boxSize={4} />}
                            onClick={() => togglePairedRoles(selectedBlot)}
                            bg="whiteAlpha.200"
                            color="white"
                            _hover={{ bg: 'whiteAlpha.300', transform: 'scale(1.05)' }}
                            _active={{ bg: 'whiteAlpha.400' }}
                            borderRadius="lg"
                            fontWeight="bold"
                            transition="all 0.2s ease"
                          >
                            {t('blotAnalysis.comparison.swapPairedRoles')}
                          </Button>
                        </Tooltip>
                      )}

                      {/* Navigation droite */}
                      <Tooltip label={t('blotAnalysis.comparison.nextBlot')} hasArrow>
                        <IconButton
                          aria-label={t('blotAnalysis.comparison.nextBlot')}
                          icon={<MdChevronRight size={22} />}
                          onClick={handleNextBlot}
                          isDisabled={nav ? nav.coupleIndex >= nav.couples.length - 1 : selectedBlotIndex === displayedBlotsArray.length - 1}
                          bg="whiteAlpha.200"
                          color="white"
                          _hover={{ bg: 'whiteAlpha.300', transform: 'scale(1.05)' }}
                          _active={{ bg: 'whiteAlpha.400' }}
                          _disabled={{ opacity: 0.3, cursor: 'not-allowed', transform: 'none' }}
                          size="sm"
                          borderRadius="lg"
                          transition="all 0.2s ease"
                        />
                      </Tooltip>

                      {/* Biologist validation — reuses manualBlotResults (records the
                          AI verdict as validated). Only for sample blots, and only once
                          a témoin exists: with none selected, selectedBlotInterpretation
                          is still a truthy "En attente" object (interpretBlot always
                          returns one), so without this check the button stayed clickable
                          and would record a bogus NÉGATIF (r.includes('POSITIF') is false
                          on "En attente") as a confirmed, validated verdict — nothing was
                          actually compared yet. */}
                      {aiAccessible && !readOnly && selectedBlot
                        && !activeControlIds.includes(selectedBlot.id)
                        && activePositiveControl
                        && selectedBlotInterpretation && (() => {
                        const isValidated = manualBlotResults[selectedBlot.id] != null
                        // The verdict is BINARY: POSITIF or NÉGATIF. Anything that isn't
                        // POSITIF is NÉGATIF — never DOUTEUX (deprecated app-wide). This also
                        // fixes configs whose negative result reads "NEGATIF" without accent
                        // (e.g. Toxocara), which previously fell through to DOUTEUX.
                        const r = String(selectedBlotInterpretation.result).toUpperCase()
                        const aiVerdict: ManualResult = r.includes('POSITIF') ? 'POSITIF' : 'NÉGATIF'
                        return (
                          <Button
                            size="sm"
                            leftIcon={<Icon as={MdCheckCircle} boxSize={4} />}
                            onClick={() => {
                              const isValidating = !isValidated
                              const newValue = isValidated ? null : aiVerdict
                              onManualResultChange?.(selectedBlot.id, newValue)
                              // Paired-comparison pathologies (CIP-WB): the verdict is a
                              // couple-level property (mère+enfant lus comme un tout), pas
                              // par bandelette — valider l'une valide l'autre du même coup
                              // au lieu de forcer une validation séparée pour chacune.
                              if (pairInfoSelected) {
                                onManualResultChange?.(pairInfoSelected.partner.id, newValue)
                              }
                              // Chain to the next strip awaiting a verdict — only when
                              // validating (un-validating should stay put so the operator
                              // can correct what they just undid).
                              if (isValidating && !goToNextBlotToValidate(selectedBlotIndex, pairInfoSelected ? [pairInfoSelected.partner.id] : undefined)) {
                                toast({
                                  title: 'Toutes les bandelettes sont validées',
                                  status: 'success',
                                  duration: 2500,
                                  isClosable: true,
                                })
                              }
                            }}
                            bg={isValidated ? 'green.400' : 'white'}
                            color={isValidated ? 'white' : 'teal.700'}
                            _hover={{ bg: isValidated ? 'green.500' : 'green.50', transform: 'scale(1.06)' }}
                            _active={{ transform: 'scale(0.97)' }}
                            borderRadius="full"
                            fontWeight="800"
                            px={5}
                            boxShadow={isValidated ? '0 4px 16px rgba(34,197,94,0.5)' : '0 4px 15px rgba(0,0,0,0.2)'}
                            transition="all 0.2s ease"
                            animation={isValidated ? 'none' : `${floatUp} 3s ease-in-out infinite`}
                          >
                            {isValidated ? 'Validé ✓' : 'Valider'}
                          </Button>
                        )
                      })()}
                    </HStack>
                    )
                  })()}
                </Flex>

                {/* Bouton fermer */}
                <IconButton
                  aria-label={t('blotAnalysis.comparison.close')}
                  icon={<Box fontSize="xl" fontWeight="bold">×</Box>}
                  onClick={onClose}
                  bg="whiteAlpha.200"
                  color="white"
                  _hover={{ bg: 'red.500', transform: 'rotate(90deg)' }}
                  size="md"
                  borderRadius="full"
                  transition="all 0.3s ease"
                />
              </Flex>

              {/* Deuxième ligne: Boutons de contrôle — uniquement en mode AI (premium) */}
              {aiAccessible && <HStack
                data-tour="nav-tools"
                spacing={2}
                w="full"
                justify="center"
                flexWrap="wrap"
                bg="teal.700"
                borderRadius="xl"
                py={2}
                px={4}
              >
                {/* Gate on activePositiveControl, not the raw legacy `positiveControl`
                    (slot 1 only) — paired-comparison pathologies (CIP-WB) never
                    populate that legacy slot, so this toggle was invisible for them
                    even though activePositiveControl (the pair partner) is real,
                    leaving the operator stuck on whichever view was last toggled
                    with no way back. */}
                {activePositiveControl && !activeControlIds.includes(selectedBlot?.id ?? -1) && (
                  <Button
                    size="sm"
                    leftIcon={<Icon as={MdLayers} boxSize={4} />}
                    bg={superposed ? 'white' : 'whiteAlpha.200'}
                    color={superposed ? 'teal.600' : 'white'}
                    _hover={{ bg: superposed ? 'gray.100' : 'whiteAlpha.300', transform: 'translateY(-2px)' }}
                    onClick={() => setSuperposed(!superposed)}
                    borderRadius="full"
                    fontWeight="semibold"
                    transition="all 0.2s ease"
                    boxShadow={superposed ? '0 4px 15px rgba(20, 184, 166, 0.4)' : 'none'}
                  >
                    {superposed ? t('blotAnalysis.comparison.superposed') : t('blotAnalysis.comparison.superpose')}
                  </Button>
                )}

                {/* PROTIS : CONTRE QUOI compare-t-on ? Trois références, trois
                    questions — le marqueur (où est la bande : l'échelle), le
                    PBS (est-elle spécifique : le fond), les protéines totales
                    (à quoi correspond-elle : la carte de l'extrait). Chaque
                    pastille n'existe que si la plaque porte la piste. */}
                {module === 'protis' && selectedBlot && (
                  <HStack spacing={1} bg="whiteAlpha.200" px={3} py={1.5} borderRadius="full">
                    <Text fontSize="xs" fontWeight="bold" color="whiteAlpha.900">Référence :</Text>
                    {([
                      ['marker', 'Marqueur'],
                      ['pbs', 'PBS'],
                      ['total', 'Prot. totales'],
                    ] as const).map(([role, label]) => {
                      const available = protisLaneRole((selectedBlot as any).strip_id) === role
                        ? false
                        : !!protisLaneOfRole(role)
                      if (!available && protisRefRole !== role) return null
                      return (
                        <Button
                          key={role}
                          size="xs"
                          bg={protisRefRole === role ? 'green.400' : 'whiteAlpha.200'}
                          color="white"
                          _hover={{ bg: protisRefRole === role ? 'green.500' : 'whiteAlpha.300' }}
                          onClick={() => setProtisRefRole(role)}
                          borderRadius="full"
                          minW="auto"
                          px={3}
                          transition="all 0.2s ease"
                        >
                          {label}
                        </Button>
                      )
                    })}
                  </HStack>
                )}

                {/* Control selector - only show if multiple controls are defined */}
                {(positiveControl2 || positiveControl3) && selectedBlot &&
                 !activeControlIds.includes(selectedBlot.id) && (
                  <HStack spacing={1} bg="whiteAlpha.200" px={3} py={1.5} borderRadius="full">
                    <Text fontSize="xs" fontWeight="bold" color="whiteAlpha.900">{t('blotAnalysis.comparison.controlLabel')}</Text>
                    {positiveControl && (
                      <Button
                        size="xs"
                        bg={selectedControlForBlot === 1 ? 'green.400' : 'whiteAlpha.200'}
                        color="white"
                        _hover={{ bg: selectedControlForBlot === 1 ? 'green.500' : 'whiteAlpha.300' }}
                        onClick={() => setSelectedControlForBlot(1)}
                        borderRadius="full"
                        minW="auto"
                        px={3}
                        transition="all 0.2s ease"
                        position="relative"
                      >
                        T1
                        {!savedBandCalibration?.[String(positiveControl?.id)] && (
                          <Box position="absolute" top="-2px" right="-2px" w="8px" h="8px" bg="orange.400" borderRadius="full" border="1px solid white" />
                        )}
                      </Button>
                    )}
                    {positiveControl2 && (
                      <Button
                        size="xs"
                        bg={selectedControlForBlot === 2 ? 'blue.400' : 'whiteAlpha.200'}
                        color="white"
                        _hover={{ bg: selectedControlForBlot === 2 ? 'blue.500' : 'whiteAlpha.300' }}
                        onClick={() => setSelectedControlForBlot(2)}
                        borderRadius="full"
                        minW="auto"
                        px={3}
                        transition="all 0.2s ease"
                        position="relative"
                      >
                        T2
                        {!savedBandCalibration?.[String(positiveControl2?.id)] && (
                          <Box position="absolute" top="-2px" right="-2px" w="8px" h="8px" bg="orange.400" borderRadius="full" border="1px solid white" />
                        )}
                      </Button>
                    )}
                    {positiveControl3 && (
                      <Button
                        size="xs"
                        bg={selectedControlForBlot === 3 ? 'red.400' : 'whiteAlpha.200'}
                        color="white"
                        _hover={{ bg: selectedControlForBlot === 3 ? 'red.500' : 'whiteAlpha.300' }}
                        onClick={() => setSelectedControlForBlot(3)}
                        borderRadius="full"
                        minW="auto"
                        px={3}
                        transition="all 0.2s ease"
                        position="relative"
                      >
                        T3
                        {!savedBandCalibration?.[String(positiveControl3?.id)] && (
                          <Box position="absolute" top="-2px" right="-2px" w="8px" h="8px" bg="orange.400" borderRadius="full" border="1px solid white" />
                        )}
                      </Button>
                    )}
                  </HStack>
                )}

                {/* Bouton "Inverser" masqué à la demande (axes profil non inversables). */}

                <Tooltip label={effectiveBandPositions.size < 2 ? 'Calibrez au moins 2 bandes pour activer' : 'Afficher la position en kDa au survol'} hasArrow>
                  <Button
                    data-tour="tool-kda"
                    size="sm"
                    bg={showKdaScale ? 'white' : 'whiteAlpha.300'}
                    color={showKdaScale ? 'teal.700' : 'white'}
                    _hover={{ bg: showKdaScale ? 'gray.100' : 'whiteAlpha.400', transform: 'translateY(-2px)' }}
                    onClick={() => setShowKdaScale(!showKdaScale)}
                    borderRadius="full"
                    fontWeight="bold"
                    fontSize="sm"
                    transition="all 0.2s ease"
                    isDisabled={effectiveBandPositions.size < 2}
                    px={4}
                  >
                    kDa
                  </Button>
                </Tooltip>

                {/* Zoom controls — two magnifiers (− / +) + reset, act on the chart. */}
                <HStack spacing={0.5} bg="whiteAlpha.200" borderRadius="full" px={1} data-tour="zoom-tools">
                  <Tooltip label="Dézoomer" hasArrow>
                    <IconButton
                      aria-label="Dézoomer"
                      icon={<MdZoomOut size={18} />}
                      size="sm"
                      variant="ghost"
                      color="white"
                      borderRadius="full"
                      _hover={{ bg: 'whiteAlpha.400' }}
                      onClick={zoomOut}
                    />
                  </Tooltip>
                  <Tooltip label={t('blotAnalysis.comparison.resetZoom')} hasArrow>
                    <IconButton
                      aria-label={t('blotAnalysis.comparison.resetZoom')}
                      icon={<MdCenterFocusWeak size={16} />}
                      size="sm"
                      variant="ghost"
                      color="white"
                      borderRadius="full"
                      _hover={{ bg: 'whiteAlpha.400' }}
                      onClick={resetZoom}
                    />
                  </Tooltip>
                  <Tooltip label="Zoomer" hasArrow>
                    <IconButton
                      aria-label="Zoomer"
                      icon={<MdZoomIn size={18} />}
                      size="sm"
                      variant="ghost"
                      color="white"
                      borderRadius="full"
                      _hover={{ bg: 'whiteAlpha.400' }}
                      onClick={zoomIn}
                    />
                  </Tooltip>
                </HStack>

                {/* Peak Sensitivity Slider */}
                <HStack
                  data-tour="tool-sensitivity"
                  spacing={3}
                  bg="whiteAlpha.200"
                  px={4}
                  py={2}
                  borderRadius="full"
                  minW="250px"
                >
                  <Tooltip
                    label={t('blotAnalysis.comparison.sensitivityTooltip', { value: peakSensitivity })}
                    hasArrow
                    placement="top"
                  >
                    <Text fontSize="xs" fontWeight="bold" color="white" whiteSpace="nowrap">
                      {t('blotAnalysis.comparison.detectedPeaks')} {enhancedSelectedPeaks.length}
                    </Text>
                  </Tooltip>
                  <Slider
                    aria-label="Peak sensitivity"
                    value={peakSensitivity}
                    onChange={(val) => setPeakSensitivity(val)}
                    onKeyDown={(e) => {
                      // Prevent arrow keys from being captured by slider - let them navigate blots
                      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                        e.stopPropagation()
                        e.preventDefault()
                        if (e.key === 'ArrowLeft') handlePreviousBlot()
                        if (e.key === 'ArrowRight') handleNextBlot()
                      }
                    }}
                    min={0}
                    max={100}
                    step={5}
                    w="120px"
                  >
                    <SliderTrack bg="whiteAlpha.300" h="6px" borderRadius="full">
                      <SliderFilledTrack
                        bgGradient="linear(to-r, cyan.400, teal.400)"
                      />
                    </SliderTrack>
                    <SliderThumb
                      boxSize={5}
                      bg="white"
                      boxShadow="0 2px 10px rgba(0,0,0,0.3)"
                      _focus={{ boxShadow: '0 0 0 3px rgba(20, 184, 166, 0.5)' }}
                    />
                  </Slider>
                </HStack>
              </HStack>}

              {/* Calibration notice — uniquement en mode AI (profils disponibles).
                  Paired-comparison pathologies (CIP-WB) have no named bands at
                  all to calibrate — activePositiveControl is the pair partner
                  there, not a real témoin, so this prompt would otherwise
                  nonsensically offer to "calibrate" a test that has no bands. */}
              {/* Sous le voile, la modale n'a rien à montrer d'IRIS : profil
                  vide, pas de verdict. Le dire, sinon elle passe pour cassée —
                  et rappeler où se coche la lecture, puisque ce n'est pas ici. */}
              {selectedBlotVeiled && (
                <HStack
                  w="full"
                  bg="purple.400"
                  px={4}
                  py={2.5}
                  borderRadius="xl"
                  spacing={3}
                  justify="center"
                  align="center"
                >
                  <Icon as={MdInfo} boxSize={5} color="white" />
                  <Text fontSize="sm" fontWeight="medium" color="white">
                    Lecture de paillasse en cours : IRIS reste masqué sur cette bandelette.
                    Cochez les bandes vues à l’œil sur sa carte, puis verrouillez.
                  </Text>
                </HStack>
              )}

              {aiAccessible && !selectedBlotVeiled && activePositiveControl && !pairInfoSelected && effectiveBandPositions.size === 0 && selectedBlot &&
               !activeControlIds.includes(selectedBlot.id) && (
                <HStack
                  w="full"
                  bg="blue.400"
                  px={4}
                  py={2.5}
                  borderRadius="xl"
                  spacing={3}
                  justify="center"
                  align="center"
                  boxShadow="0 4px 15px rgba(59, 130, 246, 0.4)"
                  display={module === 'protis' ? 'none' : undefined}
                >
                  <Icon as={MdInfo} boxSize={5} color="white" />
                  <Text fontSize="sm" fontWeight="medium" color="white">
                    Calibrer le témoin T{selectedControlForBlot} pour comparer les ratios d'intensité (optionnel)
                  </Text>
                  <Button
                    size="sm"
                    bg="white"
                    color="blue.600"
                    fontWeight="bold"
                    _hover={{ bg: 'blue.50', transform: 'translateY(-1px)' }}
                    onClick={navigateToControlAndCalibrate}
                    borderRadius="full"
                    px={5}
                    boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                    transition="all 0.2s ease"
                    leftIcon={<Icon as={MdStar} boxSize={4} />}
                  >
                    Calibrer
                  </Button>
                </HStack>
              )}
              {/* ── Barre de progression — mode validation manuelle ────── */}
              {!aiAccessible && blotsArray.length > 0 && (() => {
                // Exclude positive controls from validation count — only sample
                // blots need a manual result. Bordeaux/Dynablot multitest: scope
                // to the active pathology filter, same as the header badge above.
                const controlIds = new Set(activeControlIds)
                const scopedBlots = pathologyFilter
                  ? blotsArray.filter((b) => (b as any).pathology_label === pathologyFilter)
                  : blotsArray
                const sampleBlots = scopedBlots.filter(b => !controlIds.has(b.id))
                const validated = sampleBlots.filter(b => manualBlotResults[b.id] != null).length
                const total = sampleBlots.length
                const pct = total > 0 ? (validated / total) * 100 : 0
                const allDone = total > 0 && validated === total
                return (
                  <Box w="full" px={2} pb={1}>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" color="whiteAlpha.900" fontWeight="semibold">
                        Validation des échantillons
                      </Text>
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="whiteAlpha.800">
                          {validated} / {total} validé{total > 1 ? 's' : ''}
                        </Text>
                        {allDone && (
                          <Badge colorScheme="green" fontSize="2xs" borderRadius="full" px={2}>
                            ✓ Complet
                          </Badge>
                        )}
                      </HStack>
                    </HStack>
                    <Progress
                      value={pct}
                      size="sm"
                      borderRadius="full"
                      colorScheme={allDone ? 'green' : pct > 50 ? 'yellow' : 'orange'}
                      bg="whiteAlpha.300"
                      hasStripe={!allDone}
                      isAnimated={!allDone}
                    />
                  </Box>
                )
              })()}
            </VStack>
          </ModalHeader>

          {/* Modal Body with gradient background */}
          <ModalBody
            pb={6}
            bg="linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)"
            overflowY="auto"
            borderBottomRadius="2xl"
          >
            {selectedBlot && (
              <ScaleFade in={true} initialScale={0.95}>
              <VStack spacing={6} align="stretch" minH="full" p={2}>

                {/* Comparison mode when positive control exists and selected blot is NOT a control.
                    Gated on activePositiveControl (not the raw legacy `positiveControl`,
                    slot 1 only): paired-comparison pathologies (CIP-WB) never populate that
                    slot, so this whole rich premium comparison UI — toolbar, superposed
                    chart, everything — was silently skipped in favour of the much simpler
                    fallback below, which is why the modal looked structurally different
                    from every other test instead of using the exact same layout. */}
                {/* PROTIS : le marqueur ouvert n'a pas de référence (il EST la
                    référence) — sans cette branche il tombait dans le repli
                    ci-dessous, qui ne dessine ni bandes posées ni cases : la
                    rediffusion exacte du piège CIP-WB décrit juste au-dessus.
                    La colonne de référence, elle, reste conditionnelle. */}
                {(() => { console.info('🧪 modale: branche', (activePositiveControl || module === 'protis') && !activeControlIds.includes(selectedBlot.id) ? 'RICHE' : 'REPLI', { blot: selectedBlot.id, strip: (selectedBlot as any).strip_id, module, ref: activePositiveControl?.id ?? null, controls: activeControlIds }); return null })()}
                {(activePositiveControl || module === 'protis') &&
                 !activeControlIds.includes(selectedBlot.id) ? (
                  <Flex
                    gap={8}
                    align="start"
                    justify="center"
                  >
                    {/* Left: Blots side by side — top-aligned so image cards line up.
                        Paired-comparison pathologies (CIP-WB): the mother (M) card is
                        always rendered second (right) in the JSX below whenever the
                        opened blot is itself the mother — its partner (the child, E)
                        takes the first/"control" slot instead. row-reverse flips the
                        VISUAL order back to M-always-left without touching either
                        card's own content or bindings. */}
                    <HStack spacing={5} align="start" flexDirection={pairInfoSelected?.role === 'reference' ? 'row-reverse' : 'row'}>
                      {activePositiveControl && (
                        <VStack spacing={3}>
                          {/* Explicit-height spacer matching the verdict pill (Chakra Badge with
                              fontSize=sm + py=1.5 renders at ~32px). Plain Box with fixed h: the
                              VStack treats it as a flex item with that exact height — no Badge
                              inline-block quirks to fight with.
                              Paired-comparison pathologies (CIP-WB): the verdict is clinically
                              about the CHILD specifically (synthèse propre d'anticorps) — when
                              the child ends up on this card (opened the mother directly, its
                              partner here is the child), show the real pill instead of a blank
                              spacer, and hide the one on the other card (see below) so it isn't
                              shown twice. */}
                          {pairInfoSelected?.role === 'reference' ? (
                            aiAccessible && !selectedBlotVeiled && selectedBlotInterpretation && (
                              <HStack h="32px" spacing={2} align="center" justify="center">
                                <Badge
                                  colorScheme={selectedBlotInterpretation!.color}
                                  fontSize="sm"
                                  px={3}
                                  py={1.5}
                                  borderRadius="full"
                                  fontWeight="bold"
                                  animation={selectedBlotInterpretation!.color === 'green' ? `${pulseGlow} 2s ease-in-out infinite` : 'none'}
                                >
                                  {selectedBlotInterpretation!.result}
                                </Badge>
                              </HStack>
                            )
                          ) : (
                            aiAccessible && selectedBlotInterpretation && (
                              <Box h="32px" w="1px" aria-hidden="true" />
                            )
                          )}
                          {/* Control Label with gradient */}
                          <Box
                            px={4}
                            py={1.5}
                            borderRadius="full"
                            bgGradient={pairInfoSelected
                              ? (pairInfoSelected.role === 'reference' ? 'linear(to-r, purple.500, pink.500)' : 'linear(to-r, green.400, green.500)')
                              : ((module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'linear(to-r, green.400, green.500)' :
                                 activePositiveControl.id === effectiveControlId(2) ? 'linear(to-r, blue.400, blue.500)' : 'linear(to-r, red.400, red.500)')
                            }
                            boxShadow="0 4px 15px rgba(0,0,0,0.1)"
                          >
                            <Text fontSize="xs" fontWeight="bold" color="white">
                              {pairInfoSelected
                                ? `${pairInfoSelected.role === 'reference' ? 'E' : 'M'} - ${blotLabel(activePositiveControl)}`
                                : module === 'protis'
                                  ? `${protisRefWord} - ${blotLabel(activePositiveControl)}`
                                  : t('blotAnalysis.comparison.controlNBlot', {
                                    number: activePositiveControl.id === effectiveControlId(1) ? 1 :
                                      activePositiveControl.id === effectiveControlId(2) ? 2 : 3,
                                    id: activePositiveControl.id
                                  })}
                            </Text>
                          </Box>


                          {/* Control Image Card with glow effect — width pinned to the inner
                              image box so band markers align with the strip. */}
                          <Box position="relative" w="190px">
                            <Box
                              borderRadius="xl"
                              overflow="hidden"
                              border="3px solid"
                              borderColor={pairInfoSelected
                                ? (pairInfoSelected.role === 'reference' ? 'purple.400' : 'green.400')
                                : ((module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'green.400' :
                                   activePositiveControl.id === effectiveControlId(2) ? 'blue.400' : 'red.400')
                              }
                              bg="white"
                              w="190px"
                              h="500px"
                              boxShadow={`0 10px 40px ${pairInfoSelected
                                ? (pairInfoSelected.role === 'reference' ? 'rgba(128, 90, 213, 0.25)' : 'rgba(34, 197, 94, 0.25)')
                                : ((module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'rgba(34, 197, 94, 0.25)' :
                                   activePositiveControl.id === effectiveControlId(2) ? 'rgba(59, 130, 246, 0.25)' : 'rgba(239, 68, 68, 0.25)')
                              }`}
                              transition="all 0.3s ease"
                              cursor="crosshair"
                              onClick={() => setShowRawControlImage(!showRawControlImage)}
                              onMouseMove={(e: React.MouseEvent<HTMLDivElement>) =>
                                handleImageMouseMove(e, activePositiveControl, controlImageBounds)
                              }
                              onMouseLeave={handleImageMouseLeave}
                            >
                              {(showRawControlImage ? activePositiveControl.crop_base64 : displayedControlImage) && (
                                <img
                                  key={imageRenderKey}
                                  ref={controlImageRef}
                                  src={showRawControlImage ? `data:image/png;base64,${activePositiveControl.crop_base64}` : displayedControlImage!}
                                  alt={module === 'protis' ? `${protisRefWord} - ${blotLabel(activePositiveControl)}` : t('blotAnalysis.comparison.controlBlotAlt', { id: activePositiveControl.id })}
                                  style={{
                                    objectFit: 'contain',
                                    width: '100%',
                                    height: '100%',
                                    display: 'block',
                                    pointerEvents: 'none',
                                  }}
                                />
                              )}
                            </Box>
                            <Badge
                              position="absolute"
                              bottom={2}
                              right={2}
                              bg={showRawControlImage ? 'orange.500' : 'green.500'}
                              color="white"
                              fontSize="9px"
                              px={2}
                              py={0.5}
                              borderRadius="full"
                              opacity={0.85}
                              cursor="pointer"
                              onClick={() => setShowRawControlImage(!showRawControlImage)}
                            >
                              {showRawControlImage ? 'RAW' : 'REC'}
                            </Badge>

                            {/* Manufacturer control-line marker — same yellow dashed line as
                                the raw-image overlay, so the alignment/kDa anchor is visible
                                directly on the strip here too, not just on the source image. */}
                            {module === 'iris' && activePositiveControl.profile?.length > 0 && (() => {
                              const ctrlPos = getControlLine(activePositiveControl)
                              if (ctrlPos <= 0) return null
                              const yFrac = ctrlPos / activePositiveControl.profile.length
                              const top = controlImageBounds.topPct + yFrac * controlImageBounds.heightPct
                              const isDraggingThis = draggingControlLine?.blotId === activePositiveControl.id
                              return (
                                <React.Fragment key="control-line-marker">
                                  <Box
                                    position="absolute"
                                    left={`${controlImageBounds.leftPct}%`}
                                    width={`${controlImageBounds.widthPct}%`}
                                    top={`${top}%`}
                                    h="0"
                                    borderTop={isDraggingThis ? '2.5px dashed #f59e0b' : '2px dashed #fbbf24'}
                                    pointerEvents="none"
                                    zIndex={16}
                                  />
                                  {!readOnly && (
                                    <Box
                                      position="absolute"
                                      left={`${controlImageBounds.leftPct}%`}
                                      width={`${controlImageBounds.widthPct}%`}
                                      top={`${top}%`}
                                      h="14px"
                                      mt="-7px"
                                      cursor="row-resize"
                                      zIndex={17}
                                      title={t('blotAnalysis.comparison.dragControlLine', 'Glisser pour repositionner la ligne de contrôle')}
                                      onPointerDown={(e) => handleControlLineDragStart(e, activePositiveControl, controlImageBounds)}
                                      onPointerMove={handleControlLineDragMove}
                                      onPointerUp={handleControlLineDragEnd}
                                    />
                                  )}
                                </React.Fragment>
                              )
                            })()}

                            {/* ── Calibrated/suggested bands overlay on control image —
                                Control positions are in the control's raw profile space.
                                Positions adjusted via controlImageBounds to land on the
                                image content (not the letterboxed container). Gated by the
                                "Bandes" toggle to mirror the sample-side overlay. */}
                            {aiAccessible && showImageBands && displayedBandPositions.size > 0 && activePositiveControl.profile.length > 0 && (() => {
                              const profileLen = activePositiveControl.profile.length
                              const raw = Array.from(displayedBandPositions.entries()).map(([bandName, data]) => {
                                const yFrac = Math.max(0, Math.min(1, data.position / profileLen))
                                return { name: bandName, top: controlImageBounds.topPct + yFrac * controlImageBounds.heightPct }
                              })
                              const sides = assignLabelSides(raw)
                              const leftEdge = controlImageBounds.leftPct
                              const rightEdge = controlImageBounds.leftPct + controlImageBounds.widthPct
                              return raw.map(({ name: bandName, top }) => {
                                const bandConfig = immunoblotConfig?.bands?.specific?.find((b: any) => b.name === bandName)
                                const isRequired = bandConfig?.required ?? false
                                // Control strip → all bands are témoin bands → green.
                                // Required stays a bold "*" accent, not a colour (unified code).
                                const color = 'green.500'
                                const onLeft = sides.get(bandName) === 'left'
                                return (
                                  <Box
                                    key={`ctrl-${bandName}`}
                                    position="absolute"
                                    top={`${top}%`}
                                    {...(onLeft ? { right: `${100 - leftEdge}%` } : { left: `${rightEdge}%` })}
                                    transform="translateY(-50%)"
                                    display="flex"
                                    flexDirection={onLeft ? 'row-reverse' : 'row'}
                                    alignItems="center"
                                    pointerEvents="none"
                                    zIndex={7}
                                  >
                                    {/* Tick touches the strip edge; name sits just outside. */}
                                    <Box w="12px" h="2px" flexShrink={0} bg={color} opacity={0.9} />
                                    <Box bg={color} color="white" fontSize="8px" fontWeight="bold" px={1} py={0.5} borderRadius="sm" whiteSpace="nowrap">
                                      {bandName}{isRequired ? '*' : ''}
                                    </Box>
                                  </Box>
                                )
                              })
                            })()}

                            {/* CIP-WB paired comparison: difference markers on the control
                                image, for the case where the CHILD is sitting in the control
                                slot — i.e. selectedBlot itself is the mother (opened directly,
                                or landed on via the OLD couple-nav before it was fixed to land
                                on the child). The sample-side twin of this block only fires
                                when selectedBlot IS the child; this is its mirror for when the
                                child is the partner instead. interpretBlot resolves reference/
                                compared from pairedComparisonPartners internally, so calling it
                                on activePositiveControl here (rather than selectedBlot) still
                                returns the child's own differences, not the mother's (always 0). */}
                            {pairInfoSelected?.role === 'reference' && (() => {
                              const childInterpretation = interpretBlot(activePositiveControl)
                              const diffBands = childInterpretation?.identifiedBands ?? []
                              const profileLen = activePositiveControl.profile.length
                              if (diffBands.length === 0 || profileLen <= 0) return null
                              const diffTopPct = (d: { position: number }) =>
                                controlImageBounds.topPct + Math.max(0, Math.min(1, d.position / profileLen)) * controlImageBounds.heightPct
                              const diffSides = assignLabelSides(diffBands.map((d, i) => ({ name: String(i), top: diffTopPct(d) })))
                              const rightEdge = controlImageBounds.leftPct + controlImageBounds.widthPct
                              const leftEdge = controlImageBounds.leftPct
                              return diffBands.map((d, i) => {
                                const top = diffTopPct(d)
                                const onLeft = diffSides.get(String(i)) === 'left'
                                return (
                                  <Box
                                    key={`ctrl-diff-${i}`}
                                    position="absolute"
                                    {...(onLeft ? { right: `${100 - leftEdge}%` } : { left: `${rightEdge}%` })}
                                    top={`${top}%`}
                                    transform="translateY(-50%)"
                                    display="flex"
                                    flexDirection={onLeft ? 'row-reverse' : 'row'}
                                    alignItems="center"
                                    pointerEvents="none"
                                    zIndex={7}
                                  >
                                    <Box w="10px" h="2px" flexShrink={0} bg="purple.500" opacity={0.9} />
                                    <Box
                                      bg="purple.500"
                                      color="white"
                                      fontSize="8px"
                                      fontWeight="bold"
                                      px={1.5}
                                      py={0.5}
                                      borderRadius="sm"
                                      whiteSpace="nowrap"
                                    >
                                      +~{d.estimatedMW} kDa
                                    </Box>
                                  </Box>
                                )
                              })
                            })()}

                            {/* Hover cursor line on control image — adjusted to image content bounds */}
                            {hoverState && (() => {
                              const cursorTopPct = controlImageBounds.topPct + hoverState.relFraction * controlImageBounds.heightPct
                              return (
                                <>
                                  <Box
                                    position="absolute"
                                    left={`${controlImageBounds.leftPct}%`}
                                    top={`${cursorTopPct}%`}
                                    w={`${controlImageBounds.widthPct}%`}
                                    h="2px"
                                    bg="yellow.400"
                                    opacity={0.85}
                                    pointerEvents="none"
                                    zIndex={5}
                                    style={{ transform: 'translateY(-50%)' }}
                                  />
                                  <Box
                                    position="absolute"
                                    // Anchored to the LEFT of the strip so it never covers the band.
                                    right={`${100 - controlImageBounds.leftPct + 1}%`}
                                    top={`${Math.min(controlImageBounds.topPct + controlImageBounds.heightPct - 4, Math.max(controlImageBounds.topPct + 2, cursorTopPct))}%`}
                                    bg="yellow.900"
                                    color="yellow.100"
                                    fontSize="9px"
                                    px={1.5}
                                    py={0.5}
                                    borderRadius="md"
                                    pointerEvents="none"
                                    zIndex={6}
                                    whiteSpace="nowrap"
                                    style={{ transform: 'translateY(-50%)' }}
                                  >
                                    {Math.round(hoverState.relFraction * activePositiveControl.profile.length)} px
                                    {showKdaScale && computeKda(hoverState.chartX) !== null && (
                                      <> · {computeKda(hoverState.chartX)} kDa</>
                                    )}
                                  </Box>
                                </>
                              )
                            })()}
                          </Box>
                        </VStack>
                      )}

                      <VStack spacing={3}>
                        {/* Verdict pill — wrapped in a fixed-height Box that matches the control
                            column's spacer EXACTLY so the two image cards line up.
                            Paired-comparison pathologies (CIP-WB): the verdict is clinically
                            about the CHILD — when selectedBlot is currently the mother (opened
                            directly), the pill already shows on the control card above instead
                            (where the child sits), so it's skipped here to avoid showing twice. */}
                        {(pairInfoSelected?.role === 'reference' || module === 'protis') ? (
                          /* PROTIS : pas de verdict, mais la cale reste — c'est
                             elle qui aligne les deux cartes d'images. */
                          <Box h="32px" w="1px" aria-hidden="true" />
                        ) : aiAccessible && !selectedBlotVeiled && (
                          <HStack h="32px" spacing={2} align="center" justify="center">
                            <Badge
                              colorScheme={selectedBlotInterpretation!.color}
                              fontSize="sm"
                              px={3}
                              py={1.5}
                              borderRadius="full"
                              fontWeight="bold"
                              animation={selectedBlotInterpretation!.color === 'green' ? `${pulseGlow} 2s ease-in-out infinite` : 'none'}
                            >
                              {selectedBlotInterpretation!.result}
                            </Badge>
                            {/* Borderline hint — NOT a verdict. Flags strips that
                                nearly met the positive criteria for a manual check. */}
                            {(selectedBlotInterpretation as any).needsReview && manualBlotResults[selectedBlot!.id] == null && (
                              <Tooltip label={t('blotAnalysis.clinical.borderlineTooltip')} hasArrow placement="top">
                                <Badge bgGradient="linear(to-r, orange.400, red.500)" color="white" fontSize="xs" px={3} py={1} borderRadius="full" fontWeight="bold" cursor="help" whiteSpace="nowrap" animation={`${pulseWarn} 1.8s ease-in-out infinite`}>
                                  ⚠ {t('blotAnalysis.clinical.toCheck')}
                                </Badge>
                              </Tooltip>
                            )}
                            {/* La cotation semi-quantitative vit en ruban sur le bord haut de
                                la carte image de l'échantillon (voir plus bas) — pas ici, la
                                rangée du verdict débordait à trois badges. */}
                            {/* Species name inline, right of the result pill */}
                            {renderSpeciesDiagnosis()}
                          </HStack>
                        )}

                        {/* Sample Label with gradient */}
                        <Box
                          px={4}
                          py={1.5}
                          borderRadius="full"
                          bgGradient={pairInfoSelected?.role === 'reference' || isProtisMarkerSelected ? 'linear(to-r, green.400, green.500)' : 'linear(to-r, purple.500, pink.500)'}
                          boxShadow={pairInfoSelected?.role === 'reference' || isProtisMarkerSelected ? '0 4px 15px rgba(34, 197, 94, 0.3)' : '0 4px 15px rgba(128, 90, 213, 0.3)'}
                        >
                          <Text fontSize="xs" fontWeight="bold" color="white">
                            {pairInfoSelected
                              ? `${pairInfoSelected.role === 'reference' ? 'M' : 'E'} - ${blotLabel(blotsArray.find((b) => b.id === selectedBlot.id) ?? selectedBlot)}`
                              : isProtisMarkerSelected
                                ? `Marqueur - ${blotLabel(blotsArray.find((b) => b.id === selectedBlot.id) ?? selectedBlot)}`
                                : t('blotAnalysis.comparison.sampleBlotLabel', { id: blotLabel(blotsArray.find((b) => b.id === selectedBlot.id) ?? selectedBlot) })}
                          </Text>
                        </Box>

                        {/* Inline CTA — set this blot as positive control without leaving the
                            modal. Shown when no control is set yet for the RELEVANT scope: this
                            pathology's own slot while filtered (Bordeaux/Dynablot multitest — see
                            resolveControlSlot), otherwise all 3 shared slots, same as before.
                            Paired-comparison pathologies (CIP-WB) never have a témoin at all —
                            the pair partner already fills that role automatically, so this CTA
                            would always show (no slot is ever populated) and makes no sense. */}
                        {module === 'iris' && !pairInfoSelected && (pathologyFilter
                          ? ![positiveControl, positiveControl2, positiveControl3].some((c) => (c as any)?.pathology_label === pathologyFilter)
                          : !positiveControlId && !positiveControl2Id && !positiveControl3Id) && (
                          <Button
                            size="sm"
                            leftIcon={<Icon as={MdStar} boxSize={4} />}
                            bgGradient="linear(to-r, green.400, green.500)"
                            color="white"
                            fontWeight="bold"
                            borderRadius="full"
                            px={5}
                            boxShadow="0 4px 12px rgba(34, 197, 94, 0.35)"
                            _hover={{ bgGradient: 'linear(to-r, green.500, green.600)', transform: 'translateY(-1px)' }}
                            transition="all 0.2s ease"
                            isDisabled={readOnly}
                            onClick={(e) => { e.stopPropagation(); togglePositiveControl(selectedBlot.id, resolveControlSlot()) }}
                          >
                            {t('blotAnalysis.comparison.setAsPositiveControl', 'Définir comme témoin positif')}
                          </Button>
                        )}

                        {/* Sample Image Card with glow — width pinned to the inner image box
                            so the band markers (positioned in % of this box) align with the
                            strip content instead of a wider stretched block. */}
                        <Box position="relative" w="190px">
                          {/* Cotation semi-quantitative (Oliva 2015) — cas Bordeaux seulement.
                              En RUBAN centré à cheval sur le bord BAS de la carte image :
                              position absolue, donc AUCUN décalage de layout (la cale qui
                              aligne les deux colonnes d'images reste intacte). Justification
                              complète au survol (semiQuantTooltip), ⓘ pour l'inviter. */}
                          {(selectedBlotInterpretation as any)?.semiQuant && !activeControlIds.includes(selectedBlot!.id) && !selectedBlotVeiled && (
                            <Box position="absolute" bottom="2px" left="calc(100% + 8px)" zIndex={9}>
                              <Tooltip label={semiQuantTooltip((selectedBlotInterpretation as any).semiQuant)} hasArrow placement="right" maxW="340px">
                                <Badge
                                  // La livrée VIOLETTE de l'échantillon (même dégradé que sa
                                  // pastille d'identité), fort ou faible — la force se lit
                                  // dans le libellé et le I, pas dans la couleur.
                                  bgGradient="linear(to-r, purple.500, pink.500)"
                                  color="white"
                                  fontSize="xs"
                                  px={3}
                                  py={1}
                                  borderRadius="full"
                                  fontWeight="bold"
                                  cursor="help"
                                  whiteSpace="nowrap"
                                  boxShadow="0 2px 10px rgba(0,0,0,0.25)"
                                >
                                  {(selectedBlotInterpretation as any).semiQuant.label} · I={(selectedBlotInterpretation as any).semiQuant.I} ⓘ
                                </Badge>
                              </Tooltip>
                            </Box>
                          )}
                          <Box
                            borderRadius="xl"
                            overflow="hidden"
                            border="3px solid"
                            borderColor={pairInfoSelected?.role === 'reference' || isProtisMarkerSelected ? 'green.400' : 'purple.400'}
                            bg="white"
                            w="190px"
                            h="500px"
                            boxShadow={pairInfoSelected?.role === 'reference' || isProtisMarkerSelected ? '0 10px 40px rgba(34, 197, 94, 0.25)' : '0 10px 40px rgba(128, 90, 213, 0.25)'}
                            transition="all 0.3s ease"
                            cursor="crosshair"
                            onClick={handleSampleImageClickModal}
                            onMouseMove={(e: React.MouseEvent<HTMLDivElement>) =>
                              handleImageMouseMove(e, selectedBlot, sampleImageBounds)
                            }
                            onMouseLeave={handleImageMouseLeave}
                          >
                            <Image
                              ref={sampleImageRef}
                              src={`data:image/png;base64,${showRawImage ? selectedBlot.crop_base64 : selectedBlot.reconstructed_base64}`}
                              alt={`Blot ${selectedBlot.id}`}
                              objectFit="contain"
                              w="100%"
                              h="100%"
                              pointerEvents="none"
                              // Le voile vaut aussi en grand. L'élément reste en
                              // place — les repères de placement mesurent ses bords —
                              // mais la bandelette n'y est plus lisible : la saisie se
                              // reporte depuis la membrane, pas depuis le scan.
                              filter={selectedBlotVeiled ? 'blur(16px)' : undefined}
                            />
                          </Box>
                          <Badge
                            position="absolute"
                            bottom={2}
                            right={2}
                            bg={showRawImage ? 'orange.500' : 'purple.500'}
                            color="white"
                            fontSize="9px"
                            px={2}
                            py={0.5}
                            borderRadius="full"
                            opacity={0.85}
                            cursor="pointer"
                            zIndex={10}
                            onClick={(e) => { e.stopPropagation(); setShowRawImage(v => !v) }}
                          >
                            {showRawImage ? 'RAW' : 'REC'}
                          </Badge>

                          {/* Manufacturer control-line marker — same yellow dashed line as the
                              control image above and the raw-image overlay. */}
                          {module === 'iris' && selectedBlot.profile?.length > 0 && (() => {
                            const ctrlPos = getControlLine(selectedBlot)
                            if (ctrlPos <= 0) return null
                            const yFrac = ctrlPos / selectedBlot.profile.length
                            const top = sampleImageBounds.topPct + yFrac * sampleImageBounds.heightPct
                            const isDraggingThis = draggingControlLine?.blotId === selectedBlot.id
                            return (
                              <React.Fragment key="control-line-marker">
                                <Box
                                  position="absolute"
                                  left={`${sampleImageBounds.leftPct}%`}
                                  width={`${sampleImageBounds.widthPct}%`}
                                  top={`${top}%`}
                                  h="0"
                                  borderTop={isDraggingThis ? '2.5px dashed #f59e0b' : '2px dashed #fbbf24'}
                                  pointerEvents="none"
                                  zIndex={16}
                                />
                                {!readOnly && (
                                  <Box
                                    position="absolute"
                                    left={`${sampleImageBounds.leftPct}%`}
                                    width={`${sampleImageBounds.widthPct}%`}
                                    top={`${top}%`}
                                    h="14px"
                                    mt="-7px"
                                    cursor="row-resize"
                                    zIndex={17}
                                    title={t('blotAnalysis.comparison.dragControlLine', 'Glisser pour repositionner la ligne de contrôle')}
                                    onPointerDown={(e) => handleControlLineDragStart(e, selectedBlot, sampleImageBounds)}
                                    onPointerMove={handleControlLineDragMove}
                                    onPointerUp={handleControlLineDragEnd}
                                  />
                                )}
                              </React.Fragment>
                            )
                          })()}

                          {/* ── Unified band-label overlay on the sample image ──
                              Draggable + renameable markers (drag = updateBandYFraction, double-
                              click = rename) for the AI-identified bands and the biologist's
                              manually-placed bands, all flowing through buildPlacedBandsRecord for
                              the PDF export. A single assignLabelSides pass spreads labels left/
                              right of the strip so they never overlap (as on the control image). */}
                          {(() => {
                            // Unified sample-strip label overlay: gather the AI-identified bands
                            // AND the biologist's manually-placed bands into ONE set, then run
                            // assignLabelSides once so every label is spread left/right of the
                            // strip and none overlap (same treatment as the control image).
                            // Manual placement (blotPlacedBands) overrides the AI-projected Y.
                            const profileLen = selectedBlot.profile.length
                            const placed = blotPlacedBands.get(selectedBlot.id)
                            const entries = new Map<string, number>() // bandName -> topPct
                            const showAiBands = aiAccessible && showImageBands && !!selectedBlotInterpretation?.identifiedBands && effectiveBandPositions.size > 0 && !!activePositiveControl && profileLen > 0
                            if (showAiBands) {
                              (selectedBlotInterpretation?.identifiedBands ?? [])
                                .filter(b => b.matchedBand !== null)
                                .forEach(b => {
                                  const name = b.matchedBand!
                                  const placedY = placed?.get(name)
                                  const yFrac = Math.max(0, Math.min(1, b.position / profileLen))
                                  entries.set(name, placedY != null ? placedY * 100 : sampleImageBounds.topPct + yFrac * sampleImageBounds.heightPct)
                                })
                            }
                            // Manually-placed bands render in BOTH modes and override position.
                            if (placed) placed.forEach((yFrac, name) => entries.set(name, yFrac * 100))
                            // Drop bands the biologist deleted so no marker lingers on the image.
                            suppressedBands.get(selectedBlot.id)?.forEach(name => entries.delete(name))
                            if (entries.size === 0) return null
                            const sampleSides = assignLabelSides(Array.from(entries.entries()).map(([name, top]) => ({ name, top })))
                            const sampleRightEdge = sampleImageBounds.leftPct + sampleImageBounds.widthPct
                            const sampleLeftEdge = sampleImageBounds.leftPct
                            return Array.from(entries.entries()).map(([bandName, topPct]) => {
                              const placedY = placed?.get(bandName)
                              // Unified colour code (= gallery + PDF): blue = a band the
                              // biologist placed / renamed / added by hand (it lives in
                              // blotPlacedBands), purple = a purely auto-detected band left
                              // untouched. Keyed on the placement, NOT the band NAME — an
                              // added P40 must stay blue even when the auto-detector also has
                              // a "P40" elsewhere. Required stays a bold "*", not a colour.
                              const isHandAdded = placedY != null
                              const markerScheme = isHandAdded ? 'blue' : 'purple'
                              const onLeft = sampleSides.get(bandName) === 'left'
                              const bandConfig = immunoblotConfig?.bands?.specific?.find((b: any) => b.name === bandName)
                              const isRequired = bandConfig?.required ?? false
                              const displayLabel = getBandDisplayName(selectedBlot.id, bandName)
                              const isDragging = draggingBand?.blotId === selectedBlot.id && draggingBand?.bandName === bandName
                              const isRenaming = renamingBand?.blotId === selectedBlot.id && renamingBand?.original === bandName
                              const handleDragStart = (e: React.PointerEvent) => {
                                if (readOnly) return
                                e.stopPropagation()
                                e.preventDefault()
                                // Don't seed blotPlacedBands yet — a plain click (pointerdown +
                                // pointerup with no movement, e.g. opening the band dropdown) must
                                // never turn an untouched auto-detected band blue. Only record the
                                // gesture's origin; the effect below seeds it lazily on the FIRST
                                // real move past a small threshold — mirrors the box-drag pattern
                                // in index.tsx (moved only becomes true past ~1-3px of travel).
                                bandDragRef.current = {
                                  blotId: selectedBlot.id,
                                  bandName,
                                  startClientY: e.clientY,
                                  initialTopPct: topPct,
                                  moved: false,
                                }
                                setDraggingBand({ blotId: selectedBlot.id, bandName })
                              }
                              const startRename = () => {
                                if (readOnly) return
                                setRenameDraft(displayLabel)
                                setRenamingBand({ blotId: selectedBlot.id, original: bandName })
                              }
                              return (
                                <React.Fragment key={`ai-${bandName}`}>
                                  {/* Marker: a short tick + the band name grouped together at
                                      the RIGHT edge (reads "─P30"). The rest of the strip stays
                                      clear so the band under it is visible. */}
                                  {isRenaming && (
                                    <Box
                                      position="absolute"
                                      {...(onLeft ? { right: `${100 - sampleLeftEdge}%`, mr: 2 } : { left: `${sampleRightEdge}%`, ml: 2 })}
                                      top={`${topPct}%`}
                                      transform="translateY(-50%)"
                                      zIndex={9}
                                      w="180px"
                                      bg="white"
                                      border="1px solid"
                                      borderColor="purple.100"
                                      borderRadius="xl"
                                      boxShadow="0 12px 32px -8px rgba(76, 29, 149, 0.35)"
                                      p={2}
                                    >
                                      <Text px={1} pb={2} fontSize="2xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="purple.500">
                                        {module === 'protis' ? 'Renommer' : 'Réaffecter'} {bandName}
                                      </Text>
                                      {module === 'protis' ? (
                                        /* Mode libre : pas de catalogue à proposer — le
                                           nouveau nom se tape, comme à la pose. Sans ce
                                           garde, la liste affichait les bandes du dernier
                                           catalogue IRIS chargé (P30, P31…), qui n'ont
                                           aucun sens sur un gel de recherche. */
                                        <form onSubmit={(e) => {
                                          e.preventDefault()
                                          const next = renameDraft.trim()
                                          if (!next || next === bandName) { setRenamingBand(null); return }
                                          const taken = new Set<string>([
                                            ...selectedBlotIdentifiedBands,
                                            ...Array.from(blotPlacedBands.get(selectedBlot.id)?.keys() ?? []),
                                          ])
                                          taken.delete(bandName)
                                          if (taken.has(next)) return
                                          applyBandRename(selectedBlot.id, bandName, next, placedY ?? (topPct / 100))
                                          setRenamingBand(null)
                                        }}>
                                          <HStack spacing={1.5} px={1}>
                                            <Input
                                              size="xs"
                                              autoFocus
                                              value={renameDraft}
                                              onChange={(e) => setRenameDraft(e.target.value)}
                                              borderRadius="md"
                                            />
                                            <Button size="xs" type="submit" colorScheme="purple" isDisabled={!renameDraft.trim()}>
                                              OK
                                            </Button>
                                          </HStack>
                                        </form>
                                      ) : (
                                      <VStack spacing={0.5} align="stretch" maxH="260px" overflowY="auto">
                                        {BAND_NAMES.map((n) => (
                                          <Button
                                            key={n}
                                            size="xs"
                                            variant={n === renameDraft ? 'solid' : 'ghost'}
                                            colorScheme="purple"
                                            justifyContent="flex-start"
                                            fontWeight={n === renameDraft ? '700' : '500'}
                                            onClick={() => {
                                              applyBandRename(selectedBlot.id, bandName, n, placedY ?? (topPct / 100))
                                              setRenamingBand(null)
                                            }}
                                          >
                                            {REQUIRED_BAND_NAMES.includes(n) ? '★ ' : ''}{n}
                                          </Button>
                                        ))}
                                      </VStack>
                                      )}
                                      <Box h="1px" bg="gray.100" my={2} />
                                      <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        w="full"
                                        justifyContent="flex-start"
                                        onClick={() => {
                                          suppressBand(selectedBlot.id, bandName)
                                          setRenamingBand(null)
                                        }}
                                      >
                                        Supprimer la bande
                                      </Button>
                                      <Button
                                        size="xs"
                                        variant="ghost"
                                        w="full"
                                        justifyContent="flex-start"
                                        color="gray.500"
                                        onClick={() => setRenamingBand(null)}
                                      >
                                        Fermer
                                      </Button>
                                    </Box>
                                  )}
                                    <Tooltip label="Double-clic pour réaffecter ou supprimer la bande · glisser pour repositionner" placement="left" hasArrow openDelay={400} isDisabled={isDragging}>
                                      <Box
                                        position="absolute"
                                        {...(onLeft ? { right: `${100 - sampleLeftEdge}%` } : { left: `${sampleRightEdge}%` })}
                                        top={`${topPct}%`}
                                        transform="translateY(-50%)"
                                        display="flex"
                                        flexDirection={onLeft ? 'row-reverse' : 'row'}
                                        alignItems="center"
                                        cursor="grab"
                                        zIndex={7}
                                        sx={{ touchAction: 'none' }}
                                        onPointerDown={handleDragStart}
                                        onDoubleClick={(e) => { e.stopPropagation(); startRename() }}
                                      >
                                        {/* Tick overlaps the last 18px of the strip so it touches
                                            the band; the name sits just outside the right edge. */}
                                        <Box
                                          w="10px"
                                          h={isDragging ? '3px' : '2px'}
                                          flexShrink={0}
                                          bg={`${markerScheme}.500`}
                                          opacity={isDragging ? 1 : 0.9}
                                          boxShadow={isDragging ? `0 0 8px ${isHandAdded ? 'rgba(0,80,180,0.7)' : 'rgba(128,90,213,0.7)'}` : 'none'}
                                        />
                                        <Box
                                          bg={`${markerScheme}.500`}
                                          color="white"
                                          fontSize="8px"
                                          fontWeight="bold"
                                          px={1.5}
                                          py={0.5}
                                          borderRadius="sm"
                                          whiteSpace="nowrap"
                                          _hover={{ boxShadow: 'sm' }}
                                        >
                                          {displayLabel}{isRequired ? '*' : ''}
                                        </Box>
                                      </Box>
                                    </Tooltip>

                                </React.Fragment>
                              )
                            })
                          })()}

                          {/* CIP-WB paired comparison: difference markers on the CHILD's image
                              only, same tick+label convention as the AI band overlay above, but
                              driven by the diff detector's raw peak positions (not a témoin-matched
                              band name) since paired comparison has no calibration to project named
                              bands from — showAiBands above is always false for it. */}
                          {(() => {
                            if (pairInfoSelected?.role !== 'compared') return null
                            const diffBands = selectedBlotInterpretation?.identifiedBands ?? []
                            const profileLen = selectedBlot.profile.length
                            if (diffBands.length === 0 || profileLen <= 0) return null
                            const diffTopPct = (d: { position: number }) =>
                              sampleImageBounds.topPct + Math.max(0, Math.min(1, d.position / profileLen)) * sampleImageBounds.heightPct
                            const diffSides = assignLabelSides(diffBands.map((d, i) => ({ name: String(i), top: diffTopPct(d) })))
                            const sampleRightEdge = sampleImageBounds.leftPct + sampleImageBounds.widthPct
                            const sampleLeftEdge = sampleImageBounds.leftPct
                            return diffBands.map((d, i) => {
                              const topPct = diffTopPct(d)
                              const onLeft = diffSides.get(String(i)) === 'left'
                              return (
                                <Box
                                  key={`diff-${i}`}
                                  position="absolute"
                                  {...(onLeft ? { right: `${100 - sampleLeftEdge}%` } : { left: `${sampleRightEdge}%` })}
                                  top={`${topPct}%`}
                                  transform="translateY(-50%)"
                                  display="flex"
                                  flexDirection={onLeft ? 'row-reverse' : 'row'}
                                  alignItems="center"
                                  pointerEvents="none"
                                  zIndex={7}
                                >
                                  <Box w="10px" h="2px" flexShrink={0} bg="purple.500" opacity={0.9} />
                                  <Box
                                    bg="purple.500"
                                    color="white"
                                    fontSize="8px"
                                    fontWeight="bold"
                                    px={1.5}
                                    py={0.5}
                                    borderRadius="sm"
                                    whiteSpace="nowrap"
                                  >
                                    +~{d.estimatedMW} kDa
                                  </Box>
                                </Box>
                              )
                            })
                          })()}

                          {/* Hover cursor line on sample image — adjusted to image content bounds */}
                          {hoverState && (() => {
                            const cursorTopPct = sampleImageBounds.topPct + hoverState.relFraction * sampleImageBounds.heightPct
                            return (
                              <>
                                <Box
                                  position="absolute"
                                  left={`${sampleImageBounds.leftPct}%`}
                                  top={`${cursorTopPct}%`}
                                  w={`${sampleImageBounds.widthPct}%`}
                                  h="2px"
                                  bg="yellow.400"
                                  opacity={0.85}
                                  pointerEvents="none"
                                  zIndex={5}
                                  style={{ transform: 'translateY(-50%)' }}
                                />
                                <Box
                                  position="absolute"
                                  // Anchored to the LEFT of the strip (in the letterbox), so the
                                  // px/kDa label never covers the band being hovered.
                                  right={`${100 - sampleImageBounds.leftPct + 1}%`}
                                  top={`${Math.min(sampleImageBounds.topPct + sampleImageBounds.heightPct - 4, Math.max(sampleImageBounds.topPct + 2, cursorTopPct))}%`}
                                  bg="yellow.900"
                                  color="yellow.100"
                                  fontSize="9px"
                                  px={1.5}
                                  py={0.5}
                                  borderRadius="md"
                                  pointerEvents="none"
                                  zIndex={6}
                                  whiteSpace="nowrap"
                                  style={{ transform: 'translateY(-50%)' }}
                                >
                                  {Math.round(hoverState.relFraction * selectedBlot.profile.length)} px
                                  {showKdaScale && computeKda(hoverState.chartX) !== null && (
                                    <> · {computeKda(hoverState.chartX)} kDa</>
                                  )}
                                </Box>
                              </>
                            )
                          })()}
                        </Box>
                      </VStack>
                    </HStack>

                    {/* Right: Validation (non-premium) OR Profiles (premium/AI) */}
                    {!aiAccessible ? (
                      /* ── Non-premium: col2=buttons+bands  col3=comment ──── */
                      <HStack spacing={6} align="stretch" flexShrink={0} minH="500px">

                        {/* Col 2 — Buttons + navigation + reference bands */}
                        <VStack
                          spacing={4}
                          align="center"
                          w="260px"
                          flexShrink={0}
                          justify="center"
                          py={2}
                        >
                        {/* A sample is validated against a témoin — nothing to compare
                            against yet until one is selected. Without this, a strip
                            could be marked POSITIF/NÉGATIF/DOUTEUX before any positive
                            control exists for the pathology at all. */}
                        {module === 'iris' && !activePositiveControl && (
                          <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="lg" px={3} py={2} w="full">
                            <Text fontSize="xs" color="orange.700" fontWeight="600">
                              Sélectionnez un témoin positif avant de valider cette bandelette.
                            </Text>
                          </Box>
                        )}
                        {module === 'iris' && <ManualResultSelector
                          blotId={selectedBlot.id}
                          value={manualBlotResults[selectedBlot.id] ?? null}
                          onChange={(result) => onManualResultChange?.(selectedBlot.id, result)}
                          interpretation={immunoblotConfig?.interpretation as any ?? null}
                          isDisabled={readOnly || !activePositiveControl}
                          showHotkeys
                          detectedBandNames={new Set(blotPlacedBands.get(selectedBlot.id)?.keys() ?? [])}
                          bands={immunoblotConfig?.bands?.specific as any ?? []}
                        />}

                        {/* Auto-advance after selection — IRIS : il enchaîne la
                            VALIDATION, qui n'existe pas en PROTIS. */}
                        {module === 'iris' && manualBlotResults[selectedBlot.id] != null && (
                          <VStack spacing={2} w="full">
                            {selectedBlotIndex < displayedBlotsArray.length - 1 ? (
                              <Button
                                w="full"
                                colorScheme="blue"
                                variant="outline"
                                size="sm"
                                rightIcon={<MdChevronRight />}
                                onClick={handleNextBlot}
                              >
                                Suivante ({selectedBlotIndex + 1}/{displayedBlotsArray.length})
                              </Button>
                            ) : displayedBlotsArray.filter(b => !(activeControlIds.includes(b.id))).every(b => manualBlotResults[b.id] != null) ? (
                              <Box
                                bg="green.50"
                                border="1px solid"
                                borderColor="green.200"
                                borderRadius="xl"
                                p={3}
                                textAlign="center"
                                w="full"
                              >
                                <Text fontSize="xs" color="green.700" fontWeight="bold">
                                  ✓ Toutes validées
                                </Text>
                                <Text fontSize="2xs" color="green.600" mt={0.5}>
                                  L'export est disponible
                                </Text>
                              </Box>
                            ) : null}
                          </VStack>
                        )}

                        {protisGraduationsPanel}

                        {/* Reference bands — interactive, colored by detection status */}
                        {module === 'iris' && immunoblotConfig && (
                          <Box
                            w="full"
                            bg="gray.50"
                            borderRadius="xl"
                            border="1px solid"
                            borderColor="gray.200"
                            p={3}
                          >
                            <VStack align="center" spacing={2}>
                              <Text fontSize="xs" fontWeight="bold" color="gray.600" textAlign="center">
                                {pathologyDisplayName(immunoblotConfig.name)}
                              </Text>
                              <HStack spacing={2} justify="center">
                                <Text fontSize="2xs" color="gray.400" fontStyle="italic">
                                  Bandes de référence
                                </Text>
                                {(() => {
                                  const placed = blotPlacedBands.get(selectedBlot.id)
                                  const detected = placed?.size ?? 0
                                  return detected > 0 ? (
                                    <Badge colorScheme="blue" fontSize="2xs" borderRadius="full" px={2}>
                                      {detected} identifiée{detected > 1 ? 's' : ''}
                                    </Badge>
                                  ) : null
                                })()}
                              </HStack>
                              {/* Instruction contextuelle */}
                              <Text fontSize="2xs" color={selectedBandToPlace ? 'blue.500' : 'gray.400'} textAlign="center" fontWeight={selectedBandToPlace ? 'semibold' : 'normal'}>
                                {selectedBandToPlace
                                  ? `→ Cliquez sur l'image pour placer ${selectedBandToPlace}`
                                  : 'Sélectionnez une bande puis cliquez sur l\'image'}
                              </Text>
                              <VStack spacing={1.5} align="stretch" w="full">
                                {immunoblotConfig.bands.specific.map((band: any) => {
                                  const isDetected = blotPlacedBands.get(selectedBlot.id)?.has(band.name) ?? false
                                  const isSelected = selectedBandToPlace === band.name
                                  return (
                                    <HStack
                                      key={band.id}
                                      spacing={2}
                                      px={2}
                                      py={1}
                                      borderRadius="lg"
                                      bg={isSelected ? 'yellow.100' : isDetected ? (band.required ? 'red.50' : 'blue.50') : 'transparent'}
                                      border="1px solid"
                                      borderColor={isSelected ? 'yellow.400' : isDetected ? (band.required ? 'red.200' : 'blue.200') : 'transparent'}
                                      cursor="pointer"
                                      onClick={() => {
                                        if (isDetected) {
                                          // Already placed → remove it
                                          togglePlacedBand(selectedBlot.id, band.name)
                                        } else if (isSelected) {
                                          // Already selected → deselect
                                          setSelectedBandToPlace(null)
                                        } else {
                                          // Select this band for placement
                                          setSelectedBandToPlace(band.name)
                                        }
                                      }}
                                      transition="all 0.15s"
                                      _hover={{ bg: isSelected ? 'yellow.200' : isDetected ? undefined : 'gray.100' }}
                                    >
                                      <Box
                                        w={2.5}
                                        h={2.5}
                                        borderRadius="full"
                                        bg={isSelected ? 'yellow.500' : isDetected ? (band.required ? 'red.500' : 'blue.500') : 'gray.300'}
                                        flexShrink={0}
                                        border="2px solid"
                                        borderColor={isSelected ? 'yellow.400' : isDetected ? (band.required ? 'red.300' : 'blue.300') : 'gray.200'}
                                      />
                                      <Text
                                        fontSize="xs"
                                        fontWeight={isSelected || isDetected ? 'bold' : 'normal'}
                                        color={isSelected ? 'yellow.800' : isDetected ? (band.required ? 'red.700' : 'blue.700') : 'gray.500'}
                                        flex={1}
                                      >
                                        {band.name}
                                        <Text as="span" fontSize="2xs" color="gray.400" fontWeight="normal">
                                          {' '}({band.molecularWeight} kDa)
                                        </Text>
                                        {band.required && (
                                          <Text as="span" color="red.500" fontWeight="bold"> ★</Text>
                                        )}
                                      </Text>
                                      {isSelected && <Text fontSize="2xs" color="yellow.700" fontWeight="bold">→</Text>}
                                      {isDetected && !isSelected && (
                                        <Icon as={MdCheckCircle} boxSize={3} color={band.required ? 'red.500' : 'blue.500'} />
                                      )}
                                    </HStack>
                                  )
                                })}
                              </VStack>
                              {immunoblotConfig.bands.specific.some((b: any) => b.required) && (
                                <Text fontSize="2xs" color="red.400" textAlign="center">
                                  ★ bande obligatoire
                                </Text>
                              )}
                            </VStack>
                          </Box>
                        )}
                        </VStack>
                        {/* Col 3 — Commentaire */}
                        <VStack
                          w="220px"
                          flexShrink={0}
                          align="stretch"
                          spacing={2}
                          justify="center"
                          py={2}
                        >
                          <Box
                            bg="white"
                            borderRadius="2xl"
                            boxShadow="0 4px 20px rgba(0,0,0,0.06)"
                            border="1px solid"
                            borderColor="gray.100"
                            overflow="hidden"
                            h="full"
                            minH="300px"
                            display="flex"
                            flexDirection="column"
                          >
                            {/* Header du champ */}
                            <Box
                              px={4}
                              py={3}
                              bgGradient="linear(to-r, purple.50, pink.50)"
                              borderBottom="1px solid"
                              borderColor="gray.100"
                            >
                              <HStack spacing={2}>
                                <Icon as={LuMessageSquare} color="purple.500" boxSize={4} />
                                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                  Note
                                </Text>
                                {blotComments[selectedBlot.id] && (
                                  <Badge colorScheme="purple" variant="subtle" fontSize="2xs" borderRadius="full">
                                    rédigée
                                  </Badge>
                                )}
                              </HStack>
                            </Box>
                            {/* Textarea */}
                            <Textarea
                              // Same reason as the identifier above: writable-looking,
                              // refused server-side.
                              isReadOnly={readOnly}
                              value={blotComments[selectedBlot.id] ?? ''}
                              onChange={e => {
                                const val = e.target.value
                                setBlotComments(prev => ({ ...prev, [selectedBlot.id]: val }))
                                onBlotCommentChange?.(selectedBlot.id, val)
                              }}
                              placeholder="Observations cliniques…"
                              flex={1}
                              h="full"
                              resize="none"
                              border="none"
                              borderRadius="0"
                              fontSize="sm"
                              bg="white"
                              p={4}
                              lineHeight="1.7"
                              _focus={{ boxShadow: 'none', borderColor: 'transparent' }}
                              _placeholder={{ color: 'gray.300', fontSize: 'xs', fontStyle: 'italic' }}
                            />
                          </Box>
                        </VStack>

                      </HStack>
                    ) : (
                    /* ── Premium/AI: profiles stacked or superposed ───────── */
<Flex gap={4} align="start" flex="1">
                    <VStack spacing={4} align="stretch" flex="1">
                      {superposed && activePositiveControl ? (
                        <>
                        <Box
                          bg="white"
                          borderRadius="2xl"
                          boxShadow="0 10px 40px rgba(0,0,0,0.08)"
                          overflow="hidden"
                          transition="all 0.3s ease"
                          _hover={{ boxShadow: '0 15px 50px rgba(0,0,0,0.12)' }}
                        >
                          <Box
                            px={4}
                            py={3}
                            bgGradient="linear(to-r, purple.50, pink.50)"
                            borderBottom="1px solid"
                            borderColor="gray.100"
                          >
                            <HStack justify="space-between">
                              <HStack>
                                <Box w={3} h={3} borderRadius="full" bgGradient="linear(to-r, purple.400, pink.400)" />
                                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                                  {t('blotAnalysis.comparison.superposedProfiles')}
                                </Text>
                              </HStack>
                              <HStack spacing={2}>
                                {/* Add a band by hand → pick a valid band, then click its peak
                                    on the sample image; it is placed and counts in the verdict. */}
                                {aiAccessible && !readOnly && selectedBlot
                                  && !(activeControlIds.includes(selectedBlot.id))
                                  && (module === 'protis' || effectiveBandPositions.size > 0) && (
                                  selectedBandToPlace ? (
                                    <HStack spacing={1}>
                                      <Badge colorScheme="blue" variant="solid" fontSize="2xs" px={2} py={1} borderRadius="full" whiteSpace="nowrap">
                                        {selectedBandToPlace} — cliquez sur le pic
                                      </Badge>
                                      <Button size="xs" h="20px" variant="ghost" colorScheme="red" onClick={() => setSelectedBandToPlace(null)}>Annuler</Button>
                                    </HStack>
                                  ) : (
                                    <Menu placement="bottom-start" gutter={4}>
                                      <MenuButton
                                        as={Button}
                                        size="xs"
                                        h="28px"
                                        px={3}
                                        bg="purple.500"
                                        color="white"
                                        borderRadius="full"
                                        fontWeight="bold"
                                        fontSize="xs"
                                        boxShadow="0 2px 6px rgba(128,90,213,0.35)"
                                        _hover={{ bg: 'purple.600', transform: 'translateY(-1px)' }}
                                        _active={{ bg: 'purple.700' }}
                                        _focusVisible={{ boxShadow: '0 0 0 2px var(--chakra-colors-purple-200)' }}
                                        transition="all 0.2s ease"
                                        leftIcon={<Box w={2} h={2} borderRadius="full" bg="white" />}
                                      >
                                        + Ajouter une bande
                                      </MenuButton>
                                      <MenuList
                                        bg="white"
                                        borderColor="purple.100"
                                        borderRadius="xl"
                                        boxShadow="0 12px 32px -8px rgba(76, 29, 149, 0.35)"
                                        py={2}
                                        minW="190px"
                                        maxH="320px"
                                        overflowY="auto"
                                        zIndex={1500}
                                      >
                                        <Text px={3} pb={2} fontSize="2xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="purple.500">
                                          Bande à placer
                                        </Text>
                                        {module === 'protis' ? (
                                          /* Mode libre : un gel de recherche n'a pas de
                                             catalogue — le nom est celui que le
                                             biologiste choisit (« 17 kDa », « PC α »…),
                                             puis le geste est le même : cliquer le pic
                                             sur l'image.

                                             Sur le MARQUEUR DE MASSE, c'est la
                                             calibration : les graduations des échelles
                                             connues de la notice se proposent en
                                             pastilles, à placer une à une — le geste des
                                             témoins IRIS, appliqué à l'échelle du gel.
                                             Ces positions nommées serviront de base à la
                                             régression kDa. */
                                          <Box px={3} pb={1}>
                                            {(() => {
                                              const isMarkerLane = /marqueur|marker/i.test(String((selectedBlot as any)?.strip_id ?? ''))
                                              const taken = new Set<string>([
                                                ...selectedBlotIdentifiedBands,
                                                ...Array.from(blotPlacedBands.get(selectedBlot.id)?.keys() ?? []),
                                              ])
                                              const fmtKda = (v: number) => (v >= 30 ? String(Math.round(v)) : v.toFixed(1).replace(/\.0$/, ''))
                                              const profLen: number = ((selectedBlot as any).profile ?? []).length
                                              const peaks: number[] = (((selectedBlot as any).peaks ?? []) as number[]).slice().sort((a, b) => a - b)

                                              if (!isMarkerLane) return null

                                              const ladders = (immunoblotConfig as any)?.interpretation?.ladderProfile?.ladder?.knownLadders ?? []
                                              // MARQUEUR : si une échelle connue compte exactement
                                              // autant de graduations que de pics détectés, on les
                                              // marie du haut vers le bas — comme la suggestion du
                                              // témoin IRIS, à confirmer du regard, jamais imposée.
                                              const matching = peaks.length >= 2 && profLen
                                                ? ladders.find((l: any) => (l.values?.length ?? 0) === peaks.length)
                                                : null
                                              const autoBlock = matching ? (
                                                <Box pb={2}>
                                                  <Button
                                                    size="xs"
                                                    w="full"
                                                    colorScheme="purple"
                                                    variant="outline"
                                                    onClick={() => {
                                                      const values = (matching.values as number[]).slice().sort((x, y) => y - x)
                                                      const entries: Array<[string, number]> = values
                                                        .map((v, i) => [`${v} kDa`, peaks[i] / profLen] as [string, number])
                                                        .filter(([nm]) => !taken.has(nm))
                                                      placeBandsBatch(selectedBlot.id, entries)
                                                      setSelectedBandToPlace(null)
                                                    }}
                                                  >
                                                    ⚡ Placer {matching.values.length} graduations sur les pics ({matching.label ?? 'échelle connue'})
                                                  </Button>
                                                  <Text pt={1} fontSize="2xs" color="gray.500">
                                                    Les {peaks.length} pics détectés épousent cette échelle. Vérifiez, puis ajustez au besoin.
                                                  </Text>
                                                </Box>
                                              ) : null
                                              const values: number[] = Array.from(new Set(ladders.flatMap((l: any) => (l.values ?? []) as number[])))
                                              const free = values
                                                .sort((a, b) => b - a)
                                                .filter((v) => !taken.has(`${v} kDa`))
                                              if (!free.length) return autoBlock
                                              return (
                                                <>
                                                {autoBlock}
                                                <Box pb={2}>
                                                  <Text pb={1} fontSize="2xs" color="gray.500">
                                                    Graduations de l'échelle — placez-les du haut vers le bas :
                                                  </Text>
                                                  <Flex wrap="wrap" gap={1}>
                                                    {free.map((v) => (
                                                      <Badge
                                                        key={v}
                                                        as="button"
                                                        colorScheme="purple"
                                                        variant="outline"
                                                        borderRadius="full"
                                                        px={2}
                                                        py={0.5}
                                                        cursor="pointer"
                                                        _hover={{ bg: 'purple.50' }}
                                                        onClick={() => setSelectedBandToPlace(`${v} kDa`)}
                                                      >
                                                        {v}
                                                      </Badge>
                                                    ))}
                                                  </Flex>
                                                  <Box h="1px" bg="gray.100" mt={2} />
                                                </Box>
                                                </>
                                              )
                                            })()}
                                            <form onSubmit={(e) => {
                                              e.preventDefault()
                                              const name = freeBandName.trim()
                                              if (!name) return
                                              const taken = new Set<string>([
                                                ...selectedBlotIdentifiedBands,
                                                ...Array.from(blotPlacedBands.get(selectedBlot.id)?.keys() ?? []),
                                              ])
                                              if (taken.has(name)) return
                                              setSelectedBandToPlace(name)
                                              setFreeBandName('')
                                            }}>
                                              <HStack spacing={2}>
                                                <Input
                                                  size="sm"
                                                  autoFocus
                                                  placeholder="Nom libre (ex. 17 kDa)"
                                                  value={freeBandName}
                                                  onChange={(e) => setFreeBandName(e.target.value)}
                                                  borderRadius="md"
                                                />
                                                <Button size="sm" type="submit" colorScheme="purple" isDisabled={!freeBandName.trim()}>
                                                  Placer
                                                </Button>
                                              </HStack>
                                            </form>
                                          </Box>
                                        ) : (() => {
                                          // Hide bands already on this sample (detected or placed by hand).
                                          const alreadyOnBlot = new Set<string>([
                                            ...selectedBlotIdentifiedBands,
                                            ...Array.from(blotPlacedBands.get(selectedBlot.id)?.keys() ?? []),
                                          ])
                                          const available = BAND_NAMES.filter((n) => !alreadyOnBlot.has(n))
                                          if (available.length === 0) {
                                            return (
                                              <Text px={3} py={2} fontSize="xs" color="gray.500">
                                                Toutes les bandes sont déjà présentes
                                              </Text>
                                            )
                                          }
                                          return available.map((n) => (
                                            <MenuItem
                                              key={n}
                                              onClick={() => setSelectedBandToPlace(n)}
                                              fontSize="sm"
                                              fontWeight={REQUIRED_BAND_NAMES.includes(n) ? '700' : '500'}
                                              color="gray.800"
                                              _hover={{ bg: 'purple.50', color: 'purple.700' }}
                                              _focus={{ bg: 'purple.50' }}
                                              borderRadius="md"
                                              mx={1}
                                            >
                                              {REQUIRED_BAND_NAMES.includes(n) ? '★ ' : ''}{n}
                                            </MenuItem>
                                          ))
                                        })()}
                                      </MenuList>
                                    </Menu>
                                  )
                                )}
                                <Tooltip label={module === 'protis' ? (showTemoin ? `Masquer : ${protisRefWord}` : `Afficher : ${protisRefWord}`) : showTemoin ? t('blotAnalysis.comparison.hideControl') : t('blotAnalysis.comparison.showControl')}>
                                  <Button
                                    size="xs"
                                    variant={showTemoin ? 'solid' : 'outline'}
                                    colorScheme="green"
                                    onClick={() => setShowTemoin(!showTemoin)}
                                    leftIcon={<Box w={2} h={2} borderRadius="full" bg={showTemoin ? 'white' : 'green.500'} />}
                                  >
                                    {module === 'protis' ? protisRefWord : 'Témoin'}
                                  </Button>
                                </Tooltip>
                                <Tooltip label={showBandelette ? t('blotAnalysis.comparison.hideStrip') : t('blotAnalysis.comparison.showStrip')}>
                                  <Button
                                    size="xs"
                                    variant={showBandelette ? 'solid' : 'outline'}
                                    colorScheme="purple"
                                    onClick={() => setShowBandelette(!showBandelette)}
                                    leftIcon={<Box w={2} h={2} borderRadius="full" bg={showBandelette ? 'white' : 'purple.500'} />}
                                  >
                                    Échantillon
                                  </Button>
                                </Tooltip>
                                <Tooltip label={module === 'protis' ? (showTemoinBands ? 'Masquer les graduations du marqueur' : 'Afficher les graduations du marqueur') : showTemoinBands ? t('blotAnalysis.comparison.hideControlBands') : t('blotAnalysis.comparison.showControlBands')}>
                                  <Button
                                    size="xs"
                                    variant={showTemoinBands ? 'solid' : 'outline'}
                                    colorScheme="green"
                                    onClick={() => setShowTemoinBands(!showTemoinBands)}
                                    leftIcon={<Box w={2} h={2} borderRadius="full" bg={showTemoinBands ? 'white' : 'green.500'} />}
                                  >
                                    {module === 'protis' ? 'Graduations' : 'Bandes témoin'}
                                  </Button>
                                </Tooltip>
                              </HStack>
                            </HStack>
                          </Box>
                          <Box h="500px" p={4}>
                            {barMode ? (
                              (() => {
                                const alignedControl = getAlignedProfile({
                                  ...activePositiveControl!,
                                  profile: baselineCorrectedCache.get(activePositiveControl!.id) ?? subtractBaseline(activePositiveControl!.profile)
                                })
                                const alignedSample = getAlignedProfile({
                                  ...selectedBlotWithEnhancedPeaks!,
                                  profile: baselineCorrectedCache.get(selectedBlotWithEnhancedPeaks!.id) ?? subtractBaseline(selectedBlotWithEnhancedPeaks!.profile)
                                })
                                const maxLen = Math.max(alignedControl.profile.length, alignedSample.profile.length)
                                const barDatasets = []
                                if (showTemoin) {
                                  barDatasets.push({
                                    label: (module === 'protis' ? protisRefWord : t('blotAnalysis.comparison.controlN', { number: selectedControlForBlot })),
                                    data: alignedControl.profile,
                                    backgroundColor: pairInfoSelected ? (pairInfoSelected.role === 'reference' ? 'rgba(168, 85, 247, 0.5)' : 'rgba(34, 197, 94, 0.5)') :
                                      (module === 'protis' || activePositiveControl!.id === effectiveControlId(1)) ? 'rgba(34, 197, 94, 0.5)' :
                                      activePositiveControl!.id === effectiveControlId(2) ? 'rgba(59, 130, 246, 0.5)' : 'rgba(239, 68, 68, 0.5)',
                                    borderColor: pairInfoSelected ? (pairInfoSelected.role === 'reference' ? 'rgb(168, 85, 247)' : 'rgb(34, 197, 94)') :
                                      (module === 'protis' || activePositiveControl!.id === effectiveControlId(1)) ? 'rgb(34, 197, 94)' :
                                      activePositiveControl!.id === effectiveControlId(2) ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)',
                                    borderWidth: 2,
                                  })
                                }
                                if (showBandelette) {
                                  barDatasets.push({
                                    label: t('blotAnalysis.comparison.sample'),
                                    data: alignedSample.profile,
                                    backgroundColor: 'rgba(168, 85, 247, 0.5)',
                                    borderColor: 'rgb(168, 85, 247)',
                                    borderWidth: 2,
                                  })
                                }
                                return (
                                  <Bar
                                    options={getChartOptions(selectedBlotWithEnhancedPeaks!, module === 'protis' ? ((protisPlateMarker as any) ?? undefined) : positiveControl, protisChartArmed ? handleProtisChartPlace : undefined, protisChartBands ?? (chartBandPositions.size > 0 ? chartBandPositions : undefined))}
                                    data={{
                                      labels: Array.from({ length: maxLen }, (_, i) => i.toString()),
                                      datasets: barDatasets,
                                    }}
                                  />
                                )
                              })()
                            ) : (
                              <Line
                                ref={registerChart}
                                options={getChartOptions(selectedBlotWithEnhancedPeaks!, module === 'protis' ? ((protisPlateMarker as any) ?? undefined) : positiveControl, protisChartArmed ? handleProtisChartPlace : undefined, protisChartBands ?? (chartBandPositions.size > 0 ? chartBandPositions : undefined))}
                                // DTW disabled: keeps the sample's raw X axis so hovering on the
                                // sample image lands exactly on the corresponding peak in the chart.
                                data={getOverlayedChartData(activePositiveControl!, selectedBlotWithEnhancedPeaks!, false, showTemoin, showBandelette, selectedBlot?.peaks)}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Band Ratios Comparison Panel — quality indicator, not a
                            verdict criterion. Gated by SHOW_RATIO_COMPARISON. */}
                        {SHOW_RATIO_COMPARISON && (
                        <Box
                          bg="white"
                          borderRadius="xl"
                          boxShadow="sm"
                          p={4}
                          mt={4}
                        >
                          <HStack mb={3} justify="space-between">
                            <Text fontWeight="bold" fontSize="sm" color="gray.700">
                              Similarité avec le témoin
                            </Text>
                            {manualRatioComparison ? (
                              <HStack spacing={2}>
                                <Badge
                                  colorScheme={manualRatioComparison.isConsistent ? 'green' : 'orange'}
                                  fontSize="xs"
                                >
                                  {manualRatioComparison.overallSimilarity != null
                                    ? `${manualRatioComparison.overallSimilarity.toFixed(0)}%`
                                    : manualRatioComparison.controlIntensities.size < Math.min(BAND_NAMES.length, 3)
                                      ? `N/A (${manualRatioComparison.controlIntensities.size}/${BAND_NAMES.length} bandes calibrées)`
                                      : `N/A (${manualRatioComparison.ratioDeltas.size}/${manualRatioComparison.controlRatios.size} ratios comparables)`}
                                </Badge>
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  color="gray.500"
                                  onClick={() => setShowRatioDetails(v => !v)}
                                  rightIcon={<Box as="span" fontSize="10px">{showRatioDetails ? '▲' : '▼'}</Box>}
                                  _hover={{ bg: 'gray.50', color: 'gray.700' }}
                                  fontSize="xs"
                                >
                                  {showRatioDetails ? t('blotAnalysis.comparison.hideDetails') : t('blotAnalysis.comparison.showDetails')}
                                </Button>
                              </HStack>
                            ) : (
                              <HStack spacing={2}>
                                <Tooltip
                                  label={t('blotAnalysis.comparison.calibrationTooltip')}
                                  hasArrow
                                  placement="top"
                                >
                                  <Badge colorScheme="gray" fontSize="xs" cursor="help">
                                    Non calibré (optionnel)
                                  </Badge>
                                </Tooltip>
                                {module === 'iris' && activePositiveControl && (
                                  <Button size="xs" colorScheme="blue" variant="outline" onClick={navigateToControlAndCalibrate}>
                                    Calibrer T{selectedControlForBlot}
                                  </Button>
                                )}
                              </HStack>
                            )}
                          </HStack>

                          {manualRatioComparison ? (
                            <Collapse in={showRatioDetails} animateOpacity>
                              {/* Sub-header revealed only when details are expanded — gives the
                                  reference band context without cluttering the collapsed header. */}
                              <Text fontSize="xs" color="gray.500" mb={3} mt={1}>
                                Référence : {manualRatioComparison?.refBandName ?? REFERENCE_BAND}
                              </Text>
                              {/* Ratios Table */}
                              <Box overflowX="auto">
                                <Table size="sm" variant="simple">
                                  <Thead>
                                    <Tr>
                                      <Th fontSize="xs" px={2}>{t('blotAnalysis.comparison.ratio')}</Th>
                                      <Th fontSize="xs" px={2} isNumeric>{module === 'protis' ? 'Marqueur' : t('blotAnalysis.comparison.controlCol')}</Th>
                                      <Th fontSize="xs" px={2} isNumeric>{t('blotAnalysis.comparison.sampleCol')}</Th>
                                      <Th fontSize="xs" px={2} isNumeric>{t('blotAnalysis.comparison.diffPct')}</Th>
                                      <Th fontSize="xs" px={2}>Status</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {Array.from(manualRatioComparison.ratioDeltas.entries()).map(([ratioName, data]) => (
                                      <Tr key={ratioName}>
                                        <Td fontSize="xs" px={2} fontWeight="medium">{ratioName}</Td>
                                        <Td fontSize="xs" px={2} isNumeric>{data.control.toFixed(2)}</Td>
                                        <Td fontSize="xs" px={2} isNumeric>{data.sample.toFixed(2)}</Td>
                                        <Td fontSize="xs" px={2} isNumeric color={data.percentDiff > 25 ? 'red.500' : 'green.500'}>
                                          {data.percentDiff.toFixed(1)}%
                                        </Td>
                                        <Td fontSize="xs" px={2}>
                                          {data.percentDiff <= 25 ? (
                                            <Badge colorScheme="green" size="sm">OK</Badge>
                                          ) : (
                                            <Badge colorScheme="red" size="sm">Diff</Badge>
                                          )}
                                        </Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </Box>

                              {/* Self-comparison notice */}
                              {manualRatioComparison.isSelfComparison && (
                                <Box bg="blue.50" borderRadius="md" p={3} mb={3}>
                                  <Text fontSize="xs" color="blue.600" fontWeight="medium">
                                    ℹ️ Vous visualisez le témoin - naviguez vers un échantillon pour voir la comparaison
                                  </Text>
                                </Box>
                              )}

                              {/* Band Intensities */}
                              <Box mt={4}>
                                <Text fontWeight="semibold" fontSize="xs" color="gray.600" mb={2}>
                                  {t('blotAnalysis.comparison.bandIntensities')}
                                </Text>
                                <VStack spacing={3} align="stretch">
                                  <Box>
                                    <Text fontSize="xs" color="green.600" fontWeight="bold" mb={1}>{t('blotAnalysis.comparison.controlCalibrated')}</Text>
                                    <HStack spacing={2} flexWrap="wrap">
                                      {Array.from(manualRatioComparison.controlIntensities.entries()).map(([band, intensity]) => (
                                        <Badge key={band} variant="outline" colorScheme="green" fontSize="xs">
                                          {band}: {Math.round(intensity)}
                                        </Badge>
                                      ))}
                                    </HStack>
                                  </Box>
                                  {!manualRatioComparison.isSelfComparison && (
                                    <Box>
                                      <Text fontSize="xs" color="purple.600" fontWeight="bold" mb={1}>{t('blotAnalysis.comparison.sampleSamePos')}</Text>
                                      <HStack spacing={2} flexWrap="wrap">
                                        {Array.from(manualRatioComparison.sampleIntensities.entries()).map(([band, intensity]) => (
                                          <Badge key={band} variant="outline" colorScheme="purple" fontSize="xs">
                                            {band}: {Math.round(intensity)}
                                          </Badge>
                                        ))}
                                      </HStack>
                                    </Box>
                                  )}
                                </VStack>
                              </Box>

                              {/* Band Positions */}
                              <Box mt={3}>
                                <Text fontWeight="semibold" fontSize="xs" color="gray.600" mb={2}>
                                  {t('blotAnalysis.comparison.bandPositions')}
                                </Text>
                                <VStack spacing={2} align="stretch">
                                  <Box>
                                    <Text fontSize="xs" color="green.500" mb={1}>{t('blotAnalysis.comparison.controlShort')}</Text>
                                    <HStack spacing={2} flexWrap="wrap">
                                      {Array.from(manualRatioComparison.controlPositions.entries()).map(([band, pos]) => (
                                        <Badge key={band} variant="subtle" colorScheme="green" fontSize="xs">
                                          {band}: {Math.round(pos)}
                                        </Badge>
                                      ))}
                                    </HStack>
                                  </Box>
                                  {!manualRatioComparison.isSelfComparison && manualRatioComparison.samplePositions && (
                                    <Box>
                                      <Text fontSize="xs" color="purple.500" mb={1}>{t('blotAnalysis.comparison.sampleAdjusted')}</Text>
                                      <HStack spacing={2} flexWrap="wrap">
                                        {Array.from(manualRatioComparison.samplePositions.entries()).map(([band, pos]) => (
                                          <Badge key={band} variant="subtle" colorScheme="purple" fontSize="xs">
                                            {band}: {Math.round(pos)}
                                          </Badge>
                                        ))}
                                      </HStack>
                                    </Box>
                                  )}
                                </VStack>
                              </Box>
                            </Collapse>
                          ) : (
                            <Box textAlign="center" py={4}>
                              <Text fontSize="sm" color="gray.500" mb={3}>
                                {t('blotAnalysis.comparison.selectBandsPrompt', { count: BAND_NAMES.length, names: BAND_NAMES.join(', ') })}
                              </Text>
                              <Text fontSize="xs" color="gray.400">
                                {t('blotAnalysis.comparison.selectBandsHint')}
                              </Text>
                            </Box>
                          )}
                        </Box>
                        )}
                      </>
                      ) : (
                        <>
                          {/* Témoin Profile */}
                          {activePositiveControl && (
                            <Box
                              bg="white"
                              borderRadius="2xl"
                              boxShadow="0 8px 30px rgba(34, 197, 94, 0.1)"
                              overflow="hidden"
                              transition="all 0.3s ease"
                              _hover={{ boxShadow: '0 12px 40px rgba(34, 197, 94, 0.15)' }}
                            >
                              <Box
                                px={4}
                                py={3}
                                bgGradient={pairInfoSelected
                                  ? (pairInfoSelected.role === 'reference' ? 'linear(to-r, purple.50, pink.50)' : 'linear(to-r, green.50, emerald.50)')
                                  : ((module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'linear(to-r, green.50, emerald.50)' :
                                     activePositiveControl.id === effectiveControlId(2) ? 'linear(to-r, blue.50, cyan.50)' : 'linear(to-r, red.50, orange.50)')
                                }
                                borderBottom="1px solid"
                                borderColor="gray.100"
                              >
                                <HStack>
                                  <Box
                                    w={3}
                                    h={3}
                                    borderRadius="full"
                                    bgGradient={pairInfoSelected
                                      ? (pairInfoSelected.role === 'reference' ? 'linear(to-r, purple.400, pink.400)' : 'linear(to-r, green.400, emerald.400)')
                                      : ((module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'linear(to-r, green.400, emerald.400)' :
                                         activePositiveControl.id === effectiveControlId(2) ? 'linear(to-r, blue.400, cyan.400)' : 'linear(to-r, red.400, orange.400)')
                                    }
                                  />
                                  <Text fontSize="sm" fontWeight="bold" color="gray.700">
                                    {module === 'protis' ? 'Profil du marqueur' : t('blotAnalysis.comparison.controlProfile')}
                                  </Text>
                                  {/* Hide band annotations on the profile → clean profile+peaks
                                      to place calibration bands precisely on the peaks. */}
                                  <Tooltip label={showChartBands ? t('blotAnalysis.comparison.hideAnnotations') : t('blotAnalysis.comparison.showAnnotations')} hasArrow>
                                    <Button
                                      size="xs"
                                      variant={showChartBands ? 'outline' : 'solid'}
                                      colorScheme="orange"
                                      onClick={() => setShowChartBands(!showChartBands)}
                                      leftIcon={<Box w={2} h={2} borderRadius="full" bg={showChartBands ? 'orange.500' : 'white'} />}
                                    >
                                      {showChartBands ? t('blotAnalysis.comparison.peaksOnly') : t('blotAnalysis.comparison.annotations')}
                                    </Button>
                                  </Tooltip>
                                  <Box flex="1" />
                                  {/* Band Selection Button — single-band mode only */}
                                  {bandSelectionMode ? (
                                    <HStack spacing={2} flexWrap="wrap">
                                      <Badge
                                        colorScheme={REQUIRED_BAND_NAMES.includes(currentBandToSelect!) ? 'red' : 'orange'}
                                        fontSize="xs"
                                        px={2}
                                        py={0.5}
                                      >
                                        {REQUIRED_BAND_NAMES.includes(currentBandToSelect!) ? '★ ' : ''}{currentBandToSelect}
                                        {' '}— cliquez sur le pic
                                      </Badge>
                                      <Button size="xs" colorScheme="red" variant="ghost" onClick={cancelBandSelection}>
                                        Annuler
                                      </Button>
                                    </HStack>
                                  ) : (
                                    <Button
                                      size="xs"
                                      colorScheme="blue"
                                      variant="outline"
                                      onClick={handleCalibrateButtonClick}
                                      title={effectiveBandPositions.size > 0 ? t('blotAnalysis.calibration.restartHint') : ''}
                                    >
                                      {hasManualCalibration ? 'Tout recalibrer' : 'Calibrer les bandes'}
                                    </Button>
                                  )}
                                </HStack>
                                {/* Show band positions (saved OR auto-suggested) — click a badge to correct that band */}
                                {displayedBandPositions.size > 0 && !bandSelectionMode && (
                                  <HStack spacing={1} mt={1} flexWrap="wrap">
                                    {/* Hint when displaying auto-suggested positions */}
                                    {bandsAreSuggested && (
                                      <Badge colorScheme="cyan" fontSize="2xs" variant="outline">
                                        Suggéré — validez le blot ou cliquez pour corriger
                                      </Badge>
                                    )}
                                    {Array.from(displayedBandPositions.entries()).map(([band, data]) => {
                                      const isReq = REQUIRED_BAND_NAMES.includes(band)
                                      // "Saved" only when the operator actually calibrated — a
                                      // suggested band stays cyan even though it now feeds the verdict.
                                      const isSaved = effectiveBandPositions.has(band) && !bandsAreSuggested
                                      // Saved: red (required) / green (optional). Suggested: cyan.
                                      const scheme = isSaved ? (isReq ? 'red' : 'green') : 'cyan'
                                      return (
                                        <Tooltip key={band} label={`Cliquer pour repositionner ${band}`} hasArrow placement="top">
                                          <Badge
                                            colorScheme={scheme}
                                            fontSize="xs"
                                            variant="subtle"
                                            cursor="pointer"
                                            _hover={{ transform: 'scale(1.08)', boxShadow: 'sm' }}
                                            transition="all 0.15s ease"
                                            onClick={() => startBandSelection({ singleBand: band })}
                                          >
                                            {isReq ? '★ ' : ''}{band}: {Math.round(data.position)}
                                          </Badge>
                                        </Tooltip>
                                      )
                                    })}
                                    {/* Missing required bands → quick-add: clicking the badge selects just that band */}
                                    {REQUIRED_BAND_NAMES.filter(b => !displayedBandPositions.has(b)).map((missingBand) => (
                                      <Tooltip key={missingBand} label={`Cliquer pour calibrer ${missingBand}`} hasArrow placement="top">
                                        <Badge
                                          colorScheme="orange"
                                          fontSize="xs"
                                          variant="subtle"
                                          cursor="pointer"
                                          _hover={{ transform: 'scale(1.08)', boxShadow: 'sm' }}
                                          transition="all 0.15s ease"
                                          onClick={() => startBandSelection({ singleBand: missingBand })}
                                        >
                                          ⚠ {missingBand}
                                        </Badge>
                                      </Tooltip>
                                    ))}
                                  </HStack>
                                )}
                              </Box>
                              <HStack align="stretch" spacing={0}>
                                <Box flex="1" h="250px" p={4} cursor={bandSelectionMode || protisChartArmed ? 'crosshair' : 'default'}>
                                  {barMode ? (
                                    <Bar
                                      options={getChartOptions(activePositiveControl, activePositiveControl, bandSelectionMode ? handleBandPeakClick : undefined, chartBandPositions.size > 0 ? chartBandPositions : undefined)}
                                      data={getChartData(
                                        activePositiveControl,
                                        (module === 'protis' ? protisRefWord : t('blotAnalysis.comparison.controlN', { number: selectedControlForBlot })),
                                        pairInfoSelected ? (pairInfoSelected.role === 'reference' ? 'rgb(168, 85, 247)' : 'rgb(34, 197, 94)') :
                                        (module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'rgb(34, 197, 94)' :
                                        activePositiveControl.id === effectiveControlId(2) ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)',
                                        module === 'protis' ? activePositiveControl.peaks : undefined
                                      )}
                                    />
                                  ) : (
                                    <Line
                                      ref={registerChart}
                                      options={getChartOptions(activePositiveControl, activePositiveControl, bandSelectionMode ? handleBandPeakClick : undefined, chartBandPositions.size > 0 ? chartBandPositions : undefined)}
                                      data={getChartData(
                                        activePositiveControl,
                                        (module === 'protis' ? protisRefWord : t('blotAnalysis.comparison.controlN', { number: selectedControlForBlot })),
                                        pairInfoSelected ? (pairInfoSelected.role === 'reference' ? 'rgb(168, 85, 247)' : 'rgb(34, 197, 94)') :
                                        (module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'rgb(34, 197, 94)' :
                                        activePositiveControl.id === effectiveControlId(2) ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)',
                                        module === 'protis' ? activePositiveControl.peaks : undefined
                                      )}
                                    />
                                  )}
                                </Box>

                                {/* Guide des bandes — rappel pendant la calibration */}
                                {immunoblotConfig?.bands?.specific && (
                                  <Box
                                    w="140px"
                                    flexShrink={0}
                                    borderLeftWidth="1px"
                                    borderColor="gray.100"
                                    bg="gray.50"
                                    p={2}
                                    overflowY="auto"
                                    h="250px"
                                  >
                                    <Text fontSize="2xs" fontWeight="bold" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="wide">
                                      Bandes à identifier
                                    </Text>
                                    <VStack spacing={1} align="stretch">
                                      {[...immunoblotConfig.bands.specific]
                                        .sort((a: any, b: any) => b.molecularWeight - a.molecularWeight)
                                        .map((band: any) => {
                                          const isCalibrated = effectiveBandPositions.has(band.name)
                                          const isCurrent = bandSelectionMode && currentBandToSelect === band.name
                                          // Suggested (auto) bands are amber "≈"; operator-confirmed are green "✓".
                                          const isSuggested = isCalibrated && bandsAreSuggested
                                          const scheme = isCurrent ? 'orange' : isSuggested ? 'yellow' : isCalibrated ? 'green' : 'gray'
                                          return (
                                            <HStack
                                              key={band.name}
                                              spacing={1}
                                              bg={scheme === 'gray' ? 'white' : `${scheme}.50`}
                                              borderRadius="md"
                                              px={1.5}
                                              py={0.5}
                                              borderWidth="1px"
                                              borderColor={`${scheme}.200`}
                                            >
                                              <Text fontSize="2xs" fontWeight="bold" color={`${scheme}.700`}>
                                                {band.name}
                                              </Text>
                                              <Text fontSize="2xs" color="gray.400">
                                                {band.molecularWeight}k
                                              </Text>
                                              {band.required && (
                                                <Badge fontSize="2xs" colorScheme="red" variant="solid" px={1} py={0}>
                                                  ★
                                                </Badge>
                                              )}
                                              {isCalibrated && !isCurrent && (
                                                <Text fontSize="2xs" color={`${scheme}.500`}>{isSuggested ? '≈' : '✓'}</Text>
                                              )}
                                            </HStack>
                                          )
                                        })}
                                    </VStack>
                                  </Box>
                                )}
                              </HStack>
                            </Box>
                          )}

                          {/* Échantillon Profile */}
                          <Box
                            bg="white"
                            borderRadius="2xl"
                            boxShadow="0 8px 30px rgba(128, 90, 213, 0.1)"
                            overflow="hidden"
                            transition="all 0.3s ease"
                            _hover={{ boxShadow: '0 12px 40px rgba(128, 90, 213, 0.15)' }}
                          >
                            <Box
                              px={4}
                              py={3}
                              bgGradient="linear(to-r, purple.50, pink.50)"
                              borderBottom="1px solid"
                              borderColor="gray.100"
                            >
                              <HStack>
                                <Box w={3} h={3} borderRadius="full" bgGradient="linear(to-r, purple.400, pink.400)" />
                                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                                  {t('blotAnalysis.comparison.sampleProfile')}
                                </Text>
                              </HStack>
                            </Box>
                            <Box h={module === 'protis' && !activePositiveControl ? '500px' : '250px'} p={4}>
                              {barMode ? (
                                <Bar
                                  options={getChartOptions(selectedBlotWithEnhancedPeaks!, module === 'protis' ? ((protisPlateMarker as any) ?? undefined) : positiveControl, protisChartArmed ? handleProtisChartPlace : undefined, protisChartBands ?? (chartBandPositions.size > 0 ? chartBandPositions : undefined))}
                                  data={getChartData(
                                    selectedBlotWithEnhancedPeaks!,
                                    isProtisMarkerSelected ? 'Marqueur' : t('blotAnalysis.comparison.sample'),
                                    isProtisMarkerSelected ? 'rgb(34, 197, 94)' : 'rgb(168, 85, 247)',
                                    selectedBlot?.peaks
                                  )}
                                />
                              ) : (
                                <Line
                                  ref={registerChart}
                                  options={getChartOptions(selectedBlotWithEnhancedPeaks!, module === 'protis' ? ((protisPlateMarker as any) ?? undefined) : positiveControl, protisChartArmed ? handleProtisChartPlace : undefined, protisChartBands ?? (chartBandPositions.size > 0 ? chartBandPositions : undefined))}
                                  data={getChartData(
                                    selectedBlotWithEnhancedPeaks!,
                                    isProtisMarkerSelected ? 'Marqueur' : t('blotAnalysis.comparison.sample'),
                                    isProtisMarkerSelected ? 'rgb(34, 197, 94)' : 'rgb(168, 85, 247)',
                                    selectedBlot?.peaks
                                  )}
                                />
                              )}
                            </Box>
                          </Box>
                        </>
                      )}
                    </VStack>
                    {protisGraduationsPanel && (
                      <Box w="225px" flexShrink={0}>
                        {protisGraduationsPanel}
                      </Box>
                    )}
                    </Flex>
                    )} {/* end AI/non-premium conditional */}
                  </Flex>
                ) : (
                  /* Side-by-side mode - Enhanced UI */
                  <Flex direction={{ base: 'column', lg: 'row' }} gap={6}>
                  {/* Left side: Active Positive Control (if set, NOT comparing to itself, and selected blot is NOT a control) */}
                  {activePositiveControl && selectedBlot?.id !== activePositiveControl.id &&
                   !activeControlIds.includes(selectedBlot.id) && (
                    <VStack
                      key={`control-section-${activePositiveControl.id}-${imageRenderKey}`}
                      flex="1"
                      spacing={4}
                      align="stretch"
                      bg="white"
                      p={5}
                      borderRadius="2xl"
                      boxShadow="0 10px 40px rgba(0,0,0,0.08)"
                      transition="all 0.3s ease"
                      _hover={{ boxShadow: '0 15px 50px rgba(0,0,0,0.12)' }}
                    >
                      {/* Header with gradient */}
                      <HStack
                        spacing={3}
                        p={3}
                        borderRadius="xl"
                        bgGradient={
                          (module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'linear(to-r, green.50, emerald.50)' :
                          activePositiveControl.id === effectiveControlId(2) ? 'linear(to-r, blue.50, cyan.50)' : 'linear(to-r, red.50, orange.50)'
                        }
                      >
                        <Box
                          p={2}
                          borderRadius="lg"
                          bgGradient={
                            (module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'linear(to-r, green.400, green.500)' :
                            activePositiveControl.id === effectiveControlId(2) ? 'linear(to-r, blue.400, blue.500)' : 'linear(to-r, red.400, red.500)'
                          }
                        >
                          <Text color="white" fontWeight="800" fontSize="md" lineHeight="1">{pairInfoSelected ? '↔' : 'T+'}</Text>
                        </Box>
                        <Heading
                          size="sm"
                          color={
                            (module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'green.700' :
                            activePositiveControl.id === effectiveControlId(2) ? 'blue.700' : 'red.700'
                          }
                        >
                          {pairInfoSelected
                            ? `${pairInfoSelected.role === 'reference' ? t('blotAnalysis.comparison.comparedLabel') : t('blotAnalysis.comparison.referenceLabel')} - ${blotLabel(activePositiveControl)}`
                            : module === 'protis'
                              ? `${protisRefWord} - ${blotLabel(activePositiveControl)}`
                              : t('blotAnalysis.comparison.controlNBlot', {
                                number: activePositiveControl.id === effectiveControlId(1) ? 1 :
                                  activePositiveControl.id === effectiveControlId(2) ? 2 : 3,
                                id: blotLabel(activePositiveControl)
                              })}
                        </Heading>
                      </HStack>

                      {/* Active Positive Control Image */}
                      <Box position="relative">
                        <Box
                          position="relative"
                          borderRadius="xl"
                          overflow="hidden"
                          border="3px solid"
                          borderColor={
                            (module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'green.300' :
                            activePositiveControl.id === effectiveControlId(2) ? 'blue.300' : 'red.300'
                          }
                          bg="gray.50"
                          boxShadow={`0 8px 30px ${
                            (module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'rgba(34, 197, 94, 0.15)' :
                            activePositiveControl.id === effectiveControlId(2) ? 'rgba(59, 130, 246, 0.15)' : 'rgba(239, 68, 68, 0.15)'
                          }`}
                          transition="all 0.3s ease"
                          cursor="pointer"
                          _hover={{ transform: 'scale(1.01)' }}
                          onClick={() => setShowRawControlImage(!showRawControlImage)}
                        >
                          {(showRawControlImage ? activePositiveControl.crop_base64 : displayedControlImage) && (
                            <img
                              key={imageRenderKey}
                              src={showRawControlImage ? `data:image/png;base64,${activePositiveControl.crop_base64}` : displayedControlImage!}
                              alt={module === 'protis' ? `${protisRefWord} - ${blotLabel(activePositiveControl)}` : t('blotAnalysis.comparison.controlBlotAlt', { id: activePositiveControl.id })}
                              style={{
                                objectFit: 'contain',
                                width: '100%',
                                maxHeight: '400px',
                                display: 'block'
                              }}
                            />
                          )}
                          {/* Manufacturer control-line marker — same yellow dashed line as
                              the raw-image overlay (this view isn't letterbox-bounds-aware
                              like the draggable comparison strip above, so this is a plain
                              top% within the image box — close enough with no empty
                              top/bottom bars, which this simpler view doesn't have). */}
                          {activePositiveControl.profile?.length > 0 && (() => {
                            const ctrlPos = getControlLine(activePositiveControl)
                            if (ctrlPos <= 0) return null
                            const top = (ctrlPos / activePositiveControl.profile.length) * 100
                            const isDraggingThis = draggingControlLine?.blotId === activePositiveControl.id
                            return (
                              <React.Fragment key="control-line-marker">
                                <Box
                                  position="absolute"
                                  left={0}
                                  right={0}
                                  top={`${top}%`}
                                  h="0"
                                  borderTop={isDraggingThis ? '2.5px dashed #f59e0b' : '2px dashed #fbbf24'}
                                  pointerEvents="none"
                                  zIndex={16}
                                />
                                {!readOnly && (
                                  <Box
                                    position="absolute"
                                    left={0}
                                    right={0}
                                    top={`${top}%`}
                                    h="14px"
                                    mt="-7px"
                                    cursor="row-resize"
                                    zIndex={17}
                                    title={t('blotAnalysis.comparison.dragControlLine', 'Glisser pour repositionner la ligne de contrôle')}
                                    onPointerDown={(e) => handleControlLineDragStart(e, activePositiveControl, DEFAULT_IMAGE_BOUNDS)}
                                    onPointerMove={handleControlLineDragMove}
                                    onPointerUp={handleControlLineDragEnd}
                                  />
                                )}
                              </React.Fragment>
                            )
                          })()}
                        </Box>
                        <Badge
                          position="absolute"
                          bottom={2}
                          right={2}
                          bg={showRawControlImage ? 'orange.500' : 'green.500'}
                          color="white"
                          fontSize="9px"
                          px={2}
                          py={0.5}
                          borderRadius="full"
                          opacity={0.85}
                          cursor="pointer"
                          onClick={() => setShowRawControlImage(!showRawControlImage)}
                        >
                          {showRawControlImage ? 'RAW' : 'REC'}
                        </Badge>
                      </Box>

                      {/* Active Positive Control Profile — hidden in non-premium mode */}
                      {aiAccessible && (<>
                      <Box
                        bg="gray.50"
                        borderRadius="xl"
                        overflow="hidden"
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        <Box
                          px={4}
                          py={2}
                          bgGradient={
                            (module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'linear(to-r, green.50, emerald.50)' :
                            activePositiveControl.id === effectiveControlId(2) ? 'linear(to-r, blue.50, cyan.50)' : 'linear(to-r, red.50, orange.50)'
                          }
                          borderBottom="1px solid"
                          borderColor="gray.100"
                        >
                          <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                            {module === 'protis' ? 'Profil du marqueur' : t('blotAnalysis.comparison.selectedControlProfile')}
                          </Text>
                        </Box>
                        <Box h="350px" p={4} bg="white">
                          {barMode ? (
                            <Bar
                              options={getChartOptions(activePositiveControl, activePositiveControl, undefined, chartBandPositions.size > 0 ? chartBandPositions : undefined)}
                              data={getChartData(
                                activePositiveControl,
                                (module === 'protis' ? protisRefWord : t('blotAnalysis.comparison.controlN', { number: selectedControlForBlot })),
                                (module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'rgb(34, 197, 94)' :
                                activePositiveControl.id === effectiveControlId(2) ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)',
                                module === 'protis' ? activePositiveControl.peaks : undefined
                              )}
                            />
                          ) : (
                            <Line
                              ref={registerChart}
                              options={getChartOptions(activePositiveControl, activePositiveControl, undefined, chartBandPositions.size > 0 ? chartBandPositions : undefined)}
                              data={getChartData(
                                activePositiveControl,
                                (module === 'protis' ? protisRefWord : t('blotAnalysis.comparison.controlN', { number: selectedControlForBlot })),
                                (module === 'protis' || activePositiveControl.id === effectiveControlId(1)) ? 'rgb(34, 197, 94)' :
                                activePositiveControl.id === effectiveControlId(2) ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)',
                                module === 'protis' ? activePositiveControl.peaks : undefined
                              )}
                            />
                          )}
                        </Box>
                      </Box>
                      </>)}
                    </VStack>
                  )}

                  {/* Divider with gradient */}
                  {activePositiveControl && selectedBlot?.id !== activePositiveControl.id && (
                    <Box
                      display={{ base: 'none', lg: 'block' }}
                      w="2px"
                      bgGradient="linear(to-b, transparent, purple.300, transparent)"
                      mx={2}
                    />
                  )}

                  {/* Right side: Selected Blot (full width if it's the control itself) - Enhanced */}
                  <VStack
                    flex="1"
                    spacing={4}
                    align="stretch"
                    bg="white"
                    p={5}
                    borderRadius="2xl"
                    boxShadow="0 10px 40px rgba(128, 90, 213, 0.1)"
                    transition="all 0.3s ease"
                    _hover={{ boxShadow: '0 15px 50px rgba(128, 90, 213, 0.15)' }}
                  >
                    {/* Header with gradient */}
                    <HStack
                      spacing={3}
                      p={3}
                      borderRadius="xl"
                      bgGradient={
                        selectedBlot.id === effectiveControlId(1) ? 'linear(to-r, green.50, emerald.50)' :
                        selectedBlot.id === effectiveControlId(2) ? 'linear(to-r, blue.50, cyan.50)' :
                        selectedBlot.id === effectiveControlId(3) ? 'linear(to-r, red.50, orange.50)' : 'linear(to-r, purple.50, pink.50)'
                      }
                    >
                      {activeControlIds.includes(selectedBlot.id) ? (
                        <>
                          <Box
                            p={2}
                            borderRadius="lg"
                            bgGradient={
                              selectedBlot.id === effectiveControlId(1) ? 'linear(to-r, green.400, green.500)' :
                              selectedBlot.id === effectiveControlId(2) ? 'linear(to-r, blue.400, blue.500)' : 'linear(to-r, red.400, red.500)'
                            }
                          >
                            <Text color="white" fontWeight="800" fontSize="md" lineHeight="1">T+</Text>
                          </Box>
                          <Heading
                            size="sm"
                            color={
                              selectedBlot.id === effectiveControlId(1) ? 'green.700' :
                              selectedBlot.id === effectiveControlId(2) ? 'blue.700' : 'red.700'
                            }
                          >
                            {t('blotAnalysis.comparison.controlNBlot', {
                              number: selectedBlot.id === effectiveControlId(1) ? 1 :
                                selectedBlot.id === effectiveControlId(2) ? 2 : (pairInfoSelected ? 1 : 3),
                              id: blotLabel(blotsArray.find((b) => b.id === selectedBlot.id) ?? selectedBlot)
                            })}
                          </Heading>
                        </>
                      ) : (
                        <>
                          <Box p={2} borderRadius="lg" bgGradient="linear(to-r, purple.400, pink.400)">
                            <Icon as={MdCheckCircle} color="white" boxSize={5} />
                          </Box>
                          <Heading size="sm" color="purple.700">
                            {t('blotAnalysis.comparison.sampleToCompare')} - {blotLabel(blotsArray.find((b) => b.id === selectedBlot.id) ?? selectedBlot)}
                          </Heading>
                        </>
                      )}
                    </HStack>

                    {/* Image left / profile right (side by side) so the témoin
                        calibration fits without scrolling. */}
                    <Flex direction={{ base: 'column', lg: 'row' }} gap={4} align="start" w="full">
                    {/* Selected Blot Image + validation buttons side by side */}
                    <HStack spacing={4} align="stretch" w={{ base: 'full', lg: aiAccessible ? '190px' : 'full' }} flexShrink={0}>
                    <Box position="relative" flex="1">
                      <Box
                        position="relative"
                        borderRadius="xl"
                        overflow="hidden"
                        border="3px solid"
                        borderColor={
                          selectedBlot.id === effectiveControlId(1) ? 'green.300' :
                          selectedBlot.id === effectiveControlId(2) ? 'blue.300' :
                          selectedBlot.id === effectiveControlId(3) ? 'red.300' : 'purple.300'
                        }
                        bg="gray.50"
                        boxShadow={`0 8px 30px ${
                          selectedBlot.id === effectiveControlId(1) ? 'rgba(34, 197, 94, 0.15)' :
                          selectedBlot.id === effectiveControlId(2) ? 'rgba(59, 130, 246, 0.15)' :
                          selectedBlot.id === effectiveControlId(3) ? 'rgba(239, 68, 68, 0.15)' : 'rgba(128, 90, 213, 0.15)'
                        }`}
                        transition="all 0.3s ease"
                        cursor="crosshair"
                        onClick={(e) => { if (!handleControlImageCalibrationClick(e)) setShowRawImage(!showRawImage) }}
                        onMouseMove={(e: React.MouseEvent<HTMLDivElement>) =>
                          handleImageMouseMove(e, selectedBlot)
                        }
                        onMouseLeave={handleImageMouseLeave}
                      >
                        <Image
                          src={`data:image/png;base64,${showRawImage ? selectedBlot.crop_base64 : selectedBlot.reconstructed_base64}`}
                          alt={`Blot ${selectedBlot.id}`}
                          objectFit="contain"
                          w="100%"
                          maxH="500px"
                          filter={selectedBlotVeiled ? 'blur(16px)' : undefined}
                          pointerEvents="none"
                        />
                        {/* Manufacturer control-line marker — same yellow dashed line as
                            the raw-image overlay. */}
                        {selectedBlot.profile?.length > 0 && (() => {
                          const ctrlPos = getControlLine(selectedBlot)
                          if (ctrlPos <= 0) return null
                          const top = (ctrlPos / selectedBlot.profile.length) * 100
                          const isDraggingThis = draggingControlLine?.blotId === selectedBlot.id
                          return (
                            <React.Fragment key="control-line-marker">
                              <Box
                                position="absolute"
                                left={0}
                                right={0}
                                top={`${top}%`}
                                h="0"
                                borderTop={isDraggingThis ? '2.5px dashed #f59e0b' : '2px dashed #fbbf24'}
                                pointerEvents="none"
                                zIndex={16}
                              />
                              {!readOnly && (
                                <Box
                                  position="absolute"
                                  left={0}
                                  right={0}
                                  top={`${top}%`}
                                  h="14px"
                                  mt="-7px"
                                  cursor="row-resize"
                                  zIndex={17}
                                  title={t('blotAnalysis.comparison.dragControlLine', 'Glisser pour repositionner la ligne de contrôle')}
                                  onPointerDown={(e) => handleControlLineDragStart(e, selectedBlot, DEFAULT_IMAGE_BOUNDS)}
                                  onPointerMove={handleControlLineDragMove}
                                  onPointerUp={handleControlLineDragEnd}
                                />
                              )}
                            </React.Fragment>
                          )
                        })()}
                      </Box>
                      <Badge
                        data-tour="rec-raw"
                        position="absolute"
                        bottom={2}
                        right={2}
                        bg={showRawImage ? 'orange.500' : 'purple.500'}
                        color="white"
                        fontSize="9px"
                        px={2}
                        py={0.5}
                        borderRadius="full"
                        opacity={0.85}
                        cursor="pointer"
                        onClick={() => setShowRawImage(!showRawImage)}
                      >
                        {showRawImage ? 'RAW' : 'REC'}
                      </Badge>
                      {/* Hover cursor line */}
                      {hoverState && (
                        <>
                          <Box
                            position="absolute"
                            left={0}
                            top={`${hoverState.relFraction * 100}%`}
                            w="100%"
                            h="2px"
                            bg="yellow.400"
                            opacity={0.85}
                            pointerEvents="none"
                            zIndex={5}
                            style={{ transform: 'translateY(-50%)' }}
                          />
                          <Box
                            position="absolute"
                            left={2}
                            top={`${Math.min(92, Math.max(3, hoverState.relFraction * 100))}%`}
                            bg="yellow.900"
                            color="yellow.100"
                            fontSize="9px"
                            px={1.5}
                            py={0.5}
                            borderRadius="md"
                            pointerEvents="none"
                            zIndex={6}
                            whiteSpace="nowrap"
                            style={{ transform: 'translateY(-50%)' }}
                          >
                            {Math.round(hoverState.relFraction * selectedBlot.profile.length)} px
                            {showKdaScale && computeKda(hoverState.chartX) !== null && (
                              <> · {computeKda(hoverState.chartX)} kDa</>
                            )}
                          </Box>
                        </>
                      )}
                    </Box>

                    {/* Validation buttons — RIGHT of image, non-premium + NOT the control */}
                    {!aiAccessible &&
                     !activeControlIds.includes(selectedBlot.id) && (
                      <VStack
                        w="220px"
                        flexShrink={0}
                        spacing={3}
                        justify="center"
                        py={4}
                      >
                        {!activePositiveControl && (
                          <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="lg" px={3} py={2} w="full">
                            <Text fontSize="xs" color="orange.700" fontWeight="600">
                              Sélectionnez un témoin positif avant de valider cette bandelette.
                            </Text>
                          </Box>
                        )}
                        <ManualResultSelector
                          blotId={selectedBlot.id}
                          value={manualBlotResults[selectedBlot.id] ?? null}
                          onChange={(result) => onManualResultChange?.(selectedBlot.id, result)}
                          interpretation={immunoblotConfig?.interpretation as any ?? null}
                          isDisabled={readOnly || !activePositiveControl}
                          showHotkeys
                          detectedBandNames={new Set(blotPlacedBands.get(selectedBlot.id)?.keys() ?? [])}
                          bands={immunoblotConfig?.bands?.specific as any ?? []}
                        />

                        {manualBlotResults[selectedBlot.id] != null && selectedBlotIndex < displayedBlotsArray.length - 1 && (
                          <Button
                            w="full"
                            colorScheme="blue"
                            variant="outline"
                            size="sm"
                            rightIcon={<MdChevronRight />}
                            onClick={handleNextBlot}
                          >
                            Suivante ({selectedBlotIndex + 1}/{displayedBlotsArray.length})
                          </Button>
                        )}
                        {manualBlotResults[selectedBlot.id] != null &&
                         displayedBlotsArray.filter(b => !(activeControlIds.includes(b.id))).every(b => manualBlotResults[b.id] != null) && (
                          <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="xl" p={3} w="full" textAlign="center">
                            <Text fontSize="xs" color="green.700" fontWeight="bold">{t('blotAnalysis.gallery.allValidated')}</Text>
                            <Text fontSize="2xs" color="green.600">{t('blotAnalysis.gallery.exportAvailable')}</Text>
                          </Box>
                        )}
                      </VStack>
                    )}
                    </HStack>

                    {/* Selected Blot Profile + bands — hidden in non-premium mode */}
                    {aiAccessible && (<Box flex="1" minW={0} w="full">
                    <Box
                      bg="gray.50"
                      borderRadius="xl"
                      overflow="hidden"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Box
                        px={4}
                        py={2}
                        bgGradient={
                          activeControlIds.includes(selectedBlot.id)
                            ? 'linear(to-r, green.50, emerald.50)'
                            : 'linear(to-r, purple.50, pink.50)'
                        }
                        borderBottom="1px solid"
                        borderColor="gray.100"
                      >
                        <HStack justify="space-between">
                          <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                            {activeControlIds.includes(selectedBlot.id)
                              ? (module === 'protis' ? 'Profil du marqueur' : t('blotAnalysis.comparison.positiveControlProfile'))
                              : t('blotAnalysis.comparison.sampleProfile')}
                          </Text>

                          {/* Band Selection Button - Only for positive controls */}
                          {(activeControlIds.includes(selectedBlot.id)) && (
                            <HStack spacing={2}>
                              {/* Hide band annotations → clean profile+peaks to place
                                  calibration bands precisely on the peaks. */}
                              <Tooltip label={showChartBands ? t('blotAnalysis.comparison.hideAnnotations') : t('blotAnalysis.comparison.showAnnotations')} hasArrow>
                                <Button
                                  size="xs"
                                  variant={showChartBands ? 'outline' : 'solid'}
                                  colorScheme="orange"
                                  onClick={() => setShowChartBands(!showChartBands)}
                                  leftIcon={<Box w={2} h={2} borderRadius="full" bg={showChartBands ? 'orange.500' : 'white'} />}
                                >
                                  {showChartBands ? 'Pics seuls' : 'Annotations'}
                                </Button>
                              </Tooltip>
                              {bandSelectionMode ? (
                                <HStack spacing={2} flexWrap="wrap">
                                  <Badge
                                    colorScheme={REQUIRED_BAND_NAMES.includes(currentBandToSelect!) ? 'red' : 'orange'}
                                    fontSize="xs"
                                    px={2}
                                    py={0.5}
                                  >
                                    {REQUIRED_BAND_NAMES.includes(currentBandToSelect!) ? '★ ' : ''}{currentBandToSelect}
                                    {' '}— cliquez sur le pic ou l'image
                                  </Badge>
                                  <Button size="xs" colorScheme="red" variant="ghost" onClick={cancelBandSelection}>
                                    Annuler
                                  </Button>
                                </HStack>
                              ) : (
                                <Button
                                  size="xs"
                                  colorScheme="blue"
                                  variant="solid"
                                  onClick={handleCalibrateButtonClick}
                                  leftIcon={<Icon as={MdStar} />}
                                  title={effectiveBandPositions.size > 0 ? 'Recommencer la calibration depuis zéro — pour corriger une seule bande, cliquez sur sa pastille' : ''}
                                >
                                  {effectiveBandPositions.size > 0 ? t('blotAnalysis.calibration.recalibrateAll') : t('blotAnalysis.calibration.calibrateBands')}
                                </Button>
                              )}
                            </HStack>
                          )}
                        </HStack>

                        {/* Band pills removed — the clickable "Bandes" panel on the
                            right of the chart is now the single place to (re)position
                            each band (click a band → place it on the peak). */}
                      </Box>
                      <HStack align="stretch" spacing={0}>
                        <Box
                          flex="1"
                          h="350px"
                          p={4}
                          bg="white"
                          cursor={bandSelectionMode && (activeControlIds.includes(selectedBlot.id)) ? 'crosshair' : 'default'}
                        >
                          {barMode ? (
                            <Bar
                              options={getChartOptions(
                                selectedBlotWithEnhancedPeaks!,
                                positiveControl,
                                bandSelectionMode && (activeControlIds.includes(selectedBlot.id))
                                  ? handleBandPeakClick
                                  : undefined,
                                chartBandPositions.size > 0 ? chartBandPositions : undefined
                              )}
                              data={getChartData(
                                selectedBlotWithEnhancedPeaks!,
                                activeControlIds.includes(selectedBlot.id)
                                  ? t('blotAnalysis.comparison.positiveControl')
                                  : t('blotAnalysis.comparison.sample'),
                                selectedBlot.id === effectiveControlId(1) ? 'rgb(34, 197, 94)' :
                                selectedBlot.id === effectiveControlId(2) ? 'rgb(59, 130, 246)' :
                                selectedBlot.id === effectiveControlId(3) ? 'rgb(239, 68, 68)' : 'rgb(168, 85, 247)',
                                selectedBlot.peaks
                              )}
                            />
                          ) : (
                            <Line
                              ref={registerChart}
                              options={getChartOptions(
                                selectedBlotWithEnhancedPeaks!,
                                positiveControl,
                                bandSelectionMode && (activeControlIds.includes(selectedBlot.id))
                                  ? handleBandPeakClick
                                  : undefined,
                                chartBandPositions.size > 0 ? chartBandPositions : undefined
                              )}
                              data={getChartData(
                                selectedBlotWithEnhancedPeaks!,
                                activeControlIds.includes(selectedBlot.id)
                                  ? t('blotAnalysis.comparison.positiveControl')
                                  : t('blotAnalysis.comparison.sample'),
                                selectedBlot.id === effectiveControlId(1) ? 'rgb(34, 197, 94)' :
                                selectedBlot.id === effectiveControlId(2) ? 'rgb(59, 130, 246)' :
                                selectedBlot.id === effectiveControlId(3) ? 'rgb(239, 68, 68)' : 'rgb(168, 85, 247)',
                                selectedBlot.peaks
                              )}
                            />
                          )}
                        </Box>

                        {/* Guide des bandes — visible uniquement sur le témoin positif */}
                        {(activeControlIds.includes(selectedBlot.id)) &&
                          immunoblotConfig?.bands?.specific && (
                          <Box
                            data-tour="bands-panel"
                            w="130px"
                            flexShrink={0}
                            borderLeftWidth="1px"
                            borderColor="gray.100"
                            bg="gray.50"
                            p={2}
                            overflowY="auto"
                            h="350px"
                          >
                            <Text fontSize="2xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                              Bandes
                            </Text>
                            <Text fontSize="2xs" color="gray.400" mb={2}>{t('blotAnalysis.calibration.clickToPlace')}</Text>
                            <VStack spacing={1} align="stretch">
                              {[...immunoblotConfig.bands.specific]
                                .sort((a: any, b: any) => b.molecularWeight - a.molecularWeight)
                                .map((band: any) => {
                                  const isCalibrated = effectiveBandPositions.has(band.name)
                                  const isCurrent = bandSelectionMode && currentBandToSelect === band.name
                                  const pos = effectiveBandPositions.get(band.name)?.position
                                  // Suggested (auto) bands are amber; operator-confirmed are green.
                                  const isSuggested = isCalibrated && bandsAreSuggested
                                  const scheme = isCurrent ? 'orange' : isSuggested ? 'yellow' : isCalibrated ? 'green' : 'gray'
                                  return (
                                    <Tooltip
                                      key={band.name}
                                      label={isSuggested ? `${band.name} suggérée — cliquer pour ajuster` : isCalibrated ? `Cliquer pour repositionner ${band.name}` : `Cliquer pour placer ${band.name}`}
                                      placement="left"
                                      hasArrow
                                      openDelay={300}
                                    >
                                    <HStack
                                      spacing={1}
                                      role="button"
                                      cursor={readOnly ? 'default' : 'pointer'}
                                      onClick={() => !readOnly && startBandSelection({ singleBand: band.name })}
                                      bg={scheme === 'gray' ? 'white' : `${scheme}.50`}
                                      borderRadius="md"
                                      px={1.5}
                                      py={1}
                                      borderWidth="1px"
                                      borderColor={`${scheme}.200`}
                                      transition="all 0.12s ease"
                                      _hover={readOnly ? undefined : { borderColor: 'blue.300', bg: scheme === 'gray' ? 'blue.50' : `${scheme}.100`, transform: 'translateX(-1px)' }}
                                    >
                                      <VStack spacing={0} align="flex-start" flex={1} minW={0}>
                                        <HStack spacing={1}>
                                          <Text fontSize="xs" fontWeight="bold" color={`${scheme}.700`}>
                                            {band.name}
                                          </Text>
                                          {band.required && (
                                            <Badge fontSize="2xs" colorScheme="red" variant="solid" px={1} py={0} lineHeight="1">
                                              ★
                                            </Badge>
                                          )}
                                        </HStack>
                                        <Text fontSize="2xs" color="gray.400">
                                          {band.molecularWeight} kDa{pos != null ? ` · ${Math.round(pos)}px` : ''}
                                        </Text>
                                      </VStack>
                                      {isCurrent ? (
                                        <Text fontSize="2xs" color="orange.600" fontWeight="700">…</Text>
                                      ) : isCalibrated ? (
                                        <Text fontSize="sm" color={`${scheme}.500`}>{isSuggested ? '≈' : '✓'}</Text>
                                      ) : (
                                        <Icon as={MdChevronRight} color="gray.300" boxSize={3.5} />
                                      )}
                                    </HStack>
                                    </Tooltip>
                                  )
                                })}
                            </VStack>
                          </Box>
                        )}
                      </HStack>
                    </Box>
                      <VStack justify="center" mt={2} spacing={1} maxW="100%">
                        <Badge
                          bg={activeControlIds.includes(selectedBlot.id) ? 'orange.100' : 'purple.100'}
                          color={activeControlIds.includes(selectedBlot.id) ? 'orange.700' : 'purple.700'}
                          fontSize="xs"
                          px={3}
                          py={1}
                          borderRadius="full"
                        >
                          {enhancedSelectedPeaks.length} {enhancedSelectedPeaks.length !== 1 ? t('blotAnalysis.gallery.peaks') : t('blotAnalysis.gallery.peak')}
                        </Badge>
                      </VStack>
                    </Box>)}
                    </Flex>

                      {/* Manual Band Ratios Comparison - only for samples (not control).
                          Gated by SHOW_RATIO_COMPARISON: quality indicator, not a verdict criterion. */}
                      {SHOW_RATIO_COMPARISON && !activeControlIds.includes(selectedBlot.id) && (
                        <Box
                          w="full"
                          bg="white"
                          borderRadius="xl"
                          border="1px solid"
                          borderColor={manualRatioComparison?.isConsistent ? 'green.200' : 'gray.200'}
                          p={4}
                          mt={3}
                        >
                          <HStack justify="space-between" mb={3}>
                            <Text fontWeight="bold" fontSize="sm" color="gray.700">
                              Comparaison des ratios
                            </Text>
                            {manualRatioComparison ? (
                              <Badge
                                colorScheme={manualRatioComparison.isConsistent ? 'green' : 'orange'}
                                fontSize="xs"
                              >
                                {manualRatioComparison.overallSimilarity != null
                                  ? `Similarité: ${manualRatioComparison.overallSimilarity.toFixed(0)}%`
                                  : manualRatioComparison.controlIntensities.size < Math.min(BAND_NAMES.length, 3)
                                    ? `Similarité: N/A (${manualRatioComparison.controlIntensities.size}/${BAND_NAMES.length} bandes calibrées sur le témoin)`
                                    : `Similarité: N/A (${manualRatioComparison.ratioDeltas.size}/${manualRatioComparison.controlRatios.size} ratios comparables — signal échantillon insuffisant)`}
                              </Badge>
                            ) : (
                              <HStack spacing={2}>
                                <Tooltip
                                  label="La calibration permet de comparer les ratios d'intensité. L'interprétation (POSITIF/NÉGATIF) est basée sur le nombre de bandes détectées."
                                  hasArrow
                                  placement="top"
                                >
                                  <Badge colorScheme="gray" fontSize="xs" cursor="help">
                                    Non calibré (optionnel)
                                  </Badge>
                                </Tooltip>
                                {module === 'iris' && activePositiveControl && (
                                  <Button size="xs" colorScheme="blue" variant="outline" onClick={navigateToControlAndCalibrate}>
                                    Calibrer T{selectedControlForBlot}
                                  </Button>
                                )}
                              </HStack>
                            )}
                          </HStack>

                          {manualRatioComparison ? (
                            manualRatioComparison.isSelfComparison ? (
                              <Box bg="blue.50" borderRadius="md" p={3}>
                                <Text fontSize="xs" color="blue.600" textAlign="center">
                                  ℹ️ Calibration effectuée - naviguez vers un échantillon (→) pour voir la comparaison des ratios
                                </Text>
                                <HStack mt={2} justify="center" spacing={2} flexWrap="wrap">
                                  {Array.from(manualRatioComparison.controlRatios.entries()).map(([ratioName, ratio]) => (
                                    <Badge key={ratioName} colorScheme="green" fontSize="xs">
                                      {ratioName}: {ratio.toFixed(2)}
                                    </Badge>
                                  ))}
                                </HStack>
                              </Box>
                            ) : (
                              <Box overflowX="auto">
                                <Table size="sm" variant="simple">
                                  <Thead>
                                    <Tr>
                                      <Th fontSize="xs" px={2}>Ratio</Th>
                                      <Th fontSize="xs" px={2} isNumeric>Témoin</Th>
                                      <Th fontSize="xs" px={2} isNumeric>Échantillon</Th>
                                      <Th fontSize="xs" px={2} isNumeric>{t('blotAnalysis.comparison.diff')}</Th>
                                      <Th fontSize="xs" px={2}>OK</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {Array.from(manualRatioComparison.ratioDeltas.entries()).map(([ratioName, data]) => (
                                      <Tr key={ratioName}>
                                        <Td fontSize="xs" px={2} fontWeight="medium">{ratioName}</Td>
                                        <Td fontSize="xs" px={2} isNumeric>{data.control.toFixed(2)}</Td>
                                        <Td fontSize="xs" px={2} isNumeric>{data.sample.toFixed(2)}</Td>
                                        <Td fontSize="xs" px={2} isNumeric color={data.percentDiff > 25 ? 'red.500' : 'green.500'}>
                                          {data.percentDiff.toFixed(0)}%
                                        </Td>
                                        <Td fontSize="xs" px={2}>
                                          {data.percentDiff <= 25 ? '✓' : '✗'}
                                        </Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </Box>
                            )
                          ) : (
                            <VStack spacing={2}>
                              <Text fontSize="xs" color="gray.500" textAlign="center">
                                {activePositiveControl
                                  ? t('blotAnalysis.calibration.controlNotCalibrated', { id: activePositiveControl.id })
                                  : t('blotAnalysis.calibration.noControlSelected')}
                              </Text>
                              {activePositiveControl && (
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  leftIcon={<Icon as={MdStar} />}
                                  onClick={navigateToControlAndCalibrate}
                                >
                                  Calibrer le témoin {activePositiveControl.id}
                                </Button>
                              )}
                            </VStack>
                          )}
                        </Box>
                      )}

                      {/* Témoin (positive control): the auto-interpretation is not meaningful on
                          the control itself, so show the test guide (performances, critères,
                          bandes à identifier) — the same recap as the "i" next to the selected
                          analysis. Samples keep the interpretation card below. */}
                      {aiAccessible && immunoblotConfig && (
                        (activeControlIds.includes(selectedBlot.id)) ? (
                          <Box
                            w="full"
                            bg="white"
                            borderRadius="xl"
                            border="2px solid"
                            borderColor="teal.200"
                            overflow="hidden"
                            mt={3}
                            boxShadow="0 8px 25px rgba(20, 184, 166, 0.12)"
                          >
                            <Box px={4} py={3} bgGradient="linear(to-r, teal.50, cyan.50)" borderBottom="1px solid" borderColor="teal.100">
                              <HStack>
                                <Icon as={MdInfo} color="teal.500" boxSize={4} />
                                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                                  Guide d'analyse{immunoblotConfig.name ? ` — ${pathologyDisplayName(immunoblotConfig.name)}` : ''}
                                </Text>
                              </HStack>
                            </Box>
                            <VStack align="stretch" spacing={4} p={4}>
                              {(immunoblotConfig as any).clinicalNotes?.performance && (
                                <Box>
                                  <Text fontWeight="bold" fontSize="2xs" color="teal.600" mb={2} textTransform="uppercase" letterSpacing="wide">
                                    Performances du test
                                  </Text>
                                  <HStack spacing={4} bg="teal.50" p={3} borderRadius="md">
                                    <Box textAlign="center" flex="1">
                                      <Text fontSize="lg" fontWeight="bold" color="green.600">
                                        {(immunoblotConfig as any).clinicalNotes.performance.sensitivity}
                                      </Text>
                                      <Text fontSize="xs" color="gray.600">{t('blotAnalysis.clinical.sensitivity')}</Text>
                                      {(immunoblotConfig as any).clinicalNotes.performance.sensitivityCI && (
                                        <Text fontSize="2xs" color="gray.500">{t('blotAnalysis.clinical.ci')}: {(immunoblotConfig as any).clinicalNotes.performance.sensitivityCI}</Text>
                                      )}
                                    </Box>
                                    <Box w="1px" alignSelf="stretch" bg="teal.200" />
                                    <Box textAlign="center" flex="1">
                                      <Text fontSize="lg" fontWeight="bold" color="blue.600">
                                        {(immunoblotConfig as any).clinicalNotes.performance.specificity}
                                      </Text>
                                      <Text fontSize="xs" color="gray.600">{t('blotAnalysis.clinical.specificity')}</Text>
                                      {(immunoblotConfig as any).clinicalNotes.performance.specificityCI && (
                                        <Text fontSize="2xs" color="gray.500">{t('blotAnalysis.clinical.ci')}: {(immunoblotConfig as any).clinicalNotes.performance.specificityCI}</Text>
                                      )}
                                    </Box>
                                  </HStack>
                                </Box>
                              )}
                              <Box>
                                <Text fontWeight="bold" fontSize="2xs" color="teal.600" mb={2} textTransform="uppercase" letterSpacing="wide">
                                  Critères d'interprétation
                                </Text>
                                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                                  <Box bg="green.50" p={2} borderRadius="md" borderLeft="3px solid" borderLeftColor="green.400">
                                    <Text fontSize="xs" fontWeight="bold" color="green.700">POSITIF</Text>
                                    <Text fontSize="xs" color="gray.700">
                                      {immunoblotConfig.interpretation?.positive?.criteria?.map((c: any) => c.description).join(' ET ') || t('blotAnalysis.clinical.notDefined')}
                                    </Text>
                                  </Box>
                                  <Box bg="red.50" p={2} borderRadius="md" borderLeft="3px solid" borderLeftColor="red.400">
                                    <Text fontSize="xs" fontWeight="bold" color="red.700">NÉGATIF</Text>
                                    <Text fontSize="xs" color="gray.700">
                                      {immunoblotConfig.interpretation?.negative?.criteria?.map((c: any) => c.description).join(' ') || t('blotAnalysis.clinical.notDefined')}
                                    </Text>
                                  </Box>
                                </SimpleGrid>
                              </Box>
                              <Box>
                                <Text fontWeight="bold" fontSize="2xs" color="teal.600" mb={2} textTransform="uppercase" letterSpacing="wide">
                                  Bandes à identifier ({immunoblotConfig.bands?.reference?.primaryZone || '30-45 kDa'})
                                </Text>
                                <SimpleGrid columns={{ base: 2, sm: 3 }} spacingX={3} spacingY={1}>
                                  {(() => {
                                    // Merge the three band groups into one deduped list — some
                                    // stored configs list a band in both `specific` and
                                    // `intermediate`, which used to render it twice. Species tag
                                    // is shown as a compact M/G chip (legend below) to keep rows
                                    // to a single line in the grid.
                                    const seen = new Set<string>()
                                    const rows: Array<{ band: any; kind: string }> = []
                                    const push = (band: any, kind: string) => {
                                      if (!band?.name || seen.has(band.name)) return
                                      seen.add(band.name)
                                      rows.push({ band, kind })
                                    }
                                    ;(immunoblotConfig.bands?.specific ?? []).forEach((b: any) => push(b, b.required ? 'required' : 'specific'))
                                    ;((immunoblotConfig.bands as any)?.intermediate ?? []).forEach((b: any) => push(b, 'intermediate'))
                                    ;(immunoblotConfig.bands?.nonSpecific ?? []).forEach((b: any) => push(b, 'nonspecific'))
                                    const spTag = (s?: string) => s === 'E. multilocularis' ? 'M' : s === 'E. granulosus' ? 'G' : null
                                    return rows.map(({ band, kind }) => {
                                      const tag = kind === 'intermediate' ? spTag(band.species) : null
                                      const icon = kind === 'required' ? MdStar : kind === 'nonspecific' ? MdWarning : MdCheckCircle
                                      const iconColor = kind === 'required' ? 'red.500' : kind === 'intermediate' ? 'purple.400' : kind === 'nonspecific' ? 'gray.400' : 'green.500'
                                      return (
                                        <HStack key={band.id ?? band.name} spacing={1.5} fontSize="xs" minW={0}>
                                          <Icon as={icon} color={iconColor} boxSize={3} flexShrink={0} />
                                          <Text fontWeight={kind === 'required' ? 'bold' : 'normal'} color={kind === 'nonspecific' ? 'gray.400' : 'gray.700'} noOfLines={1}>
                                            {band.name} <Text as="span" color="gray.400">({band.molecularWeight})</Text>
                                          </Text>
                                          {tag && (
                                            <Badge colorScheme={tag === 'M' ? 'orange' : 'blue'} fontSize="2xs" variant="solid" borderRadius="sm" flexShrink={0}>{tag}</Badge>
                                          )}
                                        </HStack>
                                      )
                                    })
                                  })()}
                                </SimpleGrid>
                                {((immunoblotConfig.bands as any)?.intermediate ?? []).some((b: any) => b.species && b.species !== 'common') && (
                                  <HStack spacing={3} mt={1.5} fontSize="2xs" color="gray.500" flexWrap="wrap">
                                    <HStack spacing={1}><Badge colorScheme="orange" fontSize="2xs" variant="solid" borderRadius="sm">M</Badge><Text>E. multilocularis</Text></HStack>
                                    <HStack spacing={1}><Badge colorScheme="blue" fontSize="2xs" variant="solid" borderRadius="sm">G</Badge><Text>E. granulosus</Text></HStack>
                                    <Text fontStyle="italic">{t('blotAnalysis.clinical.speciesLegend')}</Text>
                                  </HStack>
                                )}
                              </Box>

                              {/* Species-differentiation profiles (Echinococcus P1–P5) */}
                              {(immunoblotConfig.interpretation as any)?.profiles && (
                                <Box>
                                  <Text fontWeight="bold" fontSize="2xs" color="teal.600" mb={2} textTransform="uppercase" letterSpacing="wide">
                                    Diagnostic d'espèce
                                  </Text>
                                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={1.5}>
                                    {Object.values((immunoblotConfig.interpretation as any).profiles as Record<string, any>).map((p: any) => {
                                      const known = p.species && p.species !== 'indéterminé'
                                      return (
                                        <Box key={p.name} bg={known ? 'purple.50' : 'gray.50'} p={2} borderRadius="md" borderLeft="3px solid" borderLeftColor={known ? 'purple.300' : 'gray.300'}>
                                          <HStack justify="space-between" mb={0.5}>
                                            <Text fontSize="xs" fontWeight="bold" color={known ? 'purple.700' : 'gray.600'}>{p.name}</Text>
                                            <Text fontSize="xs" fontWeight="bold" fontStyle="italic" color={known ? 'purple.700' : 'gray.500'}>{p.species}</Text>
                                          </HStack>
                                          <Text fontSize="2xs" color="gray.600" lineHeight="1.4">{p.description}</Text>
                                        </Box>
                                      )
                                    })}
                                  </SimpleGrid>
                                </Box>
                              )}

                              {/* Quality control + Limitations side by side to cut height */}
                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                {(immunoblotConfig as any).qualityControl && (
                                  <Box>
                                    <Text fontWeight="bold" fontSize="2xs" color="teal.600" mb={2} textTransform="uppercase" letterSpacing="wide">
                                      Contrôle qualité
                                    </Text>
                                    <VStack spacing={1} align="stretch" fontSize="xs">
                                      <Text><Text as="span" fontWeight="bold">{t('blotAnalysis.clinical.positiveControl')}</Text> {(immunoblotConfig as any).qualityControl.positiveControl?.description}</Text>
                                      <Text><Text as="span" fontWeight="bold">{t('blotAnalysis.clinical.negativeControl')}</Text> {(immunoblotConfig as any).qualityControl.negativeControl?.description}</Text>
                                    </VStack>
                                  </Box>
                                )}
                                {(immunoblotConfig as any).clinicalNotes?.limitations && (
                                  <Box>
                                    <Text fontWeight="bold" fontSize="2xs" color="orange.600" mb={2} textTransform="uppercase" letterSpacing="wide">
                                      Limitations
                                    </Text>
                                    <VStack spacing={1} align="stretch" fontSize="xs" color="gray.600">
                                      {(immunoblotConfig as any).clinicalNotes.limitations.map((lim: string, idx: number) => (
                                        <HStack key={idx} spacing={2} align="start">
                                          <Icon as={MdWarning} color="orange.400" boxSize={3} mt="2px" />
                                          <Text>{lim}</Text>
                                        </HStack>
                                      ))}
                                    </VStack>
                                  </Box>
                                )}
                              </SimpleGrid>
                            </VStack>
                          </Box>
                        ) : (
                        <Box
                          w="full"
                          bg="white"
                          borderRadius="xl"
                          border="2px solid"
                          borderColor={selectedBlotInterpretation!.color + '.200'}
                          overflow="hidden"
                          mt={3}
                          boxShadow={`0 8px 25px ${
                            selectedBlotInterpretation!.color === 'green' ? 'rgba(34, 197, 94, 0.15)' :
                            selectedBlotInterpretation!.color === 'orange' ? 'rgba(237, 137, 54, 0.15)' : 'rgba(239, 68, 68, 0.15)'
                          }`}
                          transition="all 0.3s ease"
                          _hover={{ transform: 'translateY(-2px)' }}
                        >
                          {/* Header with result badge */}
                          <Box
                            px={4}
                            py={3}
                            bgGradient={
                              selectedBlotInterpretation!.color === 'green' ? 'linear(to-r, green.50, emerald.50)' :
                              selectedBlotInterpretation!.color === 'orange' ? 'linear(to-r, orange.50, yellow.50)' : 'linear(to-r, red.50, pink.50)'
                            }
                            borderBottom="1px solid"
                            borderColor={selectedBlotInterpretation!.color + '.100'}
                          >
                            <HStack justify="space-between" w="full">
                              <HStack>
                                <Box
                                  w={3}
                                  h={3}
                                  borderRadius="full"
                                  bgGradient={
                                    selectedBlotInterpretation!.color === 'green' ? 'linear(to-r, green.400, emerald.400)' :
                                    selectedBlotInterpretation!.color === 'orange' ? 'linear(to-r, orange.400, yellow.400)' : 'linear(to-r, red.400, pink.400)'
                                  }
                                />
                                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                                  {t('blotAnalysis.comparison.automaticInterpretation')}
                                </Text>
                              </HStack>
                              <Badge
                                bg={selectedBlotInterpretation!.color + '.500'}
                                color="white"
                                fontSize="sm"
                                px={4}
                                py={1.5}
                                borderRadius="full"
                                fontWeight="bold"
                                boxShadow="0 2px 10px rgba(0,0,0,0.15)"
                                animation={selectedBlotInterpretation!.color === 'green' ? `${pulseGlow} 2s ease-in-out infinite` : 'none'}
                              >
                                {selectedBlotInterpretation!.result}
                              </Badge>
                              {(selectedBlotInterpretation as any).needsReview && manualBlotResults[selectedBlot!.id] == null && (
                                <Tooltip label={t('blotAnalysis.clinical.borderlineTooltip')} hasArrow placement="top">
                                  <Badge bgGradient="linear(to-r, orange.400, red.500)" color="white" fontSize="xs" px={3} py={1} borderRadius="full" fontWeight="bold" cursor="help" whiteSpace="nowrap" animation={`${pulseWarn} 1.8s ease-in-out infinite`}>
                                    ⚠ {t('blotAnalysis.clinical.toCheck')}
                                  </Badge>
                                </Tooltip>
                              )}
                              {/* Cotation semi-quantitative (Oliva 2015) — cas Bordeaux seulement. */}
                              {(selectedBlotInterpretation as any).semiQuant && !activeControlIds.includes(selectedBlot!.id) && (
                                <Tooltip label={semiQuantTooltip((selectedBlotInterpretation as any).semiQuant)} hasArrow placement="top" maxW="340px">
                                  <Badge colorScheme={(selectedBlotInterpretation as any).semiQuant.strength === 'strong' ? 'purple' : 'cyan'} variant="solid" fontSize="xs" px={3} py={1} borderRadius="full" fontWeight="bold" cursor="help" whiteSpace="nowrap">
                                    {(selectedBlotInterpretation as any).semiQuant.label} · I={(selectedBlotInterpretation as any).semiQuant.I} ⓘ
                                  </Badge>
                                </Tooltip>
                              )}
                            </HStack>
                          </Box>

                          <VStack align="start" spacing={3} p={4}>
                            <Text fontSize="sm" color="gray.600">
                              {selectedBlotInterpretation!.conclusion}
                            </Text>

                            {/* Guide d'analyse (config.qualityControl.analysisGuide) — built
                                for paired-comparison pathologies (CIP-WB) when the notice was
                                turned into a config, but never actually displayed anywhere in
                                the UI until now. */}
                            {(immunoblotConfig as any)?.qualityControl?.analysisGuide && (
                              <Box w="full" borderRadius="lg" border="1px solid" borderColor="blue.100" bg="blue.50" overflow="hidden">
                                <HStack
                                  as="button"
                                  onClick={() => setShowAnalysisGuide(v => !v)}
                                  w="full"
                                  justify="space-between"
                                  px={3}
                                  py={2}
                                  cursor="pointer"
                                  _hover={{ bg: 'blue.100' }}
                                >
                                  <Text fontSize="xs" fontWeight="bold" color="blue.700">
                                    📘 {(immunoblotConfig as any).qualityControl.analysisGuide.title || 'Guide d\'analyse'}
                                  </Text>
                                  <Text fontSize="xs" color="blue.500">{showAnalysisGuide ? '▲ Masquer' : '▼ Afficher'}</Text>
                                </HStack>
                                <Collapse in={showAnalysisGuide} animateOpacity>
                                  <VStack align="start" spacing={3} px={3} pb={3} fontSize="xs" color="gray.700">
                                    {(immunoblotConfig as any).qualityControl.analysisGuide.summary && (
                                      <Text>{(immunoblotConfig as any).qualityControl.analysisGuide.summary}</Text>
                                    )}
                                    {(immunoblotConfig as any).qualityControl.analysisGuide.steps?.length > 0 && (
                                      <Box>
                                        <Text fontWeight="bold" mb={1}>Étapes :</Text>
                                        <VStack as="ol" align="start" spacing={0.5} pl={4} sx={{ listStyleType: 'decimal' }}>
                                          {(immunoblotConfig as any).qualityControl.analysisGuide.steps.map((s: string, i: number) => (
                                            <Box as="li" key={i}>{s}</Box>
                                          ))}
                                        </VStack>
                                      </Box>
                                    )}
                                    {(immunoblotConfig as any).qualityControl.analysisGuide.warnings?.length > 0 && (
                                      <Box>
                                        <Text fontWeight="bold" mb={1} color="orange.700">⚠ Avertissements :</Text>
                                        <VStack as="ul" align="start" spacing={0.5} pl={4} sx={{ listStyleType: 'disc' }}>
                                          {(immunoblotConfig as any).qualityControl.analysisGuide.warnings.map((s: string, i: number) => (
                                            <Box as="li" key={i} color="orange.700">{s}</Box>
                                          ))}
                                        </VStack>
                                      </Box>
                                    )}
                                    {(immunoblotConfig as any).qualityControl.analysisGuide.goodPractice?.length > 0 && (
                                      <Box>
                                        <Text fontWeight="bold" mb={1} color="green.700">✓ Bonnes pratiques :</Text>
                                        <VStack as="ul" align="start" spacing={0.5} pl={4} sx={{ listStyleType: 'disc' }}>
                                          {(immunoblotConfig as any).qualityControl.analysisGuide.goodPractice.map((s: string, i: number) => (
                                            <Box as="li" key={i} color="green.700">{s}</Box>
                                          ))}
                                        </VStack>
                                      </Box>
                                    )}
                                  </VStack>
                                </Collapse>
                              </Box>
                            )}

                            {/* Display identified bands */}
                            {selectedBlotInterpretation!.identifiedBands && selectedBlotInterpretation!.identifiedBands!.length > 0 && (
                              <Box
                                w="full"
                                p={3}
                                bg="gray.50"
                                borderRadius="lg"
                                border="1px solid"
                                borderColor="gray.200"
                              >
                                <HStack justify="space-between" w="full" mb={2}>
                                  <Text fontSize="xs" fontWeight="bold" color="gray.700">
                                    {/* Paired-comparison pathologies (CIP-WB) have no named
                                        bands at all — matchedBand is always null there, so
                                        counting by it always read "(0)" even next to a
                                        POSITIF verdict listing real differences. Count the
                                        differences themselves instead. */}
                                    {pairInfoSelected
                                      ? `Différences détectées (${selectedBlotInterpretation!.identifiedBands!.length})`
                                      : `${t('blotAnalysis.comparison.identifiedBands')} (${selectedBlotInterpretation!.identifiedBands!.filter(b => b.matchedBand !== null).length})`}
                                  </Text>
                                  {(selectedBlotInterpretation as any)?.requiredBandId && (
                                    (selectedBlotInterpretation as any).hasRequiredBand ? (
                                      <Badge bg="green.100" color="green.700" fontSize="xs" px={2} py={0.5} borderRadius="full">
                                        {(selectedBlotInterpretation as any).requiredBandId} présente
                                      </Badge>
                                    ) : selectedBlotInterpretation!.identifiedBands!.some(b => b.matchedBand !== null) ? (
                                      <Badge bg="red.100" color="red.700" fontSize="xs" px={2} py={0.5} borderRadius="full">
                                        {(selectedBlotInterpretation as any).requiredBandId} absente
                                      </Badge>
                                    ) : null
                                  )}
                                </HStack>
                                <HStack spacing={2} flexWrap="wrap">
                                  {pairInfoSelected ? (
                                    // No named bands for this test — one badge per
                                    // difference, by pixel position (all that's known).
                                    selectedBlotInterpretation!.identifiedBands!.map((band, i) => (
                                      <Badge
                                        key={i}
                                        bg="teal.100"
                                        color="teal.700"
                                        fontSize="xs"
                                        px={3}
                                        py={1}
                                        borderRadius="full"
                                      >
                                        Différence à {Math.round(band.position)}px
                                      </Badge>
                                    ))
                                  ) : (() => {
                                    // Group bands by name and show count if multiple
                                    const groupedBands = selectedBlotInterpretation!.identifiedBands!
                                      .filter(band => band.matchedBand !== null)
                                      .reduce((acc: any, band) => {
                                        const name = band.matchedBand!
                                        if (!acc[name]) {
                                          acc[name] = { band, count: 0, avgMW: 0 }
                                        }
                                        acc[name].count++
                                        acc[name].avgMW += band.estimatedMW
                                        return acc
                                      }, {})

                                    return Object.entries(groupedBands).map(([name, data]: [string, any]) => {
                                      const avgMW = data.avgMW / data.count
                                      return (
                                        <Tooltip
                                          key={name}
                                          label={t('blotAnalysis.comparison.peakDetectedTooltip', { count: data.count, avgMW: avgMW.toFixed(1), refMW: data.band.bandMW })}
                                          hasArrow
                                        >
                                          <Badge
                                            bg={REQUIRED_BAND_NAMES.includes(name) ? 'red.100' : 'blue.100'}
                                            color={REQUIRED_BAND_NAMES.includes(name) ? 'red.700' : 'blue.700'}
                                            fontSize="xs"
                                            px={3}
                                            py={1}
                                            borderRadius="full"
                                            cursor="help"
                                            transition="all 0.2s ease"
                                            _hover={{ transform: 'scale(1.05)' }}
                                          >
                                            {name} ({avgMW.toFixed(1)} kDa) {data.count > 1 && `×${data.count}`}
                                          </Badge>
                                        </Tooltip>
                                      )
                                    })
                                  })()}
                                </HStack>
                              </Box>
                            )}
                          </VStack>
                        </Box>
                        )
                      )}
                  </VStack>
                </Flex>
                )}
              </VStack>
              </ScaleFade>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <GuidedTour
        steps={tourSteps}
        stepIndex={tourStep}
        isOpen={tourStep >= 0}
        onNext={tourNext}
        onPrev={tourPrev}
        onClose={endTour}
      />
    </>
  )
}
